<template>
  <!-- ==============================================================
       AccountSetupWidget — MASTER/ADMIN-ONLY account audit.

       A side-edge pill (portaled into App.vue's shared rail) that opens
       a modal summarizing how the SELECTED company's account is set up
       — the Machines / Assemblies / Sub Assemblies / Parts / Wear Parts /
       Companies / Users / Emails our staff have linked in the Admin area —
       plus a "Setup gaps" section flagging obvious blanks. It exists so
       staff can confirm an account was configured correctly.

       Only renders for users with the Master role (self-gated via the
       root v-if), so customers never see it. Data is fetched per-open
       from GET /account/setup-summary?companyId=<selected>.

       ALL CSS is UNSCOPED (the pill is portaled out of this component and
       the modal renders to a body portal — scoped CSS reaches neither).
       Class names are namespaced (.account-setup-*).
       ============================================================== -->
  <div class="account-setup-widget" v-if="isMasterUser">

    <!-- ===== PILL (portaled into the shared rail) ===== -->
    <portal to="account-setup-pill">
      <div class="account-setup-pill"
           @click="openModal"
           role="button"
           tabindex="0"
           @keydown.enter.space.prevent="openModal"
           aria-label="Account setup audit (admin)"
           title="Account setup audit (admin only)">
        <div class="account-setup-pill-icon">
          <svg xmlns="http://www.w3.org/2000/svg"
               viewBox="0 0 24 24"
               width="22" height="22"
               fill="none"
               stroke="currentColor"
               stroke-width="2"
               stroke-linecap="round"
               stroke-linejoin="round"
               aria-hidden="true">
            <path d="M9 2h6a1 1 0 0 1 1 1v1h2a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2V3a1 1 0 0 1 1-1z"/>
            <path d="M9 12l2 2 4-4"/>
          </svg>
        </div>
        <div class="account-setup-pill-label">SETUP</div>
      </div>
    </portal>

    <!-- ===== MODAL ===== -->
    <b-modal id="accountSetupModal"
             v-model="showModal"
             size="lg"
             scrollable
             no-close-on-backdrop
             no-close-on-esc
             hide-footer
             modal-class="account-setup-modal"
             @shown="onSetupShown"
             @hidden="onSetupHidden">
      <template #modal-title>
        <span class="account-setup-modal-title">
          <span class="account-setup-modal-icon" aria-hidden="true">⚙</span>
          Account Setup{{ headerLabel ? ' — ' + headerLabel : '' }}
        </span>
      </template>

      <div class="account-setup-body">
        <!-- Floating jump control — pins at the viewport center while scrolling a
             long category, and jumps to that category's top / bottom. -->
        <div class="account-setup-scrollnav-rail" v-show="scrollNavCat">
          <div class="account-setup-scrollnav">
            <button type="button" class="account-setup-scrollnav-btn"
                    title="Jump to the top of this list" @click="scrollNavUp">↑</button>
            <button type="button" class="account-setup-scrollnav-btn"
                    title="Jump to the bottom of this list" @click="scrollNavDown">↓</button>
          </div>
        </div>
        <!-- Add / edit a user or company — replaces the setup view while open. -->
        <div v-if="editEntity" class="account-setup-editview">
          <button type="button" class="account-setup-back" @click="closeEdit">← Back to setup</button>
          <SetupUserForm v-if="editEntity.kind === 'user'"
                         :entity-id="editEntity.id"
                         :preset-company-id="currentCompanyId"
                         @saved="onEntitySaved"
                         @cancel="closeEdit" />
          <SetupCompanyForm v-else-if="editEntity.kind === 'company'"
                            :entity-id="editEntity.id"
                            @saved="onEntitySaved"
                            @cancel="closeEdit" />
          <SetupObjectForm v-else
                           :kind="editEntity.kind"
                           :entity-id="editEntity.id"
                           :company-id="currentCompanyId"
                           :category="editEntity.catKey"
                           @saved="onEntitySaved"
                           @cancel="closeEdit" />
        </div>

        <p v-if="!editEntity" class="account-setup-lede">
          How this company's account is configured (set up by our staff in the
          Admin area). Admin-only view.
        </p>

        <div v-if="!editEntity && loading" class="account-setup-loading">Loading account setup…</div>

        <div v-else-if="!editEntity && loadError" class="account-setup-loading">
          Couldn't load the account setup — please try again.
        </div>

        <div v-else-if="!editEntity && !setup" class="account-setup-loading">
          Select a company to view its setup.
        </div>

        <template v-else-if="!editEntity">
          <!-- Setup gaps — expandable, collapsed by default -->
          <div v-if="hasAnyGaps" class="account-setup-warnings">
            <button type="button"
                    class="account-setup-warnings-title account-setup-gaps-toggle"
                    @click="gapsOpen = !gapsOpen">
              <span class="account-setup-row-toggle">{{ gapsOpen ? '−' : '+' }}</span>
              <span aria-hidden="true">⚠</span> Setup gaps ({{ gapCount }})
            </button>

            <div v-if="gapsOpen" class="account-setup-gaps-body">
              <!-- Active text gaps -->
              <ul v-if="activeGaps.length" class="account-setup-warnings-list">
                <li v-for="g in activeGaps" :key="'g-' + g.key" class="account-setup-gap-item">
                  <span class="account-setup-gap-text">{{ g.text }}</span>
                  <span class="account-setup-gap-actions">
                    <button type="button" class="account-setup-gap-hide"
                            title="Hide for now (comes back on reload)"
                            @click="hideGap(g.key)">Hide</button>
                    <button type="button" class="account-setup-gap-ok"
                            title="Looks good — move to the Setup Gap history (restorable)"
                            :disabled="gapAckBusy === g.key"
                            @click="looksGood(g.key)">Looks Good</button>
                  </span>
                </li>
              </ul>

              <!-- Active machine-with-no-assemblies gaps (kept-detail cards) -->
              <div v-if="activeBareMachines.length" class="account-setup-gapfix">
                <div v-for="m in activeBareMachines" :key="'bm-' + m.id" class="account-setup-gapfix-item">
                  <div class="account-setup-gapfix-row">
                    <span class="account-setup-gapfix-name">
                      {{ (m.code ? m.code + ' — ' : '') + (m.label || '') }} — no assemblies
                    </span>
                    <span class="account-setup-gap-actions">
                      <button type="button" class="account-setup-gapfix-btn" @click="diagnoseGap(m)">
                        {{ gapBusy === m.id ? 'Checking…' : (gapData[m.id] ? 'Hide details' : 'Details') }}
                      </button>
                      <button type="button" class="account-setup-gap-hide"
                              title="Hide for now (comes back on reload)"
                              @click="hideGap(bmKey(m))">Hide</button>
                      <button type="button" class="account-setup-gap-ok"
                              title="Looks good — move to the Setup Gap history (restorable)"
                              :disabled="gapAckBusy === bmKey(m)"
                              @click="looksGood(bmKey(m))">Looks Good</button>
                    </span>
                  </div>

                  <div v-if="gapData[m.id]" class="account-setup-gapfix-detail">
                    <p class="account-setup-gapfix-line">
                      No assemblies are linked to this machine in the catalog<template
                        v-if="gapData[m.id].sharedCompanies > 1"> — and it's shared by
                        {{ gapData[m.id].sharedCompanies }} companies (assemblies are common to all of them)</template>.
                    </p>
                    <template v-if="(gapData[m.id].similarWithAssemblies || []).length">
                      <p class="account-setup-gapfix-sub">
                        Similar “{{ gapData[m.id].typePrefix }}” machines that already have
                        assemblies — use one as a template:
                      </p>
                      <ul class="account-setup-gapfix-similar">
                        <li v-for="s in gapData[m.id].similarWithAssemblies" :key="'sim-' + s.id">
                          <span class="account-setup-gapfix-simname">{{ (s.code ? s.code + ' — ' : '') + s.label }}</span>
                          <span class="account-setup-chip">{{ s.assemblyCount }} assemblies</span>
                        </li>
                      </ul>
                    </template>
                    <p v-else class="account-setup-gapfix-sub">
                      No similar machines have assemblies yet — this type likely needs its
                      assemblies built from scratch.
                    </p>
                  </div>
                </div>
              </div>

              <div v-if="gapCount === 0" class="account-setup-ok account-setup-ok-inline">
                <span aria-hidden="true">✓</span> No active setup gaps — all acknowledged or hidden.
              </div>

              <!-- Restorable history of acknowledged ("Looks Good") gaps -->
              <div v-if="gapHistoryCount" class="account-setup-gaphist">
                <button type="button" class="account-setup-gaphist-toggle" @click="gapHistoryOpen = !gapHistoryOpen">
                  <span class="account-setup-row-toggle">{{ gapHistoryOpen ? '−' : '+' }}</span>
                  Setup gap history ({{ gapHistoryCount }})
                </button>
                <ul v-if="gapHistoryOpen" class="account-setup-warnings-list">
                  <li v-for="g in historyGaps" :key="'gh-' + g.key" class="account-setup-gap-item is-acked">
                    <span class="account-setup-gap-text">{{ g.text }}</span>
                    <button type="button" class="account-setup-gap-restore"
                            title="Restore this as an active setup gap"
                            :disabled="gapAckBusy === g.key"
                            @click="restoreGap(g.key)">Restore</button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div v-else class="account-setup-ok">
            <span aria-hidden="true">✓</span> No obvious setup gaps detected.
          </div>

          <!-- Categories -->
          <ul class="account-setup-list">
            <li v-for="cat in categories"
                :key="cat.key"
                :id="'asc-cat-' + cat.key"
                class="account-setup-item">
              <button type="button"
                      class="account-setup-row"
                      :class="{ 'is-open': !!open[cat.key], 'is-zero': !cat.count && !cat.editType }"
                      :disabled="!cat.count && !cat.editType"
                      @click="(cat.count || cat.editType) && onCatToggle(cat)">
                <span class="account-setup-row-toggle">
                  <template v-if="cat.count || cat.editType">{{ open[cat.key] ? '−' : '+' }}</template>
                </span>
                <span class="account-setup-row-label">{{ cat.label }}</span>
                <span class="account-setup-row-join" :title="cat.joinHint">{{ cat.joinHint }}</span>
                <span class="account-setup-row-count" :class="{ 'is-zero': !cat.count }">
                  {{ cat.count }}
                </span>
              </button>

              <ul v-if="open[cat.key] && (cat.items.length || cat.editType || cat.partLink)"
                  class="account-setup-detail">
                <!-- Add a brand-new object in this category (admin) -->
                <li v-if="canCreate(cat)" class="account-setup-addnew">
                  <button type="button" class="account-setup-addnew-btn"
                          @click="openAddObject(cat)">+ Add new {{ createNoun(cat) }}</button>
                </li>
                <!-- Jump straight to this category's history (long lists only) -->
                <li v-if="showListNav(cat)" class="account-setup-jumpbar">
                  <button type="button"
                          class="account-setup-jumplink"
                          @click="scrollToBottom(cat)">
                    Jump to bottom ↓
                  </button>
                </li>
                <!-- Add row — per-company editable categories only -->
                <li v-if="cat.editType" class="account-setup-add">
                  <!-- Users: scope toggle — this company vs all companies -->
                  <div v-if="cat.editType === 'user'" class="account-setup-scope">
                    <span class="account-setup-scope-label">Show</span>
                    <button type="button" class="account-setup-scope-btn"
                            :class="{ 'is-active': usersScope === 'company' }"
                            @click="setUsersScope('company')">This company</button>
                    <button type="button" class="account-setup-scope-btn"
                            :class="{ 'is-active': usersScope === 'all' }"
                            @click="setUsersScope('all')">All companies</button>
                    <!-- Accounts still awaiting the "Approved" tick in the user form. Searches EVERY
                         company: a pending account is an admin to-do wherever it lives, and scoping it
                         to the current company would hide most of them. -->
                    <button type="button" class="account-setup-scope-btn"
                            :class="{ 'is-active': usersScope === 'pending' }"
                            title="Users whose account has not been approved yet"
                            @click="setUsersScope('pending')">Pending Approval</button>
                  </div>
                  <input type="text"
                         class="account-setup-add-input"
                         :placeholder="'Search a ' + addNoun(cat) + ' to add…'"
                         :value="addTerm[cat.key] || ''"
                         @input="onAddInput(cat, $event)" />
                  <ul v-if="addableResults(cat).length"
                      class="account-setup-add-results">
                    <!-- Wear: rich result + red Add button (matches the Parts search) -->
                    <template v-if="cat.editType === 'wear'">
                      <li v-for="r in addableResults(cat)"
                          :key="cat.key + '-add-' + r.refId"
                          class="account-setup-pl-result">
                        <div class="account-setup-pl-info">
                          <span class="account-setup-pl-part">
                            {{ r.label || r.code }}
                            <span v-if="r.code" class="account-setup-chip">{{ r.code }}</span>
                            <span class="account-setup-chip is-id">ID {{ r.partId || r.refId }}</span>
                          </span>
                          <span v-if="r.assemblyName" class="account-setup-pl-assembly">
                            assembly: {{ r.assemblyName }}
                          </span>
                          <span class="account-setup-pl-machine"
                                :class="{ 'is-missing': !r.machineCode && !r.machineLabel }">
                            <template v-if="r.machineCode || r.machineLabel">
                              machine: {{ (r.machineCode ? r.machineCode + ' — ' : '') + (r.machineLabel || '') }}
                            </template>
                            <template v-else>⚠ no machine</template>
                          </span>
                        </div>
                        <button type="button"
                                class="account-setup-pl-btn is-link"
                                @click="addLink(cat, r)">Add</button>
                      </li>
                    </template>
                    <!-- Machine / User: simple result -->
                    <template v-else>
                      <li v-for="r in addableResults(cat)"
                          :key="cat.key + '-add-' + (r.refUser || r.refId)"
                          class="account-setup-add-result"
                          @click="addLink(cat, r)">
                        <span class="account-setup-add-result-label">{{ r.label || r.code }}</span>
                        <span v-if="r.code && r.code !== r.label" class="account-setup-chip">{{ r.code }}</span>
                        <span class="account-setup-add-plus">+ link</span>
                      </li>
                    </template>
                  </ul>
                  <div v-else-if="addBusy[cat.key]" class="account-setup-add-hint">Searching…</div>
                  <div v-else-if="(addTerm[cat.key] || '').length && !searchHitCount(cat)" class="account-setup-add-hint">
                    No matches to add.
                  </div>
                  <div v-if="hiddenAddCount(cat) > 0" class="account-setup-add-hint">
                    {{ hiddenAddCount(cat) }} {{ addNoun(cat) }}{{ hiddenAddCount(cat) > 1 ? 's' : '' }} hidden — already on this account by code (adding would create a duplicate-code setup gap).
                  </div>
                  <div v-if="isSearchActive(cat)"
                       class="account-setup-search-status"
                       :class="searchHitCount(cat) > 0 ? 'is-found' : 'is-notfound'">
                    <template v-if="searchHitCount(cat) > 0">✓ Found ({{ searchHitCount(cat) }}) on this account — highlighted below</template>
                    <template v-else>Not found on this account</template>
                  </div>
                  <!-- Add a brand-new user account (Users only) — opens the full form -->
                  <div v-if="cat.editType === 'user'" class="account-setup-newuser">
                    <button type="button"
                            class="account-setup-newuser-toggle"
                            @click="openAddUser">+ Add a new user for this account</button>
                  </div>
                </li>

                <!-- Add a brand-new company (Companies only) — opens the full form -->
                <li v-if="cat.key === 'companies'" class="account-setup-newuser">
                  <button type="button"
                          class="account-setup-newuser-toggle"
                          @click="openAddCompany">+ Add a new company</button>
                </li>

                <!-- Wear only: bulk "Delete all" for THIS company (per-company). -->
                <li v-if="cat.delType === 'wear' && cat.count > 0"
                    class="account-setup-deleteall">
                  <div class="account-setup-deleteall-bar">
                    <button type="button"
                            class="account-setup-deleteall-btn"
                            @click="openWearDeleteAll(cat)">Delete all</button>
                  </div>
                  <div v-if="wearDeleteConfirm === cat.key" class="account-setup-deleteall-panel">
                    <div class="account-setup-deleteall-msg">
                      Delete all {{ cat.count }} wear parts for
                      {{ headerLabel || (setup && setup.companyCode) }}? This removes ONLY
                      this company's wear parts — other customers are unaffected.
                    </div>
                    <div class="account-setup-deleteall-note">
                      Soft delete is reversible from the delete history below.
                    </div>
                    <div class="account-setup-deleteall-actions">
                      <button type="button"
                              class="account-setup-deleteall-soft"
                              :disabled="wearDeleteBusy"
                              @click="confirmWearDeleteAll(false)">
                        {{ wearDeleteBusy ? 'Working…' : 'Soft delete' }}
                      </button>
                      <button type="button"
                              class="account-setup-deleteall-perm"
                              :disabled="wearDeleteBusy"
                              @click="confirmWearDeleteAll(true)">Permanently delete</button>
                      <button type="button"
                              class="account-setup-deleteall-cancel"
                              :disabled="wearDeleteBusy"
                              @click="cancelWearDeleteAll()">Cancel</button>
                    </div>
                  </div>
                </li>

                <!-- Parts: search any part -> Link/Unlink the MACHINE it sits under -->
                <li v-if="cat.partLink" class="account-setup-add">
                  <input type="text"
                         class="account-setup-add-input"
                         :placeholder="cat.key === 'parts' ? 'Search a part by name, ID or Code…' : 'Search an assembly by name or ID…'"
                         :value="partTerm[cat.key] || ''"
                         @input="onPartInput(cat, $event)" />
                  <ul v-if="(partResults[cat.key] || []).length" class="account-setup-add-results">
                    <li v-for="r in partResults[cat.key]"
                        :key="cat.key + '-pl-' + r.partId"
                        class="account-setup-pl-result">
                      <div class="account-setup-pl-info">
                        <span class="account-setup-pl-part">
                          {{ r.partLabel || r.partCode }}
                          <span v-if="r.partCode" class="account-setup-chip">{{ r.partCode }}</span>
                          <span class="account-setup-chip is-id">ID {{ r.partId }}</span>
                          <span v-if="r.linked" class="account-setup-pl-linked" title="Linked to this company" aria-label="Linked">✓</span>
                        </span>
                        <span v-if="r.assemblyName" class="account-setup-pl-assembly">
                          assembly: {{ r.assemblyName }}
                        </span>
                        <span class="account-setup-pl-machine" :class="{ 'is-missing': !r.machineId }">
                          <template v-if="r.machineId">
                            machine: {{ (r.machineCode ? r.machineCode + ' — ' : '') + (r.machineLabel || '') }}
                          </template>
                          <template v-else>⚠ part has no machine — not reachable by any customer</template>
                        </span>
                      </div>
                      <button v-if="r.machineId && !r.linked"
                              type="button"
                              class="account-setup-pl-btn is-link"
                              :disabled="partBusyKey === ('p' + r.partId)"
                              @click="linkPartMachine(cat, r)">Link machine</button>
                      <span v-else-if="r.machineId && r.linked"
                            class="account-setup-pl-onaccount"
                            title="This machine is already on the account. To remove: use the × on a row to deactivate an assembly, or the Machines list to unlink the whole machine.">✓ on account</span>
                    </li>
                  </ul>
                  <div v-else-if="partBusy[cat.key]" class="account-setup-add-hint">Searching…</div>
                  <div v-else-if="(partTerm[cat.key] || '').length" class="account-setup-add-hint">
                    No matching {{ cat.key === 'parts' ? 'parts' : 'assemblies' }}.
                  </div>
                </li>

                <!-- Parts / Wear: Part ID · Part Code · Part Description columns -->
                <template v-if="cat.tableMode">
                  <li class="account-setup-thead">
                    <span class="asc-col-id asc-id-head">
                      Part ID
                      <span class="asc-dots">
                        <span class="asc-dot asc-dot-green"
                              :class="{ 'is-active': colorFilter[cat.key] === 'green' }"
                              :title="colorCount(cat, 'green') + ' green — machine + customers (click to filter)'"
                              @click="toggleColorFilter(cat, 'green')"></span>
                        <span class="asc-dot asc-dot-orange"
                              :class="{ 'is-active': colorFilter[cat.key] === 'orange' }"
                              :title="colorCount(cat, 'orange') + ' orange — partially linked (click to filter)'"
                              @click="toggleColorFilter(cat, 'orange')"></span>
                        <span class="asc-dot asc-dot-red"
                              :class="{ 'is-active': colorFilter[cat.key] === 'red' }"
                              :title="colorCount(cat, 'red') + ' red — orphaned (click to filter)'"
                              @click="toggleColorFilter(cat, 'red')"></span>
                      </span>
                    </span>
                    <span class="asc-col-arrow">→</span>
                    <span class="asc-col-asmcode">Assembly ID / Code</span>
                    <span class="asc-col-code">Part Code</span>
                    <span class="asc-col-desc">Part Description</span>
                    <span v-if="cat.delType" class="asc-col-x"></span>
                  </li>
                  <template v-for="(it, i) in filteredItems(cat)">
                    <li :key="cat.key + '-' + i"
                        class="account-setup-trow account-setup-trow-click"
                        :class="{ 'is-deleted': isDeleted(cat, it), 'account-setup-search-hit': isSearchActive(cat) }"
                        title="Click to see linked customers & machines"
                        @click="toggleImpact(cat, it)">
                      <span class="asc-col-id" :class="idColorClass(it)">{{ itemId(it) || '—' }}</span>
                      <span class="asc-col-arrow">→</span>
                      <span class="asc-col-asmcode">{{ it.assemblyId ? (it.assemblyId + (it.assembly ? ' / ' + it.assembly : '')) : '—' }}</span>
                      <span class="asc-col-code">{{ itemCode(it) || '—' }}</span>
                      <span class="asc-col-desc">
                        <button type="button" class="account-setup-edit-link"
                                :title="'Edit this ' + (cat.delType === 'wear' ? 'wear part' : 'part')"
                                @click.stop="openEditObject(cat, it)"><span class="asc-desc-main">{{ itemLabel(it) }}</span> <span class="account-setup-edit-pencil">✎</span></button>
                        <span v-if="it.item || itemMachine(it) || itemAssembly(it)" class="asc-sub"><template v-if="it.item">item: <strong>{{ it.item }}</strong></template><template v-if="it.item && (itemMachine(it) || itemAssembly(it))"> · </template><template v-if="itemMachine(it)">machine: {{ itemMachine(it) }}</template><template v-if="itemMachine(it) && itemAssembly(it)"> · </template><template v-if="itemAssembly(it)">assembly: {{ itemAssembly(it) }}</template></span>
                      </span>
                      <template v-if="cat.delType">
                        <button v-if="isDeleted(cat, it)"
                                type="button"
                                class="account-setup-undo-btn asc-col-x"
                                :disabled="delBusyKey === delRowKey(cat, it)"
                                title="Restore"
                                @click.stop="undoDelete(cat, it)">↺</button>
                        <button v-else
                                type="button"
                                class="account-setup-remove asc-col-x"
                                :disabled="delBusyKey === delRowKey(cat, it)"
                                :title="cat.delType === 'part' ? 'Deactivate (global — affects all customers)' : 'Remove from this company'"
                                @click.stop="deleteRow(cat, it)">×</button>
                      </template>
                    </li>
                    <li v-if="impactOpen[delRowKey(cat, it)]"
                        :key="cat.key + '-imp-' + i"
                        class="account-setup-impact">
                      <div v-if="impactBusy === itemId(it)" class="account-setup-add-hint">
                        Loading linked customers &amp; machines…
                      </div>
                      <template v-else>
                        <div v-if="cat.delType === 'wear'" class="account-setup-impact-note">
                          This wear part belongs to <strong>{{ headerLabel || 'this company' }}</strong> only — removing it won't affect the other companies below (they just have the same machine).
                        </div>
                        <div class="account-setup-impact-row">
                          <span class="account-setup-impact-label">Machines ({{ impactCount(it, 'm') }})</span>
                          <span class="account-setup-impact-vals">{{ impactMachines(it) }}</span>
                        </div>
                        <div class="account-setup-impact-row">
                          <span class="account-setup-impact-label">{{ cat.delType === 'wear' ? 'Companies with this machine' : 'Customers' }} ({{ impactCount(it, 'c') }})</span>
                          <span class="account-setup-impact-vals">
                            <template v-if="impactCustOpen[delRowKey(cat, it)] || impactCount(it, 'c') <= 8">{{ impactCompanies(it) }}</template>
                            <button v-else type="button" class="account-setup-impact-viewall" @click.stop="expandImpactCompanies(cat, it)">View all {{ impactCount(it, 'c') }} companies</button>
                          </span>
                        </div>
                      </template>
                    </li>
                  </template>
                  <li v-if="(colorFilter[cat.key] === 'orange' || colorFilter[cat.key] === 'red') && gaps[cat.key] === undefined"
                      class="account-setup-add-hint">
                    Scanning all {{ cat.count }} {{ cat.delType === 'wear' ? 'wear parts' : 'parts' }} for gaps…
                  </li>
                  <li v-else-if="(colorFilter[cat.key] === 'orange' || colorFilter[cat.key] === 'red') && !filteredItems(cat).length"
                      class="account-setup-add-hint">
                    No {{ colorFilter[cat.key] }} {{ cat.delType === 'wear' ? 'wear parts' : 'parts' }} — every one reaches a machine with a customer ✓
                  </li>
                </template>
                <!-- Users: ID · Name · Email · Link; each row expands to companies / password / deactivate -->
                <template v-else-if="cat.key === 'users'">
                  <li v-if="cat.items.length" class="account-setup-thead account-setup-uhead">
                    <span class="asc-col-gap"></span>
                    <span class="asc-col-uexp"></span>
                    <span class="asc-col-id asc-col-id-user">ID</span>
                    <span class="asc-col-uname">Name</span>
                    <span class="asc-col-uemail">Email</span>
                    <span class="asc-col-ucreated">Created</span>
                    <span class="asc-col-uappr">Approve</span>
                    <span class="account-setup-uactions-head">Manage</span>
                  </li>
                  <template v-for="(it, i) in filteredItems(cat)">
                    <li :key="'u-' + i"
                        class="account-setup-trow account-setup-urow"
                        :class="{ 'account-setup-search-hit': isSearchActive(cat), 'is-uopen': !!userExpanded[it.ref] }">
                      <span class="asc-col-gap"><span v-if="isGapRow(cat, it)" class="account-setup-gapwarn" title="Part of an active setup gap — see Setup Gaps">⚠</span></span>
                      <button type="button" class="asc-col-uexp account-setup-uexp"
                              :title="userExpanded[it.ref] ? 'Collapse' : 'Expand — companies, password, deactivate'"
                              @click="toggleUserExpand(it)">{{ userExpanded[it.ref] ? '−' : '+' }}</button>
                      <span class="asc-col-id asc-col-id-user">
                        <span class="account-setup-id-short" :title="it.ref || ''">{{ shortId(it.ref) }}</span>
                        <button v-if="it.ref" type="button" class="account-setup-copy"
                                :title="copiedKey === it.ref ? 'Copied!' : 'Copy full ID to clipboard'"
                                @click="copyId(it.ref)">{{ copiedKey === it.ref ? '✓' : '⧉' }}</button>
                      </span>
                      <span class="asc-col-uname">{{ userName(it) || '(no name)' }}</span>
                      <span class="asc-col-uemail">{{ itemCode(it) || '—' }}</span>
                      <span class="asc-col-ucreated" :title="it.created || ''">{{ fmtCreated(it.created) }}</span>
                      <!-- Approve shortcut: writes ONLY the approval flag, no need to open the user.
                           Optimistic — the row flips at once and reverts if the server refuses (e.g. a
                           non-helpdesk Master trying to approve an internal @jcsteele.com account).
                           It's a column of its own rather than part of Manage so it never crowds the
                           destructive ✕, and so its position holds whether or not Unlink is showing. -->
                      <span class="asc-col-uappr">
                        <label v-if="it.ref" class="account-setup-appr"
                               :title="it.approved ? 'Approved — click to set back to pending' : 'Approve this account'">
                          <input type="checkbox" :checked="!!it.approved"
                                 :disabled="approvingKey === it.ref"
                                 @change="askApprove(it, $event)" />
                          <span>{{ it.approved ? 'Approved' : 'Approve' }}</span>
                        </label>
                      </span>
                      <span class="account-setup-uactions">
                        <button v-if="canRemove(it) && !usersPwFilter" type="button"
                                class="account-setup-mini-btn is-unlink"
                                title="Unlink this user from the company you're viewing (recorded in history, undoable)"
                                :disabled="removingKey === ('users:' + it.ref)"
                                @click="unlinkUserRow(it)">Unlink</button>
                        <button type="button"
                                class="account-setup-remove account-setup-udel"
                                title="Deactivate this user account (Inactive everywhere) — recorded, reversible"
                                :disabled="removingKey === ('udel:' + it.ref)"
                                @click="softDeleteUser(it)">×</button>
                        <button v-if="it.ref" type="button"
                                class="account-setup-linkbtn"
                                :class="{ 'is-open': linkUserRef === it.ref }"
                                :title="linkUserRef === it.ref ? 'Close' : 'Link this user to another company'"
                                @click="openLinkUser(it)">{{ linkUserRef === it.ref ? 'Close' : 'Link' }}</button>
                      </span>
                    </li>

                    <!-- Expanded: password features, companies table, Unlink + deactivate -->
                    <li v-if="userExpanded[it.ref]" :key="'ux-' + i" class="account-setup-udetail">
                      <div class="account-setup-udetail-head">
                        <button type="button" class="account-setup-mini-btn" title="Edit this user's full details" @click="openEditUser(it)">Edit ✎</button>
                      </div>
                      <ul class="account-setup-ucomp">
                        <li class="account-setup-ucomp-head">
                          <span class="asc-uc-id">Company ID</span>
                          <span class="asc-uc-name">Company Name</span>
                          <span class="asc-uc-title">Title</span>
                          <span class="asc-uc-pw">Password</span>
                        </li>
                        <li v-if="userCompaniesBusy[it.ref]" class="account-setup-add-hint">Loading companies…</li>
                        <li v-for="(c, ci) in (userCompanies[it.ref] || [])" :key="'uc-' + i + '-' + ci" class="account-setup-ucomp-row">
                          <span class="asc-uc-id">{{ c.id }}</span>
                          <span class="asc-uc-name">{{ c.name || c.code || '—' }}</span>
                          <span class="asc-uc-title">{{ ci === 0 ? (userTitle(it) || '—') : '' }}</span>
                          <span class="asc-uc-pw">
                            <template v-if="ci === 0">
                              <template v-if="it.hasPassword">
                                <span class="account-setup-pw-lock" title="Encrypted — an admin can retrieve this password by email">🔒</span>
                                <button type="button" class="account-setup-pw-eye" :disabled="pwBusyKey === it.ref"
                                        :title="pwSent[it.ref] ? 'Emailed to the help desk — click to resend' : 'Email this password to helpdesk@jcsteele.com'"
                                        @click="requestPasswordHelp(it)">{{ pwSent[it.ref] ? '✓' : '✉' }}</button>
                              </template>
                              <template v-else>
                                <span class="account-setup-pw-mask">••••</span>
                                <button type="button" class="account-setup-pw-eye account-setup-pw-eye-reset" :disabled="pwBusyKey === it.ref"
                                        :title="pwSent[it.ref] ? 'New password emailed — click to resend' : 'No recoverable password yet — reset it and email the new one (makes it viewable)'"
                                        @click="requestPasswordHelp(it)">{{ pwSent[it.ref] ? '✓' : '👁' }}</button>
                              </template>
                              <button type="button" class="account-setup-pw-eye account-setup-pw-link"
                                      :disabled="pwBusyKey === ('link:' + it.ref)"
                                      :title="setupLinkSent[it.ref] ? 'Set-password link re-sent — click to resend' : 'Resend the set-password (login) link to the user'"
                                      @click="resendSetupLink(it)">{{ setupLinkSent[it.ref] ? '✓' : '🔑' }}</button>
                            </template>
                          </span>
                        </li>
                        <li v-if="!userCompaniesBusy[it.ref] && !(userCompanies[it.ref] || []).length" class="account-setup-ucomp-row">
                          <span class="asc-uc-id">—</span>
                          <span class="asc-uc-name">Not linked to any company</span>
                          <span class="asc-uc-title">{{ userTitle(it) || '—' }}</span>
                          <span class="asc-uc-pw">
                            <template v-if="it.hasPassword">
                              <span class="account-setup-pw-lock" title="Encrypted — retrievable by email">🔒</span>
                              <button type="button" class="account-setup-pw-eye" :disabled="pwBusyKey === it.ref"
                                      :title="pwSent[it.ref] ? 'Emailed — click to resend' : 'Email this password to helpdesk@jcsteele.com'"
                                      @click="requestPasswordHelp(it)">{{ pwSent[it.ref] ? '✓' : '✉' }}</button>
                            </template>
                            <template v-else>
                              <span class="account-setup-pw-mask">••••</span>
                              <button type="button" class="account-setup-pw-eye account-setup-pw-eye-reset" :disabled="pwBusyKey === it.ref"
                                      :title="pwSent[it.ref] ? 'New password emailed — click to resend' : 'No recoverable password — reset and email (makes it viewable)'"
                                      @click="requestPasswordHelp(it)">{{ pwSent[it.ref] ? '✓' : '👁' }}</button>
                            </template>
                            <button type="button" class="account-setup-pw-eye account-setup-pw-link"
                                    :disabled="pwBusyKey === ('link:' + it.ref)"
                                    :title="setupLinkSent[it.ref] ? 'Set-password link re-sent — click to resend' : 'Resend the set-password (login) link to the user'"
                                    @click="resendSetupLink(it)">{{ setupLinkSent[it.ref] ? '✓' : '🔑' }}</button>
                          </span>
                        </li>
                      </ul>
                    </li>

                    <!-- Link-to-another-company search panel (opened by the Link button) -->
                    <li v-if="linkUserRef === it.ref" :key="'ulp-' + i" class="account-setup-linkpanel">
                      <div class="account-setup-linkpanel-head">
                        Link <strong>{{ linkUser.label || linkUser.code || 'this user' }}</strong> to another company
                      </div>
                      <input type="text" class="account-setup-add-input"
                             placeholder="Search companies by ID or name…"
                             :value="linkTerm" @input="onLinkCompanyInput($event)" />
                      <ul v-if="linkResults.length" class="account-setup-add-results">
                        <li v-for="c in linkResults" :key="'lc-' + c.id"
                            class="account-setup-add-result account-setup-link-result"
                            :class="{ 'is-linked': isUserLinkedTo(c) }"
                            @click="onLinkResultClick(c)">
                          <span class="account-setup-add-result-code">{{ c.id }} · {{ c.code }}</span>
                          <span class="account-setup-add-result-label">{{ c.name }}</span>
                          <span v-if="isUserLinkedTo(c)" class="account-setup-link-unlink">Unlink</span>
                          <span v-else class="account-setup-link-add">+ Link</span>
                        </li>
                      </ul>
                      <div v-else-if="linkTerm" class="account-setup-link-empty">No companies match “{{ linkTerm }}”.</div>
                      <div v-if="linkMsg" class="account-setup-link-msg" :class="{ 'is-err': linkErr }">{{ linkMsg }}</div>
                    </li>
                  </template>
                </template>
                <!-- Other categories: same ID · Code · Description columns as Parts/Wear -->
                <template v-else>
                  <li v-if="cat.items.length" class="account-setup-thead">
                    <span class="asc-col-gap"></span>
                    <span class="asc-col-id"
                          :class="{ 'asc-col-id-user': cat.key === 'users' }">ID</span>
                    <span v-if="catFlowArrow(cat)" class="asc-col-arrow">→</span>
                    <template v-if="cat.key === 'subAssemblies'">
                      <span class="asc-col-asmid">Assembly ID</span>
                      <span class="asc-col-arrow">→</span>
                    </template>
                    <span v-else-if="cat.key === 'users'" class="asc-col-asmid">Company ID</span>
                    <span class="asc-col-code">{{ cat.cols.code }}</span>
                    <span class="asc-col-desc">{{ cat.cols.desc }}</span>
                    <span v-if="cat.key === 'users'" class="asc-col-pw">Password</span>
                    <span v-if="cat.key === 'users'" class="asc-col-unlink">Unlink</span>
                    <span v-else-if="cat.editType || cat.partLink" class="asc-col-x"></span>
                    <span v-if="cat.key === 'users'" class="asc-col-link">Link</span>
                  </li>
                  <template v-for="(it, i) in filteredItems(cat)">
                  <li :key="cat.key + '-' + i"
                      class="account-setup-trow"
                      :class="{ 'account-setup-search-hit': isSearchActive(cat) }">
                    <span class="asc-col-gap"><span v-if="isGapRow(cat, it)" class="account-setup-gapwarn" title="Part of an active setup gap — see Setup Gaps">⚠</span></span>
                    <!-- Users carry a long GUID id: show the first 4 chars + a copy-to-clipboard icon. -->
                    <span v-if="cat.key === 'users'" class="asc-col-id asc-col-id-user">
                      <span class="account-setup-id-short" :title="it.ref || ''">{{ shortId(it.ref) }}</span>
                      <button v-if="it.ref"
                              type="button"
                              class="account-setup-copy"
                              :title="copiedKey === it.ref ? 'Copied!' : 'Copy full ID to clipboard'"
                              @click="copyId(it.ref)">{{ copiedKey === it.ref ? '✓' : '⧉' }}</button>
                    </span>
                    <span v-else
                          class="asc-col-id"
                          :title="String(itemId(it) || it.ref || '')">{{ itemId(it) || it.ref || '—' }}</span>
                    <span v-if="catFlowArrow(cat)" class="asc-col-arrow">→</span>
                    <template v-if="cat.key === 'subAssemblies'">
                      <span class="asc-col-asmid">{{ it.assemblyId || '—' }}</span>
                      <span class="asc-col-arrow">→</span>
                    </template>
                    <span v-else-if="cat.key === 'users'" class="asc-col-asmid">{{ (it.companyId || currentCompanyId) || '—' }}</span>
                    <span class="asc-col-code">{{ (cat.partLink ? machineCodeId(it) : itemCode(it)) || '—' }}</span>
                    <span class="asc-col-desc">
                      <button v-if="cat.key === 'users'" type="button" class="account-setup-edit-link"
                              title="Edit this user's details" @click="openEditUser(it)">{{ itemLabel(it) || '(no name)' }} <span class="account-setup-edit-pencil">✎</span></button>
                      <button v-else-if="cat.key === 'companies'" type="button" class="account-setup-edit-link"
                              title="Edit this company's details" @click="openEditCompany(it)">{{ itemLabel(it) || '(no name)' }} <span class="account-setup-edit-pencil">✎</span></button>
                      <button v-else-if="canEditRow(cat)" type="button" class="account-setup-edit-link"
                              :title="'Edit this ' + createNoun(cat)" @click="openEditObject(cat, it)">{{ itemLabel(it) || '(unnamed)' }} <span class="account-setup-edit-pencil">✎</span></button>
                      <span v-else class="asc-desc-main">{{ itemLabel(it) }}</span>
                      <span v-if="cat.partLink && it.machine" class="account-setup-asm-machine">machine: {{ it.machine }}</span>
                    </span>
                    <span v-if="cat.key === 'users'" class="asc-col-pw">
                      <template v-if="it.hasPassword">
                        <span class="account-setup-pw-lock"
                              title="Encrypted — an admin can retrieve this password by email">🔒</span>
                        <button type="button"
                                class="account-setup-pw-eye"
                                :disabled="pwBusyKey === it.ref"
                                :title="pwSent[it.ref] ? 'Password emailed to the help desk — click to resend' : 'Email this password to helpdesk@jcsteele.com'"
                                @click="requestPasswordHelp(it)">{{ pwSent[it.ref] ? '✓' : '✉' }}</button>
                      </template>
                      <template v-else>
                        <span class="account-setup-pw-mask"
                              title="No recoverable password on file yet">••••</span>
                        <button type="button"
                                class="account-setup-pw-eye account-setup-pw-eye-reset"
                                :disabled="pwBusyKey === it.ref"
                                :title="pwSent[it.ref] ? 'New password set and emailed to the help desk — click to resend' : 'No recoverable password yet — reset it and email the new one to the help desk (makes it viewable from now on)'"
                                @click="requestPasswordHelp(it)">{{ pwSent[it.ref] ? '✓' : '👁' }}</button>
                      </template>
                    </span>
                    <template v-if="cat.editType">
                      <button v-if="cat.key === 'users' && canRemove(it) && !usersPwFilter"
                              type="button"
                              class="account-setup-unlinkbtn asc-col-unlink"
                              title="Unlink this user from this company (recorded in history, undoable)"
                              :disabled="removingKey === ('users:' + it.ref)"
                              @click="unlinkUserRow(it)">Unlink</button>
                      <span v-else-if="cat.key === 'users'" class="asc-col-unlink"></span>
                      <button v-else-if="canRemove(it)"
                              type="button"
                              class="account-setup-remove asc-col-x"
                              title="Remove from this company"
                              :disabled="removingKey === rowKey(cat, it)"
                              @click="removeLink(cat, it)">×</button>
                      <span v-else class="asc-col-x"></span>
                    </template>
                    <template v-else-if="cat.partLink">
                      <button v-if="it.id"
                              type="button"
                              class="account-setup-remove asc-col-x"
                              title="Deactivate for this account (hides it from this customer's ordering)"
                              :disabled="removingKey === ('asmrow:' + it.id)"
                              @click="deactivateAssemblyRow(cat, it)">×</button>
                      <span v-else class="asc-col-x"></span>
                    </template>
                    <!-- Users: link this user to another company (search by id / name). -->
                    <button v-if="cat.key === 'users' && it.ref"
                            type="button"
                            class="account-setup-linkbtn asc-col-link"
                            :class="{ 'is-open': linkUserRef === it.ref }"
                            :title="linkUserRef === it.ref ? 'Close' : 'Link this user to another company'"
                            @click="openLinkUser(it)">{{ linkUserRef === it.ref ? 'Close' : 'Link' }}</button>
                  </li>
                  <li v-if="cat.key === 'users' && linkUserRef === it.ref"
                      :key="cat.key + '-linkpanel-' + i"
                      class="account-setup-linkpanel">
                    <div class="account-setup-linkpanel-head">
                      Link <strong>{{ linkUser.label || linkUser.code || 'this user' }}</strong> to another company
                    </div>
                    <input type="text"
                           class="account-setup-add-input"
                           placeholder="Search companies by ID or name…"
                           :value="linkTerm"
                           @input="onLinkCompanyInput($event)" />
                    <ul v-if="linkResults.length" class="account-setup-add-results">
                      <li v-for="c in linkResults"
                          :key="'lc-' + c.id"
                          class="account-setup-add-result account-setup-link-result"
                          :class="{ 'is-linked': isUserLinkedTo(c) }"
                          @click="onLinkResultClick(c)">
                        <span class="account-setup-add-result-code">{{ c.id }} · {{ c.code }}</span>
                        <span class="account-setup-add-result-label">{{ c.name }}</span>
                        <span v-if="isUserLinkedTo(c)" class="account-setup-link-unlink">Unlink</span>
                        <span v-else class="account-setup-link-add">+ Link</span>
                      </li>
                    </ul>
                    <div v-else-if="linkTerm" class="account-setup-link-empty">No companies match “{{ linkTerm }}”.</div>
                    <div v-if="linkMsg" class="account-setup-link-msg" :class="{ 'is-err': linkErr }">{{ linkMsg }}</div>
                  </li>
                  </template>
                </template>
                <li v-if="cat.count > cat.items.length && !colorFilter[cat.key]"
                    class="account-setup-detail-more">
                  …and {{ cat.count - cat.items.length }} more
                </li>

                <!-- Bottom of a long listing: jump back to the category top -->
                <li v-if="showListNav(cat)" class="account-setup-jumpbar account-setup-jumpbar-bottom">
                  <button type="button"
                          class="account-setup-jumplink"
                          @click="scrollToCatTop(cat)">
                    ↑ Back to top
                  </button>
                </li>

                <!-- Per-category history (adds + deletes) — Machines / Wear / Users / Parts -->
                <li v-if="catHasHistory(cat) && (delHistory[cat.key] || []).length"
                    :id="'asc-hist-' + cat.key"
                    class="account-setup-delhist">
                  <button type="button"
                          class="account-setup-history-toggle"
                          @click="toggleDelHist(cat.key)">
                    <span class="account-setup-row-toggle">{{ delHistOpen[cat.key] ? '−' : '+' }}</span>
                    <span class="account-setup-history-title">
                      {{ cat.label }} history ({{ (delHistory[cat.key] || []).length }})
                    </span>
                  </button>
                  <div v-if="delHistOpen[cat.key]">
                    <div class="account-setup-history-bar">
                      <button type="button"
                              class="account-setup-backtop"
                              @click="scrollToCatTop(cat)">↑ Back to top</button>
                      <button type="button"
                              class="account-setup-history-clear"
                              title="Remove all entries from this history (audit-only — does NOT change your setup)"
                              @click="clearCatHistory(cat)">Delete all</button>
                    </div>
                    <ul class="account-setup-history-list">
                      <li v-for="e in delHistory[cat.key]"
                          :key="'hist-' + e.id"
                          class="account-setup-history-item"
                          :class="{ 'is-undone': e.undone }">
                        <div class="account-setup-history-info">
                          <span class="account-setup-history-route">
                            <span class="account-setup-history-tag"
                                  :class="isAddEntry(e) ? 'is-add' : 'is-del'">{{ histActionTag(e) }}</span>
                            {{ histRoute(e) }}
                          </span>
                          <span class="account-setup-history-when">
                            <template v-if="!isBulkEntry(e) && e.type !== 'deactivate-assembly' && e.type !== 'link-user-company' && e.type !== 'unlink-user-company'">{{ e.label }} · </template><template v-if="histMachineInfo(cat, e)">{{ histMachineInfo(cat, e) }} · </template>{{ formatWhen(e.whenUtc) }}<template v-if="e.createdBy"> · {{ e.createdBy }}</template>
                          </span>
                        </div>
                        <button v-if="!e.undone"
                                type="button"
                                class="account-setup-history-undo"
                                :disabled="delBusyKey === ('h' + e.id)"
                                @click="undoFromHistory(e)">{{ histUndoLabel(e) }}</button>
                        <span v-else-if="e.type === 'wear-all-permanent'"
                              class="account-setup-history-perm"
                              title="Permanently deleted — cannot be restored">permanent</span>
                        <span v-else class="account-setup-history-undone">{{ histUndoneLabel(e) }}</span>
                        <!-- Permanently delete the USER account (frees the email) — shown on
                             deactivate entries, beside Reactivate. Hard delete, not reversible. -->
                        <button v-if="e.type === 'deactivate-user' && e.userId"
                                type="button"
                                class="account-setup-history-deluser"
                                :disabled="delBusyKey === ('du' + e.id)"
                                title="Permanently delete this user account (frees the email — cannot be undone)"
                                @click="deleteUserFromHistory(e)">{{ delBusyKey === ('du' + e.id) ? 'Deleting…' : 'Delete User' }}</button>
                        <!-- Remove the audit entry only (does NOT change the setup), EXCEPT a
                             still-active company link (deleting it would lose the Unlink). -->
                        <button v-if="!(e.type === 'link-user-company' && !e.undone)"
                                type="button"
                                class="account-setup-history-del"
                                :disabled="delBusyKey === ('hd' + e.id)"
                                title="Remove this entry from the history (audit-only — does NOT change your setup)"
                                @click="deleteHistEntry(cat, e)">×</button>
                      </li>
                    </ul>
                  </div>
                </li>
                <li :id="'asc-bottom-' + cat.key" class="account-setup-anchor-bottom" aria-hidden="true"></li>
              </ul>
            </li>

            <!-- ===== Utility Actions — account-wide tools ===== -->
            <li id="asc-cat-utility" class="account-setup-item">
              <button type="button"
                      class="account-setup-row"
                      :class="{ 'is-open': !!open.utilityActions }"
                      @click="toggleUtility">
                <span class="account-setup-row-toggle">{{ open.utilityActions ? '−' : '+' }}</span>
                <span class="account-setup-row-label">Utility Actions</span>
                <span class="account-setup-row-join">Account-wide tools — copy a company's setup, header menu, sales territories, report access, replace charts</span>
                <span class="account-setup-row-count">5</span>
              </button>
              <div v-if="open.utilityActions" class="account-setup-catbody">
                <!-- ===== Copy setup from another company ===== -->
                <div class="account-setup-copy">
                  <div class="account-setup-copy-head">
                    <span class="account-setup-copy-headleft"><span class="account-setup-copy-icon" aria-hidden="true">⧉</span> Copy setup from another company</span>
                    <button v-if="targetHistory.length"
                            type="button"
                            class="account-setup-copy-histlink"
                            :class="{ 'is-open': historyOpen }"
                            @click="historyOpen = !historyOpen">Copy history ({{ targetHistory.length }})</button>
                  </div>
            <p class="account-setup-copy-hint">
              Clones the selected company's <strong>machines</strong> and
              <strong>wear parts</strong> into this account. Assemblies,
              sub-assemblies and parts follow the machines automatically.
              Links this account already has are skipped.
            </p>
            <div class="account-setup-copy-row">
              <input type="text"
                     class="account-setup-copy-input"
                     placeholder="Search a company by name, code or ID…"
                     :value="copyTerm"
                     :disabled="copying"
                     @input="onCopyInput($event)" />
              <button v-if="copySource"
                      type="button"
                      class="account-setup-copy-clear"
                      title="Clear selection"
                      :disabled="copying"
                      @click="clearCopy">×</button>
              <button type="button"
                      class="account-setup-copy-btn"
                      :disabled="!copySource || copying"
                      @click="doCopy">
                {{ copying ? 'Copying…' : 'Copy machines + wear' }}
              </button>
            </div>
            <ul v-if="!copySource && copyMatches.length" class="account-setup-add-results account-setup-copy-results">
              <li class="account-setup-copy-rowitem is-head">
                <span class="asc-col-id">ID</span>
                <span class="asc-col-code">Customer ID</span>
                <span class="asc-col-desc">Company Name</span>
              </li>
              <li v-for="c in copyMatches"
                  :key="'copy-' + c.id"
                  class="account-setup-copy-rowitem is-pick"
                  @click="pickCopy(c)">
                <span class="asc-col-id">{{ c.id }}</span>
                <span class="asc-col-code">{{ c.code || '—' }}</span>
                <span class="asc-col-desc"><span class="asc-desc-main">{{ c.name }}</span></span>
              </li>
            </ul>
            <div v-else-if="!copySource && (copyTerm || '').trim().length" class="account-setup-add-hint">
              No matching companies.
            </div>
            <div v-if="copyResult && !copyResult.error"
                 class="account-setup-copy-result is-ok">
              <template v-if="copyResult.machinesAdded || copyResult.wearAdded">
                ✓ Added {{ copyResult.machinesAdded }} machine(s) and
                {{ copyResult.wearAdded }} wear part(s).
              </template>
              <template v-else>
                ✓ Nothing new to copy — this account already had everything from
                that company.
              </template>
            </div>
            <div v-else-if="copyResult && copyResult.error"
                 class="account-setup-copy-result is-err">
              Couldn't copy the setup — please try again.
            </div>

            <!-- Copy history — expands here within the section, below the search -->
            <div v-if="historyOpen && targetHistory.length" class="account-setup-copy-hist">
            <ul class="account-setup-history-list">
              <li v-for="h in targetHistory"
                  :key="h.id"
                  class="account-setup-history-item"
                  :class="{ 'is-undone': h.undone }">
                <div class="account-setup-history-info">
                  <span class="account-setup-history-route">{{ h.sourceCode }} → {{ h.targetCode }}</span>
                  <span class="account-setup-history-when">
                    {{ h.machinesAdded }} machine(s), {{ h.wearAdded }} wear · {{ formatWhen(h.whenUtc) }}<template v-if="h.createdBy"> · {{ h.createdBy }}</template>
                  </span>
                  <button v-if="!h.undone && historyRemoved(h)"
                          type="button"
                          class="account-setup-history-remain"
                          :aria-expanded="!!copyDetailOpen[h.id]"
                          :title="copyDetailOpen[h.id] ? 'Hide breakdown' : 'Show what is still here vs removed'"
                          @click="toggleCopyDetail(h)">
                    <span class="account-setup-cd-caret">{{ copyDetailOpen[h.id] ? '▾' : '▸' }}</span>
                    {{ historyRemainingText(h) }}
                  </button>
                </div>
                <button v-if="!h.undone && historyRemaining(h) > 0"
                        type="button"
                        class="account-setup-history-undo"
                        :disabled="undoingId === h.id"
                        @click="undoCopy(h)">
                  {{ undoingId === h.id ? 'Undoing…' : 'Undo' }}
                </button>
                <span v-else-if="h.undone" class="account-setup-history-redone-wrap">
                  <span class="account-setup-history-undone">Undone</span>
                  <button type="button"
                          class="account-setup-history-redo"
                          :disabled="undoingId === h.id || redoBlocked(h)"
                          :title="redoBlocked(h) ? 'Already copied in by a newer entry — nothing to redo' : 'Re-apply this copy'"
                          @click="redoCopy(h)">
                    {{ undoingId === h.id ? 'Redoing…' : 'Redo' }}
                  </button>
                </span>
                <span v-else class="account-setup-history-undone" title="Everything this copy added was already removed manually">Removed</span>
                <!-- Itemized audit: what this copy created, present vs removed -->
                <div v-if="copyDetailOpen[h.id]" class="account-setup-history-detail">
                  <div v-if="copyDetailBusy === h.id" class="account-setup-add-hint">Loading breakdown…</div>
                  <template v-else-if="copyDetail[h.id] && !copyDetail[h.id].error">
                    <div v-if="copyDetail[h.id].machinesTotal" class="account-setup-cd-group">
                      <div class="account-setup-cd-head">
                        Machines — {{ copyDetail[h.id].machinesPresent }} of {{ copyDetail[h.id].machinesTotal }} still here
                      </div>
                      <div v-for="(m, mi) in copyDetail[h.id].machines"
                           :key="'cdm-' + h.id + '-' + mi"
                           class="account-setup-cd-row"
                           :class="{ 'is-gone': !m.present }">
                        <span class="account-setup-cd-mark">{{ m.present ? '✓' : '✗' }}</span>
                        <span class="account-setup-cd-name">{{ (m.code ? m.code + ' — ' : '') + (m.label || (m.machineId ? 'Machine #' + m.machineId : '(removed — details unavailable)')) }}<span v-if="m.machineId" class="account-setup-cd-sub">ID {{ m.machineId }}</span></span>
                        <span class="account-setup-cd-status">{{ m.present ? 'still here' : 'removed' }}</span>
                      </div>
                    </div>
                    <div v-if="copyDetail[h.id].wearTotal" class="account-setup-cd-group">
                      <div class="account-setup-cd-head">
                        Wear parts — {{ copyDetail[h.id].wearPresent }} of {{ copyDetail[h.id].wearTotal }} still here
                      </div>
                      <div v-for="(w, wi) in copyDetail[h.id].wear"
                           :key="'cdw-' + h.id + '-' + wi"
                           class="account-setup-cd-row"
                           :class="{ 'is-gone': !w.present }">
                        <span class="account-setup-cd-mark">{{ w.present ? '✓' : '✗' }}</span>
                        <span class="account-setup-cd-name">{{ (w.code ? w.code + ' — ' : '') + (w.label || ('Part #' + w.partId)) }}<span v-if="w.machine" class="account-setup-cd-sub">{{ w.machine }}</span></span>
                        <span class="account-setup-cd-status">{{ w.present ? 'still here' : 'removed' }}</span>
                      </div>
                    </div>
                  </template>
                  <div v-else class="account-setup-add-hint">Breakdown unavailable.</div>
                </div>
              </li>
            </ul>
            <p class="account-setup-history-note">
              Saved on the server — shared across admins.
            </p>
            </div><!-- /.account-setup-copy-hist -->
                </div><!-- /.account-setup-copy -->

                <!-- ===== Menu Navigation — GLOBAL header menu (shared by all companies) ===== -->
                <div class="account-setup-nav">
                  <button type="button"
                          class="account-setup-nav-head"
                          :class="{ 'is-open': navOpen }"
                          @click="toggleNav">
                    <span class="account-setup-nav-toggle">{{ navOpen ? '−' : '+' }}</span>
                    <span class="account-setup-copy-icon" aria-hidden="true">☰</span>
                    Menu Navigation
                  </button>
                  <div v-if="navOpen" class="account-setup-nav-body">
                    <p class="account-setup-copy-hint">
                      Header menu links — <strong>global, shared by all companies</strong>.
                      Edit, reorder, add, or remove. Use ▲/▼ to change the order; saving
                      renumbers every link so the menu shows in this exact order.
                    </p>

                    <div v-if="navBusy && !navItems.length" class="account-setup-add-hint">Loading menu links…</div>
                    <div v-else-if="navLoadError" class="account-setup-add-hint">
                      Couldn't load the header menu — please try again.
                    </div>
                    <div v-else-if="!navItems.length" class="account-setup-add-hint">No header menu links yet.</div>

                    <ul v-else class="account-setup-nav-list">
                      <li class="account-setup-nav-row is-head">
                        <span class="asc-nav-move"></span>
                        <span class="asc-nav-name">Link Name</span>
                        <span class="asc-nav-url">URL</span>
                        <span class="asc-nav-roles">Roles</span>
                        <span class="asc-nav-roles">Hide from</span>
                        <span class="asc-nav-mode" title="Search links only: lock this button to Order / Quote / plain Search">Mode</span>
                        <span class="asc-nav-on">Show</span>
                        <span class="asc-nav-act"></span>
                      </li>
                      <li v-for="(it, i) in navItems"
                          :key="'nav-' + it.id"
                          class="account-setup-nav-row">
                        <span class="asc-nav-move">
                          <button type="button"
                                  class="account-setup-nav-arrow"
                                  title="Move up"
                                  :disabled="i === 0 || navReordering"
                                  @click="moveNavUp(i)">▲</button>
                          <button type="button"
                                  class="account-setup-nav-arrow"
                                  title="Move down"
                                  :disabled="i === navItems.length - 1 || navReordering"
                                  @click="moveNavDown(i)">▼</button>
                        </span>
                        <span class="asc-nav-name">
                          <input type="text"
                                 class="account-setup-nav-input"
                                 placeholder="Link Name"
                                 v-model="it.name"
                                 :disabled="navRowBusy === it.id" />
                        </span>
                        <span class="asc-nav-url">
                          <input type="text"
                                 class="account-setup-nav-input"
                                 placeholder="URL"
                                 v-model="it.url"
                                 :disabled="navRowBusy === it.id" />
                        </span>
                        <span class="asc-nav-roles">
                          <button type="button"
                                  class="account-setup-nav-rolesbtn"
                                  :disabled="navRowBusy === it.id"
                                  :title="navRoleTitle(it)"
                                  @click="navRolesOpen = (navRolesOpen === it.id ? '' : it.id)">
                            <span class="account-setup-nav-roleslbl">{{ navRolesLabel(it) }}</span> ▾
                          </button>
                          <div v-if="navRolesOpen === it.id" class="account-setup-nav-rolespanel">
                            <label v-for="r in navRoleOptions" :key="r" class="account-setup-nav-rolesopt">
                              <input type="checkbox"
                                     :checked="navRoleChecked(it, r)"
                                     :disabled="navRowBusy === it.id"
                                     @change="toggleNavRole(it, r)" />
                              <span>{{ r }}</span>
                            </label>
                            <div class="account-setup-nav-roleshint">No roles checked = visible to everyone</div>
                          </div>
                        </span>
                        <!-- HIDE FROM — the exclusion list. Needed because Report Access grants roles
                             ADDITIVELY, so someone can be Sales AND Manufacturing at once; "Sales sees
                             this, but not if they're also Manufacturing" cannot be said with the allow
                             list alone. Deny beats allow. Master is never excluded. -->
                        <span class="asc-nav-roles">
                          <button type="button"
                                  class="account-setup-nav-rolesbtn"
                                  :class="{ 'is-deny': navParseDeny(it).length }"
                                  :disabled="navRowBusy === it.id"
                                  :title="navDenyTitle(it)"
                                  @click="navDenyOpen = (navDenyOpen === it.id ? '' : it.id)">
                            <span class="account-setup-nav-roleslbl">⛔ {{ navDenyLabel(it) }}</span> ▾
                          </button>
                          <div v-if="navDenyOpen === it.id" class="account-setup-nav-rolespanel">
                            <label v-for="r in navRoleOptions" :key="'d' + r" class="account-setup-nav-rolesopt">
                              <input type="checkbox"
                                     :checked="navDenyChecked(it, r)"
                                     :disabled="navRowBusy === it.id"
                                     @change="toggleNavDeny(it, r)" />
                              <span>{{ r }}</span>
                            </label>
                            <div class="account-setup-nav-roleshint">
                              Hides the link from anyone holding a checked role — even if the Roles column
                              allows them. Master always sees every link.
                            </div>
                          </div>
                        </span>
                        <span class="asc-nav-mode">
                          <select v-if="isSearchLink(it.url)"
                                  class="account-setup-nav-modesel"
                                  :value="navMode(it)"
                                  :disabled="navRowBusy === it.id"
                                  title="This link points at Search. Order/Quote keeps THIS item highlighted and locks the Search add-to-cart box to that destination."
                                  @change="setNavMode(it, $event.target.value)">
                            <option value="">Search</option>
                            <option value="order">Order</option>
                            <option value="quote">Quote</option>
                          </select>
                        </span>
                        <span class="asc-nav-on">
                          <label class="account-setup-nav-toggle"
                                 :title="isNavActive(it) ? 'Shown in the menu - click to hide' : 'Hidden from the menu - click to show'">
                            <input type="checkbox"
                                   :checked="isNavActive(it)"
                                   :disabled="navRowBusy === it.id"
                                   @change="toggleNavActive(it)" />
                          </label>
                        </span>
                        <span class="asc-nav-act">
                          <button type="button"
                                  class="account-setup-nav-save"
                                  :disabled="navRowBusy === it.id || !navRowDirty(it)"
                                  :title="navRowDirty(it) ? 'Save this link' : 'No changes to save'"
                                  @click="saveNavRow(it)">
                            {{ navRowBusy === it.id ? 'Saving…' : (navRowSaved === it.id ? 'Saved ✓' : 'Save') }}
                          </button>
                          <button type="button"
                                  class="account-setup-remove account-setup-nav-del"
                                  title="Delete this link"
                                  :disabled="navRowBusy === it.id"
                                  @click="deleteNavRow(it)">×</button>
                        </span>
                      </li>
                    </ul>

                    <div v-if="navReordering" class="account-setup-nav-status">Saving new order…</div>
                    <div v-else-if="navReorderSaved" class="account-setup-nav-status is-ok">✓ Order saved</div>

                    <!-- Add a new link -->
                    <div class="account-setup-nav-add">
                      <input type="text"
                             class="account-setup-nav-input"
                             placeholder="New link name"
                             v-model="newNav.name"
                             :disabled="navAddBusy"
                             @keyup.enter="addNavRow" />
                      <input type="text"
                             class="account-setup-nav-input"
                             placeholder="URL (e.g. /about)"
                             v-model="newNav.url"
                             :disabled="navAddBusy"
                             @keyup.enter="addNavRow" />
                      <button type="button"
                              class="account-setup-copy-btn account-setup-nav-addbtn"
                              :disabled="navAddBusy || !(newNav.name || '').trim()"
                              @click="addNavRow">
                        {{ navAddBusy ? 'Adding…' : 'Add' }}
                      </button>
                    </div>
                  </div>
                </div><!-- /.account-setup-nav -->

                <!-- ===== Sales Territories — admin-managed State → Territory → Sales Rep (drives the report map) ===== -->
                <div class="account-setup-nav">
                  <button type="button"
                          class="account-setup-nav-head"
                          :class="{ 'is-open': salesTerrOpen }"
                          @click="toggleSalesTerr">
                    <span class="account-setup-nav-toggle">{{ salesTerrOpen ? '−' : '+' }}</span>
                    <span class="account-setup-copy-icon" aria-hidden="true">🗺</span>
                    Sales Territories
                  </button>
                  <div v-if="salesTerrOpen" class="account-setup-nav-body">
                    <p class="account-setup-copy-hint">
                      State → Territory → Sales Rep assignments — <strong>global</strong>. These color the
                      Customer Insights <strong>Territory Map</strong> and its legend. Use a 2-letter state code
                      (e.g. <code>TX</code>); type any Territory or Sales Rep — new ones are added automatically.
                      <em>International</em> rows don't color a state (kept for the rep list only).
                      <br /><strong>Auto-assign:</strong> for reporting, an order with no rep in VISUAL is classified by
                      matching the <strong>first two letters of its Customer ID</strong> to a State above → that
                      territory (and its rep, when the state maps to one). This runs alongside manual assignment; the
                      warning below still flags reps to fix at the source (VISUAL). Per-customer exceptions can override it.
                    </p>
                    <div class="asc-terr-io">
                      <button type="button" class="account-setup-copy-btn asc-terr-export"
                              @click="exportSalesTerr" :disabled="!salesTerrItems.length"
                              title="Download one CSV with the territory map + per-customer exceptions + Territory Access">⬇ Export config (CSV)</button>
                      <button type="button" class="account-setup-copy-btn asc-terr-import"
                              @click="triggerSalesTerrImport"
                              title="Restore the map, exceptions, and access from a previously exported CSV">⬆ Import (CSV)</button>
                      <input ref="salesTerrFile" type="file" accept=".csv,text/csv" class="asc-terr-file" @change="importSalesTerrFile" />
                      <span class="asc-terr-toggle" :title="autoAssignOn ? 'Prefix auto-match is ON — an order with no rep in VISUAL is classified by its Customer-ID prefix. Click to turn OFF.' : 'Prefix auto-match is OFF — orders with no VISUAL rep stay unassigned. Click to turn ON.'">
                        <span class="asc-terr-toggle-lbl">Auto-match by prefix</span>
                        <button type="button" role="switch" :aria-checked="autoAssignOn ? 'true' : 'false'"
                                class="asc-switch" :class="{ 'is-on': autoAssignOn }" :disabled="autoAssignBusy"
                                @click="toggleAutoAssign"><span class="asc-switch-knob"></span></button>
                        <span class="asc-terr-toggle-state">{{ autoAssignOn ? 'On' : 'Off' }}</span>
                      </span>
                    </div>
                    <p class="account-setup-add-hint asc-terr-io-hint">
                      One file covers all four sections here — the territory map, per-customer exceptions, Territory Access, and rep aliases.
                      When editing in Excel, keep the <code>#SALES_TERRITORIES</code> / <code>#EXCEPTIONS</code> /
                      <code>#TERRITORY_ACCESS</code> / <code>#REP_ALIASES</code> section headers. A section left out of the file is left unchanged.
                    </p>
                    <div v-if="pendingImport" class="asc-terr-import-confirm">
                      <div class="asc-terr-import-msg">
                        <b>{{ pendingImport.name }}</b> will replace your current configuration with:
                        <ul class="asc-terr-import-list">
                          <li v-if="pendingImport.territories && pendingImport.territories.length">
                            Sales Territories — <b>{{ pendingImport.territories.length }}</b> row(s)
                            <span class="asc-imp-note">(replaces all {{ salesTerrItems.length }})</span>
                          </li>
                          <li v-if="pendingImport.exceptions != null">
                            Per-customer exceptions — <b>{{ pendingImport.exceptions.length }}</b> row(s)
                            <span class="asc-imp-note">(replaces all current)</span>
                          </li>
                          <li v-if="pendingImport.access && pendingImport.access.length">
                            Territory Access — <b>{{ pendingImport.access.length }}</b> territory row(s)
                          </li>
                          <li v-if="pendingImport.aliases != null">
                            Rep aliases — <b>{{ pendingImport.aliases.length }}</b> row(s)
                            <span class="asc-imp-note">(replaces all current)</span>
                          </li>
                        </ul>
                        This can't be undone (export first to keep a backup).
                      </div>
                      <div class="asc-terr-import-actions">
                        <button type="button" class="asc-terr-import-apply" :disabled="importBusy" @click="applySalesTerrImport">{{ importBusy ? 'Importing…' : 'Replace' }}</button>
                        <button type="button" class="asc-terr-import-cancel" :disabled="importBusy" @click="pendingImport = null">Cancel</button>
                      </div>
                    </div>
                    <div v-if="importMsg" class="asc-terr-import-note">{{ importMsg }}</div>

                    <!-- Unmapped-rep warnings: reps on orders whose name matches NO assignment below, so their
                         orders fall into "(Unassigned)". Admin can Ignore (and later Restore) each one. -->
                    <div v-if="terrActiveWarnings.length || terrIgnoredWarnings.length" class="asc-terr-warns">
                      <template v-if="terrActiveWarnings.length">
                        <div class="asc-terr-warnhead">
                          ⚠ {{ terrActiveWarnings.length }} sales rep{{ terrActiveWarnings.length === 1 ? '' : 's' }}
                          on orders {{ terrActiveWarnings.length === 1 ? "isn't" : "aren't" }} mapped to a territory.
                          Add an assignment below with the <strong>exact</strong> name, or ignore.
                        </div>
                        <ul class="asc-terr-warnlist">
                          <li v-for="w in terrActiveWarnings" :key="'w-' + w.name" class="asc-terr-warnrow">
                            <button type="button" class="asc-terr-warnname-btn"
                                    :aria-expanded="expandedWarnRep === w.name ? 'true' : 'false'"
                                    title="Show this rep's orders grouped by Customer-ID prefix &amp; country"
                                    @click="toggleWarnRep(w.name)">
                              <span class="asc-terr-warncaret">{{ expandedWarnRep === w.name ? '▾' : '▸' }}</span>
                              <span class="asc-terr-warnname" :title="w.name">{{ w.name }}</span>
                            </button>
                            <span class="asc-terr-warnmeta">{{ w.orders }} order{{ w.orders === 1 ? '' : 's' }} · {{ warnMoney(w.sales) }}</span>
                            <button type="button" class="asc-terr-warnbtn is-insight"
                                    :aria-expanded="expandedWarnInsight === w.name ? 'true' : 'false'"
                                    title="Why is this rep unassigned — and how do I fix it?"
                                    @click="toggleWarnInsight(w.name)">💡 Why?</button>
                            <button type="button" class="asc-terr-warnbtn"
                                    :disabled="terrWarnBusy === w.name"
                                    title="Ignore this warning (you can restore it later)"
                                    @click="dismissTerrWarning(w.name)">{{ terrWarnBusy === w.name ? '…' : 'Ignore' }}</button>
                            <!-- Drill-down: prefix+country groups (lazy on expand) → orders (lazy on group-expand). -->
                            <div v-if="expandedWarnRep === w.name" class="asc-terr-drill">
                              <div v-if="warnGroupsBusy === w.name" class="asc-terr-drill-loading">Loading breakdown…</div>
                              <table v-else-if="(warnGroups[w.name] || []).length" class="asc-terr-drilltbl">
                                <thead><tr><th></th><th>Prefix</th><th>Country</th><th class="num">Orders</th><th class="num">Custs</th><th class="num">Sales</th><th>Current mapping</th><th></th></tr></thead>
                                <tbody>
                                  <template v-for="g in warnGroups[w.name]">
                                    <tr :key="warnGroupKey(w.name, g)">
                                      <td><button type="button" class="asc-terr-ordtoggle" :title="expandedWarnGroup === warnGroupKey(w.name, g) ? 'Hide orders' : 'Show orders'" @click="toggleWarnGroup(w.name, g)">{{ expandedWarnGroup === warnGroupKey(w.name, g) ? '▾' : '▸' }}</button></td>
                                      <td class="asc-terr-gpref">{{ g.prefix }}</td>
                                      <td>{{ g.country }}</td>
                                      <td class="num">{{ Number(g.orders || 0).toLocaleString() }}</td>
                                      <td class="num">{{ Number(g.customers || 0).toLocaleString() }}</td>
                                      <td class="num">{{ warnMoney(g.sales) }}</td>
                                      <td class="asc-terr-gmap">{{ prefixMapping(g.prefix) || '—' }}</td>
                                      <td><button type="button" class="asc-terr-warnbtn is-add" title="Map this prefix → a territory (prefills the add-row below — just pick a Territory)" @click="addGroupToMap(w.name, g)">Add</button></td>
                                    </tr>
                                    <tr v-if="expandedWarnGroup === warnGroupKey(w.name, g)" :key="warnGroupKey(w.name, g) + '|ord'">
                                      <td colspan="8">
                                        <div class="asc-terr-ordwrap">
                                          <div v-if="warnOrdersBusy" class="asc-terr-drill-loading">Loading orders…</div>
                                          <table v-else class="asc-terr-ordtbl">
                                            <thead><tr><th>Order #</th><th>Customer</th><th>State</th><th>Ship Country</th><th>Ship Zip</th><th class="num">Total</th><th>Date</th><th></th></tr></thead>
                                            <tbody>
                                              <tr v-for="(o, oi) in (warnOrders[warnGroupKey(w.name, g)] || [])" :key="(o.orderNo || 'na') + '|' + o.customerId + '|' + oi">
                                                <td>{{ o.orderNo || '—' }}</td>
                                                <td><strong>{{ o.customerId }}</strong> {{ o.customerName }}</td>
                                                <td>{{ o.customerState || '—' }}</td>
                                                <td>{{ o.shipToCountry || '—' }}</td>
                                                <td>{{ o.shipToPostal || '—' }}</td>
                                                <td class="num">{{ warnMoney(o.total) }}</td>
                                                <td>{{ fmtWarnDate(o.orderDate) }}</td>
                                                <td><button type="button" class="asc-terr-warnbtn is-add" title="Create a ship-to exception scoped to this customer + country + zip (prefills the exceptions row below)" @click="addOrderRule(w.name, o)">Add rule</button></td>
                                              </tr>
                                              <tr v-if="!(warnOrders[warnGroupKey(w.name, g)] || []).length"><td colspan="8" class="asc-terr-drill-empty">No orders found.</td></tr>
                                            </tbody>
                                          </table>
                                        </div>
                                      </td>
                                    </tr>
                                  </template>
                                </tbody>
                              </table>
                              <div v-else class="asc-terr-drill-empty">No breakdown available yet — deploy the backend (or this rep has no orders on file).</div>
                            </div>
                            <div v-if="expandedWarnInsight === w.name" class="asc-terr-insight">
                              <div class="asc-terr-insight-row"><b>Why:</b> orders under this rep carry the name “{{ w.name }}” in VISUAL, but no Sales Territories row uses that exact name — so they can’t be classified and land in the <em>unassigned</em> bucket on Sales Insights ({{ Number(w.orders || 0).toLocaleString() }} order{{ w.orders === 1 ? '' : 's' }} · {{ warnMoney(w.sales) }}).</div>
                              <div v-if="warnFuzzyMatch(w.name)" class="asc-terr-insight-row"><b>Likely cause:</b> this looks like “{{ warnFuzzyMatch(w.name).rep }}”{{ warnFuzzyMatch(w.name).territory ? ' (already mapped to ' + warnFuzzyMatch(w.name).territory + ')' : '' }} spelled differently in VISUAL — the name must match <em>exactly</em>. Add a row using VISUAL’s spelling “{{ w.name }}”.</div>
                              <div class="asc-terr-insight-row"><b>How to fix:</b> expand ▸ to see which state-prefixes &amp; countries these orders cover, then <b>Add</b> a Sales Territories row with the exact name “{{ w.name }}” and pick a territory (or map per-prefix / per-order from the breakdown).</div>
                              <div class="asc-terr-insight-row"><b>Result:</b> on the next sync these {{ Number(w.orders || 0).toLocaleString() }} order{{ w.orders === 1 ? '' : 's' }} ({{ warnMoney(w.sales) }}) move into that territory, leave the unassigned bucket, and the territory’s total on Sales Insights grows by that amount.</div>
                            </div>
                          </li>
                        </ul>
                      </template>
                      <div v-else class="asc-terr-warnhead is-clear">✓ Every sales rep on orders is mapped to a territory.</div>

                      <div v-if="terrIgnoredWarnings.length" class="asc-terr-warnignored">
                        <a href="#" class="asc-terr-warntoggle" @click.prevent="terrWarningsShowIgnored = !terrWarningsShowIgnored">
                          {{ terrWarningsShowIgnored ? '▾' : '▸' }} {{ terrIgnoredWarnings.length }} ignored warning{{ terrIgnoredWarnings.length === 1 ? '' : 's' }}
                        </a>
                        <ul v-if="terrWarningsShowIgnored" class="asc-terr-warnlist is-ignored">
                          <li v-for="w in terrIgnoredWarnings" :key="'wi-' + w.name" class="asc-terr-warnrow">
                            <span class="asc-terr-warnname" :title="w.name">{{ w.name }}</span>
                            <span class="asc-terr-warnmeta">{{ w.orders }} order{{ w.orders === 1 ? '' : 's' }} · {{ warnMoney(w.sales) }}</span>
                            <button type="button" class="asc-terr-warnbtn is-restore"
                                    :disabled="terrWarnBusy === w.name"
                                    title="Restore this warning"
                                    @click="restoreTerrWarning(w.name)">{{ terrWarnBusy === w.name ? '…' : 'Restore' }}</button>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div v-if="salesTerrBusy && !salesTerrItems.length" class="account-setup-add-hint">Loading territories…</div>
                    <div v-else-if="salesTerrLoadError" class="account-setup-add-hint">
                      Couldn't load the territories — please try again.
                      <div v-if="salesTerrLoadMsg" class="asc-terr-why">{{ salesTerrLoadMsg }}</div>
                    </div>
                    <div v-else-if="!salesTerrItems.length" class="account-setup-add-hint">No territory assignments yet.</div>

                    <ul v-else class="account-setup-nav-list">
                      <li class="account-setup-nav-row asc-terr-row is-head">
                        <span class="asc-terr-state">State</span>
                        <span class="asc-terr-terr">Territory</span>
                        <span class="asc-terr-rep">Sales Rep</span>
                        <span class="asc-terr-color">Color</span>
                        <span class="asc-nav-act"></span>
                      </li>
                      <li v-for="it in salesTerrItems"
                          :key="'terr-' + it.id"
                          class="account-setup-nav-row asc-terr-row">
                        <span class="asc-terr-state">
                          <input type="text"
                                 class="account-setup-nav-input"
                                 maxlength="60"
                                 placeholder="ST"
                                 v-model="it.state"
                                 :disabled="salesTerrRowBusy === it.id" />
                        </span>
                        <span class="asc-terr-terr">
                          <input type="text"
                                 class="account-setup-nav-input"
                                 list="asc-terr-territories"
                                 placeholder="Territory"
                                 v-model="it.territory"
                                 :disabled="salesTerrRowBusy === it.id" />
                        </span>
                        <span class="asc-terr-rep">
                          <input type="text"
                                 class="account-setup-nav-input"
                                 list="asc-terr-reps"
                                 placeholder="Sales Rep"
                                 v-model="it.salesRep"
                                 :disabled="salesTerrRowBusy === it.id" />
                        </span>
                        <span class="asc-terr-color">
                          <input type="color"
                                 class="asc-terr-colorpick"
                                 :value="it.color || territoryEffectiveColor(it.territory)"
                                 @input="$set(it, 'color', $event.target.value)"
                                 :disabled="salesTerrRowBusy === it.id"
                                 title="Territory color (used on the report's map, bars & chips)" />
                        </span>
                        <span class="asc-nav-act">
                          <button type="button"
                                  class="account-setup-nav-save"
                                  :disabled="salesTerrRowBusy === it.id || !salesTerrRowDirty(it)"
                                  :title="salesTerrRowDirty(it) ? 'Save this assignment' : 'No changes to save'"
                                  @click="saveSalesTerrRow(it)">
                            {{ salesTerrRowBusy === it.id ? 'Saving…' : (salesTerrRowSaved === it.id ? 'Saved ✓' : 'Save') }}
                          </button>
                          <button type="button"
                                  class="account-setup-remove account-setup-nav-del"
                                  title="Delete this assignment"
                                  :disabled="salesTerrRowBusy === it.id"
                                  @click="deleteSalesTerrRow(it)">×</button>
                        </span>
                      </li>
                    </ul>

                    <!-- Autocomplete sources (existing distinct Territory / Sales Rep values) -->
                    <datalist id="asc-terr-territories">
                      <option v-for="t in salesTerrDistinct('territory')" :key="'tt-' + t" :value="t"></option>
                    </datalist>
                    <datalist id="asc-terr-reps">
                      <option v-for="r in salesTerrDistinct('salesRep')" :key="'rr-' + r" :value="r"></option>
                    </datalist>

                    <!-- Add a new assignment (State prefix → Territory → Sales Rep). The rep-warning drill-down's
                         per-prefix "Add" prefills this row (State + Rep), so the admin only picks a Territory. -->
                    <div class="account-setup-nav-add asc-terr-add" ref="salesTerrAddRow">
                      <input type="text"
                             class="account-setup-nav-input"
                             maxlength="60"
                             placeholder="State (e.g. TX)"
                             v-model="newSalesTerr.state"
                             :disabled="salesTerrAddBusy"
                             @keyup.enter="addSalesTerrRow" />
                      <input type="text"
                             class="account-setup-nav-input"
                             list="asc-terr-territories"
                             placeholder="Territory"
                             v-model="newSalesTerr.territory"
                             :disabled="salesTerrAddBusy"
                             @keyup.enter="addSalesTerrRow" />
                      <input type="text"
                             class="account-setup-nav-input"
                             list="asc-terr-reps"
                             placeholder="Sales Rep"
                             v-model="newSalesTerr.salesRep"
                             :disabled="salesTerrAddBusy"
                             @keyup.enter="addSalesTerrRow" />
                      <input type="color"
                             class="asc-terr-colorpick"
                             :value="newSalesTerr.color || territoryEffectiveColor(newSalesTerr.territory)"
                             @input="newSalesTerr.color = $event.target.value"
                             :disabled="salesTerrAddBusy"
                             title="Territory color" />
                      <button type="button"
                              class="account-setup-copy-btn account-setup-nav-addbtn"
                              :disabled="salesTerrAddBusy || !(newSalesTerr.state || '').trim()"
                              @click="addSalesTerrRow">
                        {{ salesTerrAddBusy ? 'Adding…' : 'Add' }}
                      </button>
                    </div>

                    <!-- Per-customer EXCEPTIONS: override the Customer-ID-prefix auto-assign for a specific customer. -->
                    <div class="asc-terr-exc">
                      <div class="asc-terr-exc-h">🎯 Per-customer exceptions</div>
                      <p class="account-setup-add-hint asc-terr-exc-hint">
                        Override the prefix rule for a specific <strong>Customer ID</strong>. To split one customer
                        across reps by where an order ships, add a <strong>Ship-to Country</strong> and/or
                        <strong>Zip</strong> — both must match on a row (leave a field blank for “any”). Add several
                        rows for the same customer (one per destination); the <em>most specific</em> match wins. The
                        dropdowns list that customer’s past ship-to addresses. Applied on the next sync.
                      </p>
                      <div v-if="terrExcBusy && !terrExceptions.length" class="account-setup-add-hint">Loading exceptions…</div>
                      <ul v-else-if="terrExceptions.length" class="account-setup-nav-list">
                        <li class="account-setup-nav-row asc-exc-row is-head">
                          <span class="asc-exc-cust">Customer ID</span>
                          <span class="asc-exc-ctry">Ship-to Country</span>
                          <span class="asc-exc-zip">Ship-to Zip</span>
                          <span class="asc-terr-terr">Territory</span>
                          <span class="asc-terr-rep">Sales Rep</span>
                          <span class="asc-exc-on" title="Active">On</span>
                          <span class="asc-nav-act"></span>
                        </li>
                        <li v-for="ex in terrExceptions" :key="'exc-' + ex.id" class="account-setup-nav-row asc-exc-row">
                          <span class="asc-exc-cust">
                            <input type="text" class="account-setup-nav-input" maxlength="64" placeholder="Customer ID"
                                   v-model="ex.customerId" :disabled="terrExcRowBusy === ex.id"
                                   @change="ensureShipTos(ex.customerId)" />
                          </span>
                          <span class="asc-exc-ctry">
                            <input type="text" class="account-setup-nav-input" :list="'asc-exc-ctry-' + excNorm(ex.customerId)"
                                   placeholder="Any country" v-model="ex.shipCountry" :disabled="terrExcRowBusy === ex.id"
                                   @focus="ensureShipTos(ex.customerId)" />
                          </span>
                          <span class="asc-exc-zip">
                            <input type="text" class="account-setup-nav-input" :list="'asc-exc-zip-' + excNorm(ex.customerId)"
                                   placeholder="Any zip" v-model="ex.shipZip" :disabled="terrExcRowBusy === ex.id"
                                   @focus="ensureShipTos(ex.customerId)" />
                          </span>
                          <span class="asc-terr-terr">
                            <input type="text" class="account-setup-nav-input" list="asc-terr-territories" placeholder="Territory"
                                   v-model="ex.territory" :disabled="terrExcRowBusy === ex.id" />
                          </span>
                          <span class="asc-terr-rep">
                            <input type="text" class="account-setup-nav-input" list="asc-terr-reps" placeholder="Sales Rep"
                                   v-model="ex.salesRep" :disabled="terrExcRowBusy === ex.id" />
                          </span>
                          <span class="asc-exc-on">
                            <input type="checkbox" v-model="ex.isActive" :disabled="terrExcRowBusy === ex.id" title="Active — apply this exception" />
                          </span>
                          <span class="asc-nav-act">
                            <button type="button" class="account-setup-nav-save" :disabled="terrExcRowBusy === ex.id"
                                    @click="saveTerrExcRow(ex)">{{ terrExcRowBusy === ex.id ? 'Saving…' : (terrExcRowSaved === ex.id ? 'Saved ✓' : 'Save') }}</button>
                            <button type="button" class="account-setup-remove account-setup-nav-del" title="Delete this exception"
                                    :disabled="terrExcRowBusy === ex.id" @click="deleteTerrExcRow(ex)">×</button>
                          </span>
                        </li>
                      </ul>
                      <div v-else class="account-setup-add-hint">No exceptions yet.</div>

                      <!-- Per-customer ship-to option lists (populate the Country / Zip dropdowns from past orders). -->
                      <template v-for="cust in excShipCustomers">
                        <datalist :id="'asc-exc-ctry-' + cust" :key="'dl-ctry-' + cust">
                          <option v-for="c in ((shipTosByCust[cust] || {}).countries || [])" :key="c" :value="c"></option>
                        </datalist>
                        <datalist :id="'asc-exc-zip-' + cust" :key="'dl-zip-' + cust">
                          <option v-for="z in ((shipTosByCust[cust] || {}).zips || [])" :key="z.zip" :value="z.zip">{{ z.country }}</option>
                        </datalist>
                      </template>

                      <div class="account-setup-nav-add asc-exc-add" ref="terrExcAddRow">
                        <input type="text" class="account-setup-nav-input" maxlength="64" placeholder="Customer ID (e.g. IL305)"
                               v-model="newTerrExc.customerId" :disabled="terrExcAddBusy"
                               @change="ensureShipTos(newTerrExc.customerId)" @blur="ensureShipTos(newTerrExc.customerId)"
                               @keyup.enter="addTerrExcRow" />
                        <input type="text" class="account-setup-nav-input" :list="'asc-exc-ctry-' + excNorm(newTerrExc.customerId)"
                               placeholder="Any country" v-model="newTerrExc.shipCountry" :disabled="terrExcAddBusy"
                               @focus="ensureShipTos(newTerrExc.customerId)" @keyup.enter="addTerrExcRow" />
                        <input type="text" class="account-setup-nav-input" :list="'asc-exc-zip-' + excNorm(newTerrExc.customerId)"
                               placeholder="Any zip" v-model="newTerrExc.shipZip" :disabled="terrExcAddBusy"
                               @focus="ensureShipTos(newTerrExc.customerId)" @keyup.enter="addTerrExcRow" />
                        <input type="text" class="account-setup-nav-input" list="asc-terr-territories" placeholder="Territory"
                               v-model="newTerrExc.territory" :disabled="terrExcAddBusy" @keyup.enter="addTerrExcRow" />
                        <input type="text" class="account-setup-nav-input" list="asc-terr-reps" placeholder="Sales Rep"
                               v-model="newTerrExc.salesRep" :disabled="terrExcAddBusy" @keyup.enter="addTerrExcRow" />
                        <button type="button" class="account-setup-copy-btn account-setup-nav-addbtn"
                                :disabled="terrExcAddBusy || !(newTerrExc.customerId || '').trim()"
                                @click="addTerrExcRow">{{ terrExcAddBusy ? 'Adding…' : 'Add' }}</button>
                      </div>
                      <div v-if="terrExcMsg" class="account-setup-add-hint asc-exc-msg">{{ terrExcMsg }}</div>
                    </div>

                    <!-- Rep ALIASES: a departed/alternate rep name → the current rep it rolls into (replaces the
                         VMFG view's hardcoded remap CASE). Applied by the sync before the territory override. -->
                    <div class="asc-terr-exc asc-terr-alias">
                      <div class="asc-terr-exc-h">🔁 Rep aliases (old rep → current rep)</div>
                      <p class="account-setup-add-hint asc-terr-exc-hint">
                        When a rep leaves, map their old VISUAL name to the <strong>current</strong> rep here — every order
                        credited to the old name then rolls into the current rep (and that rep's territory) on the next sync.
                        This is where departed reps are handled now (instead of hardcoding them in the source view).
                        Match is case- and space-insensitive.
                      </p>
                      <div v-if="repAliasBusy && !repAliases.length" class="account-setup-add-hint">Loading aliases…</div>
                      <ul v-else-if="repAliases.length" class="account-setup-nav-list">
                        <li class="account-setup-nav-row asc-alias-row is-head">
                          <span class="asc-alias-from">Old / departed rep (VISUAL)</span>
                          <span class="asc-alias-arrow"></span>
                          <span class="asc-alias-to">Current rep</span>
                          <span class="asc-nav-act"></span>
                        </li>
                        <li v-for="a in repAliases" :key="'alias-' + a.id" class="account-setup-nav-row asc-alias-row">
                          <span class="asc-alias-from">{{ a.aliasName }}</span>
                          <span class="asc-alias-arrow">→</span>
                          <span class="asc-alias-to">{{ a.canonicalName }}</span>
                          <span class="asc-nav-act">
                            <button type="button" class="account-setup-remove account-setup-nav-del" title="Delete this alias"
                                    :disabled="repAliasRowBusy === a.id" @click="deleteRepAliasRow(a)">×</button>
                          </span>
                        </li>
                      </ul>
                      <div v-else class="account-setup-add-hint">No rep aliases yet.</div>
                      <div class="account-setup-nav-add asc-alias-add">
                        <input type="text" class="account-setup-nav-input" maxlength="100" placeholder="Old rep name (exact VISUAL spelling)"
                               v-model="newRepAlias.aliasName" :disabled="repAliasAddBusy" @keyup.enter="addRepAliasRow" />
                        <span class="asc-alias-arrow">→</span>
                        <input type="text" class="account-setup-nav-input" list="asc-terr-reps" placeholder="Current rep"
                               v-model="newRepAlias.canonicalName" :disabled="repAliasAddBusy" @keyup.enter="addRepAliasRow" />
                        <button type="button" class="account-setup-copy-btn account-setup-nav-addbtn"
                                :disabled="repAliasAddBusy || !(newRepAlias.aliasName || '').trim() || !(newRepAlias.canonicalName || '').trim()"
                                @click="addRepAliasRow">{{ repAliasAddBusy ? 'Adding…' : 'Add' }}</button>
                      </div>
                      <div v-if="repAliasMsg" class="account-setup-add-hint asc-exc-msg">{{ repAliasMsg }}</div>
                    </div>

                    <!-- Territory Access — which portal logins (emails) can see each territory in
                         Sales Insights. Same link as Account Setup → Users (rpt_UserTerritory),
                         editable from either side. -->
                    <div class="asc-terr-access" v-if="salesTerrAccessTerritories().length">
                      <div class="asc-terr-access-head">Territory Access</div>
                      <p class="account-setup-add-hint asc-terr-access-hint">
                        Assign <b>Entra and local security groups</b> per territory — start typing and pick one
                        (Tab or Enter). A <b>👥 Entra group</b> pulls its members from Microsoft; a
                        <b>🛡 local role</b> (Master, Sales, Sales Insights…) pulls its members from this
                        portal's own user list, so it needs no Microsoft sign-in. Members can see that
                        territory's data in Sales Insights and are provisioned (Sales role + NC999) on their
                        first Microsoft sign-in. ↻ re-syncs membership; one-offs go under
                        <b>manual emails</b>. Master and Sales All always see everything. Also settable per
                        user in Account Setup → Users.
                      </p>
                      <ul class="account-setup-nav-list">
                        <li class="account-setup-nav-row asc-terr-acc-row is-head">
                          <span class="asc-terr-acc-terr">Territory</span>
                          <span class="asc-terr-acc-emails">Entra and Local Security Groups</span>
                          <span class="asc-nav-act"></span>
                        </li>
                        <li v-for="t in salesTerrAccessTerritories()"
                            :key="'acc-' + t"
                            class="account-setup-nav-row asc-terr-acc-row asc-racc-row">
                          <span class="asc-terr-acc-terr" :title="t">{{ t }}</span>
                          <span class="asc-terr-acc-emails">
                            <div class="asc-racc-groups">
                              <span v-for="(grp, gi) in (territoryGroups[t] || [])"
                                    :key="'tgc-' + t + '-' + grp.id"
                                    class="asc-racc-chip"
                                    :class="{ 'is-role': isRoleGroup(grp) }"
                                    @mouseenter="rgHoverStart(grp, $event)"
                                    @mouseleave="rgHoverEnd">
                                <span class="asc-racc-chip-ic" aria-hidden="true">{{ isRoleGroup(grp) ? '🛡' : '👥' }}</span>{{ grp.name }}
                                <button type="button" class="asc-racc-chip-x"
                                        :title="isRoleGroup(grp) ? 'Remove this local role (re-syncs members)' : 'Remove this group (re-syncs members)'"
                                        :disabled="tgBusy === t"
                                        @click="tgRemove(t, gi)">×</button>
                              </span>
                              <span class="asc-racc-typeahead">
                                <input type="text"
                                       class="account-setup-nav-input asc-racc-input"
                                       :placeholder="(territoryGroups[t] || []).length ? 'Add another group or role…' : 'Type an Entra group or local role…'"
                                       :value="tgTerm[t] || ''"
                                       autocomplete="off"
                                       @input="tgInput(t, $event)"
                                       @keydown="tgKey(t, $event)"
                                       @focus="tgInput(t, $event)"
                                       @blur="tgBlur" />
                                <ul v-if="tgSug.open && tgSug.key === t" class="asc-racc-sug">
                                  <li v-if="tgSug.busy" class="asc-racc-sug-note">Searching Entra…</li>
                                  <li v-if="tgSug.note" class="asc-racc-sug-note">{{ tgSug.note }}</li>
                                  <li v-for="(it, i) in tgSug.items"
                                      :key="'tgs-' + it.id"
                                      class="asc-racc-sug-item" :class="{ 'is-hi': tgSug.hi === i, 'is-role': it.isRole }"
                                      @mousedown.prevent="tgPick(t, it)"
                                      @mouseenter="tgSug.hi = i">
                                    <span aria-hidden="true">{{ it.isRole ? '🛡' : '👥' }}</span> {{ it.name }}
                                    <span v-if="it.kind || it.mail" class="asc-racc-sug-kind">{{ it.kind }}{{ it.mail ? " · " + it.mail : "" }}</span>
                                  </li>
                                  <li v-if="!tgSug.busy && !tgSug.items.length" class="asc-racc-sug-note">No matching groups or roles.</li>
                                </ul>
                              </span>
                              <button v-if="(territoryGroups[t] || []).length"
                                      type="button" class="asc-racc-sync"
                                      :disabled="tgBusy === t"
                                      title="Re-sync members — Entra groups from Microsoft, local roles from the portal"
                                      @click="tgSync(t)">{{ tgBusy === t ? '…' : '↻' }}</button>
                            </div>
                            <div class="asc-racc-sub">
                              <span>{{ (territoryGroupEmails[t] || []).length }} member{{ (territoryGroupEmails[t] || []).length === 1 ? '' : 's' }} from {{ accessSourceLabel(territoryGroups[t]) }}</span>
                              <button type="button" class="asc-racc-manual-toggle"
                                      :class="{ 'is-open': tgManualOpen === t }"
                                      @click="tgManualOpen = tgManualOpen === t ? '' : t">
                                {{ tgManualCount(t) }} manual email{{ tgManualCount(t) === 1 ? '' : 's' }} {{ tgManualOpen === t ? '▴' : '▾' }}
                              </button>
                            </div>
                            <div v-if="tgManualOpen === t" class="asc-racc-manual">
                              <input type="text"
                                     class="account-setup-nav-input"
                                     placeholder="one-off@external.com, another@company.com"
                                     :value="territoryManual[t] || ''"
                                     @input="$set(territoryManual, t, $event.target.value)"
                                     :disabled="territoryUsersBusy === t" />
                              <button type="button"
                                      class="account-setup-nav-save"
                                      :disabled="territoryUsersBusy === t"
                                      @click="saveTerritoryUsers(t)">
                                {{ territoryUsersBusy === t ? 'Saving…' : (territoryUsersSaved === t ? 'Saved ✓' : 'Save') }}
                              </button>
                            </div>
                          </span>
                          <span class="asc-nav-act"></span>
                        </li>
                      </ul>
                      <p v-if="territoryUsersMsg"
                         class="account-setup-add-hint"
                         :class="{ 'asc-terr-acc-warn': territoryUsersUnknown.length }">
                        {{ territoryUsersMsg }}
                      </p>

                      <!-- Pending provisioning: logins added to a territory that have no portal account yet
                           (never signed in). Access activates automatically on their first Microsoft sign-in. -->
                      <div v-if="territoryPending.length" class="asc-terr-pending">
                        <span class="asc-terr-pending-ic" aria-hidden="true">⏳</span>
                        <div class="asc-terr-pending-body">
                          <div class="asc-terr-pending-head">
                            Pending provisioning — {{ territoryPending.length }}
                            login{{ territoryPending.length === 1 ? '' : 's' }} added but not yet signed in
                          </div>
                          <p class="asc-terr-pending-note">
                            These emails have territory access saved but no portal account yet. Access turns on
                            automatically the first time each signs in with Microsoft.
                          </p>
                          <ul class="asc-terr-pending-list">
                            <li v-for="p in territoryPending" :key="'pend-' + p.email">
                              <span class="asc-terr-pending-email" :title="p.email">{{ p.email }}</span>
                              <span class="asc-terr-pending-terrs">{{ (p.territories || []).join(', ') }}</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                      <div v-else-if="territoryAccessLoaded" class="asc-terr-allset">
                        <span aria-hidden="true">✓</span> Everyone with territory access has signed in.
                      </div>
                    </div>
                  </div>
                </div><!-- /.account-setup-nav (Sales Territories) -->

                <!-- ===== Report Access — which portal logins (emails) can see each report on the
                     Reports dashboard / Sales Insights page. Mirrors Territory Access; provisions a
                     role on first Microsoft sign-in. Master always sees every report. ===== -->
                <div class="account-setup-nav">
                  <button type="button"
                          class="account-setup-nav-head"
                          :class="{ 'is-open': reportAccessOpen }"
                          @click="toggleReportAccess">
                    <span class="account-setup-nav-toggle">{{ reportAccessOpen ? '−' : '+' }}</span>
                    <span class="account-setup-copy-icon" aria-hidden="true">📊</span>
                    Report Access
                  </button>
                  <div v-if="reportAccessOpen" class="account-setup-nav-body">
                    <p class="account-setup-add-hint">
                      Assign <b>Entra and local security groups</b> per report — start typing and pick one
                      (Tab or Enter). A <b>👥 Entra group</b> resolves its members from Microsoft; a
                      <b>🛡 local role</b> resolves from this portal's own user list, so it needs no
                      Microsoft sign-in. Members get that report and its role on their first Microsoft
                      sign-in (<b>Sales Insights</b> → Sales, <b>Manufacturing Insights</b> → Manufacturing,
                      <b>Foundry Insights</b> → Foundry). ↻ re-syncs membership. Master always sees every
                      report. Addresses that can't live in a group (externals, one-offs) go under
                      <b>manual emails</b>.
                    </p>
                    <ul class="account-setup-nav-list">
                      <li class="account-setup-nav-row asc-terr-acc-row is-head">
                        <span class="asc-terr-acc-terr">Report</span>
                        <span class="asc-terr-acc-emails">Entra and Local Security Groups</span>
                        <span class="asc-nav-act"></span>
                      </li>
                      <li v-for="g in reportAccessGroups"
                          :key="'racc-' + g.key"
                          class="account-setup-nav-row asc-terr-acc-row asc-racc-row">
                        <span class="asc-terr-acc-terr" :title="g.label">{{ g.label }}</span>
                        <span class="asc-terr-acc-emails">
                          <div class="asc-racc-groups">
                            <span v-for="(grp, gi) in (reportGroups[g.key] || [])"
                                  :key="'rgc-' + g.key + '-' + grp.id"
                                  class="asc-racc-chip"
                                  :class="{ 'is-role': isRoleGroup(grp) }"
                                  @mouseenter="rgHoverStart(grp, $event)"
                                  @mouseleave="rgHoverEnd">
                              <span class="asc-racc-chip-ic" aria-hidden="true">{{ isRoleGroup(grp) ? '🛡' : '👥' }}</span>{{ grp.name }}
                              <button type="button" class="asc-racc-chip-x"
                                      :title="isRoleGroup(grp) ? 'Remove this local role (re-syncs members)' : 'Remove this group (re-syncs members)'"
                                      :disabled="rgBusy === g.key"
                                      @click="rgRemove(g.key, gi)">×</button>
                            </span>
                            <span class="asc-racc-typeahead">
                              <input type="text"
                                     class="account-setup-nav-input asc-racc-input"
                                     :placeholder="(reportGroups[g.key] || []).length ? 'Add another group or role…' : 'Type an Entra group or local role…'"
                                     :value="rgTerm[g.key] || ''"
                                     autocomplete="off"
                                     @input="rgInput(g.key, $event)"
                                     @keydown="rgKey(g.key, $event)"
                                     @focus="rgInput(g.key, $event)"
                                     @blur="rgBlur" />
                              <ul v-if="rgSug.open && rgSug.key === g.key" class="asc-racc-sug">
                                <li v-if="rgSug.busy" class="asc-racc-sug-note">Searching Entra…</li>
                                <li v-if="rgSug.note" class="asc-racc-sug-note">{{ rgSug.note }}</li>
                                <li v-for="(it, i) in rgSug.items"
                                    :key="'rgs-' + it.id"
                                    class="asc-racc-sug-item" :class="{ 'is-hi': rgSug.hi === i, 'is-role': it.isRole }"
                                    @mousedown.prevent="rgPick(g.key, it)"
                                    @mouseenter="rgSug.hi = i">
                                  <span aria-hidden="true">{{ it.isRole ? '🛡' : '👥' }}</span> {{ it.name }}
                                  <span v-if="it.kind || it.mail" class="asc-racc-sug-kind">{{ it.kind }}{{ it.mail ? " · " + it.mail : "" }}</span>
                                </li>
                                <li v-if="!rgSug.busy && !rgSug.items.length" class="asc-racc-sug-note">No matching groups or roles.</li>
                              </ul>
                            </span>
                            <button v-if="(reportGroups[g.key] || []).length"
                                    type="button" class="asc-racc-sync"
                                    :disabled="rgBusy === g.key"
                                    title="Re-sync members — Entra groups from Microsoft, local roles from the portal"
                                    @click="rgSync(g.key)">{{ rgBusy === g.key ? '…' : '↻' }}</button>
                          </div>
                          <div class="asc-racc-sub">
                            <span>{{ (reportGroupEmails[g.key] || []).length }} member{{ (reportGroupEmails[g.key] || []).length === 1 ? '' : 's' }} from {{ accessSourceLabel(reportGroups[g.key]) }}</span>
                            <button type="button" class="asc-racc-manual-toggle"
                                    :class="{ 'is-open': rgManualOpen === g.key }"
                                    @click="rgManualOpen = rgManualOpen === g.key ? '' : g.key">
                              {{ rgManualCount(g.key) }} manual email{{ rgManualCount(g.key) === 1 ? '' : 's' }} {{ rgManualOpen === g.key ? '▴' : '▾' }}
                            </button>
                          </div>
                          <div v-if="rgManualOpen === g.key" class="asc-racc-manual">
                            <input type="text"
                                   class="account-setup-nav-input"
                                   placeholder="one-off@external.com, another@company.com"
                                   :value="reportManual[g.key] || ''"
                                   @input="$set(reportManual, g.key, $event.target.value)"
                                   :disabled="reportUsersBusy === g.key" />
                            <button type="button"
                                    class="account-setup-nav-save"
                                    :disabled="reportUsersBusy === g.key"
                                    @click="saveReportUsers(g.key)">
                              {{ reportUsersBusy === g.key ? 'Saving…' : (reportUsersSaved === g.key ? 'Saved ✓' : 'Save') }}
                            </button>
                          </div>
                        </span>
                        <span class="asc-nav-act"></span>
                      </li>
                    </ul>
                    <p v-if="reportUsersMsg"
                       class="account-setup-add-hint"
                       :class="{ 'asc-terr-acc-warn': reportUsersUnknown.length }">
                      {{ reportUsersMsg }}
                    </p>

                    <!-- Pending provisioning: report-access logins with no portal account yet (never signed in). -->
                    <div v-if="reportPending.length" class="asc-terr-pending">
                      <span class="asc-terr-pending-ic" aria-hidden="true">⏳</span>
                      <div class="asc-terr-pending-body">
                        <div class="asc-terr-pending-head">
                          Pending provisioning — {{ reportPending.length }}
                          login{{ reportPending.length === 1 ? '' : 's' }} added but not yet signed in
                        </div>
                        <p class="asc-terr-pending-note">
                          These emails have report access saved but no portal account yet. Access — and the
                          report's role — turns on automatically the first time each signs in with Microsoft.
                        </p>
                        <ul class="asc-terr-pending-list">
                          <li v-for="p in reportPending" :key="'rpend-' + p.email">
                            <span class="asc-terr-pending-email" :title="p.email">{{ p.email }}</span>
                            <span class="asc-terr-pending-terrs">{{ (p.reports || []).join(', ') }}</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div v-else-if="reportAccessLoaded" class="asc-terr-allset">
                      <span aria-hidden="true">✓</span> Everyone with report access has signed in.
                    </div>
                  </div>
                </div><!-- /.account-setup-nav (Report Access) -->

                <!-- ===== Replace Charts — search any chart, upload a corrected image ===== -->
                <div class="account-setup-nav">
                  <button type="button"
                          class="account-setup-nav-head"
                          :class="{ 'is-open': rcOpen }"
                          @click="toggleReplaceCharts">
                    <span class="account-setup-nav-toggle">{{ rcOpen ? '−' : '+' }}</span>
                    <span class="account-setup-copy-icon" aria-hidden="true">🖼</span>
                    Replace Charts
                  </button>
                  <div v-if="rcOpen" class="account-setup-nav-body">
                    <p class="account-setup-copy-hint">
                      Find any chart by <strong>machine, assembly, or sub-assembly</strong> and upload a corrected
                      image — the replacement serves everywhere that chart appears. The
                      "Report incorrect chart" helpdesk email links straight to the reported row here.
                    </p>
                    <div class="account-setup-copy-row">
                      <input type="text"
                             class="account-setup-copy-input"
                             placeholder="Search machine, assembly, or sub-assembly… (or an assembly #)"
                             v-model="rcTerm"
                             @input="rcSearchDebounced" />
                    </div>
                    <div v-if="rcBusy" class="account-setup-add-hint">Searching…</div>
                    <ul v-else-if="rcRows.length" class="account-setup-add-results asc-rc-results">
                      <li class="asc-rc-row is-head">
                        <span class="asc-rc-machine">Machine</span>
                        <span class="asc-rc-asm">Assembly</span>
                        <span class="asc-rc-sub">Sub-Assembly</span>
                        <span class="asc-rc-has">Chart</span>
                        <span class="asc-rc-mod" title="When the chart image was last uploaded or replaced">Date Modified</span>
                        <span class="asc-rc-act"></span>
                      </li>
                      <template v-for="r in rcRows">
                        <li :key="'rc-' + r.id"
                            class="asc-rc-row is-pick"
                            :class="{ 'is-open': rcExpanded === r.id }"
                            @click="rcToggleRow(r)">
                          <span class="asc-rc-machine">{{ r.machineName || '—' }}</span>
                          <span class="asc-rc-asm">{{ r.assemblyName || '—' }}</span>
                          <span class="asc-rc-sub">{{ r.subAssemblyName || '—' }}</span>
                          <!-- Same at-a-glance icon as Search's "Choose an Assembly" dropdown:
                               full-strength 🖼 = a chart is on file, faded/greyscale = none. -->
                          <span class="asc-rc-has asc-rc-drawing-ico" :class="{ 'is-none': !r.hasChart }"
                                :title="r.hasChart ? 'Has a chart on file' : 'No chart on file'"
                                aria-hidden="true">🖼</span>
                          <span class="asc-rc-mod" :class="{ 'is-none': !r.modifiedUtc }"
                                :title="r.modifiedUtc ? '' : 'Not replaced since the original import'">{{ r.modifiedUtc ? formatWhen(r.modifiedUtc) : '—' }}</span>
                          <span class="asc-rc-act">{{ rcExpanded === r.id ? '▲' : '▼' }}</span>
                        </li>
                        <li v-if="rcExpanded === r.id" :key="'rcx-' + r.id" class="asc-rc-detail" @click.stop>
                          <div v-if="rcDone" class="account-setup-copy-result is-ok">
                            ✓ Chart replaced — it now serves everywhere this
                            {{ r.subAssemblyName ? 'sub-assembly' : 'assembly' }} appears.
                            (Pages that already showed the old chart may need a refresh.)
                          </div>
                          <div v-if="rcError" class="account-setup-copy-result is-err">{{ rcError }}</div>
                          <div class="asc-rc-panes">
                            <div class="asc-rc-pane">
                              <div class="asc-rc-pane-h">
                                <span>{{ rcDone ? 'Previous chart' : 'Current chart' }}</span>
                                <span class="asc-rc-zoom">
                                  <button v-if="!rcEditMode && rcCurUrl" type="button" class="asc-rc-editbtn"
                                          title="Edit the item-number bubbles on this chart (same editor as the Search page)"
                                          @click="rcStartBalloonEdit">&#9998; Edit bubbles</button>
                                  <template v-if="rcEditMode">
                                    <button type="button" class="asc-rc-editbtn is-save" :disabled="rcEditSaving || !rcEditDirty"
                                            @click="rcSaveBalloonEdit">{{ rcEditSaving ? 'Saving…' : 'Save bubbles' }}</button>
                                    <button type="button" class="asc-rc-editbtn" :disabled="rcEditSaving"
                                            @click="rcCancelBalloonEdit">Cancel</button>
                                  </template>
                                  <button type="button" title="Zoom out" @click="rcZoom('cur', -1)">−</button>
                                  <button type="button" title="Zoom in" @click="rcZoom('cur', 1)">+</button>
                                </span>
                              </div>
                              <div v-if="rcEditMode" class="asc-rc-editrow">
                                <span class="asc-rc-edithint">Click empty chart to add a bubble · drag a bubble to move · &#10530; resizes · select to rename/delete</span>
                                <span v-if="rcEditSelected >= 0 && rcEditBalloons[rcEditSelected]" class="asc-rc-editsel">
                                  <input ref="rcEditCodeInput"
                                         v-model="rcEditBalloons[rcEditSelected].code"
                                         class="asc-rc-codeinput" placeholder="code e.g. 5L"
                                         @input="rcEditDirty = true"
                                         @keydown.enter.prevent="rcEditSelected = -1" />
                                  <button type="button" class="asc-rc-delbtn" title="Delete this bubble"
                                          @click="rcRemoveBalloon(rcEditSelected)">&#10005;</button>
                                </span>
                                <span v-if="rcEditError" class="asc-rc-editerr">{{ rcEditError }}</span>
                              </div>
                              <div class="asc-rc-imgwrap" :class="{ 'is-editing': rcEditMode }"
                                   @wheel.prevent="rcWheel('cur', $event)"
                                   @mousedown="rcPanStart('cur', $event)"
                                   @click="rcCanvasClick">
                                <div v-if="rcCurUrl" class="asc-rc-canvas" :style="{ transform: 'scale(' + rcZoomCur + ')' }">
                                  <img ref="rcImg" :src="rcCurUrl" alt="Current chart" draggable="false" @load="rcMeasureImg" />
                                  <!-- Bubble layer sits INSIDE the scaled canvas so it tracks zoom for free.
                                       View mode = the same red rings customers see on Search; edit mode = the
                                       Search modal's editor markers (drag/resize/rename/delete). -->
                                  <div v-if="rcImgBox.cw && (rcEditMode || rcBalloons.items.length)"
                                       class="asc-rc-balloon-layer"
                                       :style="{ left: rcImgBox.ox + 'px', top: rcImgBox.oy + 'px', width: rcImgBox.cw + 'px', height: rcImgBox.ch + 'px' }">
                                    <template v-if="!rcEditMode">
                                      <span v-for="(b, bi) in rcBalloons.items"
                                            :key="'rcb-' + bi + '-' + b.code"
                                            class="asc-rc-hotspot"
                                            :style="rcHotspotStyle(b.box)"
                                            :title="'Item ' + b.code"></span>
                                    </template>
                                    <template v-else>
                                      <div v-for="(b, ai) in rcEditBalloons"
                                           :key="'rce-' + ai"
                                           class="asc-rc-marker" :class="{ 'is-selected': rcEditSelected === ai }"
                                           :style="rcHotspotStyle({ x: b.x, y: b.y, w: b.w, h: b.h })"
                                           @mousedown.stop.prevent="rcStartMarkerDrag(ai)"
                                           @click.stop="rcEditSelected = ai">
                                        <span class="asc-rc-mcode">{{ b.code || '?' }}</span>
                                        <button v-if="rcEditSelected === ai" type="button" class="asc-rc-mresize"
                                                @mousedown.stop.prevent="rcStartMarkerResize(ai)" title="Drag to resize">&#9707;</button>
                                      </div>
                                    </template>
                                  </div>
                                </div>
                                <div v-else-if="rcDetail" class="asc-rc-noimg">No chart on file yet.</div>
                                <div v-else class="asc-rc-noimg">Loading…</div>
                              </div>
                            </div>
                            <div class="asc-rc-pane">
                              <div class="asc-rc-pane-h">
                                <span>Corrected chart</span>
                                <span class="asc-rc-zoom">
                                  <button type="button" title="Zoom out" @click="rcZoom('new', -1)">−</button>
                                  <button type="button" title="Zoom in" @click="rcZoom('new', 1)">+</button>
                                </span>
                              </div>
                              <div class="asc-rc-imgwrap"
                                   @wheel.prevent="rcWheel('new', $event)"
                                   @mousedown="rcPanStart('new', $event)">
                                <div v-if="rcFile.dataUri" class="asc-rc-canvas" :style="{ transform: 'scale(' + rcZoomNew + ')' }">
                                  <img :src="rcFile.dataUri" alt="Corrected chart" draggable="false" />
                                </div>
                                <div v-else class="asc-rc-noimg">Pick the corrected image below.</div>
                              </div>
                            </div>
                          </div>
                          <div class="asc-rc-actions">
                            <input type="file"
                                   ref="rcFileInput"
                                   accept="image/png,image/jpeg"
                                   class="asc-rc-fileinput"
                                   @change="rcFileChange" />
                            <button type="button"
                                    class="account-setup-copy-btn"
                                    :disabled="rcUploading"
                                    @click="rcPick">{{ rcFile.name ? 'Choose a different image…' : 'Choose corrected chart…' }}</button>
                            <button type="button"
                                    class="account-setup-copy-btn asc-rc-pastebtn"
                                    :disabled="rcUploading"
                                    title="Paste a screenshot from your clipboard (or just press Ctrl+V)"
                                    @click="rcPasteClick">📋 Paste</button>
                            <span v-if="rcFile.name" class="asc-rc-filename">{{ rcFile.name }}</span>
                            <button type="button"
                                    class="account-setup-copy-btn asc-rc-uploadbtn"
                                    :disabled="!rcFile.dataUri || rcUploading || !rcDetail"
                                    @click="rcUpload(r)">{{ rcUploading ? 'Uploading…' : 'Upload' }}</button>
                          </div>
                          <div v-if="rcFile.error" class="asc-rc-fileerr">{{ rcFile.error }}</div>
                          <p class="account-setup-copy-hint asc-rc-note">
                            PNG or JPG, max 3&nbsp;MB — pick a file, or screenshot the chart and press <b>Ctrl+V</b>
                            (or 📋 Paste) to drop it straight in. The upload replaces the chart for this exact
                            {{ r.subAssemblyName ? 'sub-assembly' : 'assembly' }} only.
                            Scroll or −/+ to zoom, click-drag to pan either side; bubble edits save for everyone (Search included).
                          </p>
                        </li>
                      </template>
                    </ul>
                    <div v-else-if="rcSearched && (rcTerm || '').trim()" class="account-setup-add-hint">
                      No matching charts.
                    </div>
                  </div>
                </div><!-- /.account-setup-nav (Replace Charts) -->
              </div><!-- /utility catbody -->
            </li>

            <!-- ===== Event Logs & History — all-company at-a-glance feed ===== -->
            <li id="asc-cat-events" class="account-setup-item">
              <button type="button"
                      class="account-setup-row"
                      :class="{ 'is-open': !!open.events }"
                      @click="toggleEvents">
                <span class="account-setup-row-toggle">{{ open.events ? '−' : '+' }}</span>
                <span class="account-setup-row-label">Event Logs &amp; History</span>
                <span class="account-setup-row-join">Every change &amp; system event across all companies — newest first</span>
                <span class="account-setup-row-count" :class="{ 'is-zero': !eventLog.length }">{{ eventLog.length }}</span>
              </button>
              <div v-if="open.events" class="account-setup-catbody account-setup-eventsdetail">
                <div class="account-setup-events-sub">
                  All companies · newest first · click an entry for details
                  <button type="button" class="account-setup-events-refresh" :disabled="eventBusy"
                          title="Refresh" @click="loadEventLog">⟳</button>
                </div>
                <label class="account-setup-debug-toggle"
                       title="Shows a live diagnostics overlay (route, card &amp; cart positions and sizes) on EVERY page. Admin-only; persists in this browser until turned off.">
                  <input type="checkbox" :checked="debugOverlay"
                         @change="toggleDebugOverlay($event.target.checked)" />
                  <span>🐞 Debug overlay <small>— live layout/alignment diagnostics on every page</small></span>
                </label>
                <!-- data-activity-context: the recorder walks up for the nearest one, so every
                     click in here records WHERE it happened, not just the route behind the
                     overlay. Bound to the live mode so it says which report was open. -->
                <div :data-activity-context="activityContextLabel">
                <div class="account-setup-events-diag">
                  <select class="account-setup-events-diagtype" v-model="diagType" title="What to diagnose">
                    <option value="email-delivery">Email not received (reset)</option>
                    <option value="user-create">New-user creation</option>
                    <option value="user-login">User can't log in</option>
                    <option value="account-status">Account status</option>
                    <option value="user-insights">Browse User Insights</option>
                  </select>
                  <input v-if="!userInsightsMode" type="text" class="account-setup-events-diaginput"
                         :placeholder="diagPlaceholder"
                         v-model="diagEmail" @keyup.enter="runDiagnose" />
                  <button v-if="!userInsightsMode" type="button" class="account-setup-events-diagbtn"
                          :disabled="diagBusy || !diagEmail" @click="runDiagnose">{{ diagBusy ? '…' : 'Check' }}</button>
                  <button v-else type="button" class="account-setup-events-diagbtn"
                          :disabled="uiBusy" @click="loadActivityUsers">{{ uiBusy ? '…' : 'Refresh' }}</button>
                </div>

                <!-- ===== Chat — answers for WHICHEVER report the dropdown has selected.
                     Grounded server-side in the same aggregate the cards show, so the chat and
                     the screen cannot disagree. -->
                <div v-if="logChatAvailable" class="asc-lc">
                  <input type="text" class="asc-lc-in" v-model="logAsk"
                         :placeholder="logChatPlaceholder" @keyup.enter="askTheLogs" />
                  <button type="button" class="account-setup-mini-btn is-gi"
                          :disabled="logAskBusy || !logAsk" @click="askTheLogs">
                    {{ logAskBusy ? "Thinking…" : "Ask" }}</button>
                </div>
                <div v-if="logAnswer" class="asc-as-insight">
                  <div class="asc-as-insight-h">Answer
                    <button type="button" class="asc-lc-x" title="Dismiss" @click="logAnswer = ''">&times;</button>
                  </div>
                  <div class="asc-as-insight-b">{{ logAnswer }}</div>
                </div>

                <!-- ===== Account Status report =====
                     The dropdown selection is the report. Counts come pre-aggregated from the
                     server; the drill lists name the accounts so a card is a worklist rather
                     than a statistic. -->
                <template v-if="accountStatusMode">
                  <div v-if="asBusy && !asData" class="account-setup-events-empty">Loading account status…</div>
                  <div v-else-if="asData && asData.available === false" class="account-setup-events-empty">
                    Account status is unavailable — the backend needs publishing.
                  </div>
                  <template v-else-if="asData && asData.summary">
                    <div class="asc-as-grid">
                      <div v-for="k in asCards" :key="k.key" class="asc-as-card" :class="{ 'is-warn': k.warn }">
                        <div class="asc-as-num">{{ k.value }}</div>
                        <div class="asc-as-lbl">{{ k.label }}</div>
                        <div v-if="k.note" class="asc-as-note">{{ k.note }}</div>
                      </div>
                    </div>
                    <div class="asc-as-actions">
                      <button type="button" class="account-setup-mini-btn" @click="loadAccountStatus">Refresh</button>
                      <button type="button" class="account-setup-mini-btn is-gi"
                              :disabled="asInsightBusy" @click="generateAccountInsights">
                        {{ asInsightBusy ? "Generating…" : "Generate Insights" }}</button>
                      <button type="button" class="account-setup-mini-btn" @click="presentAccountStatus">Presentation</button>
                    </div>
                    <div v-if="asInsight" class="asc-as-insight">
                      <div class="asc-as-insight-h">Insights <small>generated {{ asInsightAt }}</small></div>
                      <div class="asc-as-insight-b">{{ asInsight }}</div>
                    </div>
                    <!-- Drill lists: who, not just how many. -->
                    <div v-for="b in asBuckets" :key="'b-' + b.key" class="asc-as-bucket">
                      <div class="asc-as-bucket-h">{{ b.label }} <small>({{ b.rows.length }} shown)</small></div>
                      <ul class="asc-as-bucket-l">
                        <li v-for="(r, i) in b.rows" :key="b.key + i">
                          <span class="asc-as-em">{{ r.email }}</span>
                          <span class="asc-as-dt">{{ fmtCreated(r.created) }}</span>
                        </li>
                      </ul>
                    </div>
                  </template>
                </template>
                <!-- ===== Browse User Insights — accounts by most recent activity =====
                     Expanding a row loads that account's play-by-play. Same row chrome as the
                     event feed below, so the panel reads as one thing in two modes. -->
                <template v-if="userInsightsMode">
                  <div class="asc-as-actions">
                    <button type="button" class="account-setup-mini-btn is-gi"
                            :disabled="uiInsightBusy" @click="generateUserInsights">
                      {{ uiInsightBusy ? "Generating…" : "Generate Insights" }}</button>
                    <button type="button" class="account-setup-mini-btn" @click="presentUserInsights">Presentation</button>
                  </div>
                  <div v-if="uiInsight" class="asc-as-insight">
                    <div class="asc-as-insight-h">Insights <small>generated {{ uiInsightAt }}</small></div>
                    <div class="asc-as-insight-b">{{ uiInsight }}</div>
                  </div>
                  <input type="text" class="account-setup-events-search"
                         placeholder="Filter people (name or email)…" v-model="uiSearch" />
                  <div v-if="uiBusy && !uiUsers.length" class="account-setup-events-empty">Loading activity…</div>
                  <div v-else-if="!uiUsers.length" class="account-setup-events-empty">
                    No activity recorded yet — nothing is captured until the user-activity table
                    has been created on this database and the backend published.
                  </div>
                  <div v-else-if="!filteredActivityUsers.length" class="account-setup-events-empty">No one matches “{{ uiSearch }}”.</div>
                  <ul v-else class="account-setup-events-list">
                    <template v-for="u in filteredActivityUsers">
                      <li :key="'ui-' + u.userId" class="account-setup-events-item asc-ui-row"
                          @click="toggleActivityUser(u)">
                        <span class="asc-ui-caret">{{ uiExpanded[u.userId] ? '−' : '+' }}</span>
                        <span class="asc-ui-who">
                          <strong>{{ u.name || '(no name)' }}</strong>
                          <small>{{ u.email }}</small>
                        </span>
                        <span class="asc-ui-when" :title="u.lastActivity">{{ fmtActivityWhen(u.lastActivity) }}</span>
                        <span class="asc-ui-counts" :title="u.totalEvents + ' event(s) all time'">
                          {{ u.recentEvents }} recent · {{ u.totalEvents }} total
                        </span>
                      </li>
                      <li v-if="uiExpanded[u.userId]" :key="'uit-' + u.userId" class="asc-ui-detail">
                        <div v-if="uiTimelineBusy[u.userId]" class="account-setup-events-empty">Loading…</div>
                        <div v-else-if="!(uiTimeline[u.userId] || []).length" class="account-setup-events-empty">No events.</div>
                        <template v-else v-for="d in timelineDays(u.userId)">
                          <div :key="'dh-' + u.userId + d.key" class="asc-ui-day">
                            <span class="asc-ui-day-l">{{ d.label }} <small>({{ d.rows.length }} events)</small></span>
                            <button type="button" class="account-setup-mini-btn is-gi"
                                    :disabled="uiDayBusy[u.userId + '|' + d.key]"
                                    @click.stop="summarizeDay(u.userId, d.key)">
                              {{ uiDayBusy[u.userId + '|' + d.key] ? "Summarizing…" : "Summarize play-by-play" }}</button>
                          </div>
                          <div v-if="uiDayText[u.userId + '|' + d.key]"
                               :key="'ds-' + u.userId + d.key" class="asc-as-insight">
                            <div class="asc-as-insight-b">{{ uiDayText[u.userId + "|" + d.key] }}</div>
                          </div>
                          <ul :key="'dl-' + u.userId + d.key" class="asc-ui-events">
                          <li v-for="ev in d.rows" :key="'e' + ev.id" class="asc-ui-event">
                            <span class="asc-ui-kind" :class="'is-' + ev.kind">{{ activityIcon(ev.kind) }}</span>
                            <span class="asc-ui-time" :title="ev.occurredAt">{{ fmtActivityClock(ev.occurredAt) }}</span>
                            <span class="asc-ui-what">{{ describeActivity(ev) }}</span>
                            <!-- An admin previewing this account did this, not the account holder. -->
                            <span v-if="ev.viaAdmin" class="asc-ui-via" title="Performed by an admin using View as User">via admin</span>
                          </li>
                          </ul>
                        </template>
                        <button v-if="(uiTimeline[u.userId] || []).length >= 200 && !uiTimelineEnd[u.userId]"
                                type="button" class="account-setup-mini-btn"
                                :disabled="uiTimelineMoreBusy[u.userId]"
                                @click.stop="loadActivityTimeline(u.userId, true)">
                          {{ uiTimelineMoreBusy[u.userId] ? "Loading…" : "Load older" }}</button>
                      </li>
                    </template>
                  </ul>
                </template>
                </div>

                <input v-if="!userInsightsMode" type="text" class="account-setup-events-search"
                       placeholder="Search events (company, user, action, error…)"
                       v-model="eventSearch" />
                <div v-if="!userInsightsMode && eventBusy && !eventLog.length" class="account-setup-events-empty">Loading…</div>
                <div v-else-if="!userInsightsMode && !eventLog.length" class="account-setup-events-empty">No events yet.</div>
                <div v-else-if="!userInsightsMode && !filteredEventLog.length" class="account-setup-events-empty">No events match “{{ eventSearch }}”.</div>
                <ul v-else-if="!userInsightsMode" class="account-setup-events-list">
                  <li v-for="ev in filteredEventLog" :key="ev.id"
                      class="account-setup-events-item"
                      :class="{ 'is-open': expandedEvents[ev.id] }">
                    <button type="button" class="account-setup-events-row" @click="toggleEvent(ev.id)">
                      <span class="account-setup-events-dot" :class="eventDotClass(ev)"></span>
                      <span class="account-setup-events-rowmain">
                        <span class="account-setup-events-rowtitle">{{ ev.title }}</span>
                        <span class="account-setup-events-meta"><template v-if="ev.company">{{ ev.company }} · </template>{{ formatWhen(ev.whenUtc) }}<template v-if="ev.who"> · {{ ev.who }}</template></span>
                      </span>
                      <span class="account-setup-events-caret">{{ expandedEvents[ev.id] ? '▾' : '▸' }}</span>
                    </button>
                    <div v-if="expandedEvents[ev.id]" class="account-setup-events-detail">
                      <p v-if="ev.detail" class="account-setup-events-line">{{ ev.detail }}</p>
                      <div v-if="ev.exception" class="account-setup-events-exc">
                        <span class="account-setup-events-exc-label">Error</span>
                        <code>{{ ev.exception }}</code>
                      </div>
                      <div v-if="(ev.suggestions || []).length" class="account-setup-events-sugg">
                        <div class="account-setup-events-sugg-label">Suggestions to fix</div>
                        <ul>
                          <li v-for="(s, si) in ev.suggestions" :key="ev.id + '-s' + si">{{ s }}</li>
                        </ul>
                      </div>
                      <div v-if="ev.data && hasKeys(ev.data)" class="account-setup-events-data">
                        <div v-for="(val, k) in ev.data" :key="ev.id + '-d-' + k" class="account-setup-events-datarow">
                          <span class="account-setup-events-datakey">{{ k }}</span>
                          <span class="account-setup-events-dataval">{{ val }}</span>
                        </div>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </li>
          </ul>
        </template>
      </div>
    </b-modal>

    <!-- ===== Group-members hover popup (shared by Report Access + Territory Access chips) =====
         Hover a 👥 chip → members (display names, email as the sub-line) fetched live from
         Microsoft Graph, cached per group for the session. position:fixed escapes the modal's
         scroll container; mouseenter on the popup keeps it open so long lists can be scrolled.
         A 🛡 LOCAL ROLE chip has no Graph identity — it shows an explanatory note instead (its
         membership is the portal's own user list, expanded server-side on every sync). -->
    <div v-if="rgHover.groupId"
         class="asc-racc-members"
         :class="{ 'is-up': rgHover.up }"
         :style="{ left: rgHover.x + 'px', top: rgHover.y + 'px' }"
         @mouseenter="rgHoverKeep"
         @mouseleave="rgHoverEnd">
      <div class="asc-racc-members-h">
        <span aria-hidden="true">{{ rgHover.isRole ? '🛡' : '👥' }}</span> {{ rgHover.name }}<template v-if="rgHover.members"> · {{ rgHover.members.length }} member{{ rgHover.members.length === 1 ? '' : 's' }}</template>
      </div>
      <div v-if="rgHover.busy" class="asc-racc-members-note">Loading members…</div>
      <div v-else-if="rgHover.error" class="asc-racc-members-note">{{ rgHover.error }}</div>
      <ul v-else-if="rgHover.members && rgHover.members.length" class="asc-racc-members-list">
        <li v-for="(m, mi) in rgHover.members" :key="'rgm-' + mi">
          <span class="asc-racc-member-name">{{ m.name || m.email }}</span>
          <span v-if="m.name && m.email" class="asc-racc-member-mail">{{ m.email }}</span>
        </li>
      </ul>
      <div v-else class="asc-racc-members-note">{{ rgHover.note || "No members." }}</div>
    </div>

    <!-- APPROVE CONFIRM. Matches the report's centred processing overlay (dim + blur backdrop, white
         card with the red top rule). Its .ci-proc-* classes live in CustomerInsights.vue's SCOPED style
         block, so they cannot be reused by class name here — these are local classes with the same look. -->
    <div v-if="approveConfirm" class="asc-appr-overlay" @click.self="approveConfirm = null">
      <div class="asc-appr-box" role="dialog" aria-modal="true">
        <div class="asc-appr-txt">{{ approveConfirm.msg }}</div>
        <div v-if="approveConfirm.email" class="asc-appr-sub">{{ approveConfirm.email }}</div>
        <div class="asc-appr-btns">
          <button type="button" class="asc-appr-cancel" @click="approveConfirm = null">Cancel</button>
          <button type="button" class="asc-appr-go" @click="approveConfirmGo">
            {{ approveConfirm.next ? "Yes, approve" : "Yes, set pending" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import FeApiService from "@/assets/js/FeApiService";
import APIService from "@/assets/js/APIService";
import SetupUserForm from "@/components/Common/SetupUserForm.vue";
import SetupCompanyForm from "@/components/Common/SetupCompanyForm.vue";
import SetupObjectForm from "@/components/Common/SetupObjectForm.vue";

const apiService = new FeApiService();
// Separate instance for the global header-navigation editor (Menu Navigation
// tool in Utility Actions). The nav endpoints live on APIService, not on the
// FeApiService the rest of this widget uses — keep them on a distinct `navApi`
// so the existing FeApiService `apiService` calls are untouched.
const navApi = new APIService();

// Replace Charts bubble overlay: same OCR-map decoder the Search page uses, so
// the bubbles shown/edited here are EXACTLY what Search renders.
import { extractBalloons } from "@/assets/js/calloutLocator";

// Report Access — Entra security-group typeahead + membership resolution. Reads go SERVER-FIRST
// (app-only Graph, gated by GraphGroups:Enabled): no Microsoft sign-in prompt, and membership comes
// from the permission consented on the APP REGISTRATION rather than from whoever is signed in. With
// the flag off — or against a backend that predates those routes — entraGroups transparently falls
// back to the old delegated in-browser flow, so behavior is unchanged until the flag is switched on.
// fetchGroupMembersDetailed powers the member-names hover popup on the group chips.
import { searchEntraGroups, fetchGroupMemberEmails, fetchGroupMembersDetailed, fetchGroupInfo } from "@/assets/js/entraGroups";

// A Territory-Access / Report-Access chip can ALSO be one of the portal's own roles. Such a chip rides
// in the SAME `groups` array as the Entra chips — told apart only by this id prefix: "role:<Role Name>".
// A colon can never appear in an Entra group object id (they are GUIDs), so the two id spaces cannot
// collide and the posted payload shape { id, name } is unchanged. The SERVER expands a role chip from
// AspNetUserRoles → AspNetUsers.Email; the browser never resolves one (it has no access to that data).
const LOCAL_ROLE_PREFIX = "role:";

export default {
  name: "AccountSetupWidget",

  components: { SetupUserForm, SetupCompanyForm, SetupObjectForm },

  data() {
    return {
      showModal: false,
      // In-modal add/edit form view: null = normal setup view; otherwise
      // { kind: 'user'|'company', id: <id or null for add> }.
      editEntity: null,
      // Event Logs & History (right panel) — cross-company feed.
      eventLog: [],
      // Global debug overlay on/off (persisted per-browser; App.vue renders
      // the overlay). Toggled by the checkbox in the Event Logs panel below.
      debugOverlay:
        typeof localStorage !== "undefined" &&
        localStorage.getItem("jcs_debugOverlay") === "1",
      eventBusy: false,
      expandedEvents: {},
      eventSearch: "",
      diagEmail: "",
      diagType: "email-delivery",
      diagBusy: false,
      // Per-user expand (companies / password / deactivate) in the Users list.
      userExpanded: {},
      userCompanies: {},
      userCompaniesBusy: {},
      loading: false,
      loadError: false,
      setup: null,
      // Per-category expand state (key -> bool).
      open: {},
      // ----- Replace Charts (Utility Actions tool) -----
      // Search any chart (assembly OR sub-assembly row) and upload a corrected
      // image via the existing Master-only PUT /assembly/{id}. The
      // report-incorrect-chart helpdesk email deep-links here with
      // ?replaceChart={assemblyId} (see rcOpenFromQuery).
      rcOpen: false,
      rcTerm: "",
      rcBusy: false,
      rcSearched: false,
      rcRows: [],
      rcExpanded: 0,        // Assembly.Id of the expanded row
      rcDetail: null,       // full entity for the expanded row — the PUT echoes it back
      rcZoomCur: 1,
      rcZoomNew: 1,
      rcFile: { name: "", dataUri: "", error: "" },
      rcUploading: false,
      rcDone: false,
      rcError: "",
      rcStamp: Date.now(), // cache-buster: a replaced blob keeps the same URL
      rcBlobBase: "https://steeleimages.blob.core.windows.net/images/",
      // Bubble overlay + editor (carried over from the Search page's fullscreen
      // modal — same OCR map, same overrides store, same save payload, so a fix
      // made here is immediately live on Search and vice versa).
      rcCalloutMap: null,        // base OCR map (public/assembly-callouts.json), fetched once
      rcCalloutOverrides: null,  // admin overrides blob (Settings row via /callout/overrides)
      rcImgBox: { cw: 0, ch: 0, ox: 0, oy: 0 },   // current-chart img base box (scale=1)
      rcNatural: { w: 0, h: 0 },                  // natural px — seeds ref dims for un-OCR'd charts
      rcEditMode: false,
      rcEditBalloons: [],
      rcEditSelected: -1,
      rcEditDirty: false,
      rcEditSaving: false,
      rcEditError: "",
      // Copy-from-company state.
      companies: [],
      copySource: "",
      copyTerm: "",
      copying: false,
      copyResult: null,
      // Menu Navigation (GLOBAL header menu) editor — nested collapsible in
      // Utility Actions. Lazy-loaded the first time it's expanded. No companyId
      // anywhere: these links are shared by every company.
      navOpen: false,
      navLoaded: false,
      navBusy: false,
      navLoadError: false,
      navItems: [],            // header items, sorted by sortOrder asc
      // FALLBACK ONLY — the real list is fetched from GET /navigation/roles (loadNavRoleOptions),
      // which derives it from Roles.AnyRole so Constants.cs is the single source of truth. This
      // literal used to BE the source and silently went stale the moment Manufacturing and Foundry
      // were added for Report Access, which is exactly why those roles could not be targeted. It now
      // includes all seven so even a failed fetch degrades to something correct-as-of-today.
      navRoleOptions: ["Master", "Sales All", "Sales", "Australian", "Customer",
                       "Sales Insights", "Manufacturing Insights", "Foundry Insights",
                       "Work Order Insights", "All Insights"],
      navRolesOpen: "",        // id of the row whose Roles dropdown is open
      navDenyOpen: "",         // id of the row whose "Hide from" (deny) dropdown is open
      navOriginal: {},         // id -> { name, url } baseline for dirty-check
      navRowBusy: "",          // item id whose row save/delete is in flight
      navRowSaved: "",         // item id that just saved (brief ✓)
      navReordering: false,    // a reorder persist is in flight
      navReorderSaved: false,  // brief "order saved" flag
      newNav: { name: "", url: "" },
      navAddBusy: false,
      // Sales Territories editor (nested in Utility Actions). Admin-managed
      // State → Territory → Sales Rep; drives the Customer Insights map.
      salesTerrOpen: false,
      salesTerrLoaded: false,
      salesTerrBusy: false,
      autoAssignOn: true,          // Sales Territories: Customer-ID-prefix auto-match toggle (server-persisted)
      autoAssignBusy: false,
      salesTerrLoadError: false,
      // The server's explanation for that failure, when it sent one. Master-only screen.
      salesTerrLoadMsg: "",
      salesTerrItems: [],
      expandedWarnInsight: "",       // 💡 which unmapped-rep warning has its "why / how / result" explainer open
      salesTerrOriginal: {},     // id -> {state,territory,salesRep} baseline for dirty-check
      salesTerrRowBusy: "",
      salesTerrRowSaved: "",
      newSalesTerr: { state: "", territory: "", salesRep: "", color: "" },
      pendingImport: null,       // { name, rows:[{state,territory,salesRep,color}] } awaiting confirm
      importBusy: false,
      importMsg: "",
      // Per-customer territory EXCEPTIONS (override the Customer-ID-prefix auto-assign rule).
      terrExceptions: [],
      newTerrExc: { customerId: "", territory: "", salesRep: "", shipCountry: "", shipZip: "", isActive: true },
      repAliases: [],                                    // departed/alternate rep -> current rep (rpt_RepAlias)
      newRepAlias: { aliasName: "", canonicalName: "" },
      repAliasBusy: false, repAliasAddBusy: false, repAliasRowBusy: 0, repAliasMsg: "",
      terrExcBusy: false, terrExcLoaded: false, terrExcAddBusy: false,
      terrExcRowBusy: 0, terrExcRowSaved: 0, terrExcMsg: "",
      shipTosByCust: {}, shipTosLoading: {},   // per-customer past ship-to (country/zip) options for the dropdowns
      salesTerrAddBusy: false,
      // Unmapped-rep warnings (reps on orders that match no assignment) + admin ignore state.
      terrWarnings: [],              // [{ name, orders, sales, ignored }]
      terrWarnBusy: "",              // rep name currently ignoring/restoring
      terrWarningsShowIgnored: false,
      // Unmapped-rep DRILL-DOWN (all lazy): expand a rep → its (prefix+country) groups; expand a group → its orders.
      expandedWarnRep: "",           // rep name whose breakdown is open ("" = none)
      warnGroups: {},                // rep -> [{ prefix, country, orders, customers, sales }]
      warnGroupsBusy: "",            // rep whose groups are loading
      expandedWarnGroup: "",         // "rep|prefix|country" of the open group's order list
      warnOrders: {},                // "rep|prefix|country" -> [{ orderNo, customerId, customerName, customerState, shipToCountry, shipToPostal, total, orderDate }]
      warnOrdersBusy: false,
      // Territory Access (login emails per territory) — edits the same rpt_UserTerritory link as
      // Account Setup → Users, but from the territory side.
      territoryEmails: {},        // territory -> comma-separated EFFECTIVE emails (groups + manual — feeds the CSV export)
      territoryUsersBusy: "",     // territory currently saving
      territoryUsersSaved: "",    // territory just saved (shows ✓)
      territoryUsersMsg: "",      // status / warning line under the access list
      territoryUsersUnknown: [],  // emails that matched no portal login
      // Entra security groups per territory — same machinery as Report Access (rg*), keyed by territory.
      // A chip whose id starts with "role:" is a LOCAL portal role, resolved server-side (see
      // LOCAL_ROLE_PREFIX above); everything else is an Entra group id (GUID).
      territoryGroups: {},        // territory -> [{id, name}]
      territoryGroupEmails: {},   // territory -> [emails] resolved from the groups (last sync)
      territoryManual: {},        // territory -> "a@x.com, b@y.com" (manual extras input)
      tgManualOpen: "",           // territory whose manual expander is open
      tgBusy: "",                 // territory currently group-syncing
      tgTerm: {},                 // territory -> typeahead input text
      tgSug: { key: "", items: [], open: false, hi: -1, busy: false, note: "" },   // note = an "Entra unavailable" hint shown above the local roles
      territoryPending: [],       // aggregate readout: [{ email, territories:[] }] granted access but not yet signed in
      territoryAccessLoaded: false, // true once the pending readout has loaded (so "all signed in" doesn't flash first)
      // Report Access (login emails per report) — governs who sees each report on the Reports dashboard.
      reportAccessGroups: [
        { key: "sales-insights", label: "Sales Insights" },
        { key: "manufacturing-insights", label: "Manufacturing Insights" },
        { key: "foundry-insights", label: "Foundry Insights" },
        { key: "work-order-insights", label: "Work Order Insights" },
        { key: "accounting-insights", label: "Accounting Insights" },
        // Granting "All Insights" is equivalent to adding someone to all four lists above — the role
        // it provisions is a COVERING role that satisfies every report gate, the way Master does.
        { key: "all-insights", label: "All Insights" }
      ],
      reportEmails: {},              // reportKey -> comma-separated emails (editable in the grid)
      reportUsersBusy: "",           // reportKey currently saving
      reportUsersSaved: "",          // reportKey just saved (shows ✓)
      reportUsersMsg: "",            // status / warning line under the access list
      reportUsersUnknown: [],        // emails that matched no portal login (pre-registered)
      // Entra security groups per report (the chips) + the emails they resolve to. Group changes
      // save + sync membership immediately; MANUAL emails live in their own expander and survive
      // group syncs (external addresses like WCS@DELTANET.COM can't be Entra members).
      reportGroups: {},              // reportKey -> [{id, name}]
      reportGroupEmails: {},         // reportKey -> [emails] resolved from the groups (last sync)
      reportManual: {},              // reportKey -> "a@x.com, b@y.com" (the manual-extras input)
      rgManualOpen: "",              // reportKey whose manual expander is open
      rgBusy: "",                    // reportKey currently group-syncing (search stays usable)
      rgTerm: {},                    // reportKey -> typeahead input text
      rgSug: { key: "", items: [], open: false, hi: -1, busy: false, note: "" },   // one dropdown at a time
      // Group-members hover popup (shared by the Report Access AND Territory Access chips).
      // Members come live from Graph on first hover and are cached per group for the session.
      // isRole = the hovered chip is a LOCAL portal role: no Graph read, just the explanatory note.
      rgHover: { groupId: "", name: "", isRole: false, members: null, busy: false, error: "", note: "", x: 0, y: 0, up: false },
      reportPending: [],             // [{ email, reports:[] }] granted access but not yet signed in
      reportAccessLoaded: false,     // true once the pending readout has loaded
      reportAccessOpen: false,       // section expanded?
      reportAccessLoadedOnce: false, // lazy-load guard
      // Copy history (browser-local) + undo state.
      history: [],
      historyOpen: false,
      undoingId: "",
      // Per-copy itemized audit (machines/wear still here vs removed), keyed by logId.
      copyDetail: {},
      copyDetailOpen: {},
      copyDetailBusy: "",
      // Inline add/remove (Machines / Wear / Users) state.
      addTerm: {},
      addResults: {},
      addBusy: {},
      removingKey: "",
      // Floating jump control: the long category currently centered in the viewport.
      scrollNavCat: "",
      // Per-user password-help request state (lock icon in the Users list).
      pwBusyKey: "",
      pwSent: {},
      setupLinkSent: {},
      // Header eyeball on Users: show all users (any company) whose password can be viewed.
      // "company" | "all" | "pending". Was a boolean (this-company vs all-companies); a third
      // scope needed a real enum. usersPwFilter survives as a COMPUTED so the existing references
      // (incl. the two !usersPwFilter guards on the row remove button) keep working unchanged.
      usersScope: "company",
      approvingKey: "",   // user ref currently being approved (disables just that row's checkbox)
      approveConfirm: null, // { it, next, email, msg } while the confirm overlay is up

      // ----- Browse User Insights (Event Logs & History -> the diagType dropdown) -----
      uiUsers: [],          // accounts ordered by most recent activity
      uiBusy: false,
      uiSearch: "",
      uiExpanded: {},       // userId -> bool
      uiTimeline: {},       // userId -> events, newest first
      uiTimelineBusy: {},   // userId -> bool

      // ----- Account Status report (the "account-status" dropdown selection) -----
      asData: null,
      asBusy: false,
      asInsight: "",       // Generate Insights text for the Account Status report
      asInsightAt: "",
      asInsightBusy: false,

      // ----- Chat, shared by every Event Logs report -----
      logAsk: "",
      logAnswer: "",
      logAskBusy: false,
      // Browse User Insights: its own Generate Insights result
      uiInsight: "",
      uiInsightAt: "",
      uiInsightBusy: false,
      // Per-account, per-DAY play-by-play. Keyed "userId|yyyy-mm-dd".
      uiDayText: {},
      uiDayBusy: {},
      // Paging needs its OWN flag. Reusing uiTimelineBusy made "Load older" blank the whole
      // scroller back to "Loading…" and re-mount it scrolled to the top — you lost your place
      // every time, which on a 200-row page is most of the point of paging.
      uiTimelineMoreBusy: {},
      // Was the LAST page short? That, not the running total, is what says there is no more.
      uiTimelineEnd: {},
      viewableUsers: [],
      // Copy-the-full-GUID-id button state (Users list shows only the first 4 chars).
      copiedKey: "",
      // Link-a-user-to-another-company inline panel (Users list).
      linkUserRef: "",
      linkUser: {},
      linkTerm: "",
      linkResults: [],
      linkUserCompanyIds: [],
      linkBusy: false,
      linkMsg: "",
      linkErr: false,
      // Create-a-new-user form (Users category).
      newUserOpen: false,
      newUserBusy: false,
      newUserMsg: "",
      newUser: { firstName: "", lastName: "", email: "", password: "" },
      // Setup-gap diagnosis + expand/acknowledge state.
      gapBusy: "",
      gapData: {},
      gapsOpen: false,         // Setup Gaps section — collapsed by default
      gapHistoryOpen: false,   // acknowledged-gaps history sub-section
      hiddenGapKeys: [],       // session-only "Hide" (cleared on reopen/reload)
      gapAckBusy: "",          // gap key currently being acknowledged/restored
      // Parts search -> link the part's machine.
      partTerm: {},
      partResults: {},
      partBusy: {},
      partBusyKey: "",
      // Parts/Wear delete (deactivate) + inline undo + server history.
      delHistory: { part: [], wear: [] },
      deletedRows: {},
      delBusyKey: "",
      delHistOpen: {},
      // Wear bulk "Delete all" — cat.key the inline confirm panel is open for, +
      // a busy flag while the soft/permanent request is in flight.
      wearDeleteConfirm: "",
      wearDeleteBusy: false,
      // Per-part impact (linked customers + machines) for row expand + delete.
      impactOpen: {},
      impact: {},
      impactBusy: 0,
      // Per-row "View all companies" toggle for the expand's company list
      // (collapsed by default when long so it isn't an alarming wall of codes).
      impactCustOpen: {},
      // Per-part reach counts (drive the red/orange/green Part ID color).
      reach: {},
      // Per-category server gap lists (the FULL orange/red set across the whole
      // wear/parts set, independent of the display cap). Keyed by cat.key.
      gaps: {},
      gapsLoaded: {},
      // Per-category color filter ("" | "green" | "orange" | "red").
      colorFilter: {},
    };
  },

  computed: {
    // Back-compat shim: TRUE whenever the list is showing the global pool ("all" or "pending"),
    // which is exactly when the per-row remove button must be hidden (those rows are cross-company).
    usersPwFilter() { return this.usersScope !== "company"; },
    ...mapGetters({ currentUser: "currentUser" }),

    // The diagnose dropdown doubles as a mode switch: every other option asks for an
    // email and runs a check, this one replaces the feed with the per-user browse list.
    accountStatusMode() { return this.diagType === "account-status"; },
    // Reads like a breadcrumb because that is what support needs: "Reports Dashboard" is
    // the route, but the PLACE was Account Setup -> Event Logs & History -> whichever report.
    activityContextLabel() {
      var names = {
        "email-delivery": "Email not received",
        "user-create": "New-user creation",
        "user-login": "User can't log in",
        "account-status": "Account status",
        "user-insights": "Browse User Insights"
      };
      return "Account Setup › Event Logs & History › " + (names[this.diagType] || this.diagType);
    },
    // Chat only where a grounded aggregate exists behind it. The three diagnose-only
    // selections have no report payload yet, so offering a chat there would invite questions
    // it has no data to answer.
    logChatAvailable() { return this.accountStatusMode || this.userInsightsMode; },
    logChatPlaceholder() {
      return this.userInsightsMode
        ? "Ask about user activity — who is busiest, what are people searching…"
        : "Ask about account health — who is unapproved, who cannot sign in…";
    },
    // Card faces. Ordered so the two that are actually actionable read first: 31 unapproved
    // (none newer than 30 days, i.e. nothing is being approved at all) and 21 active accounts
    // with no company, who can sign in to an empty portal.
    asCards() {
      var s = (this.asData && this.asData.summary) || {};
      var p = this.asData ? this.asData.privilegedNoReportAccess : 0;
      return [
        { key: "unapproved", label: "Awaiting approval", value: s.unapproved || 0, warn: (s.unapproved || 0) > 0,
          note: (s.unapproved_30plus || 0) + " over 30 days" },
        { key: "nocompany", label: "Active, no company", value: s.noCompanyLink || 0, warn: (s.noCompanyLink || 0) > 0,
          note: "can sign in, see nothing" },
        { key: "nopw", label: "Never set a password", value: s.neverSetPassword || 0, warn: (s.neverSetPassword || 0) > 0,
          note: "setup link unused" },
        { key: "priv", label: "Privileged, no report access", value: p || 0, warn: (p || 0) > 0 },
        { key: "locked", label: "Locked out", value: s.lockedOut || 0, warn: (s.lockedOut || 0) > 0 },
        { key: "inactlink", label: "Inactive, still linked", value: s.inactiveButLinked || 0 },
        { key: "active", label: "Active accounts", value: s.activeAccounts || 0 },
        { key: "total", label: "Total accounts", value: s.totalAccounts || 0,
          note: (s.internalAccounts || 0) + " internal / " + (s.customerAccounts || 0) + " customer" }
      ];
    },
    asBuckets() {
      var d = (this.asData && this.asData.drill) || [];
      var defs = [
        { key: "unapproved", label: "Awaiting approval" },
        { key: "no-company", label: "Active with no company" },
        { key: "never-password", label: "Never set a password" },
        { key: "locked-out", label: "Locked out" }
      ];
      return defs
        .map(function (x) { return { key: x.key, label: x.label, rows: d.filter(function (r) { return r.bucket === x.key; }) }; })
        .filter(function (x) { return x.rows.length; });
    },
    userInsightsMode() { return this.diagType === "user-insights"; },
    filteredActivityUsers() {
      const q = (this.uiSearch || "").trim().toLowerCase();
      if (!q) return this.uiUsers;
      return this.uiUsers.filter((u) =>
        String(u.name || "").toLowerCase().indexOf(q) >= 0 ||
        String(u.email || "").toLowerCase().indexOf(q) >= 0);
    },
    // Only helpdesk@jcsteele.com may see INTERNAL @jcsteele.com accounts. Keyed off realUser, NOT
    // currentUser: currentUser follows "View as User" impersonation, so a Master previewing someone
    // else would otherwise shift what the gate allows. realUser is always the signed-in account —
    // exactly what the server checks (the token's email claim), and the two must agree.
    // Fails CLOSED: no resolvable signed-in email means internal accounts stay hidden.
    seesInternalAccounts() {
      var g = (this.$store && this.$store.getters) ? this.$store.getters : null;
      var ru = g ? g.realUser : null;
      if (String((ru && ru.email) || "").trim().toLowerCase() !== "helpdesk@jcsteele.com") return false;
      // While previewing another account via "View as User", answer as THAT account would see it.
      // Without this, helpdesk previewing a Master still saw internal accounts, so the preview
      // could not be used to check the rule. The preview payload is { id, name, role } with no
      // email (Head.vue onUserChange), so ANY active preview closes the gate — you only ever
      // preview an account other than your own, and closed is the safe direction.
      if (g && g.viewingAsUser) return false;
      return true;
    },

    // Replace Charts: the expanded row's CURRENT chart URL, cache-busted (a
    // replaced blob keeps the same name/URL, so the browser would otherwise
    // keep showing the stale image).
    rcCurUrl() {
      if (!this.rcDetail || !this.rcDetail.imagePath) return "";
      return this.rcBlobBase + this.rcDetail.imagePath + "?v=" + this.rcStamp;
    },
    // The OCR-map / overrides key for the expanded chart. Search keys entries by
    // decodeURI(url minus the blob base) — its URLs are blobBase + raw imagePath,
    // so this reduces to decodeURI(imagePath). Must stay identical or the two
    // editors would write to different entries.
    rcBalloonKey() {
      if (!this.rcDetail || !this.rcDetail.imagePath) return null;
      try { return decodeURI(this.rcDetail.imagePath); } catch (e) { return this.rcDetail.imagePath; }
    },
    // Effective bubbles for the expanded chart — mirror of Search's
    // dockedBalloons (minus the parts-list partCode lookup): a managed override
    // IS the set (scaled from its ref dims); otherwise the OCR map's tokens.
    rcBalloons() {
      var empty = { entry: null, items: [] };
      var path = this.rcBalloonKey;
      if (!path || !this.rcCalloutMap) return empty;
      var entry = this.rcCalloutMap[path];
      var ov = this.rcCalloutOverrides && this.rcCalloutOverrides[path];
      if ((!entry || !entry.w) && ov && ov.w) entry = { w: ov.w, h: ov.h, t: [] };
      if (!entry || !entry.w) return empty;
      var norm = function (s) { return String(s || "").toUpperCase().replace(/[^A-Z0-9]/g, ""); };
      var raw;
      if (ov && ov.managed) {
        var sx = entry.w / (ov.w || entry.w), sy = entry.h / (ov.h || entry.h);
        raw = (ov.balloons || []).map(function (ab) {
          return { code: norm(ab.code), box: { x: ab.x * sx, y: ab.y * sy, w: ab.w * sx, h: ab.h * sy } };
        }).filter(function (b) { return b.code; });
      } else {
        raw = (entry.t && entry.t.length) ? extractBalloons(entry) : [];
      }
      return { entry: entry, items: raw };
    },

    // Unmapped-rep warnings split by the admin's ignore state (drives the Sales Territories warnings banner).
    terrActiveWarnings() { return (this.terrWarnings || []).filter((w) => w && !w.ignored); },
    terrIgnoredWarnings() { return (this.terrWarnings || []).filter((w) => w && w.ignored); },

    // Distinct (uppercased) Customer IDs in the exceptions list + the new-row draft — drives the per-customer
    // ship-to <datalist> elements so each row's Country / Zip dropdown lists that customer's own past addresses.
    excShipCustomers() {
      const seen = {}; const out = [];
      (this.terrExceptions || []).forEach((ex) => {
        const c = this.excNorm(ex.customerId);
        if (c && !seen[c]) { seen[c] = 1; out.push(c); }
      });
      const nc = this.excNorm(this.newTerrExc && this.newTerrExc.customerId);
      if (nc && !seen[nc]) { seen[nc] = 1; out.push(nc); }
      return out;
    },

    // Master/admin detection — mirrors the same defensive check used on the
    // Account dashboard / SavedCarts (flags, single role string, or roles[]).
    isMasterUser() {
      const u = this.currentUser || {};
      if (u.isAdmin === true || u.isMaster === true) return true;
      const matches = function (val) {
        if (!val) return false;
        const s = typeof val === "string" ? val : (val && val.name) || (val && val.role) || "";
        const low = String(s).toLowerCase();
        return low === "master" || low === "admin";
      };
      if (matches(u.role)) return true;
      if (Array.isArray(u.roles) && u.roles.some(matches)) return true;
      return false;
    },

    warnings() {
      return (this.setup && this.setup.warnings) || [];
    },
    // Keyed gaps from the server; fall back to the legacy string warnings (pre-deploy)
    // so the section still renders (acknowledge just won't persist until deployed).
    gapList() {
      const g = (this.setup && this.setup.gaps) || null;
      if (g && g.length) return g;
      return (this.warnings || []).map((w, i) => ({ key: "w-" + i, text: w }));
    },
    acknowledgedKeys() {
      return (this.setup && this.setup.acknowledgedGaps) || [];
    },
    activeGaps() {
      return this.gapList.filter((g) => !this.isAcked(g.key) && !this.isHidden(g.key));
    },
    activeBareMachines() {
      return this.bareMachines.filter((m) => {
        const k = this.bmKey(m);
        return !this.isAcked(k) && !this.isHidden(k);
      });
    },
    historyGaps() {
      const out = [];
      this.gapList.forEach((g) => { if (this.isAcked(g.key)) out.push({ key: g.key, text: g.text }); });
      this.bareMachines.forEach((m) => {
        const k = this.bmKey(m);
        if (this.isAcked(k)) {
          out.push({ key: k, text: (m.code ? m.code + " — " : "") + (m.label || "") + " — no assemblies" });
        }
      });
      return out;
    },
    gapCount() {
      return this.activeGaps.length + this.activeBareMachines.length;
    },
    gapHistoryCount() {
      return this.historyGaps.length;
    },
    hasAnyGaps() {
      return this.gapList.length > 0 || this.bareMachines.length > 0;
    },

    headerLabel() {
      if (!this.setup) return "";
      return this.setup.companyCode || this.setup.companyName || "";
    },

    // Normalize the API model into a uniform list the template iterates.
    categories() {
      const s = this.setup || {};
      // Only the per-company link tables are editable inline.
      const editTypes = { machines: "machine", wearParts: "wear", users: "user" };
      // Column header labels — every category uses the same ID · Code · Description
      // layout/order as Parts/Wear; only the 2nd/3rd labels differ per category.
      const colDefs = {
        machines: { code: "Code", desc: "Description" },
        assemblies: { code: "Machine Code / ID", desc: "Name" },
        subAssemblies: { code: "Machine Code / ID", desc: "Name" },
        companies: { code: "Code", desc: "Company" },
        users: { code: "Email", desc: "Name" },
      };
      const cat = (key, label, c) => ({
        key,
        label,
        count: (c && c.count) || 0,
        joinHint: (c && c.joinHint) || "",
        items: (c && c.items) || [],
        editType: editTypes[key] || "",
        partLink: key === "parts" || key === "assemblies" || key === "subAssemblies",
        tableMode: key === "parts" || key === "wearParts",
        delType: key === "parts" ? "part" : (key === "wearParts" ? "wear" : ""),
        cols: colDefs[key] || { code: "Code", desc: "Description" },
      });
      // Emails are intentionally omitted — they already show in the Users column.
      return [
        cat("machines", "Machines", s.machines),
        cat("assemblies", "Assemblies", s.assemblies),
        cat("subAssemblies", "Sub Assemblies", s.subAssemblies),
        cat("parts", "Parts", s.parts),
        cat("wearParts", "Wear Parts", s.wearParts),
        cat("companies", "Companies", s.companies),
        cat("users", "Users", s.users),
      ];
    },

    // Companies the admin can copy FROM (everything except the one being viewed).
    copyCandidates() {
      const cur = String(localStorage.companyId || "");
      return (this.companies || []).filter((c) => String(c.id) !== cur);
    },
    // Copy-from search matches (by name / code / id), capped for the dropdown.
    copyMatches() {
      const t = (this.copyTerm || "").trim().toLowerCase();
      if (!t) return [];
      return this.copyCandidates
        .filter(
          (c) =>
            (c.code || "").toLowerCase().includes(t) ||
            (c.name || "").toLowerCase().includes(t) ||
            String(c.id) === t
        )
        .slice(0, 25);
    },

    // Copy history for the company currently being viewed (newest first).
    targetHistory() {
      const cur = String(localStorage.companyId || "");
      return (this.history || []).filter((h) => String(h.targetCompanyId) === cur);
    },

    // Machines linked to this company that have no assemblies (gap "Fix" targets).
    bareMachines() {
      return (this.setup && this.setup.bareMachines) || [];
    },
    // Users whose email domain differs from the account's majority (the "users-domain"
    // gap) — keyed by user ref, so each odd row can show the ⚠ marker.
    oddballDomainRefs() {
      const users = (this.setup && this.setup.users && this.setup.users.items) || [];
      const byRef = {};
      const counts = {};
      users.forEach((u) => {
        const email = (u.code || "");
        const at = email.lastIndexOf("@");
        if (at < 0) return;
        const d = email.slice(at + 1).toLowerCase();
        byRef[u.ref] = d;
        counts[d] = (counts[d] || 0) + 1;
      });
      const domains = Object.keys(counts);
      if (domains.length <= 1) return {};
      let maj = domains[0];
      domains.forEach((d) => { if (counts[d] > counts[maj]) maj = d; });
      const odd = {};
      Object.keys(byRef).forEach((ref) => { if (byRef[ref] !== maj) odd[ref] = true; });
      return odd;
    },

    // The company id for the account currently being viewed (the header dropdown
    // writes it to localStorage). Used by the Users listing's "Company ID" column.
    currentCompanyId() {
      return Number(localStorage.companyId) || "";
    },
    diagPlaceholder() {
      if (this.diagType === "user-create") return "Email for the new user that won't create…";
      if (this.diagType === "user-login") return "Email of the user who can't log in…";
      if (this.diagType === "account-status") return "Email of the account to check (active? approved?)…";
      return "Email not receiving password resets…";
    },
    // Event Logs & History — text filter across title / company / who / type / detail / data.
    filteredEventLog() {
      const t = (this.eventSearch || "").trim().toLowerCase();
      if (!t) return this.eventLog;
      return this.eventLog.filter((ev) => {
        let hay = [ev.title, ev.company, ev.who, ev.type, ev.detail, ev.exception]
          .filter(Boolean)
          .join(" ");
        if (ev.data) {
          for (const k in ev.data) hay += " " + k + " " + ev.data[k];
        }
        return hay.toLowerCase().indexOf(t) !== -1;
      });
    },
  },

  watch: {
    // The dropdown is a report picker now, so choosing one loads it. Guarded on "not already
    // loaded" so flicking between selections does not re-query — Refresh is the explicit way
    // to re-read.
    diagType(v) {
      if (v === "account-status" && !this.asData) this.loadAccountStatus();
      if (v === "user-insights" && !this.uiUsers.length) this.loadActivityUsers();
    },
    // Deep link from the report-incorrect-chart helpdesk email — reacts to
    // in-app navigations that add ?replaceChart= (mounted() covers the
    // full-page-load case, e.g. right after the login redirect).
    "$route.query.replaceChart"(val) {
      if (val) this.rcOpenFromQuery();
    },
  },
  mounted() {
    // The header "My Account → Admin Dashboard" item opens this panel (the
    // same view as the SETUP pill) by emitting a root event handled here.
    this.$root.$on("open-account-setup", this.openModal);
    this.rcOpenFromQuery();
    // Ctrl+V-a-screenshot into Replace Charts — the handler no-ops unless a Replace-Charts row is
    // expanded AND the clipboard holds an image, so every other paste on the page is untouched.
    if (typeof document !== "undefined") document.addEventListener("paste", this.rcDocPaste);
  },
  beforeDestroy() {
    this.$root.$off("open-account-setup", this.openModal);
    if (typeof document !== "undefined") document.removeEventListener("paste", this.rcDocPaste);
  },

  methods: {
    // Toggle the global debug overlay (rendered by App.vue). Persists per
    // browser and notifies App.vue live via the shared $cartBus.
    toggleDebugOverlay(on) {
      this.debugOverlay = !!on;
      try {
        localStorage.setItem("jcs_debugOverlay", on ? "1" : "0");
      } catch (e) {
        /* ignore */
      }
      if (this.$cartBus) this.$cartBus.$emit("debug-overlay-toggle", !!on);
    },
    // ----- In-modal add/edit of full User / Company details -----
    openEditUser(it) {
      if (!it || !it.ref) return;
      this.editEntity = { kind: "user", id: it.ref };
    },
    openAddUser() {
      this.editEntity = { kind: "user", id: null };
    },
    openEditCompany(it) {
      const id = it && it.id;
      if (!id) return;
      this.editEntity = { kind: "company", id: id };
    },
    openAddCompany() {
      this.editEntity = { kind: "company", id: null };
    },
    // ----- In-modal add/edit of catalog objects (machine/assembly/part/wear) -----
    kindForCat(cat) {
      return { machines: "machine", assemblies: "assembly", subAssemblies: "assembly", parts: "part", wearParts: "wear" }[cat && cat.key] || "";
    },
    canCreate(cat) {
      return !!cat && ["machines", "assemblies", "subAssemblies", "parts"].indexOf(cat.key) !== -1;
    },
    canEditRow(cat) {
      return !!cat && ["machines", "assemblies", "subAssemblies"].indexOf(cat.key) !== -1;
    },
    createNoun(cat) {
      return { machines: "machine", assemblies: "assembly", subAssemblies: "sub-assembly", parts: "part" }[cat && cat.key] || "object";
    },
    openEditObject(cat, it) {
      const kind = this.kindForCat(cat);
      if (!kind || !it) return;
      // Wear edits target the Wear row (linkId); everything else its own id.
      const id = kind === "wear" ? (it.linkId || 0) : (it.id || 0);
      if (!id) return;
      this.editEntity = { kind: kind, id: id, catKey: cat.key };
    },
    openAddObject(cat) {
      const kind = this.kindForCat(cat);
      if (!kind || kind === "wear") return;   // new wear is added via the wear add-search
      this.editEntity = { kind: kind, id: null, catKey: cat.key };
    },
    closeEdit() {
      this.editEntity = null;
    },
    onEntitySaved(info) {
      this.editEntity = null;
      this.fetchSetup();
      this.loadCompanies();   // refresh Copy/Link company pickers
      this.loadEventLog();
      if (info && info.kind === "user") this.loadDeleteHistory("users");
      if (info && info.category) this.loadDeleteHistory(info.category);
    },
    // ----- Event Logs & History (right panel) -----
    loadEventLog() {
      this.eventBusy = true;
      apiService
        .getSetupEventLog()
        .then((rows) => { this.eventLog = Array.isArray(rows) ? rows : []; })
        .catch(() => { this.eventLog = []; })
        .then(() => { this.eventBusy = false; });
    },
    toggleEvent(id) {
      this.$set(this.expandedEvents, id, !this.expandedEvents[id]);
    },
    toggleUtility() {
      this.$set(this.open, "utilityActions", !this.open.utilityActions);
    },

    // ----- Replace Charts (Utility Actions tool) -----
    toggleReplaceCharts() {
      this.rcOpen = !this.rcOpen;
    },
    rcSearchDebounced() {
      clearTimeout(this._rcTimer);
      this._rcTimer = setTimeout(this.rcSearch, 300);
    },
    rcSearch() {
      var term = (this.rcTerm || "").trim();
      if (term.length < 2 && !/^\d+$/.test(term)) {
        this.rcRows = [];
        this.rcSearched = false;
        return;
      }
      this.rcBusy = true;
      var self = this;
      navApi
        .chartSearch(term)
        .then(function (rows) {
          self.rcRows = Array.isArray(rows) ? rows : [];
          self.rcSearched = true;
          // Keep an already-open row expanded only if it's still in the results.
          if (self.rcExpanded && !self.rcRows.some(function (x) { return Number(x.id) === Number(self.rcExpanded); })) {
            self.rcExpanded = 0;
            self.rcDetail = null;
          }
        })
        .catch(function () {
          self.rcRows = [];
          self.rcSearched = true;
        })
        .finally(function () {
          self.rcBusy = false;
        });
    },
    rcToggleRow(r) {
      if (!r) return;
      if (this.rcExpanded === r.id) {
        this.rcExpanded = 0;
        this.rcDetail = null;
        return;
      }
      this.rcExpanded = r.id;
      this.rcDetail = null;
      this.rcDone = false;
      this.rcError = "";
      this.rcFile = { name: "", dataUri: "", error: "" };
      this.rcZoomCur = 1;
      this.rcZoomNew = 1;
      this.rcStamp = Date.now();
      this.rcCancelBalloonEdit();
      this.rcImgBox = { cw: 0, ch: 0, ox: 0, oy: 0 };
      this.rcEnsureCalloutData();
      var self = this;
      navApi
        .getAssemblyDetails({ id: r.id })
        .then(function (data) {
          if (self.rcExpanded === r.id) self.rcDetail = data;
        })
        .catch(function () {
          if (self.rcExpanded === r.id) self.rcError = "Couldn't load that chart's record — please try again.";
        });
    },
    rcPick() {
      var el = this.$refs.rcFileInput;
      if (Array.isArray(el)) el = el[0];   // ref sits inside a v-for template
      if (el) el.click();
    },
    rcFileChange(e) {
      this.$set(this.rcFile, "error", "");
      var files = (e.target && e.target.files) || [];
      if (!files.length) return;
      var file = files[0];
      var name = String(file.name || "");
      if (!/\.(png|jpe?g)$/i.test(name)) {
        this.$set(this.rcFile, "error", "Please pick a PNG or JPG image.");
        return;
      }
      this.rcStageImage(file, name);
    },
    // Stage an image blob as the CORRECTED chart (shared by the file picker, the 📋 Paste button and
    // Ctrl+V) — same 3 MB cap as the picker; the preview fills the Corrected pane and Upload arms.
    rcStageImage(blob, name) {
      if (!blob) return;
      if (blob.size > 3 * 1024 * 1024) {
        this.$set(this.rcFile, "error", "That image is over 3 MB — please resize it and try again.");
        return;
      }
      var self = this;
      var reader = new FileReader();
      reader.onload = function (ev) {
        self.$set(self.rcFile, "dataUri", ev.target.result);
        self.$set(self.rcFile, "name", name);
        self.rcZoomNew = 1;
        self.rcDone = false;
      };
      reader.readAsDataURL(blob);
    },
    // 📋 Paste button → read the clipboard directly (Chrome/Edge; needs the site's clipboard permission).
    // Screenshots land on the clipboard as image/png — staged exactly like a picked file.
    rcPasteClick() {
      var self = this;
      this.$set(this.rcFile, "error", "");
      if (!navigator.clipboard || !navigator.clipboard.read) {
        this.$set(this.rcFile, "error", "This browser can't read the clipboard from a button — click the row and press Ctrl+V instead.");
        return;
      }
      navigator.clipboard.read().then(function (items) {
        for (var i = 0; i < (items || []).length; i++) {
          var it = items[i];
          var type = (it.types || []).find(function (t) { return t === "image/png" || t === "image/jpeg"; });
          if (type) {
            return it.getType(type).then(function (blob) {
              self.rcStageImage(blob, "pasted-chart." + (type === "image/jpeg" ? "jpg" : "png"));
            });
          }
        }
        self.$set(self.rcFile, "error", "No image on the clipboard — take the screenshot first, then click Paste.");
      }).catch(function () {
        self.$set(self.rcFile, "error", "Clipboard access was blocked — click the row and press Ctrl+V instead.");
      });
    },
    // Ctrl+V anywhere while a Replace-Charts row is expanded → stage the pasted screenshot as the
    // corrected chart. Only consumes IMAGE pastes (text pastes into inputs are untouched); registered
    // on document in mounted so it works without focusing a specific pane.
    rcDocPaste(e) {
      if (!this.rcDetail) return;   // no Replace-Charts row open → normal paste behavior everywhere
      var items = (e.clipboardData && e.clipboardData.items) || [];
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        if (it.kind === "file" && /^image\/(png|jpeg)$/i.test(it.type)) {
          var blob = it.getAsFile();
          if (blob) {
            e.preventDefault();
            this.$set(this.rcFile, "error", "");
            this.rcStageImage(blob, "pasted-chart." + (/jpeg/i.test(it.type) ? "jpg" : "png"));
          }
          return;
        }
      }
    },
    rcZoom(which, dir) {
      var f = dir > 0 ? 1.25 : 1 / 1.25;
      if (which === "cur") this.rcZoomCur = Math.min(6, Math.max(1, Math.round(this.rcZoomCur * f * 100) / 100));
      else this.rcZoomNew = Math.min(6, Math.max(1, Math.round(this.rcZoomNew * f * 100) / 100));
    },
    rcWheel(which, e) {
      this.rcZoom(which, e.deltaY < 0 ? 1 : -1);
    },
    // ── Drag-to-pan (both panes) ─────────────────────────────────────────
    // Click-drag scrolls the overflow container — works at any zoom. A >3px
    // move sets _rcPanMoved so the click-to-add-bubble handler no-ops after a
    // pan (same suppression trick as the Search modal's _dragMoved).
    rcPanStart(which, e) {
      if (e.button !== 0) return;
      var el = e.currentTarget;
      this._rcPan = { el: el, x: e.clientX, y: e.clientY, sl: el.scrollLeft, st: el.scrollTop };
      this._rcPanMoved = false;
      e.preventDefault();
      var self = this;
      this._rcPanMove = function (ev) {
        var p = self._rcPan; if (!p) return;
        var dx = ev.clientX - p.x, dy = ev.clientY - p.y;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) self._rcPanMoved = true;
        p.el.scrollLeft = p.sl - dx;
        p.el.scrollTop = p.st - dy;
      };
      this._rcPanEnd = function () {
        self._rcPan = null;
        window.removeEventListener("mousemove", self._rcPanMove);
        window.removeEventListener("mouseup", self._rcPanEnd);
      };
      window.addEventListener("mousemove", this._rcPanMove);
      window.addEventListener("mouseup", this._rcPanEnd);
    },
    // ── Bubble overlay + editor (ported from the Search modal) ───────────
    // Load the base OCR map + saved overrides once — the SAME sources Search
    // uses, so both editors see and write the same bubbles.
    rcEnsureCalloutData() {
      var self = this;
      if (!this._rcMapPromise) {
        this._rcMapPromise = fetch("/assembly-callouts.json")
          .then(function (r) { return r.ok ? r.json() : {}; })
          .then(function (m) { self.rcCalloutMap = m || {}; })
          .catch(function () { self.rcCalloutMap = {}; });
      }
      if (!this._rcOvPromise) {
        this._rcOvPromise = apiService.getCalloutOverrides()
          .then(function (d) { self.rcCalloutOverrides = (d && d.images) ? d.images : {}; })
          .catch(function () { self.rcCalloutOverrides = {}; });
      }
      return Promise.all([this._rcMapPromise, this._rcOvPromise]);
    },
    // Measure the current-chart img's BASE box (scale=1 — offset/client metrics
    // ignore CSS transforms) so the bubble layer can sit on it; the layer lives
    // INSIDE the scaled canvas so it tracks zoom automatically.
    rcMeasureImg() {
      var self = this;
      this.$nextTick(function () {
        var el = self.$refs.rcImg;
        if (Array.isArray(el)) el = el[0];
        if (!el || !el.clientWidth) return;
        self.rcImgBox = { cw: el.clientWidth, ch: el.clientHeight, ox: el.offsetLeft, oy: el.offsetTop };
        self.rcNatural = { w: el.naturalWidth || 0, h: el.naturalHeight || 0 };
      });
    },
    // Position one bubble (px within the layer). Same circle-around-the-number
    // math as Search's calloutHotspotStyle, incl. object-fit letterbox handling.
    rcHotspotStyle(box) {
      var b = this.rcImgBox, entry = this.rcBalloons.entry;
      if (!b.cw || !b.ch || !entry || !entry.w || !entry.h || !box) return { display: "none" };
      var scale = Math.min(b.cw / entry.w, b.ch / entry.h);
      var offX = (b.cw - entry.w * scale) / 2;
      var offY = (b.ch - entry.h * scale) / 2;
      var cx = offX + (box.x + box.w / 2) * scale;
      var cy = offY + (box.y + box.h / 2) * scale;
      var d = Math.max(box.w, box.h) * scale * 1.6;
      return { left: (cx - d / 2) + "px", top: (cy - d / 2) + "px", width: d + "px", height: d + "px" };
    },
    // Screen point → entry-space px, via the img's on-screen rect (already
    // includes the canvas scale) — mirror of Search's _modalPxToEntry.
    rcPxToEntry(clientX, clientY) {
      var el = this.$refs.rcImg;
      if (Array.isArray(el)) el = el[0];
      if (!el) return null;
      var rect = el.getBoundingClientRect();
      var box = this.rcImgBox, entry = this.rcBalloons.entry;
      if (!rect.width || !rect.height || !box.cw || !entry || !entry.w) return null;
      var fx = (clientX - rect.left) / rect.width, fy = (clientY - rect.top) / rect.height;
      var elx = fx * box.cw, ely = fy * box.ch;
      var cs = Math.min(box.cw / entry.w, box.ch / entry.h);
      var offX = (box.cw - entry.w * cs) / 2, offY = (box.ch - entry.h * cs) / 2;
      return { x: (elx - offX) / cs, y: (ely - offY) / cs };
    },
    // Click empty chart in edit mode (not a pan, not a marker) → add a bubble.
    rcCanvasClick(e) {
      if (!this.rcEditMode) return;
      if (this._rcPanMoved) return;
      var p = this.rcPxToEntry(e.clientX, e.clientY);
      var entry = this.rcBalloons.entry;
      if (!p || !entry) return;
      var d = Math.max(entry.w, entry.h) * 0.03;
      this.rcEditBalloons.push({ code: "", x: p.x - d / 2, y: p.y - d / 2, w: d, h: d });
      this.rcEditSelected = this.rcEditBalloons.length - 1;
      this.rcEditDirty = true;
      var self = this;
      this.$nextTick(function () {
        var el = self.$refs.rcEditCodeInput;
        if (Array.isArray(el)) el = el[0];
        if (el && el.focus) el.focus();
      });
    },
    rcStartMarkerDrag(idx) {
      if (!this.rcEditMode) return;
      this._rcPanMoved = false;
      this.rcEditSelected = idx;
      this._rcEditDrag = { idx: idx, mode: "move" };
      this._rcBindEditDrag();
    },
    rcStartMarkerResize(idx) {
      if (!this.rcEditMode) return;
      this._rcPanMoved = false;
      this.rcEditSelected = idx;
      this._rcEditDrag = { idx: idx, mode: "resize" };
      this._rcBindEditDrag();
    },
    _rcBindEditDrag() {
      var self = this;
      this._rcOnEditMove = function (ev) { self._rcEditDragMove(ev); };
      this._rcOnEditUp = function () { self._rcEditDragEnd(); };
      window.addEventListener("mousemove", this._rcOnEditMove);
      window.addEventListener("mouseup", this._rcOnEditUp);
    },
    _rcEditDragMove(e) {
      var d = this._rcEditDrag; if (!d) return;
      this._rcPanMoved = true;   // a marker drag also suppresses click-to-add
      var p = this.rcPxToEntry(e.clientX, e.clientY);
      var b = this.rcEditBalloons[d.idx];
      if (!p || !b) return;
      if (d.mode === "move") {
        this.$set(b, "x", p.x - b.w / 2);
        this.$set(b, "y", p.y - b.h / 2);
      } else {
        var cx = b.x + b.w / 2, cy = b.y + b.h / 2;
        var r = Math.max(6, Math.sqrt((p.x - cx) * (p.x - cx) + (p.y - cy) * (p.y - cy)));
        this.$set(b, "x", cx - r); this.$set(b, "y", cy - r);
        this.$set(b, "w", r * 2); this.$set(b, "h", r * 2);
      }
      this.rcEditDirty = true;
    },
    _rcEditDragEnd() {
      this._rcEditDrag = null;
      if (this._rcOnEditMove) window.removeEventListener("mousemove", this._rcOnEditMove);
      if (this._rcOnEditUp) window.removeEventListener("mouseup", this._rcOnEditUp);
    },
    rcRemoveBalloon(idx) {
      this.rcEditBalloons.splice(idx, 1);
      if (this.rcEditSelected === idx) this.rcEditSelected = -1;
      else if (this.rcEditSelected > idx) this.rcEditSelected--;
      this.rcEditDirty = true;
    },
    rcStartBalloonEdit() {
      var self = this;
      this.rcEnsureCalloutData().then(function () {
        var path = self.rcBalloonKey;
        if (!path) return;
        // Brand-new chart (no OCR entry, no override): seed reference dims from
        // the image's natural size so rcBalloons can synthesize an entry.
        var entry = self.rcBalloons.entry;
        if ((!entry || !entry.w)) {
          var nat = self.rcNatural || {};
          if (nat.w && nat.h && !(self.rcCalloutOverrides[path] && self.rcCalloutOverrides[path].w)) {
            self.$set(self.rcCalloutOverrides, path, { w: nat.w, h: nat.h, managed: true, balloons: [] });
          }
        }
        self.rcEditBalloons = (self.rcBalloons.items || []).map(function (it) {
          return { code: it.code, x: it.box.x, y: it.box.y, w: it.box.w, h: it.box.h };
        });
        self.rcEditSelected = -1;
        self.rcEditDirty = false;
        self.rcEditError = "";
        self.rcEditMode = true;
        self.rcMeasureImg();
      });
    },
    rcCancelBalloonEdit() {
      this.rcEditMode = false;
      this.rcEditBalloons = [];
      this.rcEditSelected = -1;
      this.rcEditDirty = false;
      this.rcEditError = "";
      this._rcEditDrag = null;
    },
    // Persist — IDENTICAL payload shape to Search's saveBalloonEdit, so both
    // editors share one overrides store (managed:true = this set IS the truth).
    rcSaveBalloonEdit() {
      var self = this;
      var path = this.rcBalloonKey;
      if (!path) { this.rcCancelBalloonEdit(); return; }
      var norm = function (s) { return String(s || "").toUpperCase().replace(/[^A-Z0-9]/g, ""); };
      var entry = this.rcBalloons.entry;
      var refW = (entry && entry.w) || 0, refH = (entry && entry.h) || 0;
      var balloons = this.rcEditBalloons
        .filter(function (b) { return norm(b.code); })
        .map(function (b) {
          return { code: norm(b.code), x: Math.round(b.x), y: Math.round(b.y), w: Math.round(b.w), h: Math.round(b.h) };
        });
      var images = {};
      Object.keys(this.rcCalloutOverrides || {}).forEach(function (k) { images[k] = self.rcCalloutOverrides[k]; });
      images[path] = { w: refW, h: refH, managed: true, balloons: balloons };
      var email = (this.currentUser && this.currentUser.email) || "";
      var payload = { images: images, updatedUtc: new Date().toISOString(), updatedBy: email };
      this.rcEditSaving = true;
      this.rcEditError = "";
      apiService.saveCalloutOverrides(payload)
        .then(function () {
          self.rcCalloutOverrides = images;   // reactive reassign → rcBalloons recomputes
          self.rcEditSaving = false;
          self.rcEditMode = false;
          self.rcEditSelected = -1;
          self.rcEditDirty = false;
        })
        .catch(function (err) {
          self.rcEditSaving = false;
          var status = err && err.response && err.response.status;
          self.rcEditError = (status === 401 || status === 403)
            ? "You don't have permission to save (Master only)."
            : "Save failed — please try again.";
        });
    },
    rcUpload(row) {
      if (!this.rcDetail || !this.rcFile.dataUri || this.rcUploading) return;
      this.rcUploading = true;
      this.rcError = "";
      var d = this.rcDetail;
      var self = this;
      // PUT /assembly/{id} is a FULL-MODEL update — echo the fetched entity
      // back verbatim. parentAssemblyId is CRITICAL: omitting it would orphan
      // a sub-assembly into a top-level assembly. Only imagePath (data-URI →
      // the server uploads it to blob) and originalImagePath are new.
      navApi
        .updateAssembly({
          id: d.id,
          name: d.name,
          machineId: d.machineId,
          parentAssemblyId: d.parentAssemblyId,
          active: d.active,
          imagePath: this.rcFile.dataUri,
          originalImagePath: this.rcFile.name
        })
        .then(function () {
          self.rcDone = true;
          if (row) {
            row.hasChart = true;
            self.$set(row, "modifiedUtc", new Date().toISOString());
          }
          // Re-fetch so the "previous chart" pane and imagePath reflect the save.
          return navApi.getAssemblyDetails({ id: d.id }).then(function (fresh) {
            self.rcDetail = fresh;
            self.rcStamp = Date.now();
          }).catch(function () { /* non-fatal — the success banner already shows */ });
        })
        .catch(function (err) {
          self.rcError =
            (err && err.response && err.response.data && err.response.data.title) ||
            "Something went wrong saving the chart. Your picked file is still selected — try Upload again.";
        })
        .finally(function () {
          self.rcUploading = false;
        });
    },
    // Deep link from the report-incorrect-chart helpdesk email:
    // /account/index?replaceChart={assemblyId} → open the panel, open Utility
    // Actions → Replace Charts, search that id, and expand its row. The param
    // is stripped afterward so a refresh doesn't re-trigger.
    rcOpenFromQuery() {
      var q = (this.$route && this.$route.query) || {};
      var id = Number(q.replaceChart) || 0;
      if (!id || !this.isMasterUser) return;
      this.openModal();
      this.$set(this.open, "utilityActions", true);
      this.rcOpen = true;
      this.rcTerm = String(id);
      var self = this;
      this.rcBusy = true;
      navApi
        .chartSearch(String(id))
        .then(function (rows) {
          self.rcRows = Array.isArray(rows) ? rows : [];
          self.rcSearched = true;
          var hit = self.rcRows.find(function (x) { return Number(x.id) === id; });
          if (hit) self.rcToggleRow(hit);
        })
        .catch(function () {
          self.rcRows = [];
          self.rcSearched = true;
        })
        .finally(function () {
          self.rcBusy = false;
        });
      var next = {};
      Object.keys(q).forEach(function (k) { if (k !== "replaceChart") next[k] = q[k]; });
      this.$router.replace({ query: next }).catch(function () {});
    },

    // ----- Menu Navigation (GLOBAL header menu) -----
    // Lazy-load the header links the first time the sub-section is expanded.
    toggleNav() {
      this.navOpen = !this.navOpen;
      if (this.navOpen && !this.navLoaded && !this.navBusy) this.loadHeaderNav();
    },
    // GET /navigation/?type=header. The endpoint may return either a raw array
    // or a paginated wrapper { data: [...] } — accept both (same as
    // Admin/Navigation/Index.vue). Sort ascending by sortOrder for display.
    // Pull the picker's role list from the backend (derived from Roles.AnyRole) so adding a role to
    // Constants.cs is all it ever takes. Best-effort: on any failure the hardcoded fallback in data()
    // stands, so the panel still works — it just can't learn about a role added after this build.
    loadNavRoleOptions() {
      navApi
        .getNavigationRoleNames()
        .then((names) => {
          const list = (Array.isArray(names) ? names : [])
            .map((n) => String(n || "").trim())
            .filter(Boolean);
          if (list.length) this.navRoleOptions = list;   // whole-array assign — Vue 2 reactivity
        })
        .catch(() => { /* keep the fallback list */ });
    },
    loadHeaderNav() {
      this.navBusy = true;
      this.navLoadError = false;
      this.loadNavRoleOptions();
      navApi
        // GetTree returns ALL top-level header items (incl Search), unpaginated.
        // The flat /navigation/?type=header is paged (page 1 only) and was
        // hiding Search — which is a real DB nav row, not a hardcode.
        .getUserHeaderNavigation()
        .then((response) => {
          let rows = [];
          if (Array.isArray(response)) rows = response;
          else if (response && Array.isArray(response.data)) rows = response.data;
          rows.sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0));
          this.navItems = rows;
          // Snapshot name/url so Save can light up only on real edits.
          const base = {};
          rows.forEach((r) => { base[r.id] = { name: r.name || "", url: r.url || "" }; });
          this.navOriginal = base;
          this.navLoaded = true;
          // Reorder/add/delete reload through here; keep the live nav in sync.
          this.$root.$emit("nav-updated");
        })
        .catch(() => { this.navLoadError = true; })
        .then(() => { this.navBusy = false; });
    },
    // A row is "dirty" when its name or url differs from the loaded baseline.
    navRowDirty(it) {
      const base = this.navOriginal[it.id];
      if (!base) return true;
      return (it.name || "") !== base.name || (it.url || "") !== base.url;
    },
    // Save a single row. Send the FULL item (so other fields — header/footer/
    // parent/target/tag — aren't dropped) with the edited name/url/sortOrder.
    saveNavRow(it) {
      if (this.navRowBusy) return;
      this.navRowBusy = it.id;
      this.navRowSaved = "";
      navApi
        .updateNavigation(Object.assign({}, it))
        .then(() => {
          this.$set(this.navOriginal, it.id, { name: it.name || "", url: it.url || "" });
          this.navRowSaved = it.id;
          setTimeout(() => { if (this.navRowSaved === it.id) this.navRowSaved = ""; }, 1500);
          this.$root.$emit("nav-updated"); // refresh the live top nav
        })
        .catch((err) => this.navFailed(err))
        .then(() => { this.navRowBusy = ""; });
    },
    // Whether a row is currently flagged visible (DB Active). A missing flag is
    // treated as visible so rows predating the column still show.
    isNavActive(it) {
      return !!it && it.active !== false && it.active !== 0;
    },
    // Flip a link's visibility (DB Active) and persist right away. Optimistic --
    // revert on failure. Emits "nav-updated" so the live top nav re-fetches.
    toggleNavActive(it) {
      if (this.navRowBusy) return;
      const next = !this.isNavActive(it);
      this.$set(it, "active", next);
      this.navRowBusy = it.id;
      this.navRowSaved = "";
      navApi
        .updateNavigation(Object.assign({}, it))
        .then(() => {
          this.navRowSaved = it.id;
          setTimeout(() => { if (this.navRowSaved === it.id) this.navRowSaved = ""; }, 1500);
          this.$root.$emit("nav-updated");
        })
        .catch((err) => {
          this.$set(it, "active", !next); // revert the optimistic flip
          this.navFailed(err);
        })
        .then(() => { this.navRowBusy = ""; });
    },
    // True when a link points at the Search page — only then does the
    // Search-mode dropdown (Order/Quote) make sense.
    isSearchLink(url) {
      var p = (url || "").toString().toLowerCase().split("?")[0].replace(/\/+$/, "");
      return p === "/search" || p === "/search/index";
    },
    // Current Search mode for a row, read from its `tag`. "" | "order" | "quote".
    navMode(it) {
      var t = ((it && it.tag) || "").toString().toLowerCase();
      return t === "order" || t === "quote" ? t : "";
    },
    // Set a row's Search mode (stored in `tag`) and persist immediately, like
    // the Show toggle. Emits "nav-updated" so the live nav re-reads it.
    setNavMode(it, mode) {
      if (this.navRowBusy) return;
      var clean = mode === "order" || mode === "quote" ? mode : "";
      var prev = it.tag;
      this.$set(it, "tag", clean);
      this.navRowBusy = it.id;
      this.navRowSaved = "";
      navApi
        .updateNavigation(Object.assign({}, it))
        .then(() => {
          this.navRowSaved = it.id;
          setTimeout(() => { if (this.navRowSaved === it.id) this.navRowSaved = ""; }, 1500);
          this.$root.$emit("nav-updated");
        })
        .catch((err) => {
          this.$set(it, "tag", prev); // revert on failure
          this.navFailed(err);
        })
        .then(() => { this.navRowBusy = ""; });
    },
    // ----- Per-link role visibility -----
    //   it.roles     = CSV of roles ALLOWED to see the link; empty = everyone.
    //   it.denyRoles = CSV of roles that must NOT see it; evaluated after allow and WINS.
    // Both pickers share these helpers via a `field` argument so the two can never drift apart.
    navParseField(it, field) {
      return ((it && it[field]) || "").toString().split(",").map((s) => s.trim()).filter(Boolean);
    },
    navParseRoles(it) {
      return this.navParseField(it, "roles");
    },
    navParseDeny(it) {
      return this.navParseField(it, "denyRoles");
    },
    navRoleChecked(it, role) {
      return this.navParseRoles(it).indexOf(role) >= 0;
    },
    navDenyChecked(it, role) {
      return this.navParseDeny(it).indexOf(role) >= 0;
    },
    navRolesLabel(it) {
      var r = this.navParseRoles(it);
      return r.length ? r.join(", ") : "All roles";
    },
    navDenyLabel(it) {
      var r = this.navParseDeny(it);
      return r.length ? r.join(", ") : "No one";
    },
    navRoleTitle(it) {
      var r = this.navParseRoles(it);
      var d = this.navParseDeny(it);
      var s = r.length
        ? "Only these roles see this link: " + r.join(", ")
        : "Visible to everyone (no roles selected)";
      if (d.length) s += " — EXCEPT anyone holding: " + d.join(", ");
      return s;
    },
    navDenyTitle(it) {
      var d = this.navParseDeny(it);
      return d.length
        ? "Hidden from anyone holding: " + d.join(", ") + " (even if a role above allows them). Master always sees every link."
        : "No exclusions. Pick a role here to hide the link from anyone who holds it, even if the Roles column allows them.";
    },
    // Toggle a role on/off and persist immediately (like the Show / Mode controls,
    // since role edits don't mark the row 'dirty' for the Save button).
    // `field` is "roles" (allow) or "denyRoles" (deny) — ONE implementation for both pickers so a
    // fix to the save/revert path can never apply to only one of them.
    toggleNavRole(it, role, field) {
      if (this.navRowBusy) return;
      var key = field || "roles";
      var roles = this.navParseField(it, key);
      var idx = roles.indexOf(role);
      if (idx >= 0) roles.splice(idx, 1);
      else roles.push(role);
      var prev = it[key];
      this.$set(it, key, roles.join(","));
      this.navRowBusy = it.id;
      this.navRowSaved = "";
      navApi
        .updateNavigation(Object.assign({}, it))
        .then(() => {
          this.navRowSaved = it.id;
          setTimeout(() => { if (this.navRowSaved === it.id) this.navRowSaved = ""; }, 1500);
          this.$root.$emit("nav-updated"); // live nav re-reads role visibility
        })
        .catch((err) => {
          this.$set(it, key, prev); // revert on failure
          this.navFailed(err);
        })
        .then(() => { this.navRowBusy = ""; });
    },
    toggleNavDeny(it, role) {
      this.toggleNavRole(it, role, "denyRoles");
    },
    moveNavUp(i) {
      if (i <= 0) return;
      this.navItems.splice(i - 1, 0, this.navItems.splice(i, 1)[0]);
      this.persistNavOrder();
    },
    moveNavDown(i) {
      if (i >= this.navItems.length - 1) return;
      this.navItems.splice(i + 1, 0, this.navItems.splice(i, 1)[0]);
      this.persistNavOrder();
    },
    // Renumber EVERY item's sortOrder to its new array index (0,1,2,…) and
    // persist only the ones whose number actually changed. The live data has
    // duplicate/gappy sort orders, so we never just swap two values — we
    // rewrite the whole sequence so the server order matches the array. Reload
    // when done so the UI reflects exactly what the server stored.
    persistNavOrder() {
      if (this.navReordering) return;
      const changed = [];
      this.navItems.forEach((it, idx) => {
        if (Number(it.sortOrder) !== idx) {
          it.sortOrder = idx;
          changed.push(it);
        }
      });
      if (!changed.length) return;
      this.navReordering = true;
      this.navReorderSaved = false;
      // Send the full item for each changed row (preserves the other fields).
      const calls = changed.map((it) => navApi.updateNavigation(Object.assign({}, it)));
      Promise.all(calls)
        .then(() => {
          this.navReorderSaved = true;
          setTimeout(() => { this.navReorderSaved = false; }, 2000);
        })
        .catch((err) => this.navFailed(err))
        .then(() => {
          this.navReordering = false;
          this.loadHeaderNav(); // re-sync with the server
        });
    },
    // Add a new header link with sensible defaults so it shows in the header.
    // target = 1 (Same Window) — the value the Edit form uses.
    addNavRow() {
      const name = (this.newNav.name || "").trim();
      if (!name || this.navAddBusy) return;
      const maxOrder = this.navItems.reduce(
        (m, it) => Math.max(m, Number(it.sortOrder) || 0), -1
      );
      this.navAddBusy = true;
      navApi
        .createNavigation({
          name: name,
          url: (this.newNav.url || "").trim(),
          header: true,
          footer: false,
          sortOrder: maxOrder + 1,
          parentId: null,
          target: 1,
        })
        .then(() => {
          this.newNav = { name: "", url: "" };
          this.loadHeaderNav();
        })
        .catch((err) => this.navFailed(err))
        .then(() => { this.navAddBusy = false; });
    },
    deleteNavRow(it) {
      if (this.navRowBusy) return;
      if (!window.confirm('Delete the menu link "' + (it.name || it.url || "this item") + '"?')) return;
      this.navRowBusy = it.id;
      navApi
        .deleteNavigation(it)
        .then(() => { this.loadHeaderNav(); })
        .catch((err) => this.navFailed(err))
        .then(() => { this.navRowBusy = ""; });
    },
    navFailed(err) {
      // Mirror the toast pattern used by the Navigation admin views, but stay
      // defensive — fall back to a generic message if the error shape differs.
      const data = err && err.error && err.error.response && err.error.response.data;
      this.$bvToast.toast(data ? "TraceID: " + data.traceId : "Please try again.", {
        title: (data && data.title) || "Menu update failed",
        autoHideDelay: 8000,
        appendToast: false,
        variant: "danger",
        solid: true,
      });
    },
    // ----- Sales Territories (admin-managed State → Territory → Sales Rep) -----
    toggleSalesTerr() {
      this.salesTerrOpen = !this.salesTerrOpen;
      if (this.salesTerrOpen && !this.salesTerrLoaded && !this.salesTerrBusy) this.loadSalesTerr();
    },
    loadSalesTerr() {
      this.loadTerrExceptions();
      this.loadRepAliases();
      this.salesTerrBusy = true;
      this.salesTerrLoadError = false;
      this.salesTerrLoadMsg = "";
      navApi
        .getSalesTerritories()
        .then((rows) => {
          const list = Array.isArray(rows) ? rows : [];
          this.salesTerrItems = list;
          const base = {};
          list.forEach((r) => {
            base[r.id] = { state: r.state || "", territory: r.territory || "", salesRep: r.salesRep || "", color: r.color || "" };
          });
          this.salesTerrOriginal = base;
          this.salesTerrLoaded = true;
          this.loadTerritoryUsers();
          this.loadTerrWarnings();
          this.loadAutoAssign();
        })
        .catch((err) => {
          this.salesTerrLoadError = true;
          // A 503 from this controller carries the reason as a plain string body.
          var r = err && err.response;
          var d = r && r.data;
          this.salesTerrLoadMsg = (typeof d === "string" ? d : (d && d.message)) || "";
        })
        .then(() => { this.salesTerrBusy = false; });
    },
    // Unmapped-rep warnings — reps on orders that match no assignment (would land in "(Unassigned)").
    loadTerrWarnings() {
      navApi
        .getTerritoryWarnings()
        .then((rows) => { this.terrWarnings = Array.isArray(rows) ? rows : []; })
        .catch(() => { this.terrWarnings = []; });
    },
    // Customer-ID-prefix auto-match toggle (server-persisted; the rules proc reads it each sync).
    loadAutoAssign() {
      navApi
        .getTerritoryAutoAssign()
        .then((r) => { this.autoAssignOn = !r || r.on !== false; })   // default ON
        .catch(() => { this.autoAssignOn = true; });
    },
    toggleAutoAssign() {
      if (this.autoAssignBusy) return;
      var next = !this.autoAssignOn;
      this.autoAssignBusy = true;
      this.autoAssignOn = next;   // optimistic
      navApi
        .setTerritoryAutoAssign(next)
        .then((r) => { if (r && typeof r.on === "boolean") this.autoAssignOn = r.on; })
        .catch(() => { this.autoAssignOn = !next; })   // revert on failure
        .then(() => { this.autoAssignBusy = false; });
    },
    dismissTerrWarning(name) {
      if (!name || this.terrWarnBusy) return;
      this.terrWarnBusy = name;
      navApi
        .dismissTerritoryWarning(name)
        .then(() => { const w = (this.terrWarnings || []).find((x) => x && x.name === name); if (w) this.$set(w, "ignored", true); })
        .catch(() => {})
        .then(() => { this.terrWarnBusy = ""; });
    },
    restoreTerrWarning(name) {
      if (!name || this.terrWarnBusy) return;
      this.terrWarnBusy = name;
      navApi
        .restoreTerritoryWarning(name)
        .then(() => { const w = (this.terrWarnings || []).find((x) => x && x.name === name); if (w) this.$set(w, "ignored", false); })
        .catch(() => {})
        .then(() => { this.terrWarnBusy = ""; });
    },
    // Prefill the "add assignment" row with the EXACT order rep name (spaces/case and all) so a click maps a
    // warned rep without retyping — the admin just picks the Territory. Scrolls the add row into view + focuses it.
    addWarnToMap(w) {
      if (!w || !w.name) return;
      this.newSalesTerr.salesRep = w.name;
      this.$nextTick(() => {
        const box = this.$refs.salesTerrAddRow;
        if (!box) return;
        if (box.scrollIntoView) box.scrollIntoView({ block: "center", behavior: "smooth" });
        const terr = box.querySelector("input[list='asc-terr-territories']");
        if (terr) terr.focus();
      });
    },
    // Prefill the add-row from a rep-warning drill-down prefix group: State=prefix + Rep=rep, so the admin only
    // picks a Territory then clicks Add (creates the global prefix→territory map row).
    addGroupToMap(rep, g) {
      if (!rep || !g || !g.prefix) return;
      this.newSalesTerr.state = String(g.prefix || "").trim().toUpperCase();
      this.newSalesTerr.salesRep = rep;
      this.newSalesTerr.territory = "";
      this.newSalesTerr.color = "";
      this.$nextTick(() => {
        const box = this.$refs.salesTerrAddRow;
        if (!box) return;
        if (box.scrollIntoView) box.scrollIntoView({ block: "center", behavior: "smooth" });
        const terr = box.querySelector("input[list='asc-terr-territories']");
        if (terr) terr.focus();
      });
    },
    // Prefill the per-customer EXCEPTION add-row from a drill-down order: Customer + its ship-to Country + Zip, so a
    // rule can attribute just that customer's country/zip-scoped orders (splits a shared prefix). Admin picks Territory/Rep.
    addOrderRule(rep, o) {
      if (!o || !o.customerId) return;
      this.newTerrExc = {
        customerId: String(o.customerId || "").trim(),
        territory: "",
        salesRep: rep || "",
        shipCountry: String(o.shipToCountry || "").trim(),
        shipZip: String(o.shipToPostal || "").trim(),
        isActive: true,
      };
      if (typeof this.ensureShipTos === "function") this.ensureShipTos(this.newTerrExc.customerId);
      this.$nextTick(() => {
        const box = this.$refs.terrExcAddRow;
        if (box && box.scrollIntoView) box.scrollIntoView({ block: "center", behavior: "smooth" });
      });
    },
    // Compact money for the warning meta line.
    warnMoney(n) { return "$" + Math.round(Number(n) || 0).toLocaleString(); },
    // Download the Sales Territories grid (State → Territory → Sales Rep → Color) as a CSV — client-side, no backend.
    // Export ALL THREE territory-config sections into one CSV: the map (#SALES_TERRITORIES), the per-customer
    // exceptions (#EXCEPTIONS) and the territory→login access (#TERRITORY_ACCESS). One file round-trips everything.
    exportSalesTerr() {
      var self = this;
      var esc = function (v) { v = (v == null ? "" : String(v)); return /[",\n\r]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v; };
      var lines = [];
      // Section 1 — Sales Territories map.
      lines.push("#SALES_TERRITORIES");
      lines.push("State,Territory,Sales Rep,Color");
      (this.salesTerrItems || []).forEach(function (r) {
        lines.push([esc(r.state), esc(r.territory), esc(r.salesRep), esc(r.color)].join(","));
      });
      // Section 2 — Per-customer exceptions (incl. ship-to conditions).
      lines.push("");
      lines.push("#EXCEPTIONS");
      lines.push("Customer ID,Ship-to Country,Ship-to Zip,Territory,Sales Rep,Active");
      (this.terrExceptions || []).forEach(function (e) {
        lines.push([esc(e.customerId), esc(e.shipCountry), esc(e.shipZip), esc(e.territory), esc(e.salesRep), esc(e.isActive ? "Yes" : "No")].join(","));
      });
      // Section 3 — Territory Access (which logins see each territory in Sales Insights).
      lines.push("");
      lines.push("#TERRITORY_ACCESS");
      lines.push("Territory,Logins");
      this.salesTerrAccessTerritories().forEach(function (t) {
        lines.push([esc(t), esc(self.territoryEmails[t] || "")].join(","));
      });
      // Section 4 — Rep aliases (old / departed VISUAL rep -> current rep).
      lines.push("");
      lines.push("#REP_ALIASES");
      lines.push("Old Rep (VISUAL),Current Rep");
      (this.repAliases || []).forEach(function (a) {
        lines.push([esc(a.aliasName), esc(a.canonicalName)].join(","));
      });
      var csv = "﻿" + lines.join("\r\n");   // BOM so Excel reads UTF-8 correctly
      try {
        var blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        var url = URL.createObjectURL(blob);
        var a = document.createElement("a");
        a.href = url; a.download = "sales-territory-config.csv";
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (e) { /* noop */ }
    },
    // ----- Import Sales Territories from CSV (replace-all; the round-trip with exportSalesTerr) -----
    triggerSalesTerrImport() {
      this.importMsg = "";
      if (this.$refs.salesTerrFile) this.$refs.salesTerrFile.click();
    },
    importSalesTerrFile(ev) {
      var input = ev.target;
      var file = input && input.files && input.files[0];
      if (input) input.value = "";   // reset so re-picking the same file still fires change
      if (!file) return;
      var self = this;
      var reader = new FileReader();
      reader.onload = function (e) {
        var rows = self.parseCsv(e.target.result || "");
        if (!rows.length) { self.pendingImport = null; self.importMsg = "That file had no rows."; return; }
        var sec = self.splitCsvSections(rows);
        // A section applies only when its #MARKER is present in the file; an absent section is left UNTOUCHED
        // (so a legacy Sales-Territories-only export never wipes the exceptions or access).
        var territories = sec.SALES_TERRITORIES ? self.parseTerrSection(sec.SALES_TERRITORIES) : null;
        var exceptions = sec.EXCEPTIONS ? self.parseExcSection(sec.EXCEPTIONS) : null;
        var access = sec.TERRITORY_ACCESS ? self.parseAccessSection(sec.TERRITORY_ACCESS) : null;
        var aliases = sec.REP_ALIASES ? self.parseAliasSection(sec.REP_ALIASES) : null;
        if (!sec._sawMarker) {
          // Legacy file (no markers): the whole thing is the Sales Territories map — require real rows.
          if (!territories || !territories.length) { self.pendingImport = null; self.importMsg = "No valid rows found (each row needs a State)."; return; }
          exceptions = null; access = null; aliases = null;
        }
        var hasContent = (territories && territories.length) || (exceptions && exceptions.length) || (access && access.length) || (aliases && aliases.length);
        var hasPresentSection = !!(sec.EXCEPTIONS || sec.TERRITORY_ACCESS || sec.REP_ALIASES);   // an explicit (even empty) section is a valid "replace"
        if (!hasContent && !hasPresentSection) { self.pendingImport = null; self.importMsg = "No valid rows found in that file."; return; }
        self.pendingImport = { name: file.name, territories: territories, exceptions: exceptions, access: access, aliases: aliases };
      };
      reader.onerror = function () { self.importMsg = "Couldn't read that file."; };
      reader.readAsText(file);
    },
    // Split a parsed CSV into #SALES_TERRITORIES / #EXCEPTIONS / #TERRITORY_ACCESS blocks. A row whose first cell
    // starts with '#' begins a section; following rows belong to it. A file with no markers is treated as a legacy
    // Sales-Territories-only export (sets ._sawMarker=false so callers know not to touch exceptions/access).
    splitCsvSections(rows) {
      var sections = {}; var current = null; var sawMarker = false;
      var alias = {
        "sales_territories": "SALES_TERRITORIES", "sales territories": "SALES_TERRITORIES", "territories": "SALES_TERRITORIES",
        "exceptions": "EXCEPTIONS", "exception": "EXCEPTIONS",
        "territory_access": "TERRITORY_ACCESS", "territory access": "TERRITORY_ACCESS", "access": "TERRITORY_ACCESS",
        "rep_aliases": "REP_ALIASES", "rep aliases": "REP_ALIASES", "aliases": "REP_ALIASES", "alias": "REP_ALIASES"
      };
      (rows || []).forEach(function (r) {
        var first = ((r && r[0]) || "").trim();
        if (first.charAt(0) === "#") {
          sawMarker = true;
          var name = first.slice(1).trim().toLowerCase();
          current = alias[name] || name.toUpperCase().replace(/\s+/g, "_");
          if (!sections[current]) sections[current] = [];
          return;
        }
        if (current) sections[current].push(r);
      });
      if (!sawMarker) sections.SALES_TERRITORIES = rows || [];
      sections._sawMarker = sawMarker;
      return sections;
    },
    // Header-mapped column picker for a section (falls back to positional order when no header is recognized).
    _secRows(rows, headerKeys, defaults, mapper) {
      if (!rows || !rows.length) return [];
      var head = rows[0].map(function (h) { return (h || "").trim().toLowerCase(); });
      var find = function (names) { for (var i = 0; i < head.length; i++) { if (names.indexOf(head[i]) >= 0) return i; } return -1; };
      var hasHeader = false; var col = {};
      Object.keys(headerKeys).forEach(function (k) { var idx = find(headerKeys[k]); col[k] = idx; if (idx >= 0) hasHeader = true; });
      if (!hasHeader) col = defaults;
      var out = [];
      for (var r = hasHeader ? 1 : 0; r < rows.length; r++) {
        var f = rows[r] || [];
        var pick = function (ci) { return (ci != null && ci >= 0 && ci < f.length) ? (f[ci] || "").trim() : ""; };
        var obj = mapper(pick, col);
        if (obj) out.push(obj);
      }
      return out;
    },
    parseTerrSection(rows) {
      return this._secRows(rows,
        { state: ["state"], territory: ["territory"], salesRep: ["sales rep", "salesrep", "rep"], color: ["color", "colour"] },
        { state: 0, territory: 1, salesRep: 2, color: 3 },
        function (pick, col) {
          var state = pick(col.state);
          if (!state) return null;
          return { state: state, territory: pick(col.territory), salesRep: pick(col.salesRep), color: pick(col.color) };
        });
    },
    parseExcSection(rows) {
      var truthy = function (v) { v = (v || "").trim().toLowerCase(); return v === "" || v === "yes" || v === "y" || v === "true" || v === "1" || v === "on" || v === "active"; };
      return this._secRows(rows,
        { customerId: ["customer id", "customerid", "customer"], shipCountry: ["ship-to country", "ship to country", "ship country", "country"],
          shipZip: ["ship-to zip", "ship to zip", "ship zip", "zip", "postal"], territory: ["territory"],
          salesRep: ["sales rep", "salesrep", "rep"], active: ["active", "on", "enabled"] },
        { customerId: 0, shipCountry: 1, shipZip: 2, territory: 3, salesRep: 4, active: 5 },
        function (pick, col) {
          var cust = pick(col.customerId);
          if (!cust) return null;
          return { customerId: cust, shipCountry: pick(col.shipCountry), shipZip: pick(col.shipZip),
                   territory: pick(col.territory), salesRep: pick(col.salesRep),
                   isActive: (col.active != null && col.active >= 0) ? truthy(pick(col.active)) : true };
        });
    },
    parseAccessSection(rows) {
      return this._secRows(rows,
        { territory: ["territory"], emails: ["logins", "logins (emails)", "emails", "email"] },
        { territory: 0, emails: 1 },
        function (pick, col) {
          var t = pick(col.territory);
          if (!t) return null;
          return { territory: t, emails: pick(col.emails) };
        });
    },
    parseAliasSection(rows) {
      return this._secRows(rows,
        { aliasName: ["old rep (visual)", "old rep", "old / departed rep (visual)", "old / departed rep", "alias", "alias name", "from"],
          canonicalName: ["current rep", "canonical", "canonical name", "to"] },
        { aliasName: 0, canonicalName: 1 },
        function (pick, col) {
          var a = pick(col.aliasName), c = pick(col.canonicalName);
          if (!a || !c) return null;   // both sides required (old name + current rep)
          return { aliasName: a, canonicalName: c };
        });
    },
    // Minimal RFC-4180-ish CSV parser: quoted fields, escaped "" quotes, CRLF/LF. Returns array of string[].
    parseCsv(text) {
      text = String(text || "").replace(/^﻿/, "");
      if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);   // strip UTF-8 BOM if present
      var rows = [], row = [], field = "", i = 0, q = false;
      while (i < text.length) {
        var ch = text[i];
        if (q) {
          if (ch === '"') { if (text[i + 1] === '"') { field += '"'; i += 2; continue; } q = false; i++; continue; }
          field += ch; i++; continue;
        }
        if (ch === '"') { q = true; i++; continue; }
        if (ch === ",") { row.push(field); field = ""; i++; continue; }
        if (ch === "\r") { i++; continue; }
        if (ch === "\n") { row.push(field); rows.push(row); row = []; field = ""; i++; continue; }
        field += ch; i++;
      }
      if (field.length || row.length) { row.push(field); rows.push(row); }
      return rows.filter(function (r) { return r.length && !(r.length === 1 && !String(r[0]).trim()); });
    },
    applySalesTerrImport() {
      if (!this.pendingImport || this.importBusy) return;
      var self = this;
      var p = this.pendingImport;
      this.importBusy = true;
      var summary = [];
      // 1) Sales Territories map (replace-all) — only when the file carried real rows (backend rejects an empty map).
      var chain = (p.territories && p.territories.length)
        ? navApi.importSalesTerritories(p.territories).then(function (res) {
            var n = (res && res.imported != null) ? res.imported : p.territories.length;
            summary.push(n + " assignment" + (n === 1 ? "" : "s"));
          })
        : Promise.resolve();
      // 2) Per-customer exceptions (replace-all) — applied whenever the #EXCEPTIONS section was present (empty = clear).
      chain = chain.then(function () {
        if (p.exceptions == null) return;
        return navApi.importTerritoryExceptions(p.exceptions).then(function (res) {
          var n = (res && res.imported != null) ? res.imported : p.exceptions.length;
          summary.push(n + " exception" + (n === 1 ? "" : "s"));
        });
      });
      // 3) Territory Access — reuse the per-territory save (email→login match + pre-registration) for each row.
      chain = chain.then(function () {
        if (!p.access || !p.access.length) return;
        return p.access.reduce(function (c, row) {
          return c.then(function () {
            var emails = String(row.emails || "").split(/[\s,;]+/).map(function (x) { return x.trim(); }).filter(Boolean);
            return navApi.setTerritoryUsers({ territory: row.territory, emails: emails }).catch(function () { /* skip a bad row */ });
          });
        }, Promise.resolve()).then(function () {
          summary.push(p.access.length + " access row" + (p.access.length === 1 ? "" : "s"));
        });
      });
      // 4) Rep aliases (replace-all) — applied whenever the #REP_ALIASES section was present (empty = clear).
      chain = chain.then(function () {
        if (p.aliases == null) return;
        return navApi.importRepAliases(p.aliases).then(function (res) {
          var n = (res && res.imported != null) ? res.imported : p.aliases.length;
          summary.push(n + " rep alias" + (n === 1 ? "" : "es"));
        });
      });
      chain.then(function () {
        self.pendingImport = null;
        self.importMsg = summary.length ? ("✓ Imported " + summary.join(", ") + ".") : "Nothing to import.";
        self.loadSalesTerr();   // one call cascades: exceptions + map + Territory Access (fresh list) + warnings
        self.loadRepAliases();  // refresh the rep-alias list too (part of the same config file)
        setTimeout(function () { self.importMsg = ""; }, 6000);
      }).catch(function (err) {
        var m = (err && err.response && err.response.data) ? err.response.data : "Import failed.";
        self.importMsg = (typeof m === "string" ? m : "Import failed.");
      }).then(function () { self.importBusy = false; });
    },
    // ----- Per-customer territory exceptions (override the prefix auto-assign) -----
    loadTerrExceptions() {
      if (this.terrExcBusy) return;
      this.terrExcBusy = true;
      navApi.getTerritoryExceptions()
        .then((rows) => {
          this.terrExceptions = Array.isArray(rows) ? rows : [];
          this.terrExcLoaded = true;
          // Prefetch each customer's past ship-tos so the Country / Zip dropdowns are ready.
          this.terrExceptions.forEach((ex) => this.ensureShipTos(ex.customerId));
        })
        .catch(() => { this.terrExceptions = []; })
        .finally(() => { this.terrExcBusy = false; });
    },
    // Normalize a Customer ID to the datalist-id / ship-to cache key (trimmed + uppercased).
    excNorm(custId) { return (custId || "").trim().toUpperCase(); },
    // Lazily load a customer's distinct past ship-to (country, zip) options for the dropdowns.
    ensureShipTos(custId) {
      const c = this.excNorm(custId);
      if (!c || this.shipTosByCust[c] || this.shipTosLoading[c]) return;
      this.$set(this.shipTosLoading, c, true);
      navApi.getExceptionShipTos(c)
        .then((res) => {
          const pairs = (res && res.pairs) || [];
          const countries = []; const seenC = {};
          const zips = []; const seenZ = {};
          pairs.forEach((p) => {
            const co = ((p.country != null ? p.country : p.Country) || "").trim();
            const zip = ((p.zip != null ? p.zip : p.Zip) || "").trim();
            if (co && !seenC[co.toLowerCase()]) { seenC[co.toLowerCase()] = 1; countries.push(co); }
            if (zip && !seenZ[zip.toLowerCase()]) { seenZ[zip.toLowerCase()] = 1; zips.push({ zip, country: co }); }
          });
          this.$set(this.shipTosByCust, c, { countries, zips, pairs });
        })
        .catch(() => { this.$set(this.shipTosByCust, c, { countries: [], zips: [], pairs: [] }); })
        .finally(() => { this.$set(this.shipTosLoading, c, false); });
    },
    // Rep aliases (departed/alternate rep -> current rep). Applied by the sync before the territory override.
    loadRepAliases() {
      this.repAliasBusy = true;
      navApi
        .getRepAliases()
        .then((rows) => { this.repAliases = Array.isArray(rows) ? rows : []; })
        .catch(() => { this.repAliases = []; })
        .then(() => { this.repAliasBusy = false; });
    },
    addRepAliasRow() {
      var alias = (this.newRepAlias.aliasName || "").trim();
      var canon = (this.newRepAlias.canonicalName || "").trim();
      if (!alias || !canon || this.repAliasAddBusy) return;
      this.repAliasAddBusy = true; this.repAliasMsg = "";
      navApi
        .createRepAlias({ aliasName: alias, canonicalName: canon })
        .then((row) => {
          if (row && row.id) { this.repAliases.push(row); this.newRepAlias = { aliasName: "", canonicalName: "" }; }
          else { this.repAliasMsg = "Couldn't add the alias — please try again."; }
        })
        .catch(() => { this.repAliasMsg = "Couldn't add the alias — please try again."; })
        .then(() => { this.repAliasAddBusy = false; });
    },
    deleteRepAliasRow(a) {
      if (!a || this.repAliasRowBusy) return;
      this.repAliasRowBusy = a.id;
      navApi
        .deleteRepAlias(a.id)
        .then(() => { this.repAliases = this.repAliases.filter((x) => x.id !== a.id); })
        .catch(() => {})
        .then(() => { this.repAliasRowBusy = 0; });
    },
    addTerrExcRow() {
      const cust = (this.newTerrExc.customerId || "").trim();
      if (!cust) return;
      this.terrExcAddBusy = true; this.terrExcMsg = "";
      navApi.createTerritoryException({
        customerId: cust,
        territory: (this.newTerrExc.territory || "").trim(),
        salesRep: (this.newTerrExc.salesRep || "").trim(),
        shipCountry: (this.newTerrExc.shipCountry || "").trim(),
        shipZip: (this.newTerrExc.shipZip || "").trim(),
        isActive: true,
      })
        .then((saved) => {
          if (saved && saved.id) {
            const i = this.terrExceptions.findIndex((x) => x.id === saved.id);
            if (i >= 0) this.$set(this.terrExceptions, i, saved); else this.terrExceptions.push(saved);
            // Keep the Customer ID so several destinations can be added quickly; clear the rest.
            this.newTerrExc = { customerId: cust, territory: "", salesRep: "", shipCountry: "", shipZip: "", isActive: true };
          } else { this.terrExcMsg = (saved && saved.message) || "Couldn't add the exception."; }
        })
        .catch(() => { this.terrExcMsg = "Couldn't add the exception — please try again."; })
        .finally(() => { this.terrExcAddBusy = false; });
    },
    saveTerrExcRow(ex) {
      this.terrExcRowBusy = ex.id; this.terrExcMsg = "";
      navApi.updateTerritoryException({
        id: ex.id, customerId: (ex.customerId || "").trim(),
        territory: (ex.territory || "").trim(), salesRep: (ex.salesRep || "").trim(),
        shipCountry: (ex.shipCountry || "").trim(), shipZip: (ex.shipZip || "").trim(),
        note: ex.note, isActive: !!ex.isActive,
      })
        .then(() => { this.terrExcRowSaved = ex.id; setTimeout(() => { if (this.terrExcRowSaved === ex.id) this.terrExcRowSaved = 0; }, 1500); })
        .catch(() => { this.terrExcMsg = "Couldn't save — please try again."; })
        .finally(() => { this.terrExcRowBusy = 0; });
    },
    deleteTerrExcRow(ex) {
      this.terrExcRowBusy = ex.id;
      navApi.deleteTerritoryException({ id: ex.id })
        .then(() => { this.terrExceptions = this.terrExceptions.filter((x) => x.id !== ex.id); })
        .catch(() => { this.terrExcMsg = "Couldn't delete — please try again."; })
        .finally(() => { this.terrExcRowBusy = 0; });
    },
    // Distinct existing values for the Territory / Sales Rep autocomplete lists.
    salesTerrDistinct(field) {
      const seen = {};
      const out = [];
      (this.salesTerrItems || []).forEach((it) => {
        const v = it && it[field] ? String(it[field]).trim() : "";
        if (v && !seen[v.toLowerCase()]) { seen[v.toLowerCase()] = 1; out.push(v); }
      });
      return out.sort((a, b) => a.localeCompare(b));
    },
    // ----- Territory Access (which logins can see each territory) -----
    // Distinct territories from the saved rows — one editable email field each.
    salesTerrAccessTerritories() {
      const seen = {};
      const out = [];
      (this.salesTerrItems || []).forEach((it) => {
        const v = it && it.territory ? String(it.territory).trim() : "";
        if (v && !seen[v.toLowerCase()]) { seen[v.toLowerCase()] = 1; out.push(v); }
      });
      return out.sort((a, b) => a.localeCompare(b));
    },
    // Load territory -> login-emails (Master-only) and seed each field reactively.
    loadTerritoryUsers() {
      this.loadNavRoleOptions();   // the local-role chips need the role list (same source as the nav picker)
      navApi
        .getTerritoryUsers()
        .then((rows) => {
          const map = {};
          (Array.isArray(rows) ? rows : []).forEach((r) => {
            if (r && r.territory) map[String(r.territory).trim()] = r;
          });
          this.salesTerrAccessTerritories().forEach((t) => {
            const r = map[t] || {};
            const manual = Array.isArray(r.manualEmails) ? r.manualEmails : (r.emails || []);
            this.$set(this.territoryManual, t, manual.join(", "));
            this.$set(this.territoryGroups, t, Array.isArray(r.groups) ? r.groups : []);
            this.$set(this.territoryGroupEmails, t, Array.isArray(r.groupEmails) ? r.groupEmails : []);
            this.$set(this.territoryEmails, t, (r.emails || []).join(", "));   // EFFECTIVE list — feeds the CSV export
          });
          this.loadTerritoryPending();
        })
        .catch(() => {});
    },

    // ----- Local-role chips (shared by Territory Access and Report Access) -----
    // A chip whose id starts with "role:" is one of the portal's own roles, not an Entra group.
    isRoleGroup(g) {
      return String((g && g.id) || "").toLowerCase().indexOf(LOCAL_ROLE_PREFIX) === 0;
    },
    // The local roles matching what's typed, shaped EXACTLY like a Graph hit so the dropdown, the
    // chips and the posted payload treat both kinds identically. This never calls Graph — which is
    // what makes local roles pickable with no Microsoft sign-in and when the Entra search fails.
    // navRoleOptions is the same list the Menu Navigation role picker uses: GET /navigation/Roles,
    // derived server-side from Roles.AnyRole, so Constants.cs stays the single source of truth.
    localRoleMatches(term, assignedIds) {
      const q = String(term || "").trim().toLowerCase();
      const taken = {};
      (assignedIds || []).forEach((id) => { taken[String(id || "").toLowerCase()] = 1; });
      return (this.navRoleOptions || [])
        .map((r) => String(r || "").trim())
        .filter((r) => r && r.toLowerCase().indexOf(q) >= 0)
        // Nothing typed (the focus/discovery list) = plain A–Z. Once there IS a term, rank prefix
        // matches first, then shortest ("sales" → Sales, Sales All, Sales Insights).
        .sort((a, b) => {
          if (!q) return a.localeCompare(b);
          const pa = a.toLowerCase().indexOf(q) === 0 ? 0 : 1;
          const pb = b.toLowerCase().indexOf(q) === 0 ? 0 : 1;
          return pa !== pb ? pa - pb : (a.length - b.length || a.localeCompare(b));
        })
        .map((r) => ({ id: LOCAL_ROLE_PREFIX + r, name: r, kind: "Local role", isRole: true }))
        .filter((it) => taken[it.id.toLowerCase()] !== 1);
    },
    // Adding a LOCAL ROLE is a bigger action than adding an Entra group: the server expands it to
    // EVERY user currently holding that role, and provisioning is additive AND ONE-WAY — removing
    // the chip later deletes the access rows but does NOT take back the role grant or the NC999
    // company link those accounts received (nothing in the API revokes either). "Customer" is the
    // sharp edge — it is by far the largest role — so make the blast radius explicit before posting.
    confirmRoleChip(it, grantLabel) {
      if (!this.isRoleGroup(it)) return true;   // Entra chips keep their existing one-click behaviour
      return window.confirm(
        'Add the local role "' + (it.name || "") + '" to this access list?\n\n' +
        "Every portal user who currently holds that role becomes a member, and each matched account is " +
        "granted " + (grantLabel || "access") + " right away.\n\n" +
        "That grant is ADDITIVE and is NOT undone by removing the chip again — it has to be reversed by " +
        "hand. Be careful with broad roles such as Customer."
      );
    },
    // "2 groups" / "1 local role" / "2 groups + 1 local role" — used by the member-count line under
    // the chips and by the sync message, so it is visible that the count covers BOTH kinds.
    accessSourceLabel(list) {
      const arr = Array.isArray(list) ? list : [];
      const roles = arr.filter((g) => this.isRoleGroup(g)).length;
      const groups = arr.length - roles;
      const bits = [];
      if (groups || !roles) bits.push(groups + " group" + (groups === 1 ? "" : "s"));
      if (roles) bits.push(roles + " local role" + (roles === 1 ? "" : "s"));
      return bits.join(" + ");
    },

    // ----- Entra group typeahead + membership sync (Territory Access — mirrors rg*) -----
    tgManualCount(t) {
      return String(this.territoryManual[t] || "").split(/[\s,;]+/).filter((e) => e).length;
    },
    tgInput(t, e) {
      const term = String(e.target.value || "").trim();
      this.$set(this.tgTerm, t, e.target.value);
      // "busy" only when an Entra search will really run (2+ chars). The local-role list is instant,
      // so focusing the empty box immediately reveals the pickable portal roles.
      this.tgSug = { key: t, items: this.tgSug.key === t ? this.tgSug.items : [], open: true, hi: -1,
                     busy: term.length >= 2, note: "" };
      clearTimeout(this._tgTimer);
      this._tgTimer = setTimeout(() => this.tgSearch(t), term.length >= 2 ? 300 : 0);
    },
    // Two sources in ONE dropdown: the portal's own roles (local, instant) then the Entra groups.
    tgSearch(t) {
      const term = String(this.tgTerm[t] || "").trim();
      const assigned = (this.territoryGroups[t] || []).map((g) => g.id);
      const roles = this.localRoleMatches(term, assigned);
      if (term.length < 2) {
        // Too short for a Graph search, but the local roles are already in hand — show them.
        this.tgSug = { key: t, items: roles, open: roles.length > 0, hi: roles.length ? 0 : -1, busy: false, note: "" };
        return;
      }
      this.tgSug = { key: t, items: roles, open: true, hi: roles.length ? 0 : -1, busy: true, note: "" };
      searchEntraGroups(term)
        .then((items) => {
          if (this.tgSug.key !== t) return;
          const list = roles.concat((items || []).filter((it) => assigned.indexOf(it.id) < 0));
          this.tgSug = { key: t, items: list, open: true, hi: list.length ? 0 : -1, busy: false, note: "" };
        })
        .catch((err) => {
          if (this.tgSug.key !== t) return;
          // Entra unreachable / not consented: keep the dropdown OPEN on the local roles rather than
          // closing it — picking a local role is precisely the path that does not need Graph.
          this.tgSug = { key: t, items: roles, open: true, hi: roles.length ? 0 : -1, busy: false,
                         note: "Entra search unavailable — showing local roles only." };
          this.territoryUsersMsg = /interaction|consent|403|401/i.test(String(err))
            ? "Microsoft needs admin consent for Group.Read.All — add it to the app registration and grant admin consent. Local roles work without it."
            : "Couldn't search Entra groups — local roles are still listed.";
        });
    },
    tgKey(t, e) {
      const s = this.tgSug;
      if (!s.open || s.key !== t) {
        if (e.key === "Escape") this.tgBlur();
        return;
      }
      if (e.key === "ArrowDown") { e.preventDefault(); if (s.items.length) s.hi = (s.hi + 1) % s.items.length; }
      else if (e.key === "ArrowUp") { e.preventDefault(); if (s.items.length) s.hi = (s.hi - 1 + s.items.length) % s.items.length; }
      else if (e.key === "Tab" || e.key === "Enter") {
        if (s.items.length) {
          e.preventDefault();
          this.tgPick(t, s.items[s.hi >= 0 ? s.hi : 0]);
        }
      }
      else if (e.key === "Escape") { this.tgSug = { key: "", items: [], open: false, hi: -1, busy: false }; }
    },
    tgBlur() {
      setTimeout(() => { this.tgSug = { key: "", items: [], open: false, hi: -1, busy: false }; }, 150);
    },
    tgPick(t, it) {
      if (!it || !it.id) return;
      // A local role expands server-side to everyone holding it and provisions them irreversibly —
      // confirm first. Territory Access provisions Sales Insights access + the NC999 company link.
      if (!this.confirmRoleChip(it, "Sales Insights access and the NC999 company link")) {
        this.tgSug = { key: "", items: [], open: false, hi: -1, busy: false, note: "" };
        return;
      }
      const list = (this.territoryGroups[t] || []).slice();
      if (!list.some((g) => g.id === it.id)) list.push({ id: it.id, name: it.name });
      this.$set(this.territoryGroups, t, list);
      this.$set(this.tgTerm, t, "");
      this.tgSug = { key: "", items: [], open: false, hi: -1, busy: false, note: "" };
      this.tgSync(t);
    },
    tgRemove(t, idx) {
      const list = (this.territoryGroups[t] || []).slice();
      list.splice(idx, 1);
      this.$set(this.territoryGroups, t, list);
      this.tgSync(t);
    },
    // Resolve EVERY assigned group's members fresh from Entra and store groups + membership
    // (rpt_TerritoryAccessGroup + Source='group' rows). Matched accounts provision immediately
    // (Sales role + NC999, additive).
    tgSync(t) {
      const groups = (this.territoryGroups[t] || []).slice();
      // Only real ENTRA chips are resolved here (delegated Graph, in this browser). A LOCAL ROLE chip
      // is posted as-is and expanded SERVER-side from the portal user list — so a territory that uses
      // only local roles syncs with no Microsoft sign-in and no Graph consent at all.
      const entra = groups.filter((g) => !this.isRoleGroup(g));
      this.tgBusy = t;
      this.territoryUsersMsg = "";
      this.territoryUsersUnknown = [];
      Promise.all(entra.map((g) => fetchGroupMemberEmails(g.id)))
        .then((lists) => {
          const seen = {};
          const members = [];
          lists.forEach((l) => (l || []).forEach((e) => {
            const k = e.toLowerCase();
            if (!seen[k]) { seen[k] = true; members.push(e); }
          }));
          return navApi.setTerritoryGroups({ territory: t, groups: groups, memberEmails: members });
        })
        .then((res) => {
          const d = res || {};
          this.$set(this.territoryGroupEmails, t, d.groupEmails || []);
          this.$set(this.territoryGroups, t, d.groups || groups);
          this.tgRefreshEffective(t);
          const pending = d.pending || [];
          this.territoryUsersUnknown = pending;
          const n = (d.groupEmails || []).length;
          this.territoryUsersMsg =
            'Synced "' + t + '" — ' + n + " member" + (n === 1 ? "" : "s") + " from " +
            this.accessSourceLabel(groups) +
            (pending.length ? " (" + pending.length + " pre-registered; provisions with Sales access on first Microsoft sign-in)" : ".");
          this.loadTerritoryPending();
        })
        .catch((err) => {
          const data = err && err.response && err.response.data;
          this.territoryUsersMsg = (typeof data === "string" && data) ||
            (/interaction|consent|403|401/i.test(String(err))
              ? "Microsoft needs admin consent for Group.Read.All — add it to the app registration and grant admin consent."
              : 'Couldn\'t sync "' + t + '" from Entra — please try again.');
        })
        .then(() => { this.tgBusy = ""; });
    },
    // Rebuild the EFFECTIVE email list (groups + manual) for one territory — the CSV export reads it.
    tgRefreshEffective(t) {
      const seen = {};
      const all = [];
      (this.territoryGroupEmails[t] || []).concat(
        String(this.territoryManual[t] || "").split(/[\s,;]+/).filter((e) => e)
      ).forEach((e) => {
        const k = String(e).toLowerCase();
        if (!seen[k]) { seen[k] = true; all.push(e); }
      });
      this.$set(this.territoryEmails, t, all.join(", "));
    },
    // Aggregate readout across ALL territories: which access logins still have no portal account (never
    // signed in). Master-only endpoint; a non-Master / missing table resolves to an empty list.
    loadTerritoryPending() {
      navApi
        .getTerritoryPending()
        .then((res) => {
          const list = (res && res.pending) || [];
          this.territoryPending = Array.isArray(list) ? list : [];
          this.territoryAccessLoaded = true;   // success → the "✓ all signed in" line may show when empty
        })
        // On error (e.g. the endpoint isn't deployed yet) leave territoryAccessLoaded false so NEITHER the
        // pending list nor the green "all signed in" line shows — avoids a misleading false-positive.
        .catch(() => { this.territoryPending = []; });
    },
    // Save one territory's MANUAL email extras (group-synced members are managed by tgSync and
    // survive this save). Reports back any emails pre-registered (no account yet).
    saveTerritoryUsers(territory) {
      const t = (territory || "").trim();
      if (!t) return;
      const emails = String(this.territoryManual[t] || "")
        .split(/[\s,;]+/).map((e) => e.trim()).filter((e) => e);
      this.territoryUsersBusy = t;
      this.territoryUsersSaved = "";
      this.territoryUsersMsg = "";
      this.territoryUsersUnknown = [];
      navApi
        .setTerritoryUsers({ territory: t, emails: emails })
        .then((res) => {
          const d = res || {};
          const saved = d.emails || [];      // every manual email stored (active accounts + pre-registered)
          const pending = d.pending || [];   // stored but no portal account yet — links up on first sign-in
          this.$set(this.territoryManual, t, saved.join(", "));
          this.tgRefreshEffective(t);
          this.territoryUsersUnknown = pending;
          this.territoryUsersSaved = t;
          const groupN = (this.territoryGroupEmails[t] || []).length;
          if (pending.length) {
            this.territoryUsersMsg =
              'Saved "' + t + '" manual emails — ' + saved.length + " manual + " + groupN + " from groups/roles; " +
              pending.length + " pre-registered (activates with Sales access on first Microsoft sign-in): " + pending.join(", ");
          } else {
            this.territoryUsersMsg =
              'Saved "' + t + '" manual emails — ' + saved.length + " manual + " + groupN + " from groups/roles.";
          }
          setTimeout(() => { if (this.territoryUsersSaved === t) this.territoryUsersSaved = ""; }, 2500);
          this.loadTerritoryPending();   // refresh the aggregate "pending provisioning" readout
        })
        .catch((err) => {
          const data = err && err.response && err.response.data;
          this.territoryUsersMsg = (typeof data === "string" && data) ||
            "Couldn't save territory access — please try again.";
        })
        .then(() => { this.territoryUsersBusy = ""; });
    },

    // ----- Report Access (login emails per report) — governs who sees each report on the Reports dashboard.
    // Mirrors Territory Access; a saved email provisions the person + their role on first Microsoft sign-in.
    toggleReportAccess() {
      this.reportAccessOpen = !this.reportAccessOpen;
      if (this.reportAccessOpen && !this.reportAccessLoadedOnce) {
        this.reportAccessLoadedOnce = true;
        this.loadReportUsers();
      }
    },

    // Load per report: assigned Entra groups, their resolved member emails, and the manual extras.
    // Back-compat: an older backend returns only `emails` — treat those as manual so nothing hides.
    loadReportUsers() {
      this.loadNavRoleOptions();   // the local-role chips need the role list (same source as the nav picker)
      navApi
        .getReportUsers()
        .then((rows) => {
          const map = {};
          (Array.isArray(rows) ? rows : []).forEach((r) => {
            if (r && r.reportKey) map[String(r.reportKey).trim()] = r;
          });
          this.reportAccessGroups.forEach((g) => {
            const r = map[g.key] || {};
            const manual = Array.isArray(r.manualEmails) ? r.manualEmails : (r.emails || []);
            this.$set(this.reportManual, g.key, manual.join(", "));
            this.$set(this.reportGroups, g.key, Array.isArray(r.groups) ? r.groups : []);
            this.$set(this.reportGroupEmails, g.key, Array.isArray(r.groupEmails) ? r.groupEmails : []);
            this.$set(this.reportEmails, g.key, (r.emails || []).join(", "));   // legacy mirror
          });
          this.loadReportPending();
        })
        .catch(() => {});
    },

    // ----- Group-members hover popup (shared by Report + Territory access chips) -----
    rgHoverStart(grp, e) {
      if (!grp || !grp.id) return;
      clearTimeout(this._rgHoverClose);
      var rect = e.currentTarget.getBoundingClientRect();
      // Flip above the chip when there's no room below (max popup height ~300px).
      var up = rect.bottom + 310 > window.innerHeight;
      var px = Math.max(8, Math.min(rect.left, window.innerWidth - 300));
      var py = up ? rect.top - 6 : rect.bottom + 6;
      // LOCAL ROLE chip — there is nothing in Entra to read. Its membership is the portal's own
      // AspNetUserRoles list, expanded server-side on every sync (and already inside the member count
      // under the chips). members:null keeps the header count off and shows the note instead.
      if (this.isRoleGroup(grp)) {
        this.rgHover = {
          groupId: grp.id,
          name: grp.name,
          isRole: true,
          members: null,
          busy: false,
          error: "",
          note: "Local portal role. Members are everyone holding the “" + grp.name +
                "” role in this portal — resolved on the server each time this row syncs, not from Entra.",
          x: px,
          y: py,
          up: up
        };
        return;
      }
      this.rgHover = {
        groupId: grp.id,
        name: grp.name,
        isRole: false,
        members: null,
        busy: true,
        error: "",
        note: "",
        x: px,
        y: py,
        up: up
      };
      if (!this._rgMembersCache) this._rgMembersCache = {};
      var self = this;
      var cached = this._rgMembersCache[grp.id];
      if (cached && typeof cached.then !== "function") {
        this.rgHover = Object.assign({}, this.rgHover, { members: cached.members, note: cached.note || "", busy: false });
        return;
      }
      // ONE in-flight promise per group, stored in the cache: rapid hover-away-and-back reuses it
      // instead of firing duplicate Graph reads, and a slow first fetch's failure can't overwrite a
      // later success. Failure evicts the entry so the next hover retries.
      var p = cached;
      if (!p) {
        p = fetchGroupMembersDetailed(grp.id).then(function (r) {
          var members = (r && r.members) || [];
          if (members.length) return { members: members, note: "" };
          // 0 users came back — ask Graph what this group IS so the admin sees WHY instead of a bare
          // "No members": wrong same-named duplicate, dynamic rule matching nobody/only devices, only
          // non-user objects inside, or an on-prem group whose membership never synced to Entra.
          return fetchGroupInfo(grp.id).catch(function () { return null; }).then(function (info) {
            var bits = [];
            if (info) {
              var kind = (info.groupTypes || []).indexOf("Unified") >= 0
                ? "Microsoft 365 group" : (info.securityEnabled ? "Security group" : "Distribution list");
              bits.push("This is a " + kind + (info.mail ? " (" + info.mail + ")" : "") + ".");
              if (info.membershipRule)
                bits.push(r && r.scanned > 0
                  ? "It uses DYNAMIC membership — its rule currently matches only non-user objects."
                  : "It uses DYNAMIC membership and its rule currently matches nobody.");
              if (info.onPremisesSyncEnabled) bits.push("It syncs from on-prem AD — members only appear here once the membership itself syncs to Entra.");
            }
            if (r && r.scanned > 0)
              bits.push("It contains " + r.scanned + " non-user object" + (r.scanned === 1 ? "" : "s") + " (nested groups / contacts / devices) but no user accounts.");
            bits.push("If you expected members, a same-named duplicate group may exist — remove this chip and re-pick it (the search list shows each group's type and email).");
            return { members: [], note: "Entra returned no user members for this group. " + bits.join(" ") };
          });
        });
        this._rgMembersCache[grp.id] = p;
        p.then(function (res) { self._rgMembersCache[grp.id] = res; })
          .catch(function () { delete self._rgMembersCache[grp.id]; });
      }
      p.then(function (res) {
        if (self.rgHover.groupId === grp.id)
          self.rgHover = Object.assign({}, self.rgHover, { members: res.members, note: res.note || "", busy: false, error: "" });
      }).catch(function (e) {
        // Prefer the server's own wording: when app-only resolution is on, it explains the actionable
        // cases (403 = the GroupMember.Read.All APPLICATION permission is missing or unconsented,
        // 401 = the client secret expired). The generic line is the fallback for the delegated path.
        if (self.rgHover.groupId === grp.id)
          self.rgHover = Object.assign({}, self.rgHover, {
            busy: false,
            error: (e && e.message) || "Couldn't load members from Entra.",
          });
      });
    },
    rgHoverKeep() {
      clearTimeout(this._rgHoverClose);
    },
    rgHoverEnd() {
      var self = this;
      clearTimeout(this._rgHoverClose);
      // Small grace so the pointer can travel from the chip INTO the popup.
      this._rgHoverClose = setTimeout(function () {
        self.rgHover = { groupId: "", name: "", members: null, busy: false, error: "", note: "", x: 0, y: 0, up: false };
      }, 150);
    },

    // ----- Entra group typeahead + membership sync -----
    rgManualCount(key) {
      return String(this.reportManual[key] || "").split(/[\s,;]+/).filter((e) => e).length;
    },
    rgInput(key, e) {
      const term = String(e.target.value || "").trim();
      this.$set(this.rgTerm, key, e.target.value);
      // "busy" only when an Entra search will really run (2+ chars). The local-role list is instant,
      // so focusing the empty box immediately reveals the pickable portal roles.
      this.rgSug = { key: key, items: this.rgSug.key === key ? this.rgSug.items : [], open: true, hi: -1,
                     busy: term.length >= 2, note: "" };
      clearTimeout(this._rgTimer);
      this._rgTimer = setTimeout(() => this.rgSearch(key), term.length >= 2 ? 300 : 0);
    },
    // Two sources in ONE dropdown: the portal's own roles (local, instant) then the Entra groups.
    rgSearch(key) {
      const term = String(this.rgTerm[key] || "").trim();
      const assigned = (this.reportGroups[key] || []).map((g) => g.id);
      const roles = this.localRoleMatches(term, assigned);
      if (term.length < 2) {
        // Too short for a Graph search, but the local roles are already in hand — show them.
        this.rgSug = { key: key, items: roles, open: roles.length > 0, hi: roles.length ? 0 : -1, busy: false, note: "" };
        return;
      }
      this.rgSug = { key: key, items: roles, open: true, hi: roles.length ? 0 : -1, busy: true, note: "" };
      searchEntraGroups(term)
        .then((items) => {
          if (this.rgSug.key !== key) return;   // user moved to another row
          const list = roles.concat((items || []).filter((it) => assigned.indexOf(it.id) < 0));
          this.rgSug = { key: key, items: list, open: true, hi: list.length ? 0 : -1, busy: false, note: "" };
        })
        .catch((err) => {
          if (this.rgSug.key !== key) return;
          // Entra unreachable / not consented: keep the dropdown OPEN on the local roles rather than
          // closing it — picking a local role is precisely the path that does not need Graph.
          this.rgSug = { key: key, items: roles, open: true, hi: roles.length ? 0 : -1, busy: false,
                         note: "Entra search unavailable — showing local roles only." };
          this.reportUsersMsg = /interaction|consent|403|401/i.test(String(err))
            ? "Microsoft needs admin consent for Group.Read.All — add it to the app registration (API permissions) and grant admin consent. Local roles work without it."
            : "Couldn't search Entra groups — local roles are still listed.";
        });
    },
    rgKey(key, e) {
      const s = this.rgSug;
      if (!s.open || s.key !== key) {
        if (e.key === "Escape") this.rgBlur();
        return;
      }
      if (e.key === "ArrowDown") { e.preventDefault(); if (s.items.length) s.hi = (s.hi + 1) % s.items.length; }
      else if (e.key === "ArrowUp") { e.preventDefault(); if (s.items.length) s.hi = (s.hi - 1 + s.items.length) % s.items.length; }
      else if (e.key === "Tab" || e.key === "Enter") {
        if (s.items.length) {
          e.preventDefault();
          this.rgPick(key, s.items[s.hi >= 0 ? s.hi : 0]);
        }
      }
      else if (e.key === "Escape") { this.rgSug = { key: "", items: [], open: false, hi: -1, busy: false }; }
    },
    rgBlur() {
      // Delay so a mousedown pick on the dropdown lands first.
      setTimeout(() => { this.rgSug = { key: "", items: [], open: false, hi: -1, busy: false }; }, 150);
    },
    rgPick(key, it) {
      if (!it || !it.id) return;
      // A local role expands server-side to everyone holding it and provisions them irreversibly —
      // confirm first. Report Access provisions that report's own role + the NC999 company link.
      if (!this.confirmRoleChip(it, "this report's role and the NC999 company link")) {
        this.rgSug = { key: "", items: [], open: false, hi: -1, busy: false, note: "" };
        return;
      }
      const list = (this.reportGroups[key] || []).slice();
      if (!list.some((g) => g.id === it.id)) list.push({ id: it.id, name: it.name });
      this.$set(this.reportGroups, key, list);
      this.$set(this.rgTerm, key, "");
      this.rgSug = { key: "", items: [], open: false, hi: -1, busy: false, note: "" };
      this.rgSync(key);
    },
    rgRemove(key, idx) {
      const list = (this.reportGroups[key] || []).slice();
      list.splice(idx, 1);
      this.$set(this.reportGroups, key, list);
      this.rgSync(key);
    },
    // Resolve EVERY assigned group's members fresh from Entra, then store groups + membership
    // server-side (rpt_ReportAccessGroup + Source='group' access rows). Always full — never
    // incremental — so removals and Entra-side membership changes both take effect.
    rgSync(key) {
      const label = (this.reportAccessGroups.find((g) => g.key === key) || {}).label || key;
      const groups = (this.reportGroups[key] || []).slice();
      // Only real ENTRA chips are resolved here (delegated Graph, in this browser). A LOCAL ROLE chip
      // is posted as-is and expanded SERVER-side from the portal user list — so a report whose access
      // is defined purely by local roles syncs with no Microsoft sign-in and no Graph consent at all.
      const entra = groups.filter((g) => !this.isRoleGroup(g));
      this.rgBusy = key;
      this.reportUsersMsg = "";
      this.reportUsersUnknown = [];
      Promise.all(entra.map((g) => fetchGroupMemberEmails(g.id)))
        .then((lists) => {
          const seen = {};
          const members = [];
          lists.forEach((l) => (l || []).forEach((e) => {
            const k = e.toLowerCase();
            if (!seen[k]) { seen[k] = true; members.push(e); }
          }));
          return navApi.setReportGroups({ reportKey: key, groups: groups, memberEmails: members });
        })
        .then((res) => {
          const d = res || {};
          this.$set(this.reportGroupEmails, key, d.groupEmails || []);
          this.$set(this.reportGroups, key, d.groups || groups);
          const pending = d.pending || [];
          this.reportUsersUnknown = pending;
          const n = (d.groupEmails || []).length;
          this.reportUsersMsg =
            'Synced "' + label + '" — ' + n + " member" + (n === 1 ? "" : "s") + " from " +
            this.accessSourceLabel(groups) +
            (pending.length ? " (" + pending.length + " pre-registered; they provision on first Microsoft sign-in)" : ".");
          this.loadReportPending();
        })
        .catch((err) => {
          const data = err && err.response && err.response.data;
          this.reportUsersMsg = (typeof data === "string" && data) ||
            (/interaction|consent|403|401/i.test(String(err))
              ? "Microsoft needs admin consent for Group.Read.All — add it to the app registration and grant admin consent."
              : 'Couldn\'t sync "' + label + '" from Entra — please try again.');
        })
        .then(() => { this.rgBusy = ""; });
    },

    // Aggregate readout: report-access logins that still have no portal account (never signed in).
    loadReportPending() {
      navApi
        .getReportPending()
        .then((res) => {
          const list = (res && res.pending) || [];
          this.reportPending = Array.isArray(list) ? list : [];
          this.reportAccessLoaded = true;
        })
        .catch(() => { this.reportPending = []; });
    },

    // Save one report's MANUAL email extras (group-synced members are managed by rgSync and
    // survive this save). Reports back any emails pre-registered (no account yet).
    saveReportUsers(reportKey) {
      const k = (reportKey || "").trim();
      if (!k) return;
      const label = (this.reportAccessGroups.find((g) => g.key === k) || {}).label || k;
      const emails = String(this.reportManual[k] || "")
        .split(/[\s,;]+/).map((e) => e.trim()).filter((e) => e);
      this.reportUsersBusy = k;
      this.reportUsersSaved = "";
      this.reportUsersMsg = "";
      this.reportUsersUnknown = [];
      navApi
        .setReportUsers({ reportKey: k, emails: emails })
        .then((res) => {
          const d = res || {};
          const saved = d.emails || [];      // every manual email stored (active accounts + pre-registered)
          const pending = d.pending || [];   // stored but no portal account yet — provisions on first sign-in
          this.$set(this.reportManual, k, saved.join(", "));
          this.reportUsersUnknown = pending;
          this.reportUsersSaved = k;
          const groupN = (this.reportGroupEmails[k] || []).length;
          if (pending.length) {
            this.reportUsersMsg =
              'Saved "' + label + '" manual emails — ' + saved.length + " manual + " + groupN + " from groups/roles; " +
              pending.length + " pre-registered (provisions with its role on first Microsoft sign-in): " + pending.join(", ");
          } else {
            this.reportUsersMsg =
              'Saved "' + label + '" manual emails — ' + saved.length + " manual + " + groupN + " from groups/roles.";
          }
          setTimeout(() => { if (this.reportUsersSaved === k) this.reportUsersSaved = ""; }, 2500);
          this.loadReportPending();
        })
        .catch((err) => {
          const data = err && err.response && err.response.data;
          this.reportUsersMsg = (typeof data === "string" && data) ||
            "Couldn't save report access — please try again.";
        })
        .then(() => { this.reportUsersBusy = ""; });
    },
    salesTerrRowDirty(it) {
      const base = this.salesTerrOriginal[it.id];
      if (!base) return true;
      return (it.state || "") !== base.state ||
             (it.territory || "") !== base.territory ||
             (it.salesRep || "") !== base.salesRep ||
             (it.color || "") !== base.color;
    },
    // The territory's CURRENT effective color, for prefilling a row's picker so the admin sees what's
    // already in play (and only changes what they want): a color already assigned to that territory on
    // any row wins, else the report's built-in palette (kept in sync with CustomerInsights.terrColor).
    territoryEffectiveColor(territory) {
      const t = (territory || "").trim();
      const assigned = (this.salesTerrItems || []).find((r) => r && (r.territory || "").trim() === t && r.color);
      if (assigned) return assigned.color;
      const palette = {
        "International": "#2E86DE", "Midwest": "#8E44AD", "Mountain and West Coast": "#E67E22",
        "Northern & Canada": "#9B1B9E", "SouthEast": "#E84393",
      };
      return palette[t] || "#9e2436";
    },
    saveSalesTerrRow(it) {
      if (this.salesTerrRowBusy || !(it.state || "").trim()) return;
      this.salesTerrRowBusy = it.id;
      this.salesTerrRowSaved = "";
      navApi
        .updateSalesTerritory(Object.assign({}, it))
        .then(() => {
          this.$set(this.salesTerrOriginal, it.id, {
            state: it.state || "", territory: it.territory || "", salesRep: it.salesRep || "", color: it.color || "",
          });
          this.salesTerrRowSaved = it.id;
          setTimeout(() => { if (this.salesTerrRowSaved === it.id) this.salesTerrRowSaved = ""; }, 1500);
          this.loadTerrWarnings();   // a corrected/added rep name may resolve a warning
        })
        .catch((err) => this.salesTerrFailed(err))
        .then(() => { this.salesTerrRowBusy = ""; });
    },
    deleteSalesTerrRow(it) {
      if (this.salesTerrRowBusy) return;
      if (!window.confirm("Delete the " + (it.state || "?") + " → " + (it.territory || "?") + " assignment?")) return;
      this.salesTerrRowBusy = it.id;
      navApi
        .deleteSalesTerritory(Object.assign({}, it))
        .then(() => {
          this.salesTerrItems = this.salesTerrItems.filter((r) => r.id !== it.id);
          this.$delete(this.salesTerrOriginal, it.id);
          this.loadTerrWarnings();   // removing an assignment may create a warning
        })
        .catch((err) => this.salesTerrFailed(err))
        .then(() => { this.salesTerrRowBusy = ""; });
    },
    addSalesTerrRow() {
      if (this.salesTerrAddBusy) return;
      const row = {
        state: (this.newSalesTerr.state || "").trim(),
        territory: (this.newSalesTerr.territory || "").trim(),
        salesRep: (this.newSalesTerr.salesRep || "").trim(),
        color: (this.newSalesTerr.color || "").trim(),
      };
      if (!row.state) return;
      this.salesTerrAddBusy = true;
      navApi
        .createSalesTerritory(row)
        .then((saved) => {
          const r = saved && saved.id ? saved : Object.assign({ id: "tmp-" + this.salesTerrItems.length }, row);
          this.salesTerrItems.push(r);
          this.$set(this.salesTerrOriginal, r.id, {
            state: r.state || "", territory: r.territory || "", salesRep: r.salesRep || "", color: r.color || "",
          });
          this.newSalesTerr = { state: "", territory: "", salesRep: "", color: "" };
          this.loadTerrWarnings();   // a new assignment may resolve a warning
        })
        .catch((err) => this.salesTerrFailed(err))
        .then(() => { this.salesTerrAddBusy = false; });
    },
    // ---- Unmapped-rep drill-down: rep → (prefix + customer-country) groups → the orders behind a group.
    // All lazy: groups load when a rep is expanded, orders when a group is expanded. ----
    // 💡 unmapped-rep explainer: why the rep is unassigned, how to fix it, and the result after fixing.
    toggleWarnInsight(name) {
      this.expandedWarnInsight = (this.expandedWarnInsight === name) ? "" : name;
    },
    // Does an existing map row use a spelling variant of this VISUAL rep name? (e.g. VISUAL "JCS&S  AUSTRALIA"
    // with two spaces vs the map's "JCS&S Australia") — powers the 💡 "likely cause" hint.
    warnFuzzyMatch(name) {
      var norm = function (s) { return String(s == null ? "" : s).toUpperCase().replace(/[^A-Z0-9]/g, ""); };
      var target = norm(name);
      if (!target) return null;
      var hit = null;
      (this.salesTerrItems || []).forEach(function (row) {
        if (!row || !row.salesRep || hit) return;
        if (norm(row.salesRep) === target && String(row.salesRep) !== String(name)) hit = { rep: row.salesRep, territory: row.territory || "" };
      });
      return hit;
    },
    toggleWarnRep(rep) {
      if (this.expandedWarnRep === rep) { this.expandedWarnRep = ""; return; }
      this.expandedWarnRep = rep;
      this.expandedWarnGroup = "";
      if (!this.warnGroups[rep]) this.loadWarnGroups(rep);
    },
    loadWarnGroups(rep) {
      var self = this;
      this.warnGroupsBusy = rep;
      navApi.getTerritoryWarningGroups(rep)
        .then(function (rows) { self.$set(self.warnGroups, rep, Array.isArray(rows) ? rows : []); })
        .catch(function () { self.$set(self.warnGroups, rep, []); })
        .then(function () { if (self.warnGroupsBusy === rep) self.warnGroupsBusy = ""; });
    },
    warnGroupKey(rep, g) { return rep + "|" + (g && g.prefix) + "|" + (g && g.country); },
    toggleWarnGroup(rep, g) {
      var key = this.warnGroupKey(rep, g);
      if (this.expandedWarnGroup === key) { this.expandedWarnGroup = ""; return; }
      this.expandedWarnGroup = key;
      if (!this.warnOrders[key]) this.loadWarnOrders(rep, g);
    },
    loadWarnOrders(rep, g) {
      var self = this;
      var key = this.warnGroupKey(rep, g);
      this.warnOrdersBusy = true;
      navApi.getTerritoryWarningOrders(rep, g && g.prefix, g && g.country)
        .then(function (rows) { self.$set(self.warnOrders, key, Array.isArray(rows) ? rows : []); })
        .catch(function () { self.$set(self.warnOrders, key, []); })
        .then(function () { self.warnOrdersBusy = false; });
    },
    // How a prefix is CURRENTLY mapped (so the admin assigns with eyes open): "→ Territory (Rep)" or "" if unmapped.
    prefixMapping(prefix) {
      var p = String(prefix || "").trim().toUpperCase();
      var row = (this.salesTerrItems || []).find(function (r) { return String((r && r.state) || "").trim().toUpperCase() === p; });
      if (!row) return "";
      var t = (row.territory || "").trim(), rep = (row.salesRep || "").trim();
      return "→ " + (t || "?") + (rep ? " (" + rep + ")" : "");
    },
    fmtWarnDate(d) {
      if (!d) return "—";
      var dt = new Date(d);
      if (isNaN(dt.getTime())) return "—";
      return (dt.getMonth() + 1) + "/" + dt.getDate() + "/" + String(dt.getFullYear()).slice(2);
    },
    salesTerrFailed(err) {
      const data = err && err.error && err.error.response && err.error.response.data;
      this.$bvToast.toast(data ? "TraceID: " + data.traceId : "Please try again.", {
        title: (data && data.title) || "Sales Territory update failed",
        autoHideDelay: 8000,
        appendToast: false,
        variant: "danger",
        solid: true,
      });
    },
    toggleEvents() {
      this.$set(this.open, "events", !this.open.events);
      if (this.open.events && !this.eventBusy) this.loadEventLog();
    },
    eventDotClass(ev) {
      if (!ev || ev.ok === false) return "is-fail";
      if (ev.kind === "system") return "is-system";
      return "is-ok";
    },
    hasKeys(obj) {
      return !!obj && Object.keys(obj).length > 0;
    },
    runDiagnose() {
      const email = (this.diagEmail || "").trim();
      if (!email || this.diagBusy) return;
      this.diagBusy = true;
      apiService
        .diagnoseSetupEmail(email, this.diagType)
        .then((entry) => {
          this.diagBusy = false;
          this.loadEventLog();
          if (entry && entry.id) this.$set(this.expandedEvents, "e-" + entry.id, true);
        })
        .catch((err) => {
          this.diagBusy = false;
          const d = err && err.response && err.response.data;
          window.alert((typeof d === "string" && d) ? d : "Couldn't run the email check — please try again.");
        });
    },
    openModal(payload) {
      this.editEntity = null;
      this.open = {};
      this.copyResult = null;
      this.copySource = "";
      this.copyTerm = "";
      this.deletedRows = {};
      this.usersScope = "company";
      this.viewableUsers = [];
      this.linkUserRef = "";
      this.linkUser = {};
      this.linkTerm = "";
      this.linkResults = [];
      this.linkMsg = "";
      this.gapsOpen = false;
      this.gapHistoryOpen = false;
      this.hiddenGapKeys = [];
      this.expandedEvents = {};
      this.eventSearch = "";
      this.diagEmail = "";
      this.diagType = "email-delivery";
      this.userExpanded = {};
      this.userCompanies = {};
      this.userCompaniesBusy = {};
      this.setupLinkSent = {};
      this.showModal = true;
      this.fetchSetup();
      this.loadCompanies();
      this.loadEventLog();
      this.loadHistory();
      this.loadDeleteHistory("machines");
      this.loadDeleteHistory("wearParts");
      this.loadDeleteHistory("users");
      this.loadDeleteHistory("parts");
      this.loadDeleteHistory("assemblies");
      this.loadDeleteHistory("subAssemblies");
      // Deep-link from the report's 💡 "why unassigned" diagnostic: open straight to Sales Territories and pre-fill
      // the "add assignment" row with the unmapped rep — the SAME outcome as the banner's "Add" button (addWarnToMap:
      // seed newSalesTerr.salesRep + scroll to the add row + focus the territory input). payload is undefined for a
      // normal open (SETUP pill / header "Admin Dashboard") and a MouseEvent has no .jump, so the guard is safe.
      // The modal + this section render across several ticks, so POLL for the add-row ref before acting — a single
      // $nextTick fires before the section is laid out and the jump silently no-ops (the original bug).
      if (payload && payload.jump === "territories") {
        this.salesTerrOpen = true;
        if (!this.salesTerrLoaded && !this.salesTerrBusy) this.loadSalesTerr();
        var self = this, rep = payload.addRep, tries = 0;
        var doJump = function () {
          var box = self.$refs.salesTerrAddRow;
          if (box) {
            if (rep) self.addWarnToMap({ name: rep });
            else if (box.scrollIntoView) box.scrollIntoView({ block: "center", behavior: "smooth" });
            return;
          }
          if (++tries < 30) setTimeout(doJump, 100);   // wait for the modal + territories section to render (~3s cap)
        };
        setTimeout(doJump, 150);
      }
    },

    toggle(key) {
      this.$set(this.open, key, !this.open[key]);
    },
    onCatToggle(cat) {
      this.toggle(cat.key);
      if (cat.tableMode && this.open[cat.key]) { this.loadReach(cat); this.loadGaps(cat); }
    },
    loadReach(cat) {
      const ids = (cat.items || []).map((it) => this.itemId(it)).filter((x) => x > 0);
      const need = ids.filter((id) => !this.reach[id]);
      if (!need.length) return;
      apiService
        .getSetupPartReach(need)
        .then((rows) => {
          if (!Array.isArray(rows) || !rows.length) {
            // eslint-disable-next-line no-console
            console.warn("[AccountSetup] setup-part-reach returned no rows for", need.length, "part(s) — backend may be stale.");
          }
          (rows || []).forEach((r) => {
            this.$set(this.reach, r.partId, {
              machineCount: r.machineCount || 0,
              customerCount: r.customerCount || 0,
            });
          });
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.warn("[AccountSetup] setup-part-reach failed:", (err && err.response && err.response.status) || err);
        });
    },
    // Fetch the FULL orange/red gap set for this category from the server (scans
    // the company's entire wear/parts set, not just the capped page), so the
    // colour-dot filter surfaces a gap even when it sorts past the cap. Also seeds
    // reach[] for those rows so their Part ID colours correctly. Cached per open.
    loadGaps(cat) {
      if (!cat || !cat.tableMode || this.gapsLoaded[cat.key]) return;
      const companyId = localStorage.companyId || "";
      if (!companyId) return;
      const kind = cat.delType === "wear" ? "wear" : "parts";
      this.$set(this.gapsLoaded, cat.key, true);
      apiService
        .getSetupGaps(companyId, kind)
        .then((rows) => {
          rows = Array.isArray(rows) ? rows : [];
          this.$set(this.gaps, cat.key, rows);
          rows.forEach((r) => {
            if (r && r.id) {
              this.$set(this.reach, r.id, {
                machineCount: r.machineCount || 0,
                customerCount: r.customerCount || 0,
              });
            }
          });
        })
        .catch(() => {
          this.$set(this.gaps, cat.key, []);
        });
    },
    reloadOpenReach() {
      (this.categories || []).forEach((cat) => {
        if (cat.tableMode && this.open[cat.key]) { this.loadReach(cat); this.loadGaps(cat); }
      });
    },
    colorOf(it) {
      const r = this.reach[this.itemId(it)];
      if (!r) return "";
      const m = (r.machineCount || 0) > 0;
      const c = (r.customerCount || 0) > 0;
      if (m && c) return "green";
      if (m || c) return "orange";
      return "red";
    },
    idColorClass(it) {
      const col = this.colorOf(it);
      return col ? "asc-id-" + col : "";
    },
    colorCount(cat, color) {
      // Orange/red are the gaps — count the full server-computed set (not just the
      // capped page) so the dot reflects the true total. Green stays the page count.
      if (color === "orange" || color === "red") {
        return (this.gaps[cat.key] || []).filter((g) => g.color === color).length;
      }
      return (cat.items || []).filter((it) => this.colorOf(it) === color).length;
    },
    toggleColorFilter(cat, color) {
      this.$set(this.colorFilter, cat.key, this.colorFilter[cat.key] === color ? "" : color);
      if (color === "orange" || color === "red") this.loadGaps(cat);
    },
    filteredItems(cat) {
      const f = this.colorFilter[cat.key];
      let items;
      if (!f) {
        items = cat.items || [];
      } else if (f === "orange" || f === "red") {
        // Orange/red show the FULL server gap set (surfaces gaps past the cap).
        items = (this.gaps[cat.key] || []).filter((g) => g.color === f);
      } else {
        // green filters the loaded page (the common, bulky case).
        items = (cat.items || []).filter((it) => this.colorOf(it) === f);
      }
      // Editable categories (Machines / Wear / Users): the add-search box ALSO
      // filters the EXISTING listing, so an admin can see whether what they're
      // typing is already on the account.
      const term = this.searchTermFor(cat);
      if (term) {
        items = items.filter((it) => this.itemMatchesTerm(it, term));
      }
      // Users: viewable (retrievable-password) users sort to the TOP in BOTH
      // scopes. "All companies" swaps in the global set (every company) loaded on
      // toggle; the add-box term filters it too, for parity with this-company.
      if (cat.key === "users") {
        let list = items;
        if (this.usersPwFilter) {
          list = this.viewableUsers || [];
          // (scope-specific filters below; the internal-domain gate is applied to ALL scopes after)
          // NOT-yet-approved only. `approved` comes from AspNetUsers.EmailConfirmed (see the backend
          // AccountSetupItem.Approved comment) — the same flag the form's "Approved" checkbox writes.
          if (this.usersScope === "pending") list = list.filter((it) => !it.approved);
          if (term) list = list.filter((it) => this.itemMatchesTerm(it, term));
        }
        // INTERNAL-DOMAIN GATE. The server is the authority (AccountController.IsInternalAccount);
        // this is a second net so an internal account can never RENDER, whichever scope is showing
        // and even if some other endpoint is missed. Applied to all three scopes.
        if (!this.seesInternalAccounts) list = list.filter((it) => !this.isInternalEmail(this.itemCode(it)));
        return list
          .slice()
          .sort((a, b) => (b.hasPassword ? 1 : 0) - (a.hasPassword ? 1 : 0));
      }
      return items;
    },
    // The in-list search term for a category (its add box). Editable categories
    // only (Machines / Wear / Users). Parts is excluded — it uses a catalog
    // link-search and its listing is capped, so an in-list "Not found" could
    // be misleading.
    searchTermFor(cat) {
      if (!cat || !cat.editType) return "";
      return (this.addTerm[cat.key] || "").trim().toLowerCase();
    },
    isSearchActive(cat) {
      return this.searchTermFor(cat).length > 0;
    },
    searchHitCount(cat) {
      return this.filteredItems(cat).length;
    },
    // Add candidates, minus any whose CODE is already on the account (same code,
    // different PartId — e.g. NDC S 6250 HS as both order-only part 968 and wear
    // part 11804). Adding such a candidate would create a duplicate-code setup
    // gap, since the 2% wear discount matches by code. Rows soft-deleted this
    // session don't count as "already on the account".
    addableResults(cat) {
      let list = this.addResults[cat.key] || [];
      if (!list.length) return list;
      // Same gate as the list: never offer an internal account as a link candidate.
      if (cat && cat.key === "users" && !this.seesInternalAccounts) {
        list = list.filter((r) => !this.isInternalEmail(r && r.code));
      }
      const existingCodes = new Set();
      (cat.items || []).forEach((it) => {
        if (this.isDeleted(cat, it)) return;
        const c = String(it.code || "").trim().toLowerCase();
        if (c) existingCodes.add(c);
      });
      if (!existingCodes.size) return list;
      return list.filter((r) => {
        const c = String(r.code || "").trim().toLowerCase();
        return !c || !existingCodes.has(c);
      });
    },
    hiddenAddCount(cat) {
      return (this.addResults[cat.key] || []).length - this.addableResults(cat).length;
    },
    // Substring match on code + description + id, for the in-list wear search.
    itemMatchesTerm(it, term) {
      if (!it) return false;
      const t = String(term || "").toLowerCase();
      if (!t) return true;
      const code = String(it.code || "").toLowerCase();
      const label = String(it.label || "").toLowerCase();
      const id = String(it.id || "");
      return code.indexOf(t) !== -1 || label.indexOf(t) !== -1 || id.indexOf(t) !== -1;
    },

    // Detail rows tolerate BOTH the new object shape ({id, code, label, ...})
    // and the old plain-string shape, so the live widget keeps rendering
    // correctly before the upgraded backend is deployed.
    itemLabel(it) {
      if (it == null) return "";
      if (typeof it === "string") return it;
      return it.label || it.code || "";
    },
    itemCode(it) {
      if (!it || typeof it === "string") return "";
      return it.code && it.code !== it.label ? it.code : "";
    },
    itemId(it) {
      if (!it || typeof it === "string") return 0;
      return it.id || 0;
    },
    itemMachine(it) {
      if (!it || typeof it === "string") return "";
      return it.machine || "";
    },
    itemAssembly(it) {
      if (!it || typeof it === "string") return "";
      return it.assembly || "";
    },

    // Fetch the setup for whatever company is currently selected (the header
    // dropdown writes it to localStorage). Fetched per-open so it always
    // reflects the company the admin is currently viewing.
    fetchSetup() {
      const companyId = localStorage.companyId || "";
      this.loadError = false;
      if (!companyId) {
        this.setup = null;
        return;
      }
      this.loading = true;
      apiService
        .getAccountSetup(companyId)
        .then((data) => {
          this.setup = data || null;
          this.loading = false;
          // Links may have just changed, so cached reach (the Part ID color) AND
          // impact (the row-expand detail) are stale — clear both, then recolor
          // any open Parts/Wear tables.
          this.reach = {};
          this.impact = {};
          this.impactOpen = {};
          this.impactCustOpen = {};
          this.gaps = {};
          this.gapsLoaded = {};
          this.reloadOpenReach();
          this.loadEventLog();   // keep the cross-company feed fresh after any change
          // Any setup change can also change the cross-company user pool (deactivate, unlink,
          // create, link). No-ops on the "This company" scope, so it costs a request only when
          // that pool is the thing on screen.
          this.loadViewableUsers();
        })
        .catch(() => {
          this.setup = null;
          this.loading = false;
          this.loadError = true;
        });
    },

    loadCompanies() {
      apiService
        .getSetupCompanies()
        .then((data) => {
          this.companies = data || [];
        })
        .catch(() => {
          this.companies = [];
        });
    },

    // Clone the chosen source company's machines + wear parts into the
    // company currently being viewed, then refresh the summary.
    // Copy-from company search: pick from the filtered dropdown instead of a <select>.
    onCopyInput(ev) {
      this.copyTerm = ev.target.value;
      this.copySource = ""; // editing the text clears any prior selection
    },
    pickCopy(c) {
      if (!c) return;
      this.copySource = c.id;
      this.copyTerm = (c.code ? c.code + " — " : "") + (c.name || "");
    },
    clearCopy() {
      this.copySource = "";
      this.copyTerm = "";
    },
    doCopy() {
      const target = localStorage.companyId || "";
      if (!this.copySource || !target) return;
      const src = (this.companies || []).find(
        (c) => String(c.id) === String(this.copySource)
      );
      const srcCode = src ? src.code : "";
      const tgtCode = this.headerLabel || (this.setup && this.setup.companyCode) || "";
      const srcLabel = src ? src.code + " — " + src.name : "the source company";
      if (!window.confirm(
        "Copy machines + wear parts from " + srcLabel +
        " into this company?\n\nAssemblies, sub-assemblies and parts follow the machines automatically. Existing links are skipped."
      )) {
        return;
      }
      this.copying = true;
      this.copyResult = null;
      apiService
        .copyAccountSetup(this.copySource, target)
        .then((res) => {
          res = res || {};
          this.copyResult = {
            machinesAdded: res.machinesAdded || 0,
            wearAdded: res.wearAdded || 0,
            sourceCode: res.sourceCode || srcCode,
            targetCode: res.targetCode || tgtCode,
          };
          this.copying = false;
          this.copySource = "";
          this.copyTerm = "";
          this.fetchSetup();
          this.loadHistory();
          this.historyOpen = true;
        })
        .catch(() => {
          this.copying = false;
          this.copyResult = { error: true };
        });
    },

    // ----- Copy history (server-side) + Undo -----
    loadHistory() {
      this.copyDetail = {};
      this.copyDetailOpen = {};
      apiService
        .getSetupCopyHistory(localStorage.companyId || "")
        .then((data) => {
          this.history = Array.isArray(data) ? data : [];
        })
        .catch(() => {
          this.history = [];
        });
    },
    // Expand a copy-history row to itemize what it created and whether each
    // machine / wear part is still here or was manually removed (for auditing).
    toggleCopyDetail(h) {
      if (!h || !h.id) return;
      const open = !this.copyDetailOpen[h.id];
      this.$set(this.copyDetailOpen, h.id, open);
      if (open && !this.copyDetail[h.id]) {
        this.copyDetailBusy = h.id;
        apiService
          .getSetupCopyDetail(h.id)
          .then((d) => {
            this.$set(this.copyDetail, h.id, d || { machines: [], wear: [] });
            this.copyDetailBusy = "";
          })
          .catch(() => {
            this.$set(this.copyDetail, h.id, { machines: [], wear: [], error: true });
            this.copyDetailBusy = "";
          });
      }
    },
    // How many of a copy's created rows still exist. The backend reports
    // machinesRemaining / wearRemaining; if those fields are absent (backend not
    // yet deployed) fall back to the original added counts so Undo keeps working.
    historyRemain(h) {
      const hasM = !(h.machinesRemaining === undefined || h.machinesRemaining === null);
      const hasW = !(h.wearRemaining === undefined || h.wearRemaining === null);
      return {
        known: hasM || hasW,
        m: hasM ? (Number(h.machinesRemaining) || 0) : (Number(h.machinesAdded) || 0),
        w: hasW ? (Number(h.wearRemaining) || 0) : (Number(h.wearAdded) || 0),
      };
    },
    historyRemaining(h) {
      const r = this.historyRemain(h);
      return r.m + r.w;
    },
    historyRemoved(h) {
      const r = this.historyRemain(h);
      if (!r.known) return false;
      return r.m < (Number(h.machinesAdded) || 0) || r.w < (Number(h.wearAdded) || 0);
    },
    historyRemainingText(h) {
      const r = this.historyRemain(h);
      const parts = [];
      if ((Number(h.machinesAdded) || 0) > 0) parts.push(r.m + " of " + (Number(h.machinesAdded) || 0) + " machines");
      if ((Number(h.wearAdded) || 0) > 0) parts.push(r.w + " of " + (Number(h.wearAdded) || 0) + " wear");
      return parts.length ? parts.join(", ") + " still here" : "";
    },
    undoCopy(entry) {
      if (!entry || entry.undone) return;
      const r = this.historyRemain(entry);
      if (r.m + r.w === 0) return;
      if (!window.confirm(
        "Undo this copy? This removes the " + r.m +
        " machine(s) and " + r.w + " wear part(s) from this copy that are still on " +
        (entry.targetCode || "this company") +
        ". Anything you already deleted manually is left as-is."
      )) {
        return;
      }
      this.undoingId = entry.id;
      apiService
        .undoAccountSetupCopy({ logId: entry.id })
        .then(() => {
          this.undoingId = "";
          this.loadHistory();
          this.fetchSetup();
        })
        .catch(() => {
          this.undoingId = "";
          window.alert("Couldn't undo that copy — please try again.");
        });
    },
    // Redo is pointless once a newer, still-active copy of the same source -> target
    // exists (its rows are already in the account) — so the button is disabled then.
    redoBlocked(h) {
      if (!h) return false;
      // Only the MOST RECENT copy of a given source -> target can be redone; older
      // duplicates are superseded (a redo would re-add nothing). So block redo only
      // when a NEWER same source -> target entry exists (active OR undone). When the
      // newest one is undone, it stays redo-able — incl. right after you undo it.
      return (this.history || []).some(
        (e) =>
          e.id !== h.id &&
          e.sourceCompanyId === h.sourceCompanyId &&
          e.targetCompanyId === h.targetCompanyId &&
          String(e.whenUtc || "") > String(h.whenUtc || "")
      );
    },
    // Re-apply an undone copy (re-clone source -> target, skipping existing links).
    redoCopy(entry) {
      if (!entry || !entry.undone || this.redoBlocked(entry)) return;
      if (!window.confirm(
        "Redo this copy? This re-adds the machines + wear parts from " +
        (entry.sourceCode || "the source company") + " to " +
        (entry.targetCode || "this company") + " (links it already has are skipped)."
      )) {
        return;
      }
      this.undoingId = entry.id;
      apiService
        .redoAccountSetupCopy({ logId: entry.id })
        .then(() => {
          this.undoingId = "";
          this.loadHistory();
          this.fetchSetup();
        })
        .catch(() => {
          this.undoingId = "";
          window.alert("Couldn't redo that copy — please try again.");
        });
    },
    formatWhen(iso) {
      if (!iso) return "";
      try {
        const d = new Date(iso);
        return (
          d.toLocaleDateString() +
          " " +
          d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
        );
      } catch (e) {
        return "";
      }
    },

    // ----- Inline add / remove (Machines, Wear, Users) -----
    addNoun(cat) {
      if (cat.editType === "machine") return "machine";
      if (cat.editType === "wear") return "wear part";
      if (cat.editType === "user") return "user";
      return "item";
    },
    onAddInput(cat, ev) {
      this.$set(this.addTerm, cat.key, ev.target.value);
      this.searchAddable(cat);
    },
    searchAddable(cat) {
      const key = cat.key;
      const term = (this.addTerm[key] || "").trim();
      clearTimeout(this._addTimer);
      // Backing out of the search (empty box) clears the results instead of
      // re-showing the default candidate list.
      if (!term) {
        this.$set(this.addResults, key, []);
        this.$set(this.addBusy, key, false);
        return;
      }
      this._addTimer = setTimeout(() => {
        this.$set(this.addBusy, key, true);
        apiService
          .getSetupAddable(localStorage.companyId, cat.editType, term)
          .then((rows) => {
            this.$set(this.addResults, key, rows || []);
            this.$set(this.addBusy, key, false);
          })
          .catch(() => {
            this.$set(this.addResults, key, []);
            this.$set(this.addBusy, key, false);
          });
      }, 250);
    },
    addLink(cat, r) {
      apiService
        .addSetupLink({
          companyId: Number(localStorage.companyId) || 0,
          type: cat.editType,
          refId: r.refId || 0,
          userId: r.refUser || "",
        })
        .then(() => {
          this.$set(this.addTerm, cat.key, "");
          this.$set(this.addResults, cat.key, []);
          this.fetchSetup();
          this.loadDeleteHistory(cat.key);
        })
        .catch(() => {
          window.alert("Couldn't add that — please try again.");
        });
    },
    canRemove(it) {
      if (!it || typeof it === "string") return false;
      return !!(it.linkId || it.ref);
    },
    rowKey(cat, it) {
      return cat.key + ":" + ((it && (it.linkId || it.ref || it.id)) || "");
    },
    removeLink(cat, it) {
      if (!it || typeof it === "string") return;
      const label = it.label || it.code || "this item";
      if (!window.confirm("Remove " + label + " from this company?")) return;
      this.removingKey = this.rowKey(cat, it);
      apiService
        .removeSetupLink({
          companyId: Number(localStorage.companyId) || 0,
          type: cat.editType,
          linkId: it.linkId || 0,
          userId: it.ref || "",
        })
        .then(() => {
          this.removingKey = "";
          this.fetchSetup();
        })
        .catch(() => {
          this.removingKey = "";
          window.alert("Couldn't remove that — please try again.");
        });
    },

    // ----- Setup-gap "Fix now" diagnosis (read-only) -----
    diagnoseGap(m) {
      const id = m && m.id;
      if (!id) return;
      // Toggle closed if already loaded.
      if (this.gapData[id]) {
        this.$delete(this.gapData, id);
        return;
      }
      this.gapBusy = id;
      apiService
        .getSetupGapDiagnosis(id)
        .then((data) => {
          this.$set(this.gapData, id, data || {});
          this.gapBusy = "";
        })
        .catch(() => {
          this.gapBusy = "";
          window.alert("Couldn't analyze that gap — please try again.");
        });
    },
    goToAdmin(machineId) {
      this.showModal = false;
      this.$router.push("/admin/machines/machinedetails/" + machineId).catch(() => {});
    },
    // ----- Setup gap Hide (session) / Looks Good (persisted, restorable) -----
    bmKey(m) {
      return "machine-noasm-" + (m && m.id);
    },
    isAcked(key) {
      return this.acknowledgedKeys.indexOf(key) !== -1;
    },
    isHidden(key) {
      return this.hiddenGapKeys.indexOf(key) !== -1;
    },
    // Is this row part of an ACTIVE setup gap (not acknowledged "Looks Good" / not hidden)?
    // Drives the ⚠ marker to the left of the row.
    isGapRow(cat, it) {
      if (!cat || !it) return false;
      const active = (k) => !this.isAcked(k) && !this.isHidden(k);
      if (cat.key === "machines") {
        const id = this.itemId(it);
        if (id && (this.bareMachines || []).some((m) => Number(m.id) === Number(id)) && active("machine-noasm-" + id)) return true;
        if (active("machine-dup")) {
          const code = this.itemCode(it);
          if (code && (cat.items || []).filter((x) => this.itemCode(x) === code).length > 1) return true;
        }
        return false;
      }
      if (cat.key === "assemblies" || cat.key === "subAssemblies") {
        if (!active("asm-unnamed")) return false;
        const name = (this.itemLabel(it) || "").trim().toLowerCase();
        return !name || name === "none" || name === "n/a";
      }
      if (cat.key === "users") {
        return active("users-domain") && !!this.oddballDomainRefs[it.ref];
      }
      return false;
    },
    hideGap(key) {
      if (key && this.hiddenGapKeys.indexOf(key) === -1) this.hiddenGapKeys.push(key);
    },
    looksGood(key) {
      this._setGapAck(key, true);
    },
    restoreGap(key) {
      this._setGapAck(key, false);
    },
    _setGapAck(key, on) {
      const cid = Number(localStorage.companyId) || 0;
      if (!key || !cid || this.gapAckBusy) return;
      this.gapAckBusy = key;
      apiService
        .acknowledgeSetupGap({ companyId: cid, key: key, acknowledged: on })
        .then(() => {
          this.gapAckBusy = "";
          if (!this.setup) return;
          let arr = (this.setup.acknowledgedGaps || []).slice();
          if (on) { if (arr.indexOf(key) === -1) arr.push(key); }
          else { arr = arr.filter((k) => k !== key); }
          this.$set(this.setup, "acknowledgedGaps", arr);
        })
        .catch(() => {
          this.gapAckBusy = "";
          window.alert("Couldn't update that setup gap — please try again.");
        });
    },

    // ----- Parts: search -> Link / Unlink the part's machine -----
    onPartInput(cat, ev) {
      this.$set(this.partTerm, cat.key, ev.target.value);
      this.searchParts(cat);
    },
    searchParts(cat) {
      const key = cat.key;
      const term = (this.partTerm[key] || "").trim();
      clearTimeout(this._partTimer);
      if (!term) {
        this.$set(this.partResults, key, []);
        return;
      }
      this._partTimer = setTimeout(() => {
        this.$set(this.partBusy, key, true);
        // Parts search the part catalog; Assemblies / Sub-Assemblies search the
        // assembly tree. Both resolve the MACHINE and return the same shape.
        var req = key === "parts"
          ? apiService.getSetupPartSearch(localStorage.companyId, term)
          : apiService.getSetupAssemblySearch(localStorage.companyId, term, key === "subAssemblies");
        req
          .then((rows) => {
            this.$set(this.partResults, key, rows || []);
            this.$set(this.partBusy, key, false);
          })
          .catch(() => {
            this.$set(this.partResults, key, []);
            this.$set(this.partBusy, key, false);
          });
      }, 250);
    },
    linkPartMachine(cat, r) {
      if (!r || !r.machineId) return;
      const noun = cat.key === "parts" ? "part" : "assembly";
      const machineLabel = (r.machineCode ? r.machineCode + " — " : "") + (r.machineLabel || "");
      if (!window.confirm(
        "Link machine " + machineLabel + " to this company?\n\n" +
        "This makes the WHOLE machine — and every assembly / part under it — available to " +
        "the customer (not just this one " + noun + "), so they can order it.\n\n" +
        "It's recorded in " + cat.label + " history, where you can Unlink it to undo (which " +
        "removes the whole machine and everything under it from this customer)."
      )) {
        return;
      }
      this.partBusyKey = "p" + r.partId;
      apiService
        .addSetupLink({
          companyId: Number(localStorage.companyId) || 0,
          type: "machine",
          refId: r.machineId,
          userId: "",
          category: cat.key,
        })
        .then(() => {
          this.partBusyKey = "";
          this.searchParts(cat);
          this.fetchSetup();
          this.loadDeleteHistory(cat.key);
        })
        .catch(() => {
          this.partBusyKey = "";
          window.alert("Couldn't link that machine — please try again.");
        });
    },
    unlinkPartMachine(cat, r) {
      if (!r || !r.linkId) return;
      const noun = cat.key === "parts" ? "part" : "assembly";
      if (!window.confirm(
        "Unlink machine " +
        ((r.machineCode ? r.machineCode + " — " : "") + (r.machineLabel || "")) +
        " from this company?\n\nThis removes the WHOLE machine and every assembly/part under it from the customer — not just this one " + noun + ".\n\n" +
        "It's recorded in " + cat.label + " history, where you can undo it (re-links the machine and restores its wear)."
      )) {
        return;
      }
      this.partBusyKey = "p" + r.partId;
      apiService
        .removeSetupLink({
          companyId: Number(localStorage.companyId) || 0,
          type: "machine",
          linkId: r.linkId,
          userId: "",
          category: cat.key,
        })
        .then(() => {
          this.partBusyKey = "";
          this.searchParts(cat);
          this.fetchSetup();
          this.loadDeleteHistory(cat.key);
        })
        .catch(() => {
          this.partBusyKey = "";
          window.alert("Couldn't unlink that machine — please try again.");
        });
    },
    // Per-row "delete" on an Assembly / Sub-Assembly = unlink the machine it sits
    // under (the per-company lever; there is no per-company assembly link).
    // Per-row "delete" on an Assembly / Sub-Assembly = DEACTIVATE it for THIS
    // account only (hidden from this customer's ordering; never global). If it's
    // the last active assembly under the machine, the server auto-unlinks the
    // machine too. Reversible (Reactivate) from history.
    deactivateAssemblyRow(cat, it) {
      if (!it || !it.id) return;
      const isSub = cat.key === "subAssemblies";
      const machine = it.machine || "its machine";
      let msg;
      if (isSub) {
        msg = "Deactivate sub-assembly \"" + (it.label || "") + "\" for THIS account?\n\n" +
          "It — and anything under it — is hidden from this customer's ordering, search and quotes. " +
          "The machine " + machine + " stays linked (a machine unlinks only when ALL its top-level " +
          "assemblies are deactivated).\n\n" +
          "Other customers are NOT affected. Undo (Reactivate) from " + cat.label + " history.";
      } else {
        // Active top assemblies under the SAME machine (share the machine link id).
        const siblings = (cat.items || []).filter((x) => x && x.linkId === it.linkId);
        const others = siblings.filter((x) => x.id !== it.id).map((x) => x.label).filter(Boolean);
        if (siblings.length <= 1) {
          msg = "Deactivate assembly \"" + (it.label || "") + "\" for THIS account?\n\n" +
            "⚠ This is the ONLY active assembly under machine " + machine + " — deactivating it will " +
            "UNLINK the whole machine from this account (its wear goes too; all restored on Reactivate).\n\n" +
            "Hidden from this customer's ordering; other customers are NOT affected.";
        } else {
          const shown = others.slice(0, 12).map((n) => "  • " + n).join("\n");
          const more = others.length > 12 ? "\n  …and " + (others.length - 12) + " more" : "";
          msg = "Deactivate assembly \"" + (it.label || "") + "\" for THIS account?\n\n" +
            "This is 1 of " + siblings.length + " active assemblies under machine " + machine + ". " +
            "Deactivating it leaves " + (siblings.length - 1) + " active — the machine stays linked " +
            "until ALL are deactivated.\n\nStill active under this machine:\n\n" + shown + more + "\n\n" +
            "Hidden from this customer's ordering; other customers are NOT affected. Undo from " + cat.label + " history.";
        }
      }
      if (!window.confirm(msg)) {
        return;
      }
      this.removingKey = "asmrow:" + it.id;
      apiService
        .setupAssemblyDeactivate({
          companyId: Number(localStorage.companyId) || 0,
          assemblyId: it.id,
          category: cat.key,
        })
        .then(() => {
          this.removingKey = "";
          this.fetchSetup();
          this.loadDeleteHistory(cat.key);
        })
        .catch(() => {
          this.removingKey = "";
          window.alert("Couldn't deactivate that — please try again.");
        });
    },

    // ----- Parts / Wear delete (deactivate) + inline undo + history -----
    delEntityId(cat, it) {
      if (!it || typeof it === "string") return 0;
      return cat.delType === "wear" ? (it.linkId || 0) : (it.id || 0);
    },
    delRowKey(cat, it) {
      return cat.delType + ":" + this.delEntityId(cat, it);
    },
    isDeleted(cat, it) {
      return !!this.deletedRows[this.delRowKey(cat, it)];
    },
    deleteRow(cat, it) {
      const id = this.delEntityId(cat, it);
      if (!id) return;
      const label = (it.code ? it.code + " — " : "") + (it.label || "this part");
      if (cat.delType === "wear") {
        const m = this.itemMachine(it) ? " (machine " + this.itemMachine(it) + ")" : "";
        if (!window.confirm(
          "Remove wear part " + label + m + " from THIS company only?\n\n" +
          "Other customers are unaffected. You can undo it."
        )) {
          return;
        }
        this.performDelete(cat, it, id);
        return;
      }
      // Part = global: load impact, then confirm with affected machines + customers.
      const pid = this.itemId(it);
      this.delBusyKey = this.delRowKey(cat, it);
      this.ensureImpact(pid)
        .then((imp) => {
          this.delBusyKey = "";
          imp = imp || {};
          const machines = (imp.machines || [])
            .map((m) => m.code || m.label).join(", ") || "—";
          const comps = imp.companies || [];
          const compList = comps.slice(0, 15).map((c) => c.code).join(", ") +
            (comps.length > 15 ? ", …" : "");
          const msg = "Deactivate part " + label + "?\n\n" +
            "⚠ GLOBAL — this hides the part from EVERY customer who can reach it.\n\n" +
            "Machine(s): " + machines + "\n" +
            "Customers affected (" + comps.length + "): " + (compList || "—") + "\n\n" +
            "You can undo it.";
          if (!window.confirm(msg)) return;
          this.performDelete(cat, it, id);
        })
        .catch(() => {
          this.delBusyKey = "";
          window.alert("Couldn't read the part's impact — please try again.");
        });
    },
    performDelete(cat, it, id) {
      const key = this.delRowKey(cat, it);
      this.delBusyKey = key;
      apiService
        .setupDeactivate({
          type: cat.delType,
          id: id,
          companyId: Number(localStorage.companyId) || 0,
        })
        .then((res) => {
          this.$set(this.deletedRows, key, (res && res.logId) || "");
          this.delBusyKey = "";
          this.loadDeleteHistory(cat.key);
        })
        .catch(() => {
          this.delBusyKey = "";
          window.alert("Couldn't delete that — please try again.");
        });
    },
    ensureImpact(partId) {
      if (this.impact[partId]) return Promise.resolve(this.impact[partId]);
      return apiService.getSetupPartImpact(partId).then((data) => {
        this.$set(this.impact, partId, data || {});
        return data || {};
      });
    },
    toggleImpact(cat, it) {
      const key = this.delRowKey(cat, it);
      const open = !this.impactOpen[key];
      this.$set(this.impactOpen, key, open);
      const pid = this.itemId(it);
      if (open && pid && !this.impact[pid]) {
        this.impactBusy = pid;
        this.ensureImpact(pid)
          .then(() => { this.impactBusy = 0; })
          .catch(() => { this.impactBusy = 0; });
      }
    },
    impactCount(it, kind) {
      const d = this.impact[this.itemId(it)] || {};
      return ((kind === "m" ? d.machines : d.companies) || []).length;
    },
    impactMachines(it) {
      const d = this.impact[this.itemId(it)] || {};
      const list = (d.machines || []).map((m) => (m.code ? m.code + " — " : "") + (m.label || ""));
      return list.length ? list.join(";  ") : "—";
    },
    impactCompanies(it) {
      const d = this.impact[this.itemId(it)] || {};
      const list = (d.companies || []).map((c) => c.code || c.name);
      return list.length ? list.join(", ") : "—";
    },
    // Reveal the full company list in a row's expand (collapsed by default
    // when long). Keyed by the row's delRowKey.
    expandImpactCompanies(cat, it) {
      this.$set(this.impactCustOpen, this.delRowKey(cat, it), true);
    },
    undoDelete(cat, it) {
      const key = this.delRowKey(cat, it);
      const logId = this.deletedRows[key];
      if (!logId) return;
      this.delBusyKey = key;
      apiService
        .setupReactivate(logId)
        .then(() => {
          this.$delete(this.deletedRows, key);
          this.delBusyKey = "";
          this.loadDeleteHistory(cat.key);
        })
        .catch(() => {
          this.delBusyKey = "";
          window.alert("Couldn't restore that — please try again.");
        });
    },
    loadDeleteHistory(catKey) {
      apiService
        .getSetupDeleteHistory(localStorage.companyId || "", catKey)
        .then((data) => {
          this.$set(this.delHistory, catKey, Array.isArray(data) ? data : []);
        })
        .catch(() => {
          this.$set(this.delHistory, catKey, []);
        });
    },
    toggleDelHist(type) {
      this.$set(this.delHistOpen, type, !this.delHistOpen[type]);
    },
    undoFromHistory(e) {
      if (!e || e.undone) return;
      this.delBusyKey = "h" + e.id;
      apiService
        .setupReactivate(e.id)
        .then(() => {
          this.delBusyKey = "";
          const key = e.type + ":" + e.entityId;
          if (this.deletedRows[key]) this.$delete(this.deletedRows, key);
          this.loadDeleteHistory(this.histCatKey(e));
          this.fetchSetup();   // adds get removed / deletes get restored — refresh the listing
        })
        .catch(() => {
          this.delBusyKey = "";
          window.alert("Couldn't undo that — please try again.");
        });
    },
    // Permanently delete the user account from a deactivate-user history entry.
    deleteUserFromHistory(e) {
      const uid = e && e.userId;
      if (!uid || this.delBusyKey) return;
      const who = (e && (e.label || e.code)) || "this user";
      if (!window.confirm(
        "PERMANENTLY delete " + who + " (" + ((e && e.code) || "") + ")?\n\n" +
        "This removes the account entirely and frees the email to be re-used. It CANNOT be " +
        "undone (unlike Reactivate). Note: orders/quotes tied to the user may block deletion."
      )) {
        return;
      }
      this.delBusyKey = "du" + e.id;
      apiService
        .deleteSetupUser({ userId: uid })
        .then(() => {
          this.delBusyKey = "";
          this.loadDeleteHistory("users");
          this.fetchSetup();
          this.loadEventLog();
        })
        .catch((err) => {
          this.delBusyKey = "";
          const resp = (err && err.response) || (err && err.error && err.error.response);
          const d = resp && resp.data;
          const msg = d ? (typeof d === "string" ? d : (d.message || d.title)) : "";
          window.alert(msg || "Couldn't delete the user — it may be referenced by orders (deactivate instead).");
        });
    },
    // Mirrors the backend CategoryOf(): maps a history entry to its category key.
    histCatKey(e) {
      if (e && e.category) return e.category;
      const t = (e && e.type) || "";
      if (t === "part") return "parts";
      if (t === "add-machine") return "machines";
      if (t === "add-user" || t === "link-user-company" || t === "unlink-user-company" || t === "deactivate-user") return "users";
      return "wearParts";
    },
    isAddEntry(e) {
      return !!e && typeof e.type === "string" &&
        (e.type.indexOf("add-") === 0 || e.type === "link-user-company");
    },
    // A machine LINK event (from the Machines category, or a part-initiated link
    // filed under Parts). Its inverse is "Unlink" rather than a plain "Undo".
    isMachineLinkEntry(e) {
      return !!e && (e.type === "add-machine" || e.type === "add-part-machine");
    },
    histActionTag(e) {
      if (e && e.type === "deactivate-assembly") return "Deactivated";
      if (e && e.type === "remove-machine") return "Unlinked";
      if (e && e.type === "link-user-company") return "Linked";
      if (e && e.type === "unlink-user-company") return "Unlinked";
      if (e && e.type === "deactivate-user") return "Deactivated";
      if (!this.isAddEntry(e)) return "Removed";
      return this.isMachineLinkEntry(e) ? "Linked" : "Added";
    },
    histUndoLabel(e) {
      if (e && e.type === "deactivate-assembly") return "Reactivate";
      if (e && e.type === "remove-machine") return "Re-link";
      if (e && e.type === "link-user-company") return "Unlink";
      if (e && e.type === "unlink-user-company") return "Re-link";
      if (e && e.type === "deactivate-user") return "Reactivate";
      return this.isMachineLinkEntry(e) ? "Unlink" : "Undo";
    },
    histUndoneLabel(e) {
      if (e && e.type === "deactivate-assembly") return "Reactivated";
      if (e && e.type === "remove-machine") return "Re-linked";
      if (e && e.type === "link-user-company") return "Unlinked";
      if (e && e.type === "unlink-user-company") return "Re-linked";
      if (e && e.type === "deactivate-user") return "Reactivated";
      if (!this.isAddEntry(e)) return "Restored";
      return this.isMachineLinkEntry(e) ? "Unlinked" : "Undone";
    },
    // Machine display for a deactivate-assembly history row: prefer the stored
    // value (survives an auto-unlink), else derive it from the Machines list.
    histMachine(e) {
      return (e && e.machine) || this.machineDisplayById(e && e.machineId);
    },
    machineDisplayById(machineId) {
      if (!machineId) return "";
      const mc = this.categories.find((c) => c.key === "machines");
      const items = (mc && mc.items) || [];
      const hit = items.find((it) => Number(this.itemId(it)) === Number(machineId));
      if (!hit) return "";
      const code = this.itemCode(hit);
      return (code ? code + " — " : "") + (this.itemLabel(hit) || "");
    },
    // Whether this category's listing shows a right-arrow "→" flow between its
    // columns (illustrates the join: e.g. Sub-Assembly ID → Assembly ID → Machine
    // Code). Companies have no flow arrow.
    catFlowArrow(cat) {
      const k = cat && cat.key;
      return k === "machines" || k === "assemblies" || k === "subAssemblies" || k === "users";
    },
    // The machine CODE from an assembly item's machine display ("DLM — PUG …" -> "DLM").
    machineCodeOf(it) {
      const m = (it && it.machine) || "";
      const i = m.indexOf(" — ");
      return i > 0 ? m.slice(0, i) : m;
    },
    // Machine code with its machine id appended, e.g. "DLM (78)" — for the
    // Assemblies / Sub-Assemblies "Machine Code / ID" column.
    machineCodeId(it) {
      const code = this.machineCodeOf(it) || "—";
      return it && it.machineId ? code + " (" + it.machineId + ")" : code;
    },
    // "machine: M · N still active here" (or "· unlinked the machine") for a
    // deactivate-assembly history row.
    histMachineInfo(cat, e) {
      if (!e || e.type !== "deactivate-assembly") return "";
      const m = this.histMachine(e);
      if (!m) return "";
      if (e.unlinkedMachine) return "machine: " + m + " · unlinked the machine";
      const n = (cat.items || []).filter((it) => it && it.machine === m).length;
      return "machine: " + m + " · " + n + " still active here";
    },
    catHasHistory(cat) {
      return !!cat && (!!cat.editType || !!cat.delType || !!cat.partLink);
    },
    // Show a "jump to history" link when the rendered list is long AND there's a
    // history section to jump to (Parts / Wear in practice; read-only categories
    // like Assemblies have no history section).
    // Long lists (>10 rendered rows) get top→bottom / bottom→top jump links,
    // independent of whether the category has any history yet.
    showListNav(cat) {
      return this.filteredItems(cat).length > 10;
    },
    scrollToBottom(cat) {
      const el = document.getElementById("asc-bottom-" + cat.key);
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "end" });
    },
    scrollToCatTop(cat) {
      const el = document.getElementById("asc-cat-" + cat.key);
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    // ----- Floating jump control (pins at viewport center while scrolling) -----
    onSetupShown() {
      this._scrollEl = document.querySelector(".account-setup-modal .modal-body");
      if (this._scrollEl) {
        this._scrollEl.addEventListener("scroll", this.onSetupScroll, { passive: true });
      }
      // Flag the body so the modal backdrop goes transparent (CSS) — the
      // page's steel background shows through behind the panel.
      try { document.body.classList.add("account-setup-open"); } catch (e) { /* no-op */ }
      // Pin the panel below the top branding bar and stretch it to the footer.
      this._positionSetupModal();
      window.addEventListener("resize", this._positionSetupModal);
      this.$nextTick(() => { this._positionSetupModal(); this.updateScrollNav(); });
    },
    onSetupHidden() {
      if (this._scrollEl) {
        this._scrollEl.removeEventListener("scroll", this.onSetupScroll);
        this._scrollEl = null;
      }
      try { document.body.classList.remove("account-setup-open"); } catch (e) { /* no-op */ }
      window.removeEventListener("resize", this._positionSetupModal);
      this.scrollNavCat = "";
    },
    // Pin the Account Setup panel below the top branding bar (just above the
    // nav menu, so the logo / phone stay visible) and stretch it down to the
    // footer, so the page content behind is fully covered. Measured here and
    // on resize, fed to the modal-dialog via CSS custom properties.
    _positionSetupModal() {
      try {
        var nav = document.getElementById("main-nav");
        // Anchor the panel just BELOW the nav menu (its BOTTOM edge), not at its
        // top. Using the nav's top let the modal's own title bar slide up UNDER
        // the nav bar and get clipped ("cut off"). +10 leaves a small gap so the
        // title clears the header cleanly.
        var top = nav
          ? Math.round(nav.getBoundingClientRect().bottom) + 10
          : 130;
        top = Math.max(top, 16);
        var vh = window.innerHeight || 800;
        var footer = document.getElementById("footer");
        var footTop = footer ? Math.round(footer.getBoundingClientRect().top) : vh;
        var bottom = Math.min(footTop, vh) - 8;
        var h = Math.max(bottom - top, 320);
        var root = document.documentElement;
        root.style.setProperty("--acct-top", top + "px");
        root.style.setProperty("--acct-height", h + "px");
      } catch (e) { /* no-op */ }
    },
    onSetupScroll() {
      if (this._scrollRaf) return;
      this._scrollRaf = window.requestAnimationFrame(() => {
        this._scrollRaf = 0;
        this.updateScrollNav();
      });
    },
    // Show the floating control only while a long, open category occupies the
    // viewport center — and target THAT category's top / bottom.
    updateScrollNav() {
      const sc = this._scrollEl;
      if (!sc || sc.scrollHeight <= sc.clientHeight + 8) {
        this.scrollNavCat = "";
        return;
      }
      const r = sc.getBoundingClientRect();
      const mid = r.top + r.height / 2;
      let active = "";
      for (const cat of this.categories) {
        if (!this.open[cat.key] || !this.showListNav(cat)) continue;
        const topEl = document.getElementById("asc-cat-" + cat.key);
        const botEl = document.getElementById("asc-bottom-" + cat.key);
        if (!topEl || !botEl) continue;
        const t = topEl.getBoundingClientRect().top;
        const b = botEl.getBoundingClientRect().top;
        if (t <= mid && b >= mid) { active = cat.key; break; }
      }
      this.scrollNavCat = active;
    },
    scrollNavUp() {
      const c = this.categories.find((x) => x.key === this.scrollNavCat);
      if (c) this.scrollToCatTop(c);
    },
    scrollNavDown() {
      const c = this.categories.find((x) => x.key === this.scrollNavCat);
      if (c) this.scrollToBottom(c);
    },
    // Eyeball in the Users list: passwords are hashed and can't be shown, so this
    // emails the help desk (from parts@jcsteele.com) to assist/reset instead.
    requestPasswordHelp(it) {
      const uid = it && it.ref;
      if (!uid || this.pwBusyKey === uid) return;
      const who = it.label || it.code || "this user";
      const viewable = !!(it && it.hasPassword);
      const msg = viewable
        ? "Email " + who + "'s password to helpdesk@jcsteele.com for an admin to view?"
        : who + " has no recoverable password on file yet. Reset it now and email the new "
          + "password to helpdesk@jcsteele.com? Their current password will stop working, but "
          + "it will be viewable from now on.";
      if (!window.confirm(msg)) {
        return;
      }
      this.pwBusyKey = uid;
      apiService
        .requestUserPasswordHelp({ companyId: Number((it && it.companyId) || localStorage.companyId) || 0, userId: uid })
        .then((res) => {
          this.pwBusyKey = "";
          this.$set(this.pwSent, uid, true);
          window.alert((res && res.wasReset)
            ? who + "'s password wasn't on file yet, so a new one was set and emailed to the help desk."
            : who + "'s password was emailed to the help desk.");
        })
        .catch((err) => {
          this.pwBusyKey = "";
          const d = err && err.response && err.response.data;
          window.alert((typeof d === "string" && d) ? d : "Couldn't send the request — please try again.");
        });
    },
    // ----- Users ID column: show 4 chars, copy the full GUID -----
    shortId(ref) {
      if (!ref) return "—";
      const s = String(ref);
      return s.length > 4 ? s.slice(0, 4) + "…" : s;
    },
    copyId(ref) {
      if (!ref) return;
      const text = String(ref);
      const flag = () => {
        this.copiedKey = ref;
        setTimeout(() => { if (this.copiedKey === ref) this.copiedKey = ""; }, 1200);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(flag).catch(() => { this._copyFallback(text); flag(); });
      } else {
        this._copyFallback(text);
        flag();
      }
    },
    _copyFallback(text) {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch (e) { /* clipboard unavailable — ignore */ }
    },
    // ----- Link an existing user to another company -----
    openLinkUser(it) {
      const ref = it && it.ref;
      if (!ref) return;
      if (this.linkUserRef === ref) { this.closeLinkUser(); return; }
      this.linkUserRef = ref;
      this.linkUser = it;
      this.linkTerm = "";
      this.linkMsg = "";
      this.linkErr = false;
      this.linkBusy = false;
      this.linkUserCompanyIds = [];
      this.linkResults = this.filterCompanies("");
      this.ensureCompanies().then(() => {
        if (this.linkUserRef === ref) this.linkResults = this.filterCompanies(this.linkTerm);
      });
      this.refreshLinkUserCompanies();   // mark companies the user is already in
    },
    closeLinkUser() {
      this.linkUserRef = "";
      this.linkUser = {};
      this.linkTerm = "";
      this.linkResults = [];
      this.linkUserCompanyIds = [];
      this.linkMsg = "";
      this.linkErr = false;
    },
    // The set of company ids the open user is already linked to (drives Link/Unlink).
    refreshLinkUserCompanies() {
      const ref = this.linkUserRef;
      if (!ref) return;
      apiService
        .getSetupUserCompanies(ref)
        .then((rows) => {
          if (this.linkUserRef === ref) {
            this.linkUserCompanyIds = (Array.isArray(rows) ? rows : []).map((r) => r.id);
          }
        })
        .catch(() => { /* leave as-is */ });
    },
    isUserLinkedTo(c) {
      return !!c && this.linkUserCompanyIds.indexOf(c.id) !== -1;
    },
    onLinkResultClick(c) {
      if (this.isUserLinkedTo(c)) this.unlinkUserFromCompanyPanel(c);
      else this.linkUserToCompany(c);
    },
    ensureCompanies() {
      if (this.companies && this.companies.length) return Promise.resolve(this.companies);
      return apiService
        .getSetupCompanies()
        .then((d) => { this.companies = d || []; return this.companies; })
        .catch(() => { this.companies = []; return []; });
    },
    onLinkCompanyInput(ev) {
      const t = ev && ev.target ? ev.target.value : "";
      this.linkTerm = t;
      this.linkMsg = "";
      this.linkErr = false;
      this.linkResults = this.filterCompanies(t);
    },
    filterCompanies(term) {
      const t = (term || "").trim().toLowerCase();
      let list = this.companies || [];
      if (t) {
        list = list.filter((c) =>
          String(c.id).indexOf(t) !== -1 ||
          (c.code && c.code.toLowerCase().indexOf(t) !== -1) ||
          (c.name && c.name.toLowerCase().indexOf(t) !== -1));
      }
      return list.slice(0, 25);
    },
    linkUserToCompany(c) {
      if (!c || !this.linkUserRef || this.linkBusy) return;
      const who = this.linkUser.label || this.linkUser.code || "user";
      const label = (c.code ? c.code : c.id) + (c.name ? " — " + c.name : "");
      // The final link action: make clear only sign-in access is granted — none of
      // the account's equipment/setup comes with the user.
      if (!window.confirm(
        "Link " + who + " to " + label + "?\n\n" +
        "This grants the user sign-in access to that company only. It does NOT bring over " +
        "any machines, wear parts, parts, assemblies or sub-assemblies — that company keeps " +
        "its own setup. (To copy equipment, use “Copy setup from another company”.)\n\n" +
        "This is recorded in the User history and can be undone."
      )) {
        return;
      }
      this.linkBusy = true;
      this.linkErr = false;
      this.linkMsg = "Linking…";
      apiService
        .linkUserToCompany({
          userId: this.linkUserRef,
          companyId: c.id,
          viewCompanyId: Number(localStorage.companyId) || 0
        })
        .then((res) => {
          this.linkBusy = false;
          if (res && res.alreadyLinked) {
            this.linkMsg = who + " is already linked to " + label + ".";
          } else {
            this.linkMsg = "✓ Linked " + who + " to " + label + ". See the User history below to undo.";
            this.loadDeleteHistory("users");   // surface the new entry (with Unlink/undo)
          }
          this.refreshLinkUserCompanies();     // flip this company's row to "Unlink"
          // If we linked to the company currently on screen, refresh so the row appears.
          if (Number(c.id) === Number(localStorage.companyId)) this.fetchSetup();
        })
        .catch((err) => {
          this.linkBusy = false;
          this.linkErr = true;
          const d = err && err.response && err.response.data;
          this.linkMsg = (typeof d === "string" && d) ? d : "Couldn't link — please try again.";
        });
    },
    // Unlink from within the link panel (a company the user is already in).
    unlinkUserFromCompanyPanel(c) {
      if (!c || !this.linkUserRef || this.linkBusy) return;
      const who = this.linkUser.label || this.linkUser.code || "user";
      const label = (c.code ? c.code : c.id) + (c.name ? " — " + c.name : "");
      if (!window.confirm(
        "Unlink " + who + " from " + label + "?\n\n" +
        "They'll lose sign-in access to that company. This is recorded in the User history and can be undone."
      )) {
        return;
      }
      this.linkBusy = true;
      this.linkErr = false;
      this.linkMsg = "Unlinking…";
      apiService
        .unlinkUserFromCompany({
          userId: this.linkUserRef,
          companyId: c.id,
          viewCompanyId: Number(localStorage.companyId) || 0
        })
        .then((res) => {
          this.linkBusy = false;
          if (res && res.notLinked) {
            this.linkMsg = who + " isn't linked to " + label + ".";
          } else {
            this.linkMsg = "✓ Unlinked " + who + " from " + label + ".";
            this.loadDeleteHistory("users");
          }
          this.refreshLinkUserCompanies();   // flip the row back to "+ Link"
          if (Number(c.id) === Number(localStorage.companyId)) this.fetchSetup();
        })
        .catch((err) => {
          this.linkBusy = false;
          this.linkErr = true;
          const d = err && err.response && err.response.data;
          this.linkMsg = (typeof d === "string" && d) ? d : "Couldn't unlink — please try again.";
        });
    },
    // The row "Unlink" button — removes the user from the company being managed.
    unlinkUserRow(it) {
      const cid = Number(localStorage.companyId) || 0;
      if (!it || !it.ref || !cid) return;
      const who = it.label || it.code || "this user";
      if (!window.confirm(
        "Unlink " + who + " from this company?\n\n" +
        "They'll lose sign-in access here. This is recorded in the User history and can be undone."
      )) {
        return;
      }
      this.removingKey = "users:" + it.ref;
      apiService
        .unlinkUserFromCompany({ userId: it.ref, companyId: cid, viewCompanyId: cid })
        .then(() => {
          this.removingKey = "";
          this.fetchSetup();
          this.loadDeleteHistory("users");
        })
        .catch((err) => {
          this.removingKey = "";
          const d = err && err.response && err.response.data;
          window.alert((typeof d === "string" && d) ? d : "Couldn't unlink — please try again.");
        });
    },
    // ----- Per-user expand (companies / password / deactivate) -----
    userName(it) {
      const l = (it && it.label) || "";
      const i = l.indexOf(" — ");
      return i > 0 ? l.slice(0, i) : l;
    },
    userTitle(it) {
      const l = (it && it.label) || "";
      const i = l.indexOf(" — ");
      return i > 0 ? l.slice(i + 3) : "";
    },
    toggleUserExpand(it) {
      const ref = it && it.ref;
      if (!ref) return;
      const open = !this.userExpanded[ref];
      this.$set(this.userExpanded, ref, open);
      if (open && !this.userCompanies[ref]) this.loadUserCompanies(ref);
    },
    loadUserCompanies(ref) {
      if (!ref) return;
      this.$set(this.userCompaniesBusy, ref, true);
      apiService
        .getSetupUserCompanies(ref)
        .then((rows) => { this.$set(this.userCompanies, ref, Array.isArray(rows) ? rows : []); })
        .catch(() => { this.$set(this.userCompanies, ref, []); })
        .then(() => { this.$set(this.userCompaniesBusy, ref, false); });
    },
    softDeleteUser(it) {
      const ref = it && it.ref;
      if (!ref || this.removingKey === "udel:" + ref) return;
      const who = this.userName(it) || it.code || "this user";
      if (!window.confirm(
        "Deactivate " + who + "'s account?\n\n" +
        "They will no longer be able to sign in to ANY company. This is recorded in the User " +
        "history and can be reactivated from there."
      )) {
        return;
      }
      this.removingKey = "udel:" + ref;
      apiService
        .deactivateSetupUser({ userId: ref, companyId: 0, viewCompanyId: Number(localStorage.companyId) || 0 })
        .then(() => {
          this.removingKey = "";
          this.$set(this.userExpanded, ref, false);
          this.fetchSetup();
          this.loadDeleteHistory("users");
        })
        .catch((err) => {
          this.removingKey = "";
          const d = err && err.response && err.response.data;
          window.alert((typeof d === "string" && d) ? d : "Couldn't deactivate the user — please try again.");
        });
    },
    // Resend the "set your password" (login) link to an existing user.
    resendSetupLink(it) {
      const uid = it && it.ref;
      if (!uid || this.pwBusyKey) return;
      const who = this.userName(it) || it.code || "this user";
      if (!window.confirm(
        "Resend the set-password link to " + who + " (" + (it.code || "") + ")?\n\n" +
        "They'll get an email with a link (back to THIS site) to set their own password."
      )) {
        return;
      }
      this.pwBusyKey = "link:" + uid;
      apiService
        .resendSetupLink({ userId: uid })
        .then(() => {
          this.pwBusyKey = "";
          this.$set(this.setupLinkSent, uid, true);
          this.loadEventLog();
          window.alert("Set-password link sent to " + (it.code || who) + ".");
        })
        .catch((err) => {
          this.pwBusyKey = "";
          const d = err && err.response && err.response.data;
          window.alert((typeof d === "string" && d) ? d : "Couldn't send the link — please try again.");
        });
    },
    // Users scope toggle: false = this company, true = all companies (viewable set).
    // Short, unambiguous created date for the Users list. Returns "—" when the column is empty
    // (legacy rows predate DateCreated being populated).
    fmtCreated(v) {
      if (!v) return "—";
      const d = new Date(v);
      if (isNaN(d.getTime())) return "—";
      return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    },
    // Mirrors AccountController.IsInternalAccount — matches the DOMAIN label pair "jcsteele.com",
    // so johnh@jcsteele.com.au counts while bob@notjcsteele.com does not.
    isInternalEmail(email) {
      const e = String(email || "").trim().toLowerCase();
      const at = e.lastIndexOf("@");
      if (at < 0 || at === e.length - 1) return false;
      const labels = e.slice(at + 1).split(".");
      for (let i = 0; i + 1 < labels.length; i++) {
        if (labels[i] === "jcsteele" && labels[i + 1] === "com") return true;
      }
      return false;
    },
    // ----- Account Status report -----
    loadAccountStatus() {
      this.asBusy = true;
      apiService
        .getAccountStatus()
        .then((d) => { this.asData = d || null; })
        .catch(() => { this.asData = { available: false }; })
        .then(() => { this.asBusy = false; });
    },
    // Generate Insights. Sends only the report KEY — the backend rebuilds the numbers itself,
    // so the model always reasons over the same server-computed figures shown on the cards and
    // a forged payload cannot reach it.
    generateAccountInsights() {
      this.asInsightBusy = true;
      apiService
        .generateLogInsights("account-status")
        .then((r) => {
          if (r && r.ok && r.insight) {
            this.asInsight = r.insight;
            this.asInsightAt = new Date().toLocaleString();
          } else {
            window.alert((r && r.message) || "Couldn't generate insights.");
          }
        })
        .catch(() => { window.alert("Couldn't generate insights (network)."); })
        .then(() => { this.asInsightBusy = false; });
    },
    // Printable pack. Deliberately a self-contained window rather than reusing the report's
    // print pipeline: that one is built around the card grid and its screenshot step, and this
      // report has neither. Writing plain HTML keeps it dependency-free and predictable.
    presentAccountStatus() {
      var s = (this.asData && this.asData.summary) || {};
      var when = new Date().toLocaleString();
      var esc = function (v) { return String(v == null ? "" : v).replace(/[&<>]/g, function (c) { return ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[c]; }); };
      var cards = this.asCards.map(function (k) {
        return "<div class='c'><div class='n'>" + esc(k.value) + "</div><div class='l'>" + esc(k.label) + "</div>" +
               (k.note ? "<div class='s'>" + esc(k.note) + "</div>" : "") + "</div>";
      }).join("");
      var buckets = this.asBuckets.map(function (b) {
        return "<h2>" + esc(b.label) + "</h2><table><tr><th>Account</th><th>Created</th></tr>" +
          b.rows.map(function (r) { return "<tr><td>" + esc(r.email) + "</td><td>" + esc(String(r.created || "").slice(0, 10)) + "</td></tr>"; }).join("") +
          "</table>";
      }).join("");
      var html = "<!doctype html><html><head><title>Account Status</title><style>" +
        "body{font-family:Segoe UI,Arial,sans-serif;color:#2c3038;margin:28px;}" +
        "h1{font-size:20px;margin:0 0 2px;} .w{color:#7a828c;font-size:12px;margin-bottom:18px;}" +
        ".g{display:flex;flex-wrap:wrap;gap:10px;} .c{border:1px solid #e4e7ea;border-top:3px solid #9e2436;" +
        "border-radius:8px;padding:10px 14px;min-width:150px;} .n{font-size:24px;font-weight:700;}" +
        ".l{font-size:12px;color:#5b6470;} .s{font-size:10.5px;color:#9aa3ad;}" +
        "h2{font-size:14px;margin:22px 0 6px;} table{border-collapse:collapse;width:100%;font-size:12px;}" +
        "th,td{text-align:left;border-bottom:1px solid #eee;padding:3px 6px;} th{color:#7a828c;font-weight:700;}" +
        ".ins{white-space:pre-wrap;font-size:12.5px;line-height:1.5;border-left:3px solid #9e2436;padding:6px 12px;margin-bottom:14px;}" +
        "@media print{.c{break-inside:avoid;} table{break-inside:auto;} .ins{break-inside:avoid;}}" +
        "</style></head><body><h1>Account Status</h1>" +
        // The window is on every page on purpose: a printout without it gets found months
        // later and read as current.
        "<div class='w'>JC Steele Service Portal — generated " + esc(when) + " — " + esc(s.totalAccounts || 0) + " accounts</div>" +
        // The generated insight prints WITH the numbers it was written from — an insight
        // separated from its figures is unreviewable months later.
        (this.asInsight ? "<h2>Insights</h2><div class='ins'>" + esc(this.asInsight) + "</div>" : "") +
        "<div class='g'>" + cards + "</div>" + buckets + "</body></html>";
      var w = window.open("", "_blank");
      if (!w) { window.alert("Allow pop-ups to print the presentation."); return; }
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(function () { try { w.print(); } catch (e) { /* user can print manually */ } }, 250);
    },
    // Chat. Sends the report key + question only; the backend rebuilds the numbers.
    askTheLogs() {
      var q = (this.logAsk || "").trim();
      if (!q || this.logAskBusy) return;
      this.logAskBusy = true;
      apiService
        .askLogs(this.diagType, q)
        .then((r) => {
          if (r && r.ok && r.answer) { this.logAnswer = r.answer; this.logAsk = ""; }
          else window.alert((r && r.message) || "Couldn't answer that.");
        })
        .catch(() => { window.alert("Couldn't answer that (network)."); })
        .then(() => { this.logAskBusy = false; });
    },
    generateUserInsights() {
      this.uiInsightBusy = true;
      apiService
        .generateLogInsights("user-insights")
        .then((r) => {
          if (r && r.ok && r.insight) { this.uiInsight = r.insight; this.uiInsightAt = new Date().toLocaleString(); }
          else window.alert((r && r.message) || "Couldn't generate insights.");
        })
        .catch(() => { window.alert("Couldn't generate insights (network)."); })
        .then(() => { this.uiInsightBusy = false; });
    },
    // Printable pack for the people list. Prints the insight WITH the roster it came from,
    // for the same reason the Account Status pack does.
    presentUserInsights() {
      var esc = function (v) { return String(v == null ? "" : v).replace(/[&<>]/g, function (c) { return ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[c]; }); };
      var self = this;
      var rows = this.filteredActivityUsers.map(function (u) {
        return "<tr><td>" + esc(u.name || "") + "</td><td>" + esc(u.email) + "</td><td>" +
               esc(self.fmtActivityClock(u.lastActivity)) + "</td><td>" +
               esc(u.recentEvents) + "</td><td>" + esc(u.totalEvents) + "</td></tr>";
      }).join("");
      var html = "<!doctype html><html><head><title>User Insights</title><style>" +
        "body{font-family:Segoe UI,Arial,sans-serif;color:#2c3038;margin:28px;}" +
        "h1{font-size:20px;margin:0 0 2px;} .w{color:#7a828c;font-size:12px;margin-bottom:18px;}" +
        ".ins{white-space:pre-wrap;font-size:12.5px;line-height:1.5;border-left:3px solid #9e2436;padding:6px 12px;margin-bottom:14px;}" +
        "table{border-collapse:collapse;width:100%;font-size:12px;}" +
        "th,td{text-align:left;border-bottom:1px solid #eee;padding:3px 6px;} th{color:#7a828c;}" +
        "</style></head><body><h1>User Insights</h1>" +
        "<div class='w'>JC Steele Service Portal — generated " + esc(new Date().toLocaleString()) +
        " — " + esc(this.filteredActivityUsers.length) + " people with recorded activity</div>" +
        (this.uiInsight ? "<h2>Insights</h2><div class='ins'>" + esc(this.uiInsight) + "</div>" : "") +
        "<table><tr><th>Name</th><th>Account</th><th>Last activity</th><th>Recent</th><th>Total</th></tr>" +
        rows + "</table></body></html>";
      var w = window.open("", "_blank");
      if (!w) { window.alert("Allow pop-ups to print the presentation."); return; }
      w.document.write(html); w.document.close(); w.focus();
      setTimeout(function () { try { w.print(); } catch (e) { /* manual print */ } }, 250);
    },
    // ----- Browse User Insights -----
    loadActivityUsers() {
      this.uiBusy = true;
      apiService
        .getActivityUsers(30)
        .then((rows) => { this.uiUsers = Array.isArray(rows) ? rows : []; })
        .catch(() => { this.uiUsers = []; })
        .then(() => { this.uiBusy = false; });
    },
    toggleActivityUser(u) {
      if (!u || !u.userId) return;
      const open = !this.uiExpanded[u.userId];
      this.$set(this.uiExpanded, u.userId, open);
      // Lazy: a click-level trail is far too big to prefetch for every row.
      if (open && !(this.uiTimeline[u.userId] || []).length) this.loadActivityTimeline(u.userId, false);
    },
    // `more` pages further back using the oldest row already held (keyset, not OFFSET —
    // a busy account has tens of thousands of rows).
    loadActivityTimeline(userId, more) {
      if (!userId || this.uiTimelineBusy[userId] || this.uiTimelineMoreBusy[userId]) return;
      const have = this.uiTimeline[userId] || [];
      const last = more && have.length ? have[have.length - 1] : null;
      const before = last ? last.occurredAt : null;
      if (more) this.$set(this.uiTimelineEnd, userId, false);
      // Id ties the cursor down: several events can share one millisecond, and the
      // timestamp alone skipped their siblings on the next page.
      const beforeId = last ? last.id : null;
      // Paging sets its OWN flag. The template hides the whole list on uiTimelineBusy, so
      // reusing it for "Load older" replaced everything already read with "Loading…" and
      // re-mounted the scroller at the top — you lost your place on every page.
      this.$set(more ? this.uiTimelineMoreBusy : this.uiTimelineBusy, userId, true);
      apiService
        .getActivityTimeline(userId, before, 200, beforeId)
        .then((rows) => {
          const list = Array.isArray(rows) ? rows : [];
          // A SHORT page means there is no more. Testing the running total instead left the
          // button on screen forever, handing back zero rows on every further click.
          this.$set(this.uiTimelineEnd, userId, list.length < 200);
          this.$set(this.uiTimeline, userId, more ? have.concat(list) : list);
        })
        .catch(() => { if (!more) this.$set(this.uiTimeline, userId, []); })
        .then(() => { this.$set(more ? this.uiTimelineMoreBusy : this.uiTimelineBusy, userId, false); });
    },
    // Split a timeline into calendar days IN THE VIEWER'S timezone. Grouping on the raw
    // UTC string would split one late-evening session across two headings.
    timelineDays(userId) {
      var self = this;
      var rows = this.uiTimeline[userId] || [];
      var order = [];
      var byKey = {};
      rows.forEach(function (ev) {
        var d = self.utcDate(ev.occurredAt);
        if (!d) return;
        var key = d.getFullYear() + "-" +
                  ("0" + (d.getMonth() + 1)).slice(-2) + "-" +
                  ("0" + d.getDate()).slice(-2);
        if (!byKey[key]) {
          byKey[key] = { key: key, rows: [],
            label: d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) };
          order.push(byKey[key]);
        }
        byKey[key].rows.push(ev);
      });
      return order;   // already newest-first, because the timeline is
    },
    summarizeDay(userId, dayKey) {
      var k = userId + "|" + dayKey;
      if (this.uiDayBusy[k]) return;
      this.$set(this.uiDayBusy, k, true);
      apiService
        .summarizeUserDay(userId, dayKey)
        .then((r) => {
          if (r && r.ok && r.summary) {
            // Say so when the day was capped, rather than letting a partial story read
            // as the whole day.
            this.$set(this.uiDayText, k,
              r.summary + (r.truncated ? "\n\n(Only the first 400 events of this day were read.)" : ""));
          } else {
            this.$set(this.uiDayText, k, (r && r.message) || "Couldn't summarize that day.");
          }
        })
        .catch(() => { this.$set(this.uiDayText, k, "Couldn't summarize that day (network)."); })
        .then(() => { this.$set(this.uiDayBusy, k, false); });
    },
    activityIcon(kind) {
      switch (kind) {
        case "page": return "▤";
        case "search": return "⌕";
        case "action": return "➜";
        case "click": return "•";
        default: return "·";
      }
    },
    // One readable line per event. Falls back through the columns each kind actually
    // fills, so a row never renders as blank.
    describeActivity(ev) {
      if (!ev) return "";
      if (ev.kind === "search") {
        const n = (ev.resultCount === null || ev.resultCount === undefined) ? "" : " — " + ev.resultCount + " result(s)";
        return "Searched “" + (ev.searchTerm || "") + "”" + (ev.searchSource ? " (" + ev.searchSource + ")" : "") + n;
      }
      // The PAGE was always recorded on clicks and actions — it just was never shown, so a row
      // read "Clicked button" with no hint of where. Appended to every non-page kind.
      // Prefer the in-page context; fall back to the route when none was declared.
      var where = ev.uiContext ? "  ·  in " + ev.uiContext
                : (ev.routeName ? "  ·  on " + ev.routeName : "");
      if (ev.kind === "click") return "Clicked " + (ev.actionTarget || ev.actionName || "something") + where;
      if (ev.kind === "action") return (ev.actionName || "Action") + (ev.actionTarget ? " — " + ev.actionTarget : "") + where;
      return (ev.routeName || "Page") + (ev.routePath ? " — " + ev.routePath : "");
    },
    // The API returns UTC with NO zone marker ("2026-08-06T00:13:20.1"), which the browser
    // then reads as LOCAL — a 4-hour lie that also pushed late-evening events onto tomorrow.
    // Everything client-side must go through here. (The wire format is deliberately left
    // alone: the "load more" cursor sends these strings straight back, and the server parses
    // them as unspecified-kind UTC, which is only correct while they carry no Z.)
    utcDate(v) {
      if (!v) return null;
      var s = String(v);
      if (!/(Z|[+-]\d{2}:?\d{2})$/.test(s)) s = s.replace(" ", "T") + "Z";
      var d = new Date(s);
      return isNaN(d.getTime()) ? null : d;
    },
    fmtActivityClock(v) {
      var d = this.utcDate(v);
      if (!d) return "";
      return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    },
    // Relative for the browse list — "who was on the site just now" is the question it answers.
    fmtActivityWhen(v) {
      var d = this.utcDate(v);
      if (!d) return "—";
      // Was reading every timestamp 4h in the FUTURE, so mins went negative and EVERY
      // person showed "just now" — the one thing this column exists to distinguish.
      const mins = Math.floor((Date.now() - d.getTime()) / 60000);
      if (mins < 1) return "just now";
      if (mins < 60) return mins + "m ago";
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return hrs + "h ago";
      const days = Math.floor(hrs / 24);
      if (days < 30) return days + "d ago";
      return d.toLocaleDateString();
    },
    // Ask before writing. The checkbox is reverted immediately so the tick never runs ahead of the
    // decision — it is re-applied only once the overlay is confirmed.
    askApprove(it, ev) {
      const next = !!(ev && ev.target && ev.target.checked);
      if (ev && ev.target) ev.target.checked = !!it.approved;   // visual revert until confirmed
      this.approveConfirm = {
        it: it,
        next: next,
        email: this.itemCode(it) || "",
        // Un-ticking is the riskier direction, so name it plainly rather than reusing one message.
        msg: next
          ? "Are you sure you want to approve this account?"
          : "Are you sure you want to set this account back to pending?"
      };
    },
    approveConfirmGo() {
      const c = this.approveConfirm;
      this.approveConfirm = null;
      if (c && c.it) this.toggleApproved(c.it, c.next);
    },
    // Approve / un-approve from the list. Optimistic so the row responds immediately; on refusal we put
    // the old value back and say why, rather than leaving the tick lying about the saved state.
    toggleApproved(it, next) {
      if (!it || !it.ref) return;
      const prev = !!it.approved;
      this.$set(it, "approved", !!next);
      this.approvingKey = it.ref;
      apiService
        .setUserApproved({ userId: it.ref, approved: !!next })
        .then((res) => {
          if (res && res.ok === false) {
            this.$set(it, "approved", prev);
            window.alert(res.message || "Couldn't save the approval.");
            return;
          }
          if (res && typeof res.approved === "boolean") this.$set(it, "approved", res.approved);
        })
        .catch(() => {
          this.$set(it, "approved", prev);
          window.alert("Couldn't save the approval (network).");
        })
        .then(() => { this.approvingKey = ""; });
    },
    // Accepts the scope name; still tolerates the old boolean in case anything else calls it.
    setUsersScope(scope) {
      const target = scope === true ? "all" : scope === false ? "company" : String(scope || "company");
      this.usersScope = target;
      if (target === "company") { this.viewableUsers = []; return; }
      // "all" AND "pending" both read the global pool — pending filters it down client-side, so a
      // pending account in another company is never missed.
      this.$set(this.open, "users", true);
      this.loadViewableUsers();
    },
    // The cross-company user pool behind the "All companies" and "Pending Approval" scopes.
    // Split out of setUsersScope so fetchSetup can re-run it: the server drops deactivated users
    // (setup-viewable-users filters u.Active), but nothing was re-reading it, so after the ✕
    // deactivate the row stayed on screen and the delete looked like it had failed.
    loadViewableUsers() {
      if (this.usersScope === "company") { this.viewableUsers = []; return; }
      apiService
        .getViewableUsers()
        .then((rows) => { this.viewableUsers = Array.isArray(rows) ? rows : []; })
        .catch(() => { this.viewableUsers = []; });
    },
    // ----- Create a new user account for this company -----
    openNewUser(cat) {
      const term = ((cat && this.addTerm[cat.key]) || "").trim();
      this.newUser = { firstName: "", lastName: "", email: "", password: "" };
      // Prefill from whatever was typed in the add-search.
      if (term.indexOf("@") !== -1) this.newUser.email = term;
      else if (term) this.newUser.firstName = term;
      this.newUserMsg = "";
      this.newUserOpen = true;
    },
    cancelNewUser() {
      this.newUserOpen = false;
      this.newUserBusy = false;
      this.newUserMsg = "";
      this.newUser = { firstName: "", lastName: "", email: "", password: "" };
    },
    createUser() {
      const u = this.newUser;
      if (!u.email || !u.email.trim()) { this.newUserMsg = "Email is required."; return; }
      if (!u.firstName && !u.lastName) { this.newUserMsg = "A first or last name is required."; return; }
      this.newUserBusy = true;
      this.newUserMsg = "";
      apiService
        .createSetupUser({
          companyId: Number(localStorage.companyId) || 0,
          email: u.email.trim(),
          firstName: (u.firstName || "").trim(),
          lastName: (u.lastName || "").trim(),
          password: (u.password || "").trim(),
        })
        .then((res) => {
          this.newUserBusy = false;
          this.newUserOpen = false;
          this.newUser = { firstName: "", lastName: "", email: "", password: "" };
          this.$set(this.addTerm, "users", "");
          this.$set(this.addResults, "users", []);
          this.fetchSetup();
          const emailed = res && res.emailedSetupLink;
          window.alert("Account created" +
            (emailed ? " — a password-setup email was sent to the new user."
                     : " with the temporary password you set."));
        })
        .catch((err) => {
          this.newUserBusy = false;
          // eslint-disable-next-line no-console
          console.error("[AccountSetup] create user failed", (err && err.response) || err);
          const resp = err && err.response;
          let msg = "Couldn't create the account — please try again.";
          if (resp) {
            const d = resp.data;
            if (typeof d === "string" && d.trim()) msg = d;
            else if (d && typeof d === "object") msg = d.title || d.message || d.error || JSON.stringify(d);
            else if (resp.status === 404) msg = "Create endpoint not found (404) — the backend update isn't deployed yet.";
            else if (resp.status) msg = "Request failed (" + resp.status + ").";
          }
          this.newUserMsg = msg;
        });
    },
    // Remove ONE history entry (audit-only — does NOT change the setup).
    deleteHistEntry(cat, e) {
      if (!e || !e.id) return;
      if (!window.confirm(
        "Remove this entry from the " + cat.label + " history?\n\n" +
        "This only clears the audit record — it does NOT change your setup."
      )) {
        return;
      }
      this.delBusyKey = "hd" + e.id;
      apiService
        .setupHistoryDelete(e.id)
        .then(() => {
          this.delBusyKey = "";
          this.loadDeleteHistory(cat.key);
        })
        .catch(() => {
          this.delBusyKey = "";
          window.alert("Couldn't remove that entry — please try again.");
        });
    },
    // Clear a whole category's history (audit-only — does NOT change the setup).
    clearCatHistory(cat) {
      const n = (this.delHistory[cat.key] || []).length;
      if (!n) return;
      if (!window.confirm(
        "Delete all " + n + " " + cat.label + " history entr" + (n === 1 ? "y" : "ies") + "?\n\n" +
        "This only clears the audit log — it does NOT change your setup."
      )) {
        return;
      }
      apiService
        .setupHistoryClear(localStorage.companyId || "", cat.key)
        .then(() => {
          this.loadDeleteHistory(cat.key);
        })
        .catch(() => {
          window.alert("Couldn't clear the history — please try again.");
        });
    },

    // ----- Wear bulk "Delete all" (per-company; other customers untouched) -----
    isBulkEntry(e) {
      return !!e && (e.type === "wear-all" || e.type === "wear-all-permanent");
    },
    // History "route" label: bulk entries carry their summary in label (no code /
    // partId), so show that; single deletes keep the code / "Part N" form.
    histRoute(e) {
      if (this.isBulkEntry(e)) return e.label || "All wear parts";
      // Assemblies have a name but no code — show the name.
      if (e && e.type === "deactivate-assembly") return e.label || ("Assembly " + e.entityId);
      // User↔company link/unlink: show "User (email · id)  →  Company".
      if (e && (e.type === "link-user-company" || e.type === "unlink-user-company")) {
        var who = e.label || "User";
        var uid = e.userId || e.entityId || "";
        var email = e.email || this.histEmailFor(uid);
        var detail = [];
        if (email) detail.push(email);
        if (uid) detail.push(uid);
        if (detail.length) who += " (" + detail.join(" · ") + ")";
        return who + "  →  " + (e.code || "company");
      }
      return e.code || e.label || ("Part " + e.partId);
    },
    // Email for a user id, looked up in the loaded users list — enriches the
    // user link/unlink history with the affected user's email when the log
    // entry doesn't carry it directly (e.email). Defensive: returns "" if the
    // user isn't in the current list (e.g. already unlinked).
    histEmailFor(uid) {
      if (!uid) return "";
      try {
        var u = (this.setup && this.setup.users) || {};
        var items = u.items || u.list || [];
        for (var i = 0; i < items.length; i++) {
          var it = items[i];
          if (it && (String(it.ref) === String(uid) || String(it.id) === String(uid))) {
            return this.itemCode(it) || it.email || "";
          }
        }
      } catch (err) { /* no-op */ }
      return "";
    },
    openWearDeleteAll(cat) {
      this.wearDeleteConfirm = cat.key;
    },
    cancelWearDeleteAll() {
      if (this.wearDeleteBusy) return;
      this.wearDeleteConfirm = "";
    },
    confirmWearDeleteAll(permanent) {
      var companyId = Number(localStorage.companyId) || 0;
      if (!companyId) {
        window.alert("No company selected.");
        return;
      }
      if (permanent) {
        var n = (this.setup && this.setup.wearParts && this.setup.wearParts.count) || 0;
        if (!window.confirm(
          "Permanently delete all " + n + " wear parts for this company? " +
          "This CANNOT be undone."
        )) {
          return;
        }
      }
      this.wearDeleteBusy = true;
      var self = this;
      apiService
        .setupWearDeleteAll({ companyId: companyId, permanent: !!permanent })
        .then(function () {
          self.wearDeleteBusy = false;
          self.wearDeleteConfirm = "";
          self.fetchSetup();
          self.loadDeleteHistory("wearParts");
        })
        .catch(function () {
          self.wearDeleteBusy = false;
          window.alert("Couldn't delete the wear parts — please try again.");
        });
    },
  },
};
</script>

<style>
/* ============================================================
   COLLAPSED PILL — UNSCOPED (portaled into App.vue's rail).
   Namespaced .account-setup-pill*. Mirrors the Info / Guide pills.
   ============================================================ */
.account-setup-pill {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  background: #fff;
  color: #c0392b;
  border: 1px solid #ececec;
  border-left: 3px solid #c0392b;
  border-radius: 0 8px 8px 0;
  box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.15);
  padding: 10px 8px;
  min-width: 56px;
  width: 56px;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s ease, transform 0.18s ease;
}
.account-setup-pill:hover {
  background: #fafafa;
  transform: translateX(-2px);
}
.account-setup-pill:focus-visible {
  outline: 2px solid #c0392b;
  outline-offset: 2px;
}
.account-setup-pill-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}
.account-setup-pill-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #c0392b;
  line-height: 1;
  white-space: nowrap;
}

/* ============================================================
   MODAL — UNSCOPED (b-modal renders to a body portal).
   Namespaced under .account-setup-modal (the modal-class).
   ============================================================ */
.account-setup-modal .modal-dialog {
  /* Wide, but kept INSIDE the rails: the content sheet is rail-to-rail
     (--steele-rail-half-width * 2); subtract a margin so the panel never
     reaches/overflows the rail line. Tracks the rails at every breakpoint. */
  max-width: calc(var(--steele-rail-half-width, 484px) * 2 - 48px);
  /* Pin below the top branding bar (just above the nav menu) and stretch down
     toward the footer so the branding shows above and the panel covers the
     page content behind it. --acct-top / --acct-height are measured in
     onSetupShown (fallbacks here for first paint). */
  margin-top: var(--acct-top, 96px) !important;
  margin-bottom: 8px !important;
  height: var(--acct-height, calc(100vh - 112px)) !important;
}
.account-setup-modal .modal-content {
  height: 100%;
  max-height: 100%;
}

/* When Account Setup is open, make the backdrop fully TRANSPARENT (not the
   default heavy black dim) so the page's steel-rail background shows at full
   strength behind the panel — same in light and dark mode. The backdrop div
   still intercepts clicks (no-close-on-backdrop), so the page behind stays
   non-interactive; it just isn't darkened. The dark steel reads dark on its
   own, so any dim on top made it look "too dark". */
body.account-setup-open .modal-backdrop {
  background-color: transparent !important;
  opacity: 1 !important;
}
/* While the Account Setup panel is open, hide the header search so it
   doesn't show through above the modal. The search lives inside
   .stickyHeader (z-index 9200), which sits above the modal (z-index 9110),
   so it would otherwise float over the panel. visibility keeps layout
   stable (no header reflow); it returns when the panel closes. */
body.account-setup-open #header-search {
  visibility: hidden !important;
}
.account-setup-modal-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #2a2a2a;
}
.account-setup-modal-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #c0392b;
  color: #fff;
  font-size: 12px;
  line-height: 1;
}
.account-setup-body {
  padding: 0.1rem 0.15rem;
}
/* Expanded body for the Utility Actions + Event Logs categories. */
.account-setup-catbody {
  padding: 4px 4px 10px 8px;
}
.account-setup-eventsdetail {
  padding-top: 6px;
}
/* Copy-setup header: title on the left, "Copy history (n)" link on the right. */
.account-setup-copy-headleft {
  flex: 1 1 auto;
  min-width: 0;
}
.account-setup-copy-histlink {
  flex: 0 0 auto;
  border: none;
  background: transparent;
  color: #c0392b;
  font-weight: 700;
  font-size: 12px;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;
  text-decoration: underline;
}
.account-setup-copy-histlink.is-open {
  text-decoration: none;
}
.account-setup-copy-hist {
  margin-top: 8px;
  border-top: 1px dashed #e6c9c4;
  padding-top: 8px;
}
.account-setup-events-refresh {
  border: 1px solid #d6dade;
  background: #fff;
  border-radius: 5px;
  cursor: pointer;
  font-size: 13px;
  line-height: 1;
  padding: 3px 7px;
  color: #5b6470;
}
.account-setup-events-refresh:hover:not(:disabled) {
  border-color: #c0392b;
  color: #c0392b;
}
.account-setup-events-sub {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 10.5px;
  color: #95a0ab;
  margin: 2px 0 8px 0;
}
.account-setup-debug-toggle {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin: 0 0 10px 0;
  padding: 7px 9px;
  background: #1c2530;
  border: 1px solid #2c3a47;
  border-radius: 6px;
  cursor: pointer;
  font-size: 11.5px;
  color: #d4dde6;
  line-height: 1.35;
}
.account-setup-debug-toggle input {
  margin: 1px 0 0 0;
  flex: 0 0 auto;
  cursor: pointer;
}
.account-setup-debug-toggle small {
  color: #8a96a3;
}
.account-setup-events-diag {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
}
.account-setup-events-diagtype {
  flex: 1 1 100%;
  border: 1px solid #d6dade;
  border-radius: 5px;
  padding: 6px 8px;
  font-size: 12px;
  background: #fff;
  color: #2c3038;
}
.account-setup-events-diagtype:focus {
  outline: none;
  border-color: #c0392b;
}
.account-setup-events-diaginput {
  flex: 1 1 auto;
  min-width: 0;
  border: 1px solid #d6dade;
  border-radius: 5px;
  padding: 6px 8px;
  font-size: 12px;
}
.account-setup-events-diaginput:focus {
  outline: none;
  border-color: #c0392b;
}
.account-setup-events-diagbtn {
  flex: 0 0 auto;
  border: 1px solid #2c6fb3;
  background: #2c6fb3;
  color: #fff;
  font-weight: 700;
  font-size: 12px;
  border-radius: 5px;
  padding: 6px 12px;
  cursor: pointer;
}
.account-setup-events-diagbtn:disabled {
  opacity: 0.5;
  cursor: default;
}
.account-setup-events-empty {
  font-size: 12px;
  color: #95a0ab;
  padding: 8px 2px;
}
.account-setup-events-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.account-setup-events-item {
  border-bottom: 1px solid #eef0f2;
}
.account-setup-events-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  padding: 7px 2px;
}
.account-setup-events-row:hover {
  background: #f7f8fa;
}
.account-setup-events-dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 5px;
  background: #9aa3ad;
}
.account-setup-events-dot.is-ok {
  background: #1e7e34;
}
.account-setup-events-dot.is-fail {
  background: #c0392b;
}
.account-setup-events-dot.is-system {
  background: #2c6fb3;
}
.account-setup-events-rowmain {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.account-setup-events-rowtitle {
  font-size: 12.5px;
  font-weight: 600;
  color: #2c3038;
  line-height: 1.3;
}
.account-setup-events-item.is-open .account-setup-events-rowtitle {
  color: #c0392b;
}
.account-setup-events-meta {
  font-size: 10.5px;
  color: #95a0ab;
  margin-top: 1px;
}
.account-setup-events-caret {
  flex: 0 0 auto;
  color: #b6bdc4;
  font-size: 11px;
  margin-top: 3px;
}
.account-setup-events-detail {
  padding: 2px 2px 9px 18px;
}
.account-setup-events-line {
  font-size: 12px;
  color: #4a5159;
  margin: 0 0 6px 0;
  line-height: 1.4;
}
.account-setup-events-exc {
  background: #fdf3f2;
  border: 1px solid #f3c9c4;
  border-radius: 5px;
  padding: 6px 8px;
  margin-bottom: 8px;
}
.account-setup-events-exc-label {
  display: block;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  color: #c0392b;
  margin-bottom: 2px;
}
.account-setup-events-exc code {
  font-size: 11px;
  color: #8a2a20;
  word-break: break-word;
  white-space: pre-wrap;
}
.account-setup-events-sugg {
  margin-bottom: 8px;
}
.account-setup-events-sugg-label {
  font-size: 10.5px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #1e7e34;
  margin-bottom: 3px;
}
.account-setup-events-sugg ul {
  margin: 0;
  padding-left: 16px;
}
.account-setup-events-sugg li {
  font-size: 12px;
  color: #3f6b49;
  padding: 1px 0;
  line-height: 1.35;
}
.account-setup-events-data {
  border-top: 1px dashed #e6e8eb;
  padding-top: 6px;
}
.account-setup-events-datarow {
  display: flex;
  gap: 8px;
  font-size: 11px;
  padding: 1px 0;
}
.account-setup-events-datakey {
  flex: 0 0 96px;
  color: #95a0ab;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}
.account-setup-events-dataval {
  flex: 1 1 auto;
  min-width: 0;
  color: #2c3038;
  word-break: break-word;
}
/* In-modal add/edit form view (replaces the setup view). */
.account-setup-editview {
  padding: 2px 2px 10px 2px;
}
.account-setup-back {
  border: none;
  background: transparent;
  color: #c0392b;
  font-weight: 600;
  font-size: 12.5px;
  cursor: pointer;
  padding: 2px 0;
  margin-bottom: 6px;
}
.account-setup-back:hover {
  text-decoration: underline;
}
/* Clickable user/company name that opens the edit form. */
.account-setup-edit-link {
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: #2c3038;
  font: inherit;
  text-align: left;
}
.account-setup-edit-link:hover {
  color: #c0392b;
  text-decoration: underline;
}
.account-setup-edit-pencil {
  font-size: 11px;
  opacity: 0.5;
}
/* "Add new {category}" button at the top of each editable category. */
.account-setup-addnew { padding: 4px 0 8px; list-style: none; }
.account-setup-addnew-btn {
  border: 1px dashed #2e7d32;
  background: #fff;
  color: #2e7d32;
  font-size: 12px;
  font-weight: 700;
  border-radius: 5px;
  padding: 6px 12px;
  cursor: pointer;
}
.account-setup-addnew-btn:hover { background: #2e7d32; color: #fff; }
.account-setup-lede {
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 0.75rem;
}
.account-setup-loading {
  padding: 1.5rem 0.5rem;
  text-align: center;
  color: #777;
  font-style: italic;
}

/* Setup gaps */
.account-setup-warnings {
  background: #fdf3f2;
  border: 1px solid #f3c9c4;
  border-left: 3px solid #c0392b;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 14px;
}
.account-setup-warnings-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #c0392b;
  margin-bottom: 6px;
}
/* Expandable Setup Gaps header (button form of the title). */
.account-setup-gaps-toggle,
.account-setup-gaphist-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  margin: 0;
  text-align: left;
}
.account-setup-gaps-body {
  margin-top: 8px;
}
.account-setup-warnings-list {
  list-style: none;
  margin: 0;
  padding-left: 0;
}
.account-setup-gap-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  font-size: 13px;
  color: #8a2a20;
  padding: 5px 0;
  border-bottom: 1px solid #f3d6d2;
}
.account-setup-gap-item:last-child {
  border-bottom: none;
}
.account-setup-gap-item.is-acked {
  color: #7b8794;
}
.account-setup-gap-text {
  flex: 1 1 auto;
  min-width: 0;
  line-height: 1.35;
}
.account-setup-gap-actions {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}
.account-setup-gap-hide,
.account-setup-gap-ok,
.account-setup-gap-restore {
  border: 1px solid #d6dade;
  background: #fff;
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
  padding: 2px 8px;
  cursor: pointer;
  color: #5b6470;
}
.account-setup-gap-hide:hover {
  border-color: #7b8794;
}
.account-setup-gap-ok {
  border-color: #1e7e34;
  color: #1e7e34;
}
.account-setup-gap-ok:hover:not(:disabled) {
  background: #1e7e34;
  color: #fff;
}
.account-setup-gap-restore {
  border-color: #c0392b;
  color: #c0392b;
}
.account-setup-gap-restore:hover:not(:disabled) {
  background: #c0392b;
  color: #fff;
}
.account-setup-gap-ok:disabled,
.account-setup-gap-restore:disabled {
  opacity: 0.5;
  cursor: default;
}
.account-setup-ok-inline {
  margin-top: 8px;
}
.account-setup-gaphist {
  margin-top: 10px;
  border-top: 1px dashed #e3b4ae;
  padding-top: 8px;
}
.account-setup-gaphist-toggle {
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #7b8794;
}
.account-setup-ok {
  background: #eef8f1;
  border: 1px solid #cde8d6;
  border-radius: 6px;
  padding: 9px 14px;
  margin-bottom: 14px;
  font-size: 13px;
  font-weight: 600;
  color: #1f7a3a;
}

/* Category rows */
.account-setup-list {
  list-style: none;
  margin: 0;
  padding: 0;
  border-top: 1px solid #f0f0f0;
}
.account-setup-item {
  border-bottom: 1px solid #f0f0f0;
}
/* Leading slot that holds the ⚠ marker for rows in an active setup gap. */
.asc-col-gap {
  flex: 0 0 15px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.account-setup-gapwarn {
  font-size: 12px;
  line-height: 1;
  color: #e0a800;
}
.account-setup-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  background: none;
  border: none;
  padding: 11px 6px;
  cursor: pointer;
  text-align: left;
}
.account-setup-row:disabled {
  cursor: default;
}
.account-setup-row-toggle {
  width: 18px;
  flex-shrink: 0;
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  color: #c0392b;
  line-height: 1;
}
.account-setup-row-label {
  flex: 0 0 122px;
  font-size: 14px;
  font-weight: 600;
  color: #2a2a2a;
}
/* Table + keys this category joins on, shown inline on the row. */
.account-setup-row-join {
  flex: 1 1 auto;
  min-width: 0;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 10.5px;
  color: #95a0ab;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
}
.account-setup-row.is-zero .account-setup-row-label {
  color: #999;
  font-weight: 500;
}
.account-setup-row-count {
  flex-shrink: 0;
  min-width: 30px;
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  background: #c0392b;
  border-radius: 11px;
  padding: 2px 9px;
}
.account-setup-row-count.is-zero {
  background: #e2e2e2;
  color: #888;
}
.account-setup-detail {
  list-style: none;
  margin: 0 0 8px 0;
  padding: 4px 6px 4px 34px;
}
.account-setup-detail li {
  font-size: 12.5px;
  color: #555;
  padding: 2px 0;
  border-bottom: 1px dashed #f2f2f2;
}
.account-setup-detail-more {
  font-style: italic;
  color: #999 !important;
}

/* JOIN header + id/code chips inside an expanded category */
.account-setup-join {
  list-style: none;
  font-size: 11px !important;
  color: #7b8794 !important;
  border-bottom: 1px solid #eef0f2 !important;
  padding: 0 0 6px 0 !important;
  margin-bottom: 4px;
  word-break: break-word;
}
.account-setup-join-label {
  display: inline-block;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #fff;
  background: #9aa3ad;
  border-radius: 3px;
  padding: 1px 5px;
  margin-right: 6px;
  vertical-align: middle;
}
.account-setup-detail-row {
  display: flex !important;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

/* Parts / Wear: Part ID · Part Code · Part Description columns + header */
.account-setup-detail .account-setup-thead,
.account-setup-detail .account-setup-trow {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 2px;
}
.account-setup-detail .account-setup-thead {
  border-bottom: 1px solid #e6e6e6;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9aa3ad;
}
.account-setup-detail .account-setup-trow {
  border-bottom: 1px dashed #f2f2f2;
  font-size: 12.5px;
  color: #555;
}
.asc-col-id {
  flex: 0 0 58px;
  font-variant-numeric: tabular-nums;
  color: #5b6470;
}
/* Users carry a GUID id (AspNetUsers) — show the first 4 chars + a copy icon. */
.asc-col-id-user {
  flex: 0 0 92px;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  font-size: 11px;
}
.account-setup-id-short {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-variant-numeric: tabular-nums;
  color: #5b6470;
}
.account-setup-copy {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 12px;
  line-height: 1;
  padding: 0 2px;
  opacity: 0.6;
  color: #5b6470;
}
.account-setup-copy:hover {
  opacity: 1;
  color: #c0392b;
}
/* ===== Users category — collapsed row (ID · Name · Email · Link) + expand ===== */
.asc-col-uexp {
  flex: 0 0 22px;
}
.account-setup-uexp {
  border: 1px solid #d6dade;
  background: #fff;
  color: #5b6470;
  width: 20px;
  height: 20px;
  line-height: 1;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 700;
  padding: 0;
}
.account-setup-uexp:hover {
  border-color: #c0392b;
  color: #c0392b;
}
.account-setup-urow.is-uopen {
  background: #fbfcfd;
}
.asc-col-uname {
  flex: 1 1 38%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
  color: #2c3038;
}
.asc-col-uemail {
  flex: 1 1 42%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #5b6470;
}
.account-setup-uhead .asc-col-uname,
.account-setup-uhead .asc-col-ucreated,
.account-setup-uhead .asc-col-uappr,
.account-setup-uhead .asc-col-uemail {
  font-weight: 700;
  color: inherit;
}
/* Users rows carry more columns than the parts tables (ID · Name · Email · Created · Approve ·
   Manage), so they get their own wider, uniform column gap instead of the shared 8px. One gap
   value for every column keeps the spacing even across the row. Later in the file than the
   .account-setup-detail .account-setup-trow rule it overrides, at equal specificity. */
.account-setup-detail .account-setup-uhead,
.account-setup-detail .account-setup-urow {
  gap: 18px;
}
/* Approve is its own column so it can't crowd the ✕ in Manage. Fixed width — the label swaps
   between "Approve" and the longer "Approved", and the column must not resize when it does. */
.asc-col-uappr {
  flex: 0 0 92px;
  display: flex;
  align-items: center;
}
/* Expanded per-user detail */
.account-setup-udetail {
  padding: 8px 10px 10px 30px;
  background: #f7f9fb;
  border-left: 3px solid #c0392b;
  margin: 0 0 4px 0;
}
.account-setup-udetail-head {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 6px;
}
/* Parent-row action cluster (Unlink · ✕ deactivate · Link) + its header. The floor dropped from
   150px when Approve moved out to its own column — it only has to cover ✕ + Link now, and a
   too-wide floor left dead space between Approve and the buttons. Both values must stay equal so
   the MANAGE header keeps sitting over the buttons. */
.account-setup-uactions {
  flex: 0 0 auto;
  min-width: 110px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}
.account-setup-uactions-head {
  flex: 0 0 auto;
  min-width: 110px;
  text-align: right;
}
.account-setup-udel {
  margin: 0 1px;
}
/* Event Logs & History — search field. */
.account-setup-events-search {
  width: 100%;
  border: 1px solid #d6dade;
  border-radius: 5px;
  padding: 6px 9px;
  font-size: 12px;
  margin-bottom: 8px;
}
.account-setup-events-search:focus {
  outline: none;
  border-color: #c0392b;
}
.account-setup-udetail-pw {
  font-size: 12px;
  color: #5b6470;
  display: flex;
  align-items: center;
  gap: 6px;
}
.account-setup-udetail-actions {
  display: flex;
  gap: 6px;
}
.account-setup-mini-btn {
  border: 1px solid #d6dade;
  background: #fff;
  color: #5b6470;
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
  padding: 3px 9px;
  cursor: pointer;
}
.account-setup-mini-btn:hover {
  border-color: #c0392b;
  color: #c0392b;
}
.account-setup-mini-btn.is-unlink {
  border-color: #e3b4ae;
  color: #c0392b;
}
.account-setup-mini-btn.is-unlink:hover {
  background: #c0392b;
  color: #fff;
}
.account-setup-mini-btn:disabled {
  opacity: 0.5;
  cursor: default;
}
/* Per-user companies sub-table */
.account-setup-ucomp {
  list-style: none;
  margin: 0;
  padding: 0;
}
.account-setup-ucomp-head,
.account-setup-ucomp-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 2px;
  font-size: 12px;
}
.account-setup-ucomp-head {
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #95a0ab;
  border-bottom: 1px solid #e6e8eb;
}
.account-setup-ucomp-row {
  border-bottom: 1px solid #eef0f2;
  color: #2c3038;
}
.asc-uc-id {
  flex: 0 0 74px;
  font-variant-numeric: tabular-nums;
}
.asc-uc-name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.asc-uc-title {
  flex: 0 0 130px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #5b6470;
}
.asc-uc-pw {
  flex: 0 0 70px;
  display: flex;
  align-items: center;
  gap: 5px;
}
.asc-uc-x {
  flex: 0 0 28px;
  text-align: right;
}
/* Users "Password" column — a fixed mask (never the real password) + an eyeball
   that emails the help desk for assistance. */
.asc-col-pw {
  flex: 0 0 66px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.account-setup-pw-mask {
  letter-spacing: 2px;
  color: #9aa3ad;
  font-size: 13px;
}
/* Created column + the inline Approve shortcut in the Users list. */
/* Data cell only — the .account-setup-uhead rule above gives the HEADER the same treatment as
   Name/Email, so "Created" matches the other column headers rather than looking like a value. */
.asc-col-ucreated { flex: 0 0 104px; color: #5b6470; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.account-setup-appr { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 700;
  color: #1d6f42; cursor: pointer; user-select: none; white-space: nowrap; }
.account-setup-appr input { margin: 0; cursor: pointer; }
.account-setup-appr input:disabled { cursor: default; }

/* ----- Account Status report ----- */
.asc-as-grid { display: flex; flex-wrap: wrap; gap: 8px; margin: 8px 0; }
.asc-as-card { flex: 1 1 128px; min-width: 128px; border: 1px solid #e4e7ea; border-top: 3px solid #9aa3ad;
  border-radius: 8px; padding: 8px 10px; background: #fff; }
/* Amber, not red: these are worklists, not failures. Red would read as "something broke". */
.asc-as-card.is-warn { border-top-color: #c9821f; background: #fffdf7; }
.asc-as-num { font-size: 21px; font-weight: 700; color: #2c3038; line-height: 1.1; }
.asc-as-lbl { font-size: 11px; color: #5b6470; }
.asc-as-note { font-size: 10px; color: #9aa3ad; margin-top: 2px; }
.asc-as-actions { display: flex; gap: 6px; margin: 4px 0 10px; }
.asc-as-bucket { margin-bottom: 10px; }
.asc-as-bucket-h { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em;
  color: #7a828c; border-bottom: 1px solid #e8ecef; padding-bottom: 3px; margin-bottom: 3px; }
.asc-as-bucket-h small { font-weight: 400; text-transform: none; letter-spacing: 0; }
.asc-as-bucket-l { list-style: none; margin: 0; padding: 0; }
.asc-as-bucket-l li { display: flex; justify-content: space-between; gap: 10px; font-size: 12px;
  padding: 2px 0; border-bottom: 1px dashed #f0f3f5; }
.asc-as-em { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #3a4148; }
.asc-as-dt { flex: 0 0 auto; font-size: 11px; color: #9aa3ad; }

.asc-as-insight { border: 1px solid #e4ccd0; border-left: 3px solid #9e2436; border-radius: 6px;
  background: #fffdfd; padding: 8px 12px; margin: 0 0 12px; }
.asc-as-insight-h { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em;
  color: #9e2436; margin-bottom: 4px; }
.asc-as-insight-h small { font-weight: 400; text-transform: none; letter-spacing: 0; color: #9aa3ad; }
/* pre-wrap: the model returns paragraph breaks and they carry the structure. */
.asc-as-insight-b { font-size: 12.5px; line-height: 1.5; color: #3a4148; white-space: pre-wrap; }
.account-setup-mini-btn.is-gi { border-color: #9e2436; color: #9e2436; font-weight: 700; }

.asc-lc { display: flex; gap: 6px; margin: 6px 0 10px; }
.asc-lc-in { flex: 1 1 auto; min-width: 0; border: 1px solid #d6dade; border-radius: 5px; padding: 6px 9px; font-size: 12px; }
.asc-lc-in:focus { outline: none; border-color: #9e2436; }
.asc-lc-x { border: none; background: transparent; color: #9aa3ad; cursor: pointer; font-size: 14px; float: right; line-height: 1; }
.asc-lc-x:hover { color: #9e2436; }

.asc-ui-day { display: flex; align-items: center; justify-content: space-between; gap: 10px;
  margin: 10px 0 4px; padding-bottom: 3px; border-bottom: 1px solid #dfe4e8; }
.asc-ui-day-l { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: #5b6470; }
.asc-ui-day-l small { font-weight: 400; text-transform: none; letter-spacing: 0; color: #9aa3ad; }

.asc-terr-why { margin-top: 6px; font-size: 11px; color: #9e2436; word-break: break-word; }

/* ----- Browse User Insights ----- */
.asc-ui-row { display: flex; align-items: center; gap: 10px; cursor: pointer; }
.asc-ui-row:hover { background: #f7f9fb; }
.asc-ui-caret { flex: 0 0 12px; font-weight: 700; color: #9aa3ad; }
.asc-ui-who { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; }
.asc-ui-who strong { font-size: 12.5px; color: #2c3038; }
.asc-ui-who small { font-size: 11px; color: #7a828c; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.asc-ui-when { flex: 0 0 auto; font-size: 11px; font-weight: 700; color: #1d6f42; white-space: nowrap; }
.asc-ui-counts { flex: 0 0 auto; font-size: 10.5px; color: #9aa3ad; white-space: nowrap; }
.asc-ui-detail { padding: 6px 10px 10px 26px; background: #f7f9fb; border-left: 3px solid #c0392b; }
.asc-ui-events { list-style: none; margin: 0; padding: 0; max-height: 320px; overflow-y: auto; }
.asc-ui-event { display: flex; align-items: baseline; gap: 8px; padding: 3px 0;
  border-bottom: 1px dashed #e8ecef; font-size: 12px; }
.asc-ui-kind { flex: 0 0 14px; text-align: center; color: #9aa3ad; }
.asc-ui-kind.is-search { color: #9e2436; }
.asc-ui-kind.is-action { color: #1d6f42; }
.asc-ui-time { flex: 0 0 96px; font-size: 11px; color: #7a828c; white-space: nowrap; }
.asc-ui-what { flex: 1 1 auto; min-width: 0; color: #3a4148; word-break: break-word; }
.asc-ui-via { flex: 0 0 auto; font-size: 10px; font-weight: 700; color: #9e2436;
  border: 1px solid #e4ccd0; border-radius: 4px; padding: 0 4px; }

/* Approve confirm overlay — same visual language as the report processing overlay. */
.asc-appr-overlay { position: fixed; inset: 0; z-index: 100090; display: flex; align-items: center;
  justify-content: center; background: rgba(15,13,14,.55); backdrop-filter: blur(2.5px);
  -webkit-backdrop-filter: blur(2.5px); }
.asc-appr-box { display: flex; flex-direction: column; align-items: center; gap: 10px; background: #fff;
  border-radius: 12px; border: 1px solid #e4ccd0; border-top: 4px solid #9e2436;
  box-shadow: 0 24px 60px rgba(0,0,0,.30), 0 0 0 1px rgba(158,36,54,.12), 0 0 34px rgba(158,36,54,.18);
  padding: 20px 28px; max-width: 420px; text-align: center; }
.asc-appr-txt { font-size: 14px; font-weight: 700; color: #3a2f31; line-height: 1.45; }
.asc-appr-sub { font-size: 12px; font-weight: 600; color: #7a6a6d; word-break: break-all; }
.asc-appr-btns { display: flex; gap: 8px; margin-top: 4px; }
.asc-appr-cancel, .asc-appr-go { font-size: 12px; font-weight: 700; border-radius: 8px; padding: 7px 14px;
  cursor: pointer; }
/* Cancel: plain black label at rest, red fill with white text on hover. The colors carry
   !important because a global rule was washing the resting label out to near-white — the same
   reason the Documents page pins .btn-outline-secondary to black in compiled-app.css. */
.asc-appr-cancel { border: 1px solid #d4d4d8; background: #f7f7f8; color: #000 !important; }
.asc-appr-cancel:hover,
.asc-appr-cancel:focus {
  background: #9e2436;
  border-color: #9e2436;
  color: #fff !important;
}
.asc-appr-go { border: 1px solid #1d6f42; background: #1d6f42; color: #fff; }
.asc-appr-go:hover { filter: brightness(1.08); }

/* Users scope toggle — "This company" vs "All companies" (segmented control). */
.account-setup-scope {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}
.account-setup-scope-label {
  font-size: 11px;
  font-weight: 600;
  color: #7b8794;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.account-setup-scope-btn {
  border: 1px solid #d6dade;
  background: #fff;
  color: #5b6470;
  font-size: 11.5px;
  font-weight: 600;
  border-radius: 5px;
  padding: 4px 10px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
}
.account-setup-scope-btn:hover:not(.is-active) {
  border-color: #c0392b;
  color: #c0392b;
}
.account-setup-scope-btn.is-active {
  background: #c0392b;
  border-color: #c0392b;
  color: #fff;
  cursor: default;
}
/* Lock badge in the Password cell — flags an encrypted, email-retrievable password. */
.account-setup-pw-lock {
  font-size: 12px;
  line-height: 1;
  opacity: 0.7;
}
.account-setup-pw-eye {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  line-height: 1;
  padding: 1px 2px;
  opacity: 0.75;
}
.account-setup-pw-eye:hover:not(:disabled) {
  opacity: 1;
}
.account-setup-pw-eye:disabled {
  opacity: 0.4;
  cursor: default;
}
/* The reset-to-enable eyeball, shown for users with no recoverable password yet. */
.account-setup-pw-eye-reset {
  opacity: 0.55;
}
/* Resend set-password (login) link to the user. */
.account-setup-pw-link {
  opacity: 0.8;
}
.account-setup-pw-link:hover:not(:disabled) {
  opacity: 1;
}
/* Users "Link" column — link an existing user to another company. */
.asc-col-link {
  flex: 0 0 52px;
  text-align: right;
}
/* Users "Unlink" column — remove a user from the company being managed. */
.asc-col-unlink {
  flex: 0 0 58px;
  text-align: center;
}
.account-setup-unlinkbtn {
  border: 1px solid #e3b4ae;
  background: #fff;
  color: #c0392b;
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
  padding: 2px 8px;
  cursor: pointer;
}
.account-setup-unlinkbtn:hover {
  background: #c0392b;
  border-color: #c0392b;
  color: #fff;
}
.account-setup-unlinkbtn:disabled {
  opacity: 0.5;
  cursor: default;
}
.account-setup-linkbtn {
  border: 1px solid #d6dade;
  background: #fff;
  color: #5b6470;
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
  padding: 2px 8px;
  cursor: pointer;
}
.account-setup-linkbtn:hover {
  border-color: #c0392b;
  color: #c0392b;
}
.account-setup-linkbtn.is-open {
  background: #c0392b;
  border-color: #c0392b;
  color: #fff;
}
/* Inline company-search panel that drops below the user row being linked. */
.account-setup-linkpanel {
  padding: 8px 10px 10px 10px;
  background: #f7f8fa;
  border-left: 3px solid #c0392b;
  margin: 2px 0 6px 0;
}
.account-setup-linkpanel-head {
  font-size: 12px;
  color: #5b6470;
  margin-bottom: 6px;
}
.account-setup-linkpanel-head strong {
  color: #2c3038;
}
.account-setup-link-result {
  display: flex;
  align-items: center;
  gap: 8px;
}
.account-setup-link-result .account-setup-add-result-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 11px;
  color: #7b8794;
  flex: 0 0 auto;
}
.account-setup-link-result .account-setup-add-result-label {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.account-setup-link-add {
  flex: 0 0 auto;
  color: #c0392b;
  font-weight: 700;
  font-size: 11px;
}
/* A company the user is already linked to: tinted row + an Unlink affordance. */
.account-setup-link-result.is-linked {
  background: #fbeeec;
}
.account-setup-link-unlink {
  flex: 0 0 auto;
  color: #b03a2e;
  font-weight: 700;
  font-size: 11px;
}
.account-setup-link-result.is-linked .account-setup-add-result-label::after {
  content: " · linked";
  color: #b03a2e;
  font-weight: 600;
}
.account-setup-link-empty {
  font-size: 12px;
  color: #95a0ab;
  padding: 4px 2px;
}
.account-setup-link-msg {
  font-size: 12px;
  font-weight: 600;
  color: #1e7e34;
  padding: 4px 2px 0 2px;
}
.account-setup-link-msg.is-err {
  color: #c0392b;
}
/* Create-a-new-user form (Users category). */
.account-setup-newuser {
  padding: 8px 2px 2px 2px;
}
.account-setup-newuser-toggle {
  border: 1px dashed #c0392b;
  background: #fff;
  color: #c0392b;
  font-size: 11.5px;
  font-weight: 600;
  border-radius: 5px;
  padding: 5px 10px;
  cursor: pointer;
}
.account-setup-newuser-toggle:hover {
  background: #c0392b;
  color: #fff;
}
.account-setup-newuser-form {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  border: 1px solid #e0e2e6;
  border-radius: 6px;
  background: #fafbfc;
}
.account-setup-newuser-grid {
  display: flex;
  gap: 6px;
}
.account-setup-newuser-input {
  flex: 1 1 auto;
  width: 100%;
  border: 1px solid #d6dade;
  border-radius: 5px;
  padding: 6px 8px;
  font-size: 12.5px;
}
.account-setup-newuser-actions {
  display: flex;
  gap: 8px;
  margin-top: 2px;
}
.account-setup-newuser-create {
  border: none;
  background: #c0392b;
  color: #fff;
  font-weight: 600;
  font-size: 12px;
  border-radius: 5px;
  padding: 6px 14px;
  cursor: pointer;
}
.account-setup-newuser-create:disabled {
  opacity: 0.55;
  cursor: default;
}
.account-setup-newuser-cancel {
  border: 1px solid #d6dade;
  background: #fff;
  color: #6b7280;
  font-size: 12px;
  border-radius: 5px;
  padding: 6px 12px;
  cursor: pointer;
}
.account-setup-newuser-msg {
  font-size: 11.5px;
  color: #c0392b;
}
/* Reach-based Part ID color: red = orphaned, orange = partial, green = both. */
.asc-col-id.asc-id-red {
  color: #c0392b !important;
  font-weight: 700;
}
.asc-col-id.asc-id-orange {
  color: #d97706 !important;
  font-weight: 700;
}
.asc-col-id.asc-id-green {
  color: #1f7a3a !important;
  font-weight: 800;
}

/* Color-filter dots in the Part ID header */
.asc-id-head {
  display: flex;
  flex-direction: column;
  gap: 3px;
  align-items: flex-start;
}
.asc-dots {
  display: flex;
  gap: 5px;
}
.asc-dot {
  width: 11px;
  height: 11px;
  border-radius: 50%;
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.15);
  box-sizing: border-box;
  transition: transform 0.12s ease;
}
.asc-dot:hover {
  transform: scale(1.25);
}
.asc-dot.is-active {
  outline: 2px solid #2a2a2a;
  outline-offset: 1px;
}
.asc-dot-green {
  background: #1f7a3a;
}
.asc-dot-orange {
  background: #d97706;
}
.asc-dot-red {
  background: #c0392b;
}
.account-setup-thead .asc-col-id {
  color: #9aa3ad;
}
.asc-col-code {
  flex: 0 0 124px;
  font-weight: 600;
  color: #4a5568;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.account-setup-thead .asc-col-code {
  color: #9aa3ad;
}
.asc-col-desc {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.asc-sub {
  font-size: 10.5px;
  color: #95a0ab;
}
.asc-col-x {
  flex: 0 0 22px;
}

/* Flow arrow between columns — illustrates the join (ID → Assembly ID → …).
   Faint grey on rows; bold red in the header. */
.asc-col-arrow {
  flex: 0 0 auto;
  width: 16px;
  text-align: center;
  color: #c9ccd1;
}
.account-setup-thead .asc-col-arrow {
  color: #c0392b;
  font-weight: 700;
}
/* Assembly ID / Company ID column — same width as the ID column so the flow
   reads as parallel id cells. */
.asc-col-asmid {
  flex: 0 0 58px;
  font-variant-numeric: tabular-nums;
  color: #5b6470;
}
.account-setup-thead .asc-col-asmid {
  color: #9aa3ad;
}
/* Parts/Wear "Assembly ID / Code" — condensed, never wraps; shrinks gracefully
   and lets the Part Description column absorb the remaining width. */
.asc-col-asmcode {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 150px;
  font-size: 10px;
  color: #5b6470;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.account-setup-thead .asc-col-asmcode {
  color: #9aa3ad;
}

/* Deleted (deactivated) row + inline restore (undo) icon */
.account-setup-detail .account-setup-trow.is-deleted {
  opacity: 0.5;
}
.account-setup-trow.is-deleted .asc-col-desc,
.account-setup-trow.is-deleted .asc-col-code {
  text-decoration: line-through;
}
.account-setup-undo-btn {
  width: 20px;
  height: 20px;
  line-height: 1;
  text-align: center;
  border: 1px solid #1f7a3a;
  background: #fff;
  color: #1f7a3a;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
}
.account-setup-undo-btn:hover:not(:disabled) {
  background: #1f7a3a;
  color: #fff;
}
.account-setup-undo-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
.account-setup-delhist {
  list-style: none;
  /* Make the history box jump out from the rest of the category list. */
  border: 1px solid #f0c9c4 !important;
  border-left: 4px solid #c0392b !important;
  border-radius: 6px;
  background: #fffafa;
  padding: 4px 8px 8px 8px !important;
  margin-top: 14px;
}
/* "Jump to history" bar at the top of a long category list. */
.account-setup-jumpbar {
  list-style: none;
  border-bottom: none !important;
  padding: 2px 0 6px 0 !important;
}
.account-setup-jumplink {
  border: 1px dashed #c0392b;
  background: #fff5f4;
  color: #c0392b;
  font-size: 11.5px;
  font-weight: 600;
  border-radius: 5px;
  padding: 4px 10px;
  cursor: pointer;
}
.account-setup-jumplink:hover {
  background: #c0392b;
  color: #fff;
}
.account-setup-jumpbar-bottom {
  padding: 6px 0 2px 0 !important;
  border-bottom: none !important;
}
/* Invisible scroll target at the very bottom of a category's list. */
.account-setup-anchor-bottom {
  height: 0 !important;
  padding: 0 !important;
  border: none !important;
}
/* Floating jump control — pinned at the scroll viewport's vertical center while
   scrolling a long category; jumps to that category's top / bottom. */
.account-setup-scrollnav-rail {
  position: sticky;
  top: 50%;
  height: 0;
  z-index: 6;
  pointer-events: none;   /* don't block the list; the buttons opt back in */
}
.account-setup-scrollnav {
  position: absolute;
  right: 2px;
  top: 0;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 8px;
  pointer-events: auto;
}
.account-setup-scrollnav-btn {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid #c0392b;
  background: #fff;
  color: #c0392b;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
  transition: background 0.15s ease, color 0.15s ease;
}
.account-setup-scrollnav-btn:hover {
  background: #c0392b;
  color: #fff;
}
/* "Back to top" inside the history box. */
.account-setup-backtop {
  border: 1px solid #e0e2e6;
  background: #fff;
  color: #6b7280;
  font-size: 11px;
  font-weight: 600;
  border-radius: 5px;
  padding: 3px 10px;
  cursor: pointer;
}
.account-setup-backtop:hover {
  border-color: #c0392b;
  color: #c0392b;
}

/* Per-part impact (linked customers + machines) on row expand */
.account-setup-trow-click {
  cursor: pointer;
}
.account-setup-trow-click:hover {
  background: #f7f9fb;
}
.account-setup-impact {
  list-style: none;
  background: #f7f9fb;
  border-bottom: 1px dashed #e6e6e6 !important;
  padding: 8px 10px !important;
}
.account-setup-impact-row {
  display: flex;
  gap: 8px;
  font-size: 11.5px;
  padding: 2px 0;
}
.account-setup-impact-label {
  flex: 0 0 112px;
  font-weight: 700;
  color: #4a5568;
}
.account-setup-impact-vals {
  flex: 1 1 auto;
  min-width: 0;
  color: #555;
  word-break: break-word;
}
.account-setup-impact-note {
  font-size: 11px;
  color: #1f6f8b;
  background: #eef6f9;
  border: 1px solid #cfe6ee;
  border-radius: 5px;
  padding: 5px 8px;
  margin-bottom: 6px;
}
.account-setup-impact-viewall {
  border: 0;
  background: none;
  padding: 0;
  color: #c0392b;
  font-size: 11.5px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
}
.account-setup-impact-viewall:hover {
  color: #a3301f;
}
.account-setup-detail-main {
  flex: 1 1 auto;
}
.account-setup-chip {
  flex: 0 0 auto;
  font-size: 10.5px;
  font-weight: 600;
  color: #4a5568;
  background: #eef1f4;
  border: 1px solid #e0e4e8;
  border-radius: 4px;
  padding: 0 6px;
  line-height: 1.6;
  white-space: nowrap;
}
.account-setup-chip.is-id {
  color: #8a5a00;
  background: #fdf3e3;
  border-color: #f4e3c5;
}

/* Inline add box + remove (X) for editable categories */
.account-setup-add {
  list-style: none;
  border-bottom: 1px solid #f0f0f0 !important;
  padding: 4px 0 8px 0 !important;
}
.account-setup-add-input {
  width: 100%;
  height: 32px;
  border: 1px solid #ccd2d8;
  border-radius: 5px;
  padding: 0 10px;
  font-size: 12.5px;
  color: #2a2a2a;
  box-sizing: border-box;
}
.account-setup-add-results {
  list-style: none;
  margin: 6px 0 0 0;
  padding: 0;
  max-height: 190px;
  overflow-y: auto;
  border: 1px solid #eef0f2;
  border-radius: 5px;
}
.account-setup-add-result {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  cursor: pointer;
  border-bottom: 1px solid #f5f5f5;
}
.account-setup-add-result:last-child {
  border-bottom: none;
}
.account-setup-add-result:hover {
  background: #f3f8ff;
}
.account-setup-add-result-label {
  flex: 1 1 auto;
  font-size: 12.5px;
  color: #2a2a2a;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.account-setup-add-plus {
  flex: 0 0 auto;
  font-size: 11px;
  font-weight: 700;
  color: #1f7a3a;
}
.account-setup-add-hint {
  font-size: 11.5px;
  color: #9aa3ad;
  padding: 6px 2px 0 2px;
  font-style: italic;
}
/* Machine an assembly / sub-assembly sits under, shown under its name. */
.account-setup-asm-machine {
  display: block;
  font-size: 10.5px;
  color: #9aa3ad;
  margin-top: 1px;
}
/* Found / Not-found feedback for the in-list search (Machines / Wear / Users). */
.account-setup-search-status {
  font-size: 11.5px;
  font-weight: 600;
  padding: 6px 2px 0 2px;
}
.account-setup-search-status.is-found {
  color: #1f7a3a;
}
.account-setup-search-status.is-notfound {
  color: #d97706;
}
/* Red left-accent on rows matching the active search (like the SavedCarts card). */
.account-setup-detail .account-setup-trow.account-setup-search-hit {
  border-left: 3px solid #c0392b;
  padding-left: 6px;
}
.account-setup-remove {
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  line-height: 1;
  text-align: center;
  border: 1px solid #e3b6b1;
  background: #fff;
  color: #c0392b;
  border-radius: 50%;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
}
.account-setup-remove:hover:not(:disabled) {
  background: #c0392b;
  color: #fff;
  border-color: #c0392b;
}
.account-setup-remove:disabled {
  opacity: 0.5;
  cursor: default;
}

/* Setup-gap "Fix now" diagnosis */
.account-setup-gapfix {
  margin-top: 10px;
  border-top: 1px dashed #f0cfca;
  padding-top: 6px;
}
.account-setup-gapfix-item {
  padding: 3px 0;
}
.account-setup-gapfix-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.account-setup-gapfix-name {
  font-size: 12.5px;
  color: #8a2a20;
  font-weight: 600;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.account-setup-gapfix-btn {
  flex: 0 0 auto;
  border: 1px solid #c0392b;
  background: #fff;
  color: #c0392b;
  font-size: 11.5px;
  font-weight: 700;
  border-radius: 5px;
  padding: 3px 12px;
  cursor: pointer;
}
.account-setup-gapfix-btn:hover {
  background: #c0392b;
  color: #fff;
}
.account-setup-gapfix-detail {
  margin: 6px 0 4px 0;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid #f1d6d2;
  border-radius: 6px;
}
.account-setup-gapfix-line {
  font-size: 12px;
  color: #555;
  margin: 0 0 6px 0;
  line-height: 1.45;
}
.account-setup-gapfix-sub {
  font-size: 11.5px;
  font-weight: 600;
  color: #444;
  margin: 6px 0 4px 0;
}
.account-setup-gapfix-similar {
  list-style: none;
  margin: 0 0 6px 0;
  padding: 0;
}
.account-setup-gapfix-similar li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 3px 0;
  border-bottom: 1px dashed #f2f2f2;
}
.account-setup-gapfix-simname {
  font-size: 12px;
  color: #2a2a2a;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.account-setup-gapfix-admin {
  display: inline-block;
  margin-top: 4px;
  border: none;
  background: none;
  color: #c0392b;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  padding: 2px 0;
  text-decoration: underline;
}

/* Parts: search -> link/unlink the part's machine */
.account-setup-pl-result {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 8px;
  border-bottom: 1px solid #f5f5f5;
}
.account-setup-pl-result:last-child {
  border-bottom: none;
}
.account-setup-pl-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.account-setup-pl-part {
  font-size: 12.5px;
  color: #2a2a2a;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.account-setup-pl-machine {
  font-size: 11px;
  color: #6b7682;
}
.account-setup-pl-machine.is-missing {
  color: #c0392b;
  font-weight: 600;
}
.account-setup-pl-assembly {
  font-size: 11px;
  color: #6b7682;
}
.account-setup-pl-linked {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #1f7a3a;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
}
.account-setup-pl-btn {
  flex: 0 0 auto;
  font-size: 11.5px;
  font-weight: 700;
  border-radius: 5px;
  padding: 4px 12px;
  cursor: pointer;
  border: 1px solid #c0392b;
}
.account-setup-pl-btn.is-link {
  background: #c0392b;
  color: #fff;
}
.account-setup-pl-btn.is-link:hover:not(:disabled) {
  background: #a93226;
}
.account-setup-pl-onaccount {
  flex: 0 0 auto;
  font-size: 11px;
  font-weight: 600;
  color: #1f7a3a;
  white-space: nowrap;
}
.account-setup-pl-btn.is-unlink {
  background: #fff;
  color: #c0392b;
}
.account-setup-pl-btn.is-unlink:hover:not(:disabled) {
  background: #fdecea;
}
.account-setup-pl-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

/* ============================================================
   Wear bulk "Delete all" — button + inline 3-outcome confirm panel
   ============================================================ */
.account-setup-deleteall {
  padding: 2px 0 4px;
}
.account-setup-deleteall-bar {
  display: flex;
  justify-content: flex-end;
}
.account-setup-deleteall-btn {
  flex: 0 0 auto;
  font-size: 11.5px;
  font-weight: 700;
  border-radius: 5px;
  padding: 4px 12px;
  cursor: pointer;
  border: 1px solid #c0392b;
  background: #fff;
  color: #c0392b;
}
.account-setup-deleteall-btn:hover {
  background: #fdecea;
}
.account-setup-deleteall-panel {
  margin-top: 8px;
  padding: 10px 12px;
  background: #fdf4f3;
  border: 1px solid #e3b6b1;
  border-left: 3px solid #c0392b;
  border-radius: 6px;
}
.account-setup-deleteall-msg {
  font-size: 12.5px;
  line-height: 1.45;
  color: #4a2622;
}
.account-setup-deleteall-note {
  margin-top: 4px;
  font-size: 11.5px;
  color: #8a6d69;
}
.account-setup-deleteall-actions {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.account-setup-deleteall-soft,
.account-setup-deleteall-perm,
.account-setup-deleteall-cancel {
  flex: 0 0 auto;
  font-size: 11.5px;
  font-weight: 700;
  border-radius: 5px;
  padding: 5px 14px;
  cursor: pointer;
  border: 1px solid #c0392b;
}
.account-setup-deleteall-soft {
  background: #c0392b;
  color: #fff;
}
.account-setup-deleteall-soft:hover:not(:disabled) {
  background: #a93226;
}
.account-setup-deleteall-perm {
  background: #fff;
  color: #c0392b;
}
.account-setup-deleteall-perm:hover:not(:disabled) {
  background: #fdecea;
}
.account-setup-deleteall-cancel {
  background: #fff;
  color: #6b7280;
  border-color: #d6d9de;
}
.account-setup-deleteall-cancel:hover:not(:disabled) {
  background: #f3f4f6;
}
.account-setup-deleteall-soft:disabled,
.account-setup-deleteall-perm:disabled,
.account-setup-deleteall-cancel:disabled {
  opacity: 0.6;
  cursor: default;
}

/* ============================================================
   Copy setup from another company
   ============================================================ */
.account-setup-copy {
  margin-top: 2px;
  padding: 14px;
  background: #f7f9fb;
  border: 1px solid #e4e9ee;
  border-left: 3px solid #c0392b;
  border-radius: 6px;
}
.account-setup-copy-head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #2a2a2a;
  margin-bottom: 6px;
}
.account-setup-copy-icon {
  color: #c0392b;
  font-size: 15px;
  line-height: 1;
}
.account-setup-copy-hint {
  font-size: 12px;
  color: #667;
  margin: 0 0 10px 0;
  line-height: 1.45;
}
.account-setup-copy-row {
  display: flex;
  gap: 8px;
  align-items: stretch;
  flex-wrap: wrap;
}
.account-setup-copy-select {
  flex: 1 1 220px;
  min-width: 0;
  height: 38px;
  border: 1px solid #ccd2d8;
  border-radius: 5px;
  padding: 0 10px;
  font-size: 13px;
  color: #2a2a2a;
  background: #fff;
}
.account-setup-copy-select:disabled {
  background: #f0f0f0;
  color: #999;
}
.account-setup-copy-input {
  flex: 1 1 220px;
  min-width: 0;
  height: 38px;
  border: 1px solid #ccd2d8;
  border-radius: 5px;
  padding: 0 10px;
  font-size: 13px;
  color: #2a2a2a;
  background: #fff;
  box-sizing: border-box;
}
.account-setup-copy-input:disabled {
  background: #f0f0f0;
  color: #999;
}
.account-setup-copy-clear {
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  border: 1px solid #ccd2d8;
  border-radius: 5px;
  background: #fff;
  color: #777;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
}
.account-setup-copy-clear:hover:not(:disabled) {
  background: #f3f3f3;
  color: #333;
}
/* Copy-from search results — same ID · Customer ID · Company Name columns as categories */
.account-setup-copy-rowitem {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 8px;
  border-bottom: 1px solid #f5f5f5;
}
.account-setup-copy-rowitem:last-child {
  border-bottom: none;
}
.account-setup-copy-rowitem.is-head {
  position: sticky;
  top: 0;
  background: #fff;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.account-setup-copy-rowitem.is-head > span {
  color: #9aa3ad;
}
.account-setup-copy-rowitem.is-pick {
  cursor: pointer;
}
.account-setup-copy-rowitem.is-pick:hover {
  background: #f3f8ff;
}
.account-setup-copy-btn {
  flex: 0 0 auto;
  height: 38px;
  padding: 0 16px;
  border: none;
  border-radius: 5px;
  background: #c0392b;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;
}
.account-setup-copy-btn:hover:not(:disabled) {
  background: #a93226;
}
.account-setup-copy-btn:disabled {
  background: #d8a39d;
  cursor: default;
}
/* ===== Sales Territories editor (nested tool in Utility Actions) ===== */
.account-setup-nav-row.asc-terr-row {
  display: grid;
  grid-template-columns: 64px 1fr 1fr 44px auto;
  gap: 8px;
  align-items: center;
}
.asc-terr-row .account-setup-nav-input { width: 100%; }
.asc-terr-colorpick { width: 36px; height: 26px; padding: 0; border: 1px solid #c9ccd2; border-radius: 5px; background: none; cursor: pointer; }
html.dark-mode .asc-terr-colorpick { border-color: #3a414a; }

/* Unmapped-rep warnings banner (top of the Sales Territories section) */
.asc-terr-warns { margin: 4px 0 12px; border: 1px solid #f0d69a; background: #fdf6e3; border-radius: 8px; padding: 10px 12px; }
.asc-terr-warnhead { font-size: 12.5px; font-weight: 700; color: #8a6d1a; margin-bottom: 6px; }
.asc-terr-warnhead.is-clear { color: #2e7d5b; font-weight: 600; margin-bottom: 0; }
.asc-terr-warnlist { list-style: none; margin: 0; padding: 0; }
.asc-terr-warnlist.is-ignored { margin-top: 6px; opacity: .85; }
.asc-terr-warnrow { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; padding: 4px 0; border-top: 1px dotted #eadfb8; }
.asc-terr-warnrow:first-child { border-top: 0; }
.asc-terr-warnname { font-weight: 700; color: #6b5410; font-size: 12.5px; word-break: break-word; }
.asc-terr-warnmeta { color: #977c33; font-size: 11px; flex: 1; }
.asc-terr-warnbtn { border: 1px solid #d9b34d; background: #fff; color: #8a6d1a; border-radius: 6px; font-size: 11px; font-weight: 700; padding: 3px 10px; cursor: pointer; }
.asc-terr-warnbtn.is-restore { border-color: #b7bcc4; color: #4a525c; }
.asc-terr-warnbtn.is-add { border-color: #2e7d5b; color: #2e7d5b; }
.asc-terr-warnbtn:disabled { opacity: .6; cursor: default; }
.asc-terr-warnignored { margin-top: 8px; }
.asc-terr-warntoggle { font-size: 11.5px; font-weight: 600; color: #8a6d1a; text-decoration: none; }
.asc-terr-warntoggle:hover { text-decoration: underline; }
html.dark-mode .asc-terr-warns { background: #2c2617; border-color: #5a4a1e; }
html.dark-mode .asc-terr-warnhead { color: #e6c65f; }
html.dark-mode .asc-terr-warnhead.is-clear { color: #6fd0a3; }
html.dark-mode .asc-terr-warnname { color: #f0d98a; }
html.dark-mode .asc-terr-warnmeta { color: #c8ad63; }
html.dark-mode .asc-terr-warnrow { border-top-color: #4a3f1c; }
html.dark-mode .asc-terr-warnbtn { background: #221d10; border-color: #5a4a1e; color: #e6c65f; }
html.dark-mode .asc-terr-warnbtn.is-restore { border-color: #4a525c; color: #cfd5db; }
html.dark-mode .asc-terr-warnbtn.is-add { border-color: #6fd0a3; color: #6fd0a3; }
html.dark-mode .asc-terr-warntoggle { color: #e6c65f; }
.asc-terr-add {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.asc-terr-add .account-setup-nav-input { flex: 1 1 120px; width: auto; }
/* Unmapped-rep DRILL-DOWN: expandable rep name → (prefix + country) groups → their orders. */
.asc-terr-warnname-btn { display: inline-flex; align-items: center; gap: 6px; border: 0; background: none; padding: 0; cursor: pointer; font: inherit; text-align: left; color: inherit; }
.asc-terr-warncaret { color: #8a6d1a; font-size: 11px; width: 10px; flex: 0 0 auto; }
.asc-terr-warnbtn.is-insight { border-color: #c9a94a; }
.asc-terr-insight { flex-basis: 100%; width: 100%; margin: 2px 0 8px; padding: 8px 11px; background: #fffdf5; border: 1px solid #ecd9b0; border-left: 3px solid #e0a92a; border-radius: 8px; }
.asc-terr-insight-row { font-size: 12px; line-height: 1.55; color: #5c4c1e; padding: 2px 0; }
.asc-terr-insight-row b { color: #7a5f12; }
.asc-terr-insight-row em { font-style: italic; }
html.dark-mode .asc-terr-insight { background: #2c2718; border-color: #4a3f1c; border-left-color: #d9b34d; }
html.dark-mode .asc-terr-insight-row { color: #d8c88f; }
html.dark-mode .asc-terr-insight-row b { color: #ecd08a; }
/* Prefix auto-match on/off — a real sliding toggle (not a checkbox), right of the Import button. */
.asc-terr-toggle { display: inline-flex; align-items: center; gap: 7px; margin-left: 6px; font-size: 12px; color: #555; }
.asc-terr-toggle-lbl { font-weight: 600; }
.asc-terr-toggle-state { font-weight: 700; min-width: 20px; }
.asc-switch { position: relative; width: 42px; height: 22px; border-radius: 11px; border: 1px solid #c7ccd2; background: #cdd2d8; cursor: pointer; padding: 0; transition: background .15s ease, border-color .15s ease; flex: 0 0 auto; }
.asc-switch:disabled { opacity: .6; cursor: default; }
.asc-switch.is-on { background: #2e7d5b; border-color: #2e7d5b; }
.asc-switch-knob { position: absolute; top: 1px; left: 1px; width: 18px; height: 18px; border-radius: 50%; background: #fff; box-shadow: 0 1px 2px rgba(0,0,0,.3); transition: left .15s ease; }
.asc-switch.is-on .asc-switch-knob { left: 21px; }
html.dark-mode .asc-terr-toggle { color: #aeb6bf; }
html.dark-mode .asc-switch { background: #3a414a; border-color: #4a525c; }
html.dark-mode .asc-switch.is-on { background: #2e7d5b; border-color: #2e7d5b; }
/* Rep-alias list (old rep → current rep). */
.asc-terr-alias { margin-top: 10px; }
.asc-alias-row { display: grid; grid-template-columns: 1fr 22px 1fr auto; align-items: center; gap: 8px; }
.asc-alias-row.is-head { font-size: 10px; text-transform: uppercase; letter-spacing: .03em; color: #999; font-weight: 700; }
.asc-alias-arrow { text-align: center; color: #8a6d1a; font-weight: 700; }
.asc-alias-from, .asc-alias-to { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.asc-alias-add { display: grid; grid-template-columns: 1fr 22px 1fr auto; align-items: center; gap: 8px; }
.asc-terr-drill { flex-basis: 100%; width: 100%; margin: 2px 0 8px; padding: 4px 4px 6px 20px; }
.asc-terr-drill-loading { font-size: 12px; color: #8a6d1a; padding: 6px 2px; }
.asc-terr-drill-empty { font-size: 12px; color: #999; padding: 6px 2px; }
.asc-terr-drilltbl, .asc-terr-ordtbl { width: 100%; border-collapse: collapse; font-size: 11.5px; }
.asc-terr-drilltbl th, .asc-terr-ordtbl th { text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: .03em; color: #999; font-weight: 700; padding: 2px 6px; border-bottom: 1px solid #eee; }
.asc-terr-drilltbl td, .asc-terr-ordtbl td { padding: 3px 6px; border-bottom: 1px solid #f4f4f4; vertical-align: top; }
.asc-terr-drilltbl .num, .asc-terr-ordtbl .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
.asc-terr-gpref { font-weight: 800; color: #7a1f2b; }
.asc-terr-gmap { font-size: 10.5px; color: #888; }
.asc-terr-ordtoggle { border: 0; background: none; color: #8a6d1a; cursor: pointer; font-size: 11px; padding: 0 2px; }
.asc-terr-ordwrap { padding: 2px 0 6px 8px; }
html.dark-mode .asc-terr-warncaret, html.dark-mode .asc-terr-drill-loading, html.dark-mode .asc-terr-ordtoggle { color: #e6c65f; }
html.dark-mode .asc-terr-drilltbl th, html.dark-mode .asc-terr-ordtbl th { color: #9aa3ab; border-bottom-color: #3a3f46; }
html.dark-mode .asc-terr-drilltbl td, html.dark-mode .asc-terr-ordtbl td { border-bottom-color: #2b3037; }
html.dark-mode .asc-terr-gpref { color: #f0a8b1; }
html.dark-mode .asc-terr-gmap, html.dark-mode .asc-terr-drill-empty { color: #9aa3ab; }
/* Per-customer exceptions (override the prefix auto-assign) */
.asc-terr-io { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin: 2px 0 10px; }
.asc-terr-export, .asc-terr-import { margin: 0; }
.asc-terr-file { display: none; }
.asc-terr-import-confirm { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; justify-content: space-between; background: #fff8e6; border: 1px solid #f0d68a; border-radius: 8px; padding: 8px 10px; margin: 0 0 10px; }
.asc-terr-import-msg { flex: 1 1 220px; font-size: 12px; color: #6b5a1e; line-height: 1.4; }
.asc-terr-import-actions { display: flex; gap: 8px; flex: 0 0 auto; }
.asc-terr-import-apply { border: 0; background: #b0122b; color: #fff; font-weight: 700; font-size: 12px; border-radius: 6px; padding: 6px 12px; cursor: pointer; }
.asc-terr-import-apply:disabled { opacity: .6; cursor: default; }
.asc-terr-import-cancel { border: 1px solid #ccc; background: #fff; color: #555; font-weight: 600; font-size: 12px; border-radius: 6px; padding: 6px 12px; cursor: pointer; }
.asc-terr-import-cancel:disabled { opacity: .6; cursor: default; }
.asc-terr-import-note { font-size: 12px; font-weight: 600; color: #1f7a44; margin: 0 0 10px; }
.asc-terr-import-list { margin: 4px 0 2px; padding-left: 18px; }
.asc-terr-import-list li { margin: 1px 0; }
.asc-imp-note { color: #8a7a3e; font-weight: 400; }
.asc-terr-io-hint { margin: -4px 0 12px; }
.asc-terr-io-hint code { background: rgba(0,0,0,.05); border-radius: 3px; padding: 0 3px; font-size: 11px; }
html.dark-mode .asc-imp-note { color: #c6b06a; }
html.dark-mode .asc-terr-io-hint code { background: rgba(255,255,255,.08); }
html.dark-mode .asc-terr-import-confirm { background: #2c2718; border-color: #5c4e2a; }
html.dark-mode .asc-terr-import-msg { color: #e8d9a8; }
html.dark-mode .asc-terr-import-cancel { background: #1f242b; border-color: #3a4048; color: #cfd6dd; }
html.dark-mode .asc-terr-import-note { color: #7bd88a; }
.asc-terr-exc { margin-top: 14px; padding-top: 12px; border-top: 1px dashed #d9dee4; }
.asc-terr-exc-h { font-weight: 800; color: #7a1f2b; font-size: 12.5px; margin-bottom: 2px; }
.asc-terr-exc-hint { margin: 0 0 8px; }
.account-setup-nav-row.asc-exc-row { display: grid; grid-template-columns: 1.1fr 1fr 0.9fr 1fr 1fr 34px auto; gap: 8px; align-items: center; }
.asc-exc-row .account-setup-nav-input { width: 100%; }
.asc-exc-row.is-head { font-size: 10.5px; text-transform: uppercase; letter-spacing: .02em; color: #8a929c; font-weight: 700; }
.asc-exc-on { text-align: center; }
.asc-exc-add { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 6px; }
.asc-exc-add .account-setup-nav-input { flex: 1 1 120px; width: auto; }
.asc-exc-msg { color: #9e2436; }
html.dark-mode .asc-terr-exc { border-top-color: #3a414a; }
html.dark-mode .asc-terr-exc-h { color: #f0a8b1; }
/* Territory Access (login emails per territory) */
.asc-terr-access { margin-top: 14px; padding-top: 12px; border-top: 1px dashed #d9dee4; }
html.dark-mode .asc-terr-access { border-top-color: #3a414a; }
.asc-terr-access-head { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: #2a2a2a; }
html.dark-mode .asc-terr-access-head { color: #e3e7eb; }
.asc-terr-access-hint { margin: 4px 0 8px; }
.account-setup-nav-row.asc-terr-acc-row {
  display: grid;
  grid-template-columns: 160px 1fr auto;
  gap: 8px;
  align-items: center;
}
.asc-terr-acc-terr { font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.asc-terr-acc-row .account-setup-nav-input { width: 100%; }
.asc-terr-acc-warn { color: #c0392b; font-weight: 600; }
html.dark-mode .asc-terr-acc-warn { color: #ff8a80; }

/* ===== Report Access — Entra group chips + typeahead ===== */
.asc-racc-row { align-items: start; }
.asc-racc-row .asc-terr-acc-terr { padding-top: 6px; }
.asc-racc-groups {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.asc-racc-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 260px;
  padding: 3px 6px 3px 8px;
  border: 1px solid #b8c4d6;
  border-radius: 12px;
  background: #eef3fb;
  font-size: 12px;
  font-weight: 600;
  color: #2c4a77;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.asc-racc-chip-ic { font-size: 11px; }
/* LOCAL ROLE chip / suggestion (id "role:<Name>") — same shape as an Entra chip, amber instead of
   blue so the two kinds are never confused at a glance. Members come from the portal's own user
   list, not Graph. Amber matches the "pending provisioning" panel below. */
.asc-racc-chip.is-role {
  border-color: #f0d69a;
  background: #fdf6e3;
  color: #8a6d1a;
}
.asc-racc-chip.is-role .asc-racc-chip-x { color: #b08c37; }
.asc-racc-sug-item.is-role { color: #8a6d1a; }
.asc-racc-sug-item.is-role .asc-racc-sug-kind { color: #b08c37; font-weight: 600; }
.asc-racc-chip-x {
  border: 0;
  background: transparent;
  color: #7a8aa3;
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
  padding: 0 2px;
}
.asc-racc-chip-x:hover { color: #c0392b; }
.asc-racc-typeahead { position: relative; flex: 1 1 180px; min-width: 160px; }
.asc-racc-input { width: 100%; }
.asc-racc-sug {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  z-index: 40;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  background: #fff;
  border: 1px solid #cfd6de;
  border-radius: 6px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.14);
  max-height: 220px;
  overflow: auto;
}
.asc-racc-sug-item {
  padding: 6px 10px;
  font-size: 12.5px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.asc-racc-sug-item.is-hi { background: #fdf1f1; }
.asc-racc-sug-note { padding: 6px 10px; font-size: 12px; color: #999; }
.asc-racc-sug-kind { margin-left: 6px; font-size: 11px; color: #999; }
.asc-racc-sync {
  width: 26px;
  height: 26px;
  border: 1px solid #b8c4d6;
  border-radius: 50%;
  background: #fff;
  color: #2c4a77;
  cursor: pointer;
  line-height: 1;
  flex: none;
}
.asc-racc-sync:hover:not(:disabled) { background: #eef3fb; }
.asc-racc-sub {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
  font-size: 12px;
  color: #777;
}
.asc-racc-manual-toggle {
  border: 0;
  background: transparent;
  color: #9e2436;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}
.asc-racc-manual {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 6px;
}
.asc-racc-manual .account-setup-nav-input { flex: 1; }
/* Group-members hover popup — fixed so it escapes the modal's scroll container. */
.asc-racc-members {
  position: fixed;
  z-index: 100060;
  width: 290px;
  max-height: 300px;
  overflow: auto;
  background: #fff;
  border: 1px solid #cfd6de;
  border-top: 3px solid #9e2436;
  border-radius: 8px;
  box-shadow: 0 8px 26px rgba(0, 0, 0, 0.22);
  padding: 8px 0 6px;
}
.asc-racc-members.is-up { transform: translateY(-100%); }
.asc-racc-members-h {
  font-size: 12px;
  font-weight: 700;
  color: #2c4a77;
  padding: 0 12px 6px;
  border-bottom: 1px solid #eee;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.asc-racc-members-note { padding: 10px 12px; font-size: 12px; color: #888; }
.asc-racc-members-list { list-style: none; margin: 0; padding: 4px 0 0; }
.asc-racc-members-list li {
  display: flex;
  flex-direction: column;
  padding: 4px 12px;
  border-bottom: 1px solid #f4f4f4;
}
.asc-racc-members-list li:last-child { border-bottom: 0; }
.asc-racc-member-name { font-size: 12.5px; font-weight: 600; color: #333; }
.asc-racc-member-mail { font-size: 11px; color: #999; }
html.dark-mode .asc-racc-members { background: #23272b; border-color: #4a4f55; border-top-color: #c0392b; }
html.dark-mode .asc-racc-members-h { color: #cdd9ec; border-bottom-color: #3a3f45; }
html.dark-mode .asc-racc-members-list li { border-bottom-color: #2f343a; }
html.dark-mode .asc-racc-member-name { color: #e8eaed; }
html.dark-mode .asc-racc-member-mail { color: #8b9097; }

html.dark-mode .asc-racc-chip { background: #2c3644; border-color: #46536a; color: #cdd9ec; }
html.dark-mode .asc-racc-chip.is-role { background: #2c2617; border-color: #5a4a1e; color: #f0d98a; }
html.dark-mode .asc-racc-chip.is-role .asc-racc-chip-x { color: #c8ad63; }
html.dark-mode .asc-racc-sug-item.is-role { color: #f0d98a; }
html.dark-mode .asc-racc-sug-item.is-role .asc-racc-sug-kind { color: #c8ad63; }
html.dark-mode .asc-racc-sug { background: #23272b; border-color: #4a4f55; }
html.dark-mode .asc-racc-sug-item.is-hi { background: #3a3236; }
html.dark-mode .asc-racc-sug-note { color: #8b9097; }
html.dark-mode .asc-racc-sync { background: #2a2e33; border-color: #46536a; color: #cdd9ec; }
html.dark-mode .asc-racc-sub { color: #b9bec4; }
html.dark-mode .asc-racc-manual-toggle { color: #ff8a80; }

/* Pending provisioning — logins added to a territory with no portal account yet (never signed in). */
.asc-terr-pending {
  display: flex;
  gap: 10px;
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid #f0d69a;
  border-left: 3px solid #d9a441;
  background: #fdf6e3;
  border-radius: 8px;
}
.asc-terr-pending-ic { font-size: 16px; line-height: 1.3; flex: none; }
.asc-terr-pending-body { flex: 1; min-width: 0; }
.asc-terr-pending-head { font-size: 12.5px; font-weight: 700; color: #8a6d1a; }
.asc-terr-pending-note { margin: 3px 0 8px; font-size: 11px; color: #977c33; }
.asc-terr-pending-list { list-style: none; margin: 0; padding: 0; }
.asc-terr-pending-list li {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 4px 0;
  border-top: 1px dotted #eadfb8;
}
.asc-terr-pending-list li:first-child { border-top: 0; }
.asc-terr-pending-email {
  font-weight: 700; color: #6b5410; font-size: 12.5px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 55%;
}
.asc-terr-pending-terrs { color: #977c33; font-size: 11px; flex: 1; min-width: 0; }
.asc-terr-allset { margin-top: 12px; font-size: 12px; color: #2e7d5b; font-weight: 600; }
html.dark-mode .asc-terr-pending { background: #2c2617; border-color: #5a4a1e; border-left-color: #d9a441; }
html.dark-mode .asc-terr-pending-head { color: #e6c65f; }
html.dark-mode .asc-terr-pending-note { color: #c8ad63; }
html.dark-mode .asc-terr-pending-list li { border-top-color: #4a3f1c; }
html.dark-mode .asc-terr-pending-email { color: #f0d98a; }
html.dark-mode .asc-terr-pending-terrs { color: #c8ad63; }
html.dark-mode .asc-terr-allset { color: #6fd0a3; }

/* ===== Replace Charts — nested tool in Utility Actions ===== */
.asc-rc-results {
  max-height: none;
}
.asc-rc-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px;
  border-bottom: 1px solid #eee;
  font-size: 0.85rem;
}
.asc-rc-row.is-head {
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.72rem;
  letter-spacing: 0.04em;
  color: #666;
  background: #f7f7f7;
}
.asc-rc-row.is-pick {
  cursor: pointer;
}
.asc-rc-row.is-pick:hover {
  background: #fdf1f1;
}
.asc-rc-row.is-open {
  background: #fbe9e9;
  font-weight: 600;
}
.asc-rc-machine { flex: 0 0 22%; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.asc-rc-asm     { flex: 1 1 26%; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.asc-rc-sub     { flex: 1 1 26%; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.asc-rc-has     { flex: 0 0 44px; text-align: center; }
/* Mirrors Search's .wear-asm-drawing-ico treatment (full 🖼 = chart on file,
   faded greyscale = none) so admins read the same signal in both places. */
.asc-rc-drawing-ico { font-size: 13px; line-height: 1; }
.asc-rc-drawing-ico.is-none { opacity: 0.28; filter: grayscale(1); }
.asc-rc-mod     { flex: 0 0 128px; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #555; }
.asc-rc-mod.is-none { color: #bbb; }
.asc-rc-act     { flex: 0 0 20px; text-align: right; color: #888; }
.asc-rc-detail {
  padding: 12px 12px 14px;
  border-bottom: 1px solid #eee;
  background: #fff;
  cursor: default;
}
.asc-rc-panes {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}
.asc-rc-pane {
  flex: 1 1 300px;
  min-width: 260px;
  border: 1px dashed #ccc;
  border-radius: 6px;
  padding: 8px;
}
.asc-rc-pane-h {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 700;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #555;
  margin-bottom: 6px;
}
.asc-rc-zoom button {
  width: 24px;
  height: 24px;
  margin-left: 4px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  line-height: 1;
}
.asc-rc-zoom button:hover { background: #f2f2f2; }
.asc-rc-imgwrap {
  position: relative;
  height: 380px;
  overflow: auto;
  background: #fafafa;
  cursor: grab;
}
.asc-rc-imgwrap:active {
  cursor: grabbing;
}
.asc-rc-imgwrap.is-editing {
  cursor: crosshair;
}
/* The canvas scales as one unit (image + bubble layer) from the top-left;
   panning is the wrapper's scrollbars driven by click-drag (rcPanStart). */
.asc-rc-canvas {
  position: relative;
  width: 100%;
  transform-origin: 0 0;
}
.asc-rc-canvas img {
  display: block;
  width: 100%;
  height: auto;
  user-select: none;
}
.asc-rc-balloon-layer {
  position: absolute;
  pointer-events: none;
  z-index: 2;
}
/* View mode — the same subtle red ring customers see on the Search page. */
.asc-rc-hotspot {
  position: absolute;
  border: 1px solid rgba(192, 57, 43, 0.45);
  border-radius: 50%;
  background: transparent;
  pointer-events: none;
}
/* Edit mode — the Search modal's editor markers. */
.asc-rc-marker {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #c0392b;
  border-radius: 50%;
  background: rgba(192, 57, 43, 0.18);
  cursor: move;
  pointer-events: auto;
}
.asc-rc-marker.is-selected {
  border-color: #2e7d32;
  background: rgba(46, 125, 50, 0.22);
  box-shadow: 0 0 0 2px rgba(46, 125, 50, 0.45);
}
.asc-rc-marker .asc-rc-mcode {
  font-size: 10px;
  font-weight: 800;
  color: #c0392b;
  pointer-events: none;
}
.asc-rc-marker.is-selected .asc-rc-mcode { color: #226024; }
.asc-rc-marker .asc-rc-mresize {
  position: absolute;
  bottom: -10px;
  right: -10px;
  width: 20px;
  height: 20px;
  font-size: 12px;
  line-height: 1;
  color: #fff;
  background: #2e7d32;
  border: 1px solid #fff;
  border-radius: 50%;
  cursor: nwse-resize;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.asc-rc-editbtn {
  border: 1px solid #c0392b;
  border-radius: 4px;
  background: #fff;
  color: #c0392b;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 3px 8px;
  margin-right: 6px;
  cursor: pointer;
}
.asc-rc-editbtn:hover:not(:disabled) { background: #fdf1f1; }
.asc-rc-editbtn.is-save { background: #2e7d32; border-color: #2e7d32; color: #fff; }
.asc-rc-editbtn:disabled { opacity: 0.55; cursor: default; }
.asc-rc-editrow {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 6px;
  font-size: 0.78rem;
  color: #666;
}
.asc-rc-editsel { display: inline-flex; align-items: center; gap: 4px; }
.asc-rc-codeinput {
  width: 110px;
  padding: 2px 6px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.8rem;
}
.asc-rc-delbtn {
  width: 22px;
  height: 22px;
  border: 1px solid #c0392b;
  border-radius: 4px;
  background: #fff;
  color: #c0392b;
  cursor: pointer;
  line-height: 1;
  padding: 0;
}
.asc-rc-editerr { color: #c0392b; font-weight: 600; }
.asc-rc-noimg {
  color: #999;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 30px 0;
}
.asc-rc-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.asc-rc-fileinput { display: none; }
.asc-rc-filename {
  color: #555;
  font-size: 0.85rem;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.asc-rc-uploadbtn { background: #ed1d24; border-color: #ed1d24; }
.asc-rc-fileerr { color: #c0392b; font-size: 0.85rem; margin-top: 6px; }
.asc-rc-note { margin-top: 8px; margin-bottom: 0; }
html.dark-mode .asc-rc-row { border-bottom-color: #3a3f45; }
html.dark-mode .asc-rc-row.is-head { background: #2a2e33; color: #cfd3d8; }
html.dark-mode .asc-rc-row.is-pick:hover { background: #3a3236; }
html.dark-mode .asc-rc-row.is-open { background: #46383c; }
html.dark-mode .asc-rc-detail { background: #23272b; border-bottom-color: #3a3f45; }
html.dark-mode .asc-rc-pane { border-color: #4a4f55; }
html.dark-mode .asc-rc-pane-h { color: #cfd3d8; }
html.dark-mode .asc-rc-imgwrap { background: #2a2e33; }
html.dark-mode .asc-rc-zoom button { background: #2a2e33; border-color: #4a4f55; color: #e8eaed; }
html.dark-mode .asc-rc-editbtn { background: #2a2e33; }
html.dark-mode .asc-rc-editbtn.is-save { background: #2e7d32; color: #fff; }
html.dark-mode .asc-rc-codeinput { background: #2a2e33; border-color: #4a4f55; color: #e8eaed; }
html.dark-mode .asc-rc-delbtn { background: #2a2e33; }
html.dark-mode .asc-rc-editrow { color: #b9bec4; }

/* ===== Menu Navigation (global header menu) — nested tool in Utility Actions ===== */
.account-setup-nav {
  /* Own box with a red left accent, matching the Copy Setup tool above. */
  margin-top: 12px;
  background: #f7f9fb;
  border: 1px solid #e4e9ee;
  border-left: 3px solid #c0392b;
  border-radius: 6px;
  padding: 12px 14px;
}
.account-setup-nav-head {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #2a2a2a;
}
.account-setup-nav-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
  font-size: 15px;
  line-height: 1;
  color: #c0392b;
}
.account-setup-nav-body {
  margin-top: 8px;
}
.account-setup-nav-list {
  list-style: none;
  margin: 0 0 8px 0;
  padding: 0;
}
.account-setup-nav-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 2px;
  border-bottom: 1px solid #f0f1f3;
}
.account-setup-nav-row:last-child {
  border-bottom: none;
}
.account-setup-nav-row.is-head {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  /* Black, not the muted #9aa3ad these headings used to be — at 10px uppercase the grey was too
     faint to read against the white row area. Dark mode keeps its light override below. */
  color: #000;
  border-bottom: 1px solid #e6e8eb;
}
/* Dark mode: the column headers (State / Territory / Sales Rep, Menu Nav) were too faint on
   the dark modal — brighten them so they read. */
html.dark-mode .account-setup-nav-row.is-head { color: #e3e7eb; border-bottom-color: #3a414a; }
.asc-nav-move {
  flex: 0 0 auto;
  display: inline-flex;
  flex-direction: column;
  gap: 1px;
  width: 22px;
}
.asc-nav-name {
  flex: 1 1 40%;
  min-width: 0;
}
.asc-nav-url {
  flex: 1 1 60%;
  min-width: 0;
}
.asc-nav-act {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
/* "Mode" column — Search-intent selector (only shown for /search links). */
.asc-nav-mode {
  flex: 0 0 auto;
  width: 74px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.account-setup-nav-modesel {
  width: 100%;
  height: 28px;
  border: 1px solid #ccd2d8;
  border-radius: 5px;
  background: #fff;
  font-size: 11px;
  color: #2a2a2a;
  padding: 0 4px;
  cursor: pointer;
}
.account-setup-nav-modesel:focus {
  outline: none;
  border-color: #c0392b;
}
.account-setup-nav-row.is-head .asc-nav-mode {
  font-size: 10px;
}
/* "Roles" column — which roles may SEE this link (multi-select dropdown). */
.asc-nav-roles {
  flex: 0 0 auto;
  width: 132px;
  position: relative;
  display: inline-flex;
  align-items: center;
}
.account-setup-nav-row.is-head .asc-nav-roles { font-size: 10px; }
.account-setup-nav-rolesbtn {
  width: 100%;
  height: 28px;
  border: 1px solid #ccd2d8;
  border-radius: 5px;
  background: #fff;
  font-size: 10.5px;
  color: #2a2a2a;
  padding: 0 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
}
.account-setup-nav-rolesbtn:focus { outline: none; border-color: #c0392b; }
.account-setup-nav-roleslbl {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.account-setup-nav-rolespanel {
  position: absolute;
  top: 30px;
  left: 0;
  z-index: 40;
  background: #fff;
  border: 1px solid #ccd2d8;
  border-radius: 6px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.14);
  padding: 6px;
  min-width: 150px;
}
.account-setup-nav-rolesopt {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #2a2a2a;
  padding: 3px 4px;
  cursor: pointer;
  white-space: nowrap;
}
.account-setup-nav-rolesopt:hover { background: #f4f4f5; }
.account-setup-nav-roleshint {
  font-size: 10px;
  color: #999;
  padding: 5px 4px 2px;
  border-top: 1px solid #eee;
  margin-top: 4px;
}
/* "Hide from" button tints red once it actually excludes someone, so an exclusion is visible from
   the row without opening the panel (the label alone reads "No one" vs a role list). */
.account-setup-nav-rolesbtn.is-deny { border-color: #c0392b; color: #c0392b; }
/* ── DARK MODE ────────────────────────────────────────────────────────────────────────────────
   These two dropdown panels are the only part of Menu Navigation that never got dark styling —
   they kept the light #fff surface and #2a2a2a text, so on the dark Account Setup sheet they
   rendered as glaring white boxes with near-unreadable labels. Surfaces here match the widget's
   existing dark palette (card #232830 / input #11151a / border #3a414a / text #e3e7eb). */
html.dark-mode .account-setup-nav-rolesbtn {
  background: #11151a;
  border-color: #3a414a;
  color: #e3e7eb;
}
html.dark-mode .account-setup-nav-rolesbtn:focus { border-color: #7fbfe8; }
html.dark-mode .account-setup-nav-rolesbtn.is-deny { border-color: #e06a5c; color: #f0a8b1; }
html.dark-mode .account-setup-nav-rolespanel {
  background: #232830;
  border-color: #3a414a;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.55);
}
html.dark-mode .account-setup-nav-rolesopt { color: #e3e7eb; }
html.dark-mode .account-setup-nav-rolesopt:hover { background: #2a313a; }
html.dark-mode .account-setup-nav-roleshint {
  color: #9aa3ad;
  border-top-color: #3a414a;
}
/* "Show" column — the per-link visibility (DB Active) toggle. */
.asc-nav-on {
  flex: 0 0 auto;
  width: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.account-setup-nav-toggle {
  display: inline-flex;
  align-items: center;
  margin: 0;
  cursor: pointer;
}
.account-setup-nav-toggle input {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #c0392b;
}
.account-setup-nav-row.is-head .asc-nav-on {
  font-size: 10px;
}
.account-setup-nav-arrow {
  border: 1px solid #d6dade;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 9px;
  line-height: 1;
  padding: 2px 0;
  width: 22px;
  color: #5b6470;
}
.account-setup-nav-arrow:hover:not(:disabled) {
  border-color: #c0392b;
  color: #c0392b;
}
.account-setup-nav-arrow:disabled {
  opacity: 0.35;
  cursor: default;
}
.account-setup-nav-input {
  width: 100%;
  height: 30px;
  border: 1px solid #ccd2d8;
  border-radius: 5px;
  padding: 0 8px;
  font-size: 12px;
  color: #2a2a2a;
  background: #fff;
  box-sizing: border-box;
}
.account-setup-nav-input:focus {
  outline: none;
  border-color: #c0392b;
}
.account-setup-nav-input:disabled {
  background: #f0f0f0;
  color: #999;
}
.account-setup-nav-save {
  flex: 0 0 auto;
  height: 30px;
  padding: 0 12px;
  border: 1px solid #2c6fb3;
  background: #2c6fb3;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  border-radius: 5px;
  cursor: pointer;
  white-space: nowrap;
}
.account-setup-nav-save:hover:not(:disabled) {
  background: #245a92;
}
.account-setup-nav-save:disabled {
  opacity: 0.5;
  cursor: default;
}
.account-setup-nav-del {
  flex: 0 0 auto;
}
.account-setup-nav-status {
  font-size: 11.5px;
  font-style: italic;
  color: #9aa3ad;
  padding: 2px 2px 8px 2px;
}
.account-setup-nav-status.is-ok {
  color: #2e7d32;
  font-style: normal;
  font-weight: 600;
}
.account-setup-nav-add {
  display: flex;
  align-items: stretch;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed #eadfdc;
}
.account-setup-nav-add .account-setup-nav-input {
  flex: 1 1 140px;
  height: 34px;
}
.account-setup-nav-addbtn {
  flex: 0 0 auto;
  height: 34px;
}
.account-setup-copy-result {
  margin-top: 10px;
  font-size: 12.5px;
  font-weight: 600;
  padding: 8px 10px;
  border-radius: 5px;
}
.account-setup-copy-result.is-ok {
  background: #eef8f1;
  border: 1px solid #cde8d6;
  color: #1f7a3a;
}
.account-setup-copy-result.is-err {
  background: #fdf3f2;
  border: 1px solid #f3c9c4;
  color: #8a2a20;
}

/* ============================================================
   Copy history + Undo
   ============================================================ */
.account-setup-history {
  margin-top: 14px;
  border-top: 1px solid #eef0f2;
  padding-top: 8px;
}
.account-setup-history-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  background: none;
  border: none;
  padding: 6px 2px;
  cursor: pointer;
  text-align: left;
}
.account-setup-history-title {
  font-size: 13px;
  font-weight: 700;
  color: #2a2a2a;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.account-setup-history-list {
  list-style: none;
  margin: 4px 0 0 0;
  padding: 0;
}
.account-setup-history-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) max-content max-content max-content;
  align-items: center;
  gap: 10px;
  padding: 8px 6px;
  border-bottom: 1px solid #f2f2f2;
}
.account-setup-history-item.is-undone {
  opacity: 0.55;
}
/* Column-align the action buttons across rows (col 2 = Reactivate/Re-link, 3 = Delete User,
   4 = ×) so they line up even when a row is missing one of them. */
.account-setup-history-info {
  grid-column: 1;
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}
.account-setup-history-undo,
.account-setup-history-undone,
.account-setup-history-perm { grid-column: 2; justify-self: end; }
.account-setup-history-deluser { grid-column: 3; justify-self: end; }
.account-setup-history-del { grid-column: 4; justify-self: end; }
.account-setup-history-route {
  font-size: 13px;
  font-weight: 600;
  color: #2a2a2a;
}
.account-setup-history-when {
  font-size: 11px;
  color: #8a94a0;
}
.account-setup-history-remain {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
  padding: 0;
  border: 0;
  background: none;
  font-size: 11px;
  font-weight: 600;
  color: #d97706;
  cursor: pointer;
  text-align: left;
}
.account-setup-history-remain:hover { text-decoration: underline; }
.account-setup-cd-caret { font-size: 9px; line-height: 1; }
.account-setup-history-detail {
  flex: 0 0 100%;
  width: 100%;
  margin-top: 6px;
  padding: 8px 10px;
  background: #fafbfc;
  border: 1px solid #eef0f2;
  border-radius: 6px;
}
.account-setup-cd-group { margin-bottom: 8px; }
.account-setup-cd-group:last-child { margin-bottom: 0; }
.account-setup-cd-head {
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9aa3ad;
  margin-bottom: 3px;
}
.account-setup-cd-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
  padding: 2px 0;
  font-size: 12px;
  color: #4a5568;
}
.account-setup-cd-row.is-gone {
  color: #b0392b;
}
.account-setup-cd-mark {
  flex: 0 0 14px;
  font-weight: 700;
  color: #1f7a3a;
}
.account-setup-cd-row.is-gone .account-setup-cd-mark {
  color: #c0392b;
}
.account-setup-cd-name {
  flex: 1 1 auto;
  min-width: 0;
}
.account-setup-cd-sub {
  color: #8a94a0;
  font-size: 10.5px;
  margin-left: 6px;
}
.account-setup-cd-status {
  flex: 0 0 auto;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #9aa3ad;
}
.account-setup-cd-row.is-gone .account-setup-cd-status {
  color: #c0392b;
}
.account-setup-history-undo {
  flex: 0 0 auto;
  border: 1px solid #c0392b;
  background: #fff;
  color: #c0392b;
  font-size: 12px;
  font-weight: 600;
  border-radius: 5px;
  padding: 4px 12px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.account-setup-history-undo:hover:not(:disabled) {
  background: #c0392b;
  color: #fff;
}
.account-setup-history-undo:disabled {
  opacity: 0.6;
  cursor: default;
}
.account-setup-history-undone {
  flex: 0 0 auto;
  font-size: 11px;
  font-weight: 600;
  color: #1f7a3a;
}
/* Permanent (hard-deleted) bulk entry — neutral, truthful tag (no undo). */
.account-setup-history-perm {
  flex: 0 0 auto;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8a8f98;
  background: #f0f1f3;
  border: 1px solid #e0e2e6;
  border-radius: 4px;
  padding: 2px 7px;
}
/* Add / Remove tag on a history row (which action it records). */
.account-setup-history-tag {
  display: inline-block;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-radius: 3px;
  padding: 1px 5px;
  margin-right: 6px;
  vertical-align: middle;
}
.account-setup-history-tag.is-add {
  color: #1f7a3a;
  background: #e8f5ec;
  border: 1px solid #cfe9d7;
}
.account-setup-history-tag.is-del {
  color: #c0392b;
  background: #fdecea;
  border: 1px solid #f5cfc9;
}
/* Per-entry delete (×) — audit housekeeping, deliberately subtle. */
.account-setup-history-del {
  flex: 0 0 auto;
  border: none;
  background: transparent;
  color: #b0b5bd;
  font-size: 16px;
  line-height: 1;
  padding: 2px 4px;
  cursor: pointer;
  border-radius: 4px;
}
.account-setup-history-del:hover:not(:disabled) {
  color: #c0392b;
  background: #fbeae8;
}
/* Permanently delete the user account (beside Reactivate) — destructive. */
.account-setup-history-deluser {
  flex: 0 0 auto;
  border: 1px solid #e3b4ae;
  background: #fff;
  color: #c0392b;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  padding: 3px 8px;
  cursor: pointer;
  border-radius: 4px;
}
.account-setup-history-deluser:hover:not(:disabled) {
  background: #c0392b;
  color: #fff;
}
.account-setup-history-deluser:disabled {
  opacity: 0.5;
  cursor: default;
}
.account-setup-history-del:disabled {
  opacity: 0.5;
  cursor: default;
}
/* "Delete all" bar above the history list (clears the audit log, not the setup). */
.account-setup-history-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 6px 2px 6px;
}
.account-setup-history-clear {
  border: 1px solid #e0e2e6;
  background: #fff;
  color: #8a8f98;
  font-size: 11px;
  font-weight: 600;
  border-radius: 5px;
  padding: 3px 10px;
  cursor: pointer;
}
.account-setup-history-clear:hover {
  border-color: #c0392b;
  color: #c0392b;
}
.account-setup-history-redone-wrap {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}
.account-setup-history-redo {
  border: 1px solid #1f7a3a;
  background: #fff;
  color: #1f7a3a;
  font-size: 12px;
  font-weight: 600;
  border-radius: 5px;
  padding: 3px 10px;
  cursor: pointer;
}
.account-setup-history-redo:hover:not(:disabled) {
  background: #1f7a3a;
  color: #fff;
}
.account-setup-history-redo:disabled {
  opacity: 0.6;
  cursor: default;
}
.account-setup-history-note {
  font-size: 10.5px;
  color: #aab2bb;
  margin: 6px 2px 0 2px;
  font-style: italic;
}
</style>
